/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Billing API
 * API for managing billing lines and batches.  Billing lines are generated from completed appointments and grouped into batches for export to health insurers. Each line represents one service provided to one client. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * An afspraak\'s billing regels, each with its declarations in order (#4733)
 */
export interface AppointmentDeclarationHistoryResponse {
    /**
     * The afspraak the regels belong to
     */
    'appointmentId': string;
    /**
     * The afspraak\'s state as the agenda\'s euroteken states it — the least advanced of its declarable regels. Absent when it has none. 
     */
    'declarationState'?: DeclarationState | null;
    /**
     * Every regel on the afspraak in the order it was created, so a credit and a corrected replacement follow the original whose `id` their `line.originalLineId` names. 
     */
    'lines': Array<BillingLineHistoryResponse>;
}


export interface ArchiveBillingBatchRequest {
    /**
     * True to mark the batch as verwerkt (moving it to the archive), false to return it to the active list. 
     */
    'archived': boolean;
}
/**
 * The pre-submission controle findings for a batch that can be exported. Blocking violations never appear here — they refuse the export with a 422. 
 */
export interface BatchControleReportResponse {
    /**
     * The billing batch the controles were run over
     */
    'batchId': string;
    /**
     * The warning-level violations found; empty when the message is clean
     */
    'warnings': Array<Gds801ControleViolation>;
}
/**
 * A regel a batch declared and has since released for a redeclaration (#4624).  `line` is the regel as it stands now — its client, consult and current status. The other properties are this batch\'s own record of it: the reference it was declared under, the amount it declared, and the verdict the retour gave, all kept when the regel left. The regel itself carries no verdict once it is released, because a regel back in the pool is going out again. 
 */
export interface BatchReleasedLineResponse {
    'line': BillingLineResponse;
    /**
     * The `Referentienummer` this batch declared the regel under
     */
    'referentienummer'?: string | null;
    /**
     * What this batch declared for the regel
     */
    'declaredAmountEuro': number;
    /**
     * `APPROVED` or `REJECTED` as the retour judged it; absent while none has
     */
    'verdictStatus'?: BillingLineStatus | null;
    /**
     * What the insurer granted for this declaration
     */
    'approvedAmount'?: number | null;
    /**
     * The retourcode (COD954-VEKT) the insurer gave this declaration
     */
    'rejectionCode'?: string | null;
    /**
     * The insurer\'s own words, or the code\'s meaning where it sent none
     */
    'rejectionReason'?: string | null;
    /**
     * The COD954-VEKT `Betekenis` of `rejectionCode`, resolved per response
     */
    'rejectionCodeBetekenis'?: string | null;
    /**
     * The COD954-VEKT `Toelichting1`, published for only some codes
     */
    'rejectionCodeToelichting'?: string | null;
    /**
     * The batch the regel is in now; absent while it has not been declared again
     */
    'currentBatchId'?: string | null;
    /**
     * That batch\'s number, for \"Opnieuw gedeclareerd in batch …\"
     */
    'currentBatchNumber'?: string | null;
}


/**
 * One file of the aanvullende retourinformatie an insurer sent about a batch
 */
export interface BatchRetourBijlageResponse {
    'id': string;
    /**
     * The insurer\'s own name for the file
     */
    'fileName': string;
    'sizeBytes': number;
    /**
     * When VECOZO received it from the insurer, epoch milliseconds
     */
    'ontvangenAt': number;
}
/**
 * A single element a retourcode refers to (`BetrokkenElement`). `elementwaarde` is whatever the insurer echoed back, which on a verzekerde-level code can be a BSN. 
 */
export interface BatchRetourElement {
    /**
     * The element the code refers to, e.g. `Declarant.Zorgaanbiedercode`
     */
    'elementnaam': string;
    /**
     * The value the insurer echoed back
     */
    'elementwaarde': string;
}
/**
 * One message-level `Feedback` block of a retourbericht.
 */
export interface BatchRetourFeedback {
    'niveau': Gds802FeedbackNiveau;
    /**
     * The 4-digit Vektis retourcode (COD954-VEKT)
     */
    'retourcode': string;
    /**
     * The COD954-VEKT `Betekenis` of `retourcode`, resolved per response rather than stored — the same resolver `BillingLineResponse.rejectionCodeBetekenis` reads. Null when the code is not in the list Scrivio ships. 
     */
    'betekenis'?: string | null;
    /**
     * The COD954-VEKT `Toelichting1`, published for only some codes
     */
    'toelichting'?: string | null;
    /**
     * The insurer\'s own free text, when it sent any
     */
    'commentaar'?: string | null;
    /**
     * The element(s) the insurer named as the reason, in message order
     */
    'betrokkenElementen': Array<BatchRetourElement>;
}


/**
 * What the insurer answered for a batch as a whole (#4420).  Most retouren answer no individual regel: of the 97 in one measured VECOZO download, 59 carried no `Verzekerde`/`Prestatie` at all and stated their whole verdict here — the two `Overzicht` totals plus `Feedback` blocks on `Header`, `DeclaratieContext` and `Overzicht`. The totals are stated whether or not there are per-regel rows, so a whole-invoice approval and a whole-invoice rejection both read as an answer rather than as an empty table.  `totaalDeclaratiebedrag` and `totaalToegekendBedrag` are signed the way a regel\'s granted amount is: a credit total is negative. 
 */
export interface BatchRetourResponse {
    /**
     * Who sent a retour pulled from the VECOZO berichtenservice. `VECOZO` means its XSLT check rejected the declaration and it never reached the insurer. Absent on an uploaded retour, whose envelope Scrivio never saw. 
     */
    'afzender'?: Gds802RetourAfzender | null;
    /**
     * What became of this retour when it was imported. Anything other than `APPLIED` means the batch\'s regels do not carry this answer. Absent on a retour stored before the outcome was recorded. 
     */
    'outcome'?: Gds802ReturnFileOutcome | null;
    /**
     * `Header/Verzenddatum` — the date the insurer sent this answer
     */
    'verzenddatum'?: string | null;
    /**
     * `Overzicht/TotaalDeclaratiebedragInclBtw` — what the insurer read as declared
     */
    'totaalDeclaratiebedrag'?: number | null;
    /**
     * `Overzicht/TotaalToegekendBedragInclBtwFinancieel` — what it granted, financially settled
     */
    'totaalToegekendBedrag'?: number | null;
    /**
     * The message-level `Feedback` blocks in message order. Empty when the insurer attached none, which is what a whole-invoice approval looks like. 
     */
    'feedback': Array<BatchRetourFeedback>;
}


/**
 * One verzekeraarfactuur of a batch, and whether it reached the boekhouding (#4727)
 */
export interface BatchVerkoopfactuurResponse {
    'invoiceId': string;
    'invoiceNumber'?: string | null;
    /**
     * The declared total, which is what is booked
     */
    'totalEuro': number;
    /**
     * When it — or the version it corrects — was mailed to the boekhouding
     */
    'mailedAt'?: number | null;
    'snelStartSyncState'?: SnelStartSyncState;
    /**
     * SnelStart\'s id of the verkoopboeking; the API offers no link to it
     */
    'snelStartBoekingId'?: string | null;
    'snelStartSyncedAt'?: number | null;
    /**
     * Why the last attempt did not book, in Dutch
     */
    'snelStartLastError'?: string | null;
    'snelStartFailure'?: SnelStartBookingFailure;
}


/**
 * VECOZO\'s Berichtstatus V3 for the message. Absent until VECOZO has reported one this build knows; a value VECOZO adds later is not reported until Scrivio knows it. 
 */

export const Berichtstatus = {
    Ontvangen: 'ONTVANGEN',
    DeelsOntvangen: 'DEELS_ONTVANGEN',
    DeelberichtAanleveringAfgekeurd: 'DEELBERICHT_AANLEVERING_AFGEKEURD',
    WordtVerwerkt: 'WORDT_VERWERKT',
    AfgekeurdDoorDienst: 'AFGEKEURD_DOOR_DIENST',
    VerwerkingVoltooid: 'VERWERKING_VOLTOOID',
    WordtAangeboden: 'WORDT_AANGEBODEN',
    Bezorgd: 'BEZORGD',
    AanbiedenMislukt: 'AANBIEDEN_MISLUKT',
    BezorgingAfgebroken: 'BEZORGING_AFGEBROKEN'
} as const;

export type Berichtstatus = typeof Berichtstatus[keyof typeof Berichtstatus];


/**
 * One row of the \"Te declareren\" list (#4741, #4742) — one thing that goes to the insurer. Most rows are an openstaande prestatie: an appointment-client row as one behandelaar declares it. A consult that several behandelaren declare — a groepsconsult led together, or one cliënt seen by two or more — is one of these per behandelaar, each at its own prestatiecode and amount; every other consult is one, the organiser\'s. A row whose `soort` is CREDIT or CORRECTIE is instead a regel whose prestatie the payer already has, named by `billingLineId`. Every row is identified by `entry`. 
 */
export interface BillableAppointmentDto {
    /**
     * The row\'s identity: `prestatie:<appointmentClientId>:<practitionerId>` or `line:<billingLineId>`. What the selection holds and what `createBillingBatches` takes. 
     */
    'entry': string;
    'soort': DeclarationSoort;
    /**
     * The regel a CREDIT or CORRECTIE row is; null for a prestatie still to declare.
     */
    'billingLineId'?: string | null;
    /**
     * The declared regel a credit post reverses or a correctie replaces — what makes the two one correction. Null for a prestatie row. 
     */
    'originalLineId'?: string | null;
    /**
     * The `entry` of the other half of a credit-and-correct pair still in the list. The two are declared together, so they are selected together. Null for every other row. 
     */
    'linkedEntry'?: string | null;
    /**
     * The regel\'s own status on a CREDIT or CORRECTIE row. A batch takes READY only, so a DRAFT regel is listed and cannot be selected yet. Null for a prestatie row. 
     */
    'lineStatus'?: BillingLineStatus | null;
    /**
     * Appointment ID
     */
    'appointmentId': string;
    /**
     * AppointmentClient ID (for group appointments)
     */
    'appointmentClientId'?: string | null;
    /**
     * Client ID
     */
    'clientId': string;
    /**
     * Client display name
     */
    'clientName': string;
    /**
     * The behandelaar whose prestatie this row is (#4741)
     */
    'practitionerId': string;
    /**
     * Practitioner display name
     */
    'practitionerName': string;
    /**
     * Regiebehandelaar of the client\'s care trajectory on this row\'s service date. Null when the client has no active trajectory then, or when that trajectory names no regiebehandelaar. 
     */
    'regiebehandelaarId'?: string | null;
    /**
     * Regiebehandelaar display name; null exactly when regiebehandelaarId is null
     */
    'regiebehandelaarName'?: string | null;
    /**
     * Date of service
     */
    'serviceDate': string;
    /**
     * Duration in minutes
     */
    'durationMinutes': number;
    /**
     * Duration in quarters (15-min blocks)
     */
    'quarters': number;
    /**
     * Appointment title
     */
    'appointmentTitle'?: string | null;
    /**
     * Linked service code (via appointment type)
     */
    'serviceCodeId'?: string | null;
    /**
     * Service code string
     */
    'serviceCode'?: string | null;
    /**
     * Client\'s insurer ID
     */
    'insurerId'?: string | null;
    /**
     * Client\'s insurer name
     */
    'insurerName'?: string | null;
    /**
     * Related care trajectory ID
     */
    'careTrajectoryId'?: string | null;
    /**
     * Estimated billing amount (if calculable)
     */
    'estimatedAmount'?: number | null;
    /**
     * For group sessions (#3322): the 15-minute slice composition this client\'s billing will actually generate — GC code sized per concurrent group size, CO fallback when the client is alone in a slice. `estimatedAmount` is the sum of these slices\' tariffs. Null for individual and MDO rows.  Each behandelaar of a session led by several zorgverleners declares the groepsconsult separately (#4498), and since #4741 each of those prestaties is a row of its own: the breakdown is this row\'s behandelaar\'s kwartieren, and `estimatedAmount` is their sum. 
     */
    'sliceBreakdown'?: Array<BillableGroupSliceDto> | null;
    /**
     * 100% NZa max amount for the same service/duration, shown as context under the contracted amount
     */
    'nzaMaxAmount'?: number | null;
    /**
     * Percentage of the NZa maximum this row is priced at, whatever set it: a contract rule\'s percentage, the practice\'s own tariff (#4392), or 100.00 when neither did. Null for private rows. `ownTariffPercentage` beside it is what says the second of those three answered. 
     */
    'contractedPercentage'?: number | null;
    /**
     * Which contract rule produced this row\'s tariff (#4032), so a practice asking \"waarom is dit consult € 47,74\" is answered without reconstructing the precedence by hand. Null exactly when `isContracted` is false — an uncontracted row is priced by no rule at all.  It describes the row\'s own flat price, as `contractedPercentage` beside it does. A group session\'s `estimatedAmount` is the sum of its slices, each of which resolves its own prestatiecode and so can be priced by a different rule; the per-slice answer is on `sliceBreakdown`. 
     */
    'priceRuleScope'?: ContractPriceScope | null;
    /**
     * The practice\'s own tariff where *it* priced this row (#4392) — the rung below every contract rung and above the full NZa maximum, for a contractvrije practice that charges a fixed percentage of it.  Never set together with `priceRuleScope`: between them they name what produced the price, and both null means the row is at the full NZa maximum. Set is what tells the overview to show the charged amount under the maximum rather than \"geen contract\" — a discounted amount with no stated origin is worse than the 100% it replaces. 
     */
    'ownTariffPercentage'?: number | null;
    /**
     * Whether an active insurer contract covers this appointment. False for non-contracted insurer care and for private rows. The declaration UI uses this to suppress the non-contracted (reduced) amount, showing only the 100% NZa figure for non-contracted care.
     */
    'isContracted': boolean;
    /**
     * Whether the client has a referral letter (verwijsbrief)
     */
    'hasVerwijsbrief': boolean;
    /**
     * Whether the client has care typing (zorgvraagtypering) for this service date
     */
    'hasZorgvraagtypering': boolean;
    /**
     * Whether the client has a BSN number
     */
    'hasBsn': boolean;
    /**
     * Whether the client has an insurer assigned
     */
    'hasInsurer': boolean;
    /**
     * Whether the practitioner has an AGB code
     */
    'hasAgbCode': boolean;
    /**
     * Whether the care trajectory has a valid setting
     */
    'hasSetting': boolean;
    /**
     * The row is billed at a `clientInvoiced` service code (OV0165): insured care invoiced to the cliënt on a nota of its own per afspraak, never declared. Such a row is listed by `findPrivateBillableAppointments` and never by `findBillableAppointments`, so it is false on every row of the latter. 
     */
    'clientInvoiced': boolean;
    /**
     * Number of blocking signals (0 = ready to invoice)
     */
    'signalCount': number;
    /**
     * List of blocking signals preventing invoicing for this fact
     */
    'signals': Array<BlockingSignalDto>;
}


/**
 * One page of billable appointments (#3812).  A billable row is derived rather than read off one table: it resolves the insurance policy, tariff and care trajectory in force on the service date, and its declaration readiness from the blocking signals. Both overviews derive it in SQL (`billable_row_v`, #3973) and filter, order and cut the page there (#3974, #3975). `totalElements` counts the filtered set; `readyCount` and `blockedCount` split it by declaration readiness and are counted before the `status` filter is applied, so a UI can label its status tabs without a second request.  This response answers at the grain of the **consult**, and only that. The client grain is `findBillableClients` (#4175), which pages clients; the `clientTotals` field that used to ride along here went with #4178. 
 */
export interface BillableAppointmentPage {
    /**
     * Billable appointments on the current page
     */
    'content': Array<BillableAppointmentDto>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of billable rows matching the filters
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
    /**
     * Rows with no blocking signals, across the whole filtered set
     */
    'readyCount': number;
    /**
     * Rows with at least one blocking signal, across the whole filtered set
     */
    'blockedCount': number;
}
/**
 * Declaration readiness of a billable row, derived from its blocking signals: READY has none, BLOCKED has at least one. Rendered as \"Factureerbaar\" / \"Incompleet\" in the declaratie overview. 
 */

export const BillableAppointmentStatus = {
    Ready: 'READY',
    Blocked: 'BLOCKED'
} as const;

export type BillableAppointmentStatus = typeof BillableAppointmentStatus[keyof typeof BillableAppointmentStatus];


/**
 * One client\'s whole contribution to the declaratie queue (#4175): how many billable consults, what they add up to, and the two facts a client row can state about all of them at once.  Every figure spans the client\'s **entire filtered set**, never a page of it — which is what lets the consults themselves be fetched a page at a time from `findBillableAppointments?clientId=` without the header above them moving.  Since #4509 the row also carries its client\'s complete **selectable** set, so the grouped queue needs no request per client before a client can be ticked. It is produced under the same filters as the aggregates above, so a narrowed queue narrows it with them. 
 */
export interface BillableClientDto {
    /**
     * Client ID
     */
    'clientId': string;
    /**
     * Client display name
     */
    'clientName': string;
    /**
     * Billable rows for this client across the whole filtered set
     */
    'consultCount': number;
    /**
     * Sum of the resolvable estimated amounts of those rows
     */
    'totalAmount': number;
    /**
     * How many of those rows have no resolvable amount, and so contribute nothing to `totalAmount`. Zero for a complete total. 
     */
    'unpricedCount': number;
    /**
     * The regiebehandelaar every one of this client\'s consults names, or null when they do not all name the same one — including when any of them names none. Null is \"there is no single answer\", never \"unknown\": picking one consult\'s would be a guess about the rest. 
     */
    'regiebehandelaarName'?: string | null;
    /**
     * The verzekeraar every one of this client\'s consults names, on the same terms as `regiebehandelaarName`. 
     */
    'insurerName'?: string | null;
    /**
     * Every one of this client\'s billable consults, never paged, at the nine fields selecting them needs. `consultCount` is its length.  It is complete on purpose: a client is ticked as a whole, and a batch is created from ids the browser has to be holding. A page of them would put the checkbox back behind a request. 
     */
    'selectableConsults': Array<BillableSelectableConsultDto>;
}
/**
 * One page of clients in the zorgverzekeraar declaratie queue (#4175).  `totalElements` is the number of **distinct clients** the filters match, so the pager counts what the screen lists. Each client appears on exactly one page. 
 */
export interface BillableClientPage {
    /**
     * The clients on this page, ordered by name. Always present: an empty array is \"these filters match no client\", which is a value rather than an absence. 
     */
    'content': Array<BillableClientDto>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of clients per page
     */
    'size': number;
    /**
     * Distinct clients matching the filters, across the whole filtered set
     */
    'totalElements': number;
    /**
     * Total number of pages of clients
     */
    'totalPages': number;
}
/**
 * One 15-minute slice of a group session\'s billing preview (#3322)
 */
export interface BillableGroupSliceDto {
    /**
     * Slice start, HH:mm (appointment timezone)
     */
    'startTime': string;
    /**
     * Slice end, HH:mm (appointment timezone)
     */
    'endTime': string;
    /**
     * Clients concurrently present in this slice — determines the GC code
     */
    'groupSize': number;
    /**
     * Prestatiecode this slice bills (GC sized to the group, or CO fallback)
     */
    'serviceCode'?: string | null;
    /**
     * Tariff for this slice (one kwartier)
     */
    'tariffEuro': number;
    /**
     * The behandelaar who declares this kwartier (#4498). A groepsconsult led by several zorgverleners is declared by each of them separately, so one quarter appears once per behandelaar — at the tariff for that behandelaar\'s own beroep, and at the full group size, which is never divided between them. 
     */
    'practitionerId'?: string | null;
    /**
     * Name of `practitionerId`, so a breakdown can name the behandelaar without a second lookup
     */
    'practitionerName'?: string | null;
    /**
     * Which contract rule produced this slice\'s tariff (#4032). Carried per slice because each slice resolves its own prestatiecode, so two slices of one session can be priced by different rules. Null when no contract rule priced the slice. 
     */
    'priceRuleScope'?: ContractPriceScope | null;
}


/**
 * One behandelaar\'s prestatie of one appointment-client row (#4741) — the grain of the declaratie list, where a consult several behandelaren declare is one row per behandelaar. 
 */
export interface BillablePrestatieRef {
    'appointmentClientId': string;
    /**
     * The behandelaar whose prestatie this is — the row\'s own practitionerId
     */
    'practitionerId': string;
}
/**
 * One billable consult of a client, at the fields the grouped declaratie queue needs to select it and to say what the resulting batch would be (#4509).  Every field here is one the queue already reads off the rich `BillableAppointmentDto`, and it carries nothing else: this array is complete for the client, so a field added here is paid for on every consult on screen.  `clientId` is deliberately absent — every one of these belongs to the `BillableClientDto` carrying them, so repeating it per consult would be the same uuid sent `consultCount` times.  **It states no `declaredAmount`.** What a row declares is decided from `estimatedAmount`, `isContracted` and `nzaMaxAmount` together, and that rule is already written once for every other caller (`utils/declaredAmount.ts`). A server-computed twin would be a second answer to the same question. 
 */
export interface BillableSelectableConsultDto {
    /**
     * The row\'s identity, as `BillableAppointmentDto.entry` states it — what a batch request names
     */
    'entry': string;
    'soort': DeclarationSoort;
    /**
     * The regel a CREDIT or CORRECTIE row is; null for a prestatie still to declare
     */
    'billingLineId'?: string | null;
    /**
     * The `entry` of the other half of a credit-and-correct pair, which is selected together with this one. Null for every other row. 
     */
    'linkedEntry'?: string | null;
    /**
     * Appointment ID
     */
    'appointmentId': string;
    /**
     * Practitioner (behandelaar) ID. Not a fact the queue displays here — it is what identifies the consult: the declaratie tables key a row on (appointment, client, practitioner) and the selection is held as those keys, so a slim row without it cannot name the same consult a rich `BillableAppointmentDto` of it does, and ticking a client would select rows no list could then show as ticked. 
     */
    'practitionerId': string;
    /**
     * AppointmentClient ID, which refines the appointment to exactly this client\'s row of it (group sessions). The other half of the batch request\'s grain. 
     */
    'appointmentClientId'?: string | null;
    /**
     * The verzekeraar this consult would be declared to. A batch is cut on (client, insurer) — a GDS801 message carries exactly one `Ontvanger`, so a client whose verzekeraar changed inside the period is two batches. Null is a consult no batch can carry. 
     */
    'insurerId'?: string | null;
    /**
     * Date of service — the selection\'s `periodStart`/`periodEnd` are its min and max
     */
    'serviceDate': string;
    /**
     * Contracted amount, where one could be resolved. Null is unpriceable, never zero.
     */
    'estimatedAmount'?: number | null;
    /**
     * 100% NZa max amount for the same service and duration — what non-contracted care declares
     */
    'nzaMaxAmount'?: number | null;
    /**
     * Whether an active insurer contract covers this consult, which is what decides between the two amounts above
     */
    'isContracted': boolean;
    /**
     * The row can be batched today. False for a consult a blocking signal reaches — the \"Actie nodig\" half of the queue, listed and counted and never ticked — and for a credit or correctie regel that is still DRAFT. 
     */
    'selectable': boolean;
}


/**
 * The electronic message the batch is declared with, derived from what the batch is: - GDS801: a verzekeraar batch — `sendBillingBatchToVecozo` sends it on the berichtenservice - JW323: a gemeente batch — its JW323 goes out through the Berichten outbox (`verzendIjwOutbox`) - GEEN: a paper batch, or one naming no verzekeraar; no message is sent 
 */

export const BillingBatchDeclaratiebericht = {
    Gds801: 'GDS801',
    Jw323: 'JW323',
    Geen: 'GEEN'
} as const;

export type BillingBatchDeclaratiebericht = typeof BillingBatchDeclaratiebericht[keyof typeof BillingBatchDeclaratiebericht];


export interface BillingBatchDetailResponse {
    'batch': BillingBatchResponse;
    /**
     * The regels on the batch now — the ones a per-regel action can apply to
     */
    'lines': Array<BillingLineResponse>;
    /**
     * The regels this batch declared that have since been released for a redeclaration (#4624), each with what this batch declared and what the insurer answered about it.  History rather than work: a released regel\'s actions belong to the batch it is in now, so none is offered here. Together with `lines` this is everything the batch declared. 
     */
    'releasedLines': Array<BatchReleasedLineResponse>;
    'retour'?: BatchRetourResponse;
    /**
     * The aanvullende retourinformatie the insurer sent about this batch over the VECOZO berichtenservice (ADR-049), oldest first; empty for a batch on the portal channel. 
     */
    'retourBijlagen': Array<BatchRetourBijlageResponse>;
    /**
     * The batch\'s verzekeraarfacturen as they stand in the boekhouding (#4727) — what the practice checks once SnelStart books them. Only those already produced. 
     */
    'verkoopfacturen'?: Array<BatchVerkoopfactuurResponse>;
}
export interface BillingBatchExportDetailResponse {
    'export': BillingBatchExportSummary;
    /**
     * SHA-256 of the bytes served, lowercase hex — matches a file on disk to this export
     */
    'contentHash': string;
    /**
     * Included batches first, in batch-number order, then the refused ones
     */
    'batches': Array<BillingBatchExportEntry>;
}
export interface BillingBatchExportEntry {
    'batchId': string;
    /**
     * The batch\'s number; absent only if the batch can no longer be found
     */
    'batchNumber'?: string | null;
    'outcome': BillingBatchExportOutcome;
    /**
     * The entry\'s file name inside a ZIP; absent for a single file and a refused batch
     */
    'entryName'?: string | null;
    /**
     * Why a `REFUSED` batch was left out; absent exactly when it was included
     */
    'refusalReason'?: string | null;
    /**
     * Whether the entry was the message stored when the batch was submitted (#3962) rather than a render of current data 
     */
    'fromStoredMessage': boolean;
}


/**
 * Which download an export records (#4781). Mirrored by `BillingBatchExportKind` in Kotlin, value for value.  - `ZIP` — \"Batches downloaden\": one archive of the selected batches\' messages - `SINGLE_BATCH` — one batch\'s own message - `LINE` — one regel\'s message, from the Declaratiebericht dialog 
 */

export const BillingBatchExportKind = {
    Zip: 'ZIP',
    SingleBatch: 'SINGLE_BATCH',
    Line: 'LINE'
} as const;

export type BillingBatchExportKind = typeof BillingBatchExportKind[keyof typeof BillingBatchExportKind];


export interface BillingBatchExportListResponse {
    /**
     * Newest first
     */
    'items': Array<BillingBatchExportSummary>;
}
/**
 * Whether a batch an export was asked to hold is in the file (#4781). Mirrored by `BillingBatchExportOutcome` in Kotlin, value for value.  - `INCLUDED` — its message is in the file - `REFUSED` — selected, and left out because its message could not be produced 
 */

export const BillingBatchExportOutcome = {
    Included: 'INCLUDED',
    Refused: 'REFUSED'
} as const;

export type BillingBatchExportOutcome = typeof BillingBatchExportOutcome[keyof typeof BillingBatchExportOutcome];


export interface BillingBatchExportSummary {
    'id': string;
    /**
     * The export\'s number, e.g. `EXP-2026-0042` — also in the downloaded file\'s name
     */
    'exportNumber': string;
    'kind': BillingBatchExportKind;
    /**
     * The name the file was served under
     */
    'fileName': string;
    /**
     * How many batches the file was asked to hold, included and refused alike
     */
    'batchCount': number;
    /**
     * When the file was downloaded (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Who downloaded it; absent when that user no longer exists
     */
    'createdByName'?: string | null;
}


/**
 * One batch to create — the selected rows of one cliënt at one verzekeraar. The verzekeraar is part of the key because a GDS801 message carries exactly one `Ontvanger`, so a batch cannot span two; a cliënt whose verzekeraar changed inside the period is therefore two batches.  No period: it is the span of the batched lines\' own service dates. A period the caller could state separately is one that can disagree with the lines it is meant to describe. 
 */
export interface BillingBatchGroup {
    /**
     * The cliënt this batch declares for
     */
    'clientId': string;
    /**
     * The insurer this batch declares to
     */
    'insurerId': string;
    /**
     * The rows to batch, by their `entry` — `prestatie:<appointmentClientId>:<practitionerId>` or `line:<billingLineId>`. See `createBillingBatches`. 
     */
    'entries': Array<string>;
}
export interface BillingBatchListResponse {
    /**
     * List of billing batches for current page
     */
    'content': Array<BillingBatchResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of billing batches
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
export interface BillingBatchResponse {
    /**
     * Billing batch unique identifier
     */
    'id': string;
    /**
     * Organization ID
     */
    'organizationId': string;
    /**
     * Batch number, `yyyy-NNNNN` (e.g. \"2025-00001\"). Numbers written before #4220 are three digits and are never rewritten, so both widths occur.
     */
    'batchNumber': string;
    'status': BillingBatchStatus;
    /**
     * Billing method for this batch (VECOZO or PAPER)
     */
    'billingMethod'?: BillingBatchResponseBillingMethodEnum;
    'declaratiebericht'?: BillingBatchDeclaratiebericht;
    /**
     * Insurer ID
     */
    'insurerId': string;
    /**
     * Insurer name
     */
    'insurerName'?: string | null;
    'gemeente'?: IjwGemeente;
    /**
     * The cliënt this batch declares for. `null` for a batch created before #4370, which groups a verzekeraar\'s lines across several cliënten — such a batch genuinely has no single cliënt and is not backfilled. 
     */
    'clientId'?: string | null;
    /**
     * Start date of the billing period
     */
    'periodStart': string;
    /**
     * End date of the billing period
     */
    'periodEnd': string;
    /**
     * Total number of billing lines in the batch
     */
    'totalLines': number;
    /**
     * Total amount of all billing lines
     */
    'totalAmountEuro': number;
    /**
     * What the insurer granted for this batch as a whole, or `null` when it has not said.  Derived on read, never stored, so what a retour means stays `Gds802ImportService`\'s answer alone. In order: the applied retourbericht\'s `TotaalToegekendBedragInclBtwFinancieel` when it carries one; otherwise the sum of the regels\' `approvedAmount`, and only when **every** regel has one; otherwise `null`.  Always `null` on a batch that has not been declared — `DRAFT` and `READY`. No insurer has seen it, so there is nothing it could have granted, and a concept batch built from regels released out of an earlier declaration would otherwise satisfy the second source exactly and report that declaration\'s answer as its own.  `null` is not zero and must never be rendered as `€ 0,00`: a consumer reading it as an amount would call every unanswered batch fully rejected. A batch the insurer genuinely granted nothing on carries `0`.  Signed like `totalAmountEuro`: a credit retour is negative, as `Gds802ImportService` stores it, so `totalAmountEuro - approvedAmountEuro` is the difference on either sign. 
     */
    'approvedAmountEuro'?: number | null;
    /**
     * The declared money on this batch nobody has settled yet — neither granted, written off, credited nor redeclared (#4625); the rule `openOnly` filters on, and the \"Openstaand\" column under the \"Gedeeltelijk goedgekeurd\" and \"Afgewezen\" filters.  `0` on an answered batch that is afgehandeld. `null` on a batch the insurer has not answered (any status before `APPROVED`, `REJECTED` or `PARTIALLY_APPROVED`): before a retour every regel is open, so the figure would only restate `totalAmountEuro`. 
     */
    'openAmountEuro'?: number | null;
    /**
     * Export timestamp (milliseconds since epoch)
     */
    'exportedAt'?: number | null;
    /**
     * User ID who exported the batch
     */
    'exportedById'?: string | null;
    /**
     * URL to the export file. Not the stored declaration message — nothing writes this today, and whether a batch\'s GDS801 message was kept is `declarationMessageStoredAt`. 
     */
    'exportFileUrl'?: string | null;
    /**
     * Format of the export (csv, excel, istandaarden)
     */
    'exportFormat'?: string | null;
    /**
     * Submission timestamp (milliseconds since epoch)
     */
    'submittedAt'?: number | null;
    /**
     * VECOZO reference number
     */
    'vecozoReference'?: string | null;
    /**
     * Timestamp (milliseconds since epoch) this batch\'s GDS801 declaration message was stored as the message that was sent, or null when no stored message exists.  Non-null means `GET /api/v1/billing/batches/{batchId}/export` serves those exact bytes — what the insurer received, unchanged by anything edited since. Null means the export renders from **current** data, which can differ from what was actually sent; there is deliberately no backfill, so a batch submitted before message storage existed stays null for ever. This is the field a UI reads to label the download, and it says nothing about `archivedAt` (verwerkt), which is a separate lifecycle.  Reaching it needs `billing.read` — the operations that return this batch and the export it describes are gated the same way. 
     */
    'declarationMessageStoredAt'?: number | null;
    /**
     * Timestamp (milliseconds since epoch) the batch was marked verwerkt, or null when it is still in the active list. Independent of the status lifecycle. 
     */
    'archivedAt'?: number | null;
    /**
     * User ID who marked the batch as verwerkt
     */
    'archivedById'?: string | null;
    /**
     * Notes
     */
    'notes'?: string | null;
    /**
     * Creation timestamp (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Last update timestamp (milliseconds since epoch)
     */
    'updatedAt': number;
    /**
     * When this batch\'s verzekeraarfacturen were mailed to the practice\'s boekhouding (milliseconds since epoch) — the moment the last of them went, and only once **every** one has (#4621). `null` for a batch not sent, and for a batch declaring for several cliënten of which only some invoices went: that one still has something left to send. 
     */
    'boekhoudingSentAt'?: number | null;
    'snelStartBoekingStatus'?: SnelStartBatchBookingStatus;
    'vecozoVerzending'?: BillingBatchVecozoVerzending;
    /**
     * On a gemeente batch, its JW323 while that waits in the Berichten outbox (queued or downloaded, not yet sent) — the id `verzendIjwOutbox` sends it under. `null` on any other batch, before the batch is locked, and once the JW323 has gone out. 
     */
    'jw323BerichtId'?: string | null;
    'jw323Stand'?: UitgaandBerichtstand | null;
}

export const BillingBatchResponseBillingMethodEnum = {
    Vecozo: 'VECOZO',
    Paper: 'PAPER'
} as const;

export type BillingBatchResponseBillingMethodEnum = typeof BillingBatchResponseBillingMethodEnum[keyof typeof BillingBatchResponseBillingMethodEnum];

/**
 * One stage of the batch backlog, over every non-archived batch in it — never over a page of them.  An empty stage is three zeroes and a null `oldestSince`, never an absent object: \"geen batches\" and \"niet geladen\" are different things on the bar above the queue. 
 */
export interface BillingBatchStageFigures {
    /**
     * The statuses this stage covers — the server\'s definition, stated so a client can narrow `listBillingBatches` to exactly the set these figures count (#4653) without keeping a second list of its own (#4518). 
     */
    'statuses': Array<BillingBatchStatus>;
    /**
     * Batches in this stage.
     */
    'batches': number;
    /**
     * Declaratieregels across them, summed on each batch\'s `totalLines`.
     */
    'lines': number;
    /**
     * What they declare, summed on each batch\'s `totalAmountEuro` — what the practice sent, never what an insurer granted. That is per batch, `approvedAmountEuro`. 
     */
    'amount': number;
    /**
     * When the oldest batch in this stage entered it, in milliseconds since epoch, or null when the stage is empty. A batch\'s age in the queue is measured from the moment it reached the stage rather than from the moment it was created, so this is `createdAt` for `inBatch` and, for `awaitingReturn`, `submittedAt` falling back to `updatedAt` for a batch that reached SUBMITTED without one. 
     */
    'oldestSince'?: number | null;
}
/**
 * The batch backlog per stage. Both stages are always present: which statuses each covers is stated by `getBillingBatchStageSummary` and is the server\'s alone. 
 */
export interface BillingBatchStageSummaryResponse {
    'inBatch': BillingBatchStageFigures;
    'awaitingReturn': BillingBatchStageFigures;
}
/**
 * Billing batch lifecycle status: - DRAFT: Initial state, batch can be edited - READY: All lines validated, ready for export - EXPORTED: Export file generated and downloaded - SUBMITTED: Submitted to insurer via VECOZO - APPROVED: All lines approved by insurer - REJECTED: Batch rejected by insurer - PARTIALLY_APPROVED: Some lines approved, some rejected 
 */

export const BillingBatchStatus = {
    Draft: 'DRAFT',
    Ready: 'READY',
    Exported: 'EXPORTED',
    Submitted: 'SUBMITTED',
    Approved: 'APPROVED',
    Rejected: 'REJECTED',
    PartiallyApproved: 'PARTIALLY_APPROVED'
} as const;

export type BillingBatchStatus = typeof BillingBatchStatus[keyof typeof BillingBatchStatus];


/**
 * Where one send of a batch over the berichtenservice stands: - PENDING: the call is out, or its process died before recording the answer - DELIVERED: VECOZO accepted it (204); `berichtstatus` says what happened next - REFUSED: VECOZO refused the request itself (400/422); the message was not taken in - FAILED: no answer from VECOZO about the message - ABANDONED: a PENDING whose process never came back - REJECTED: delivered, then not forwarded to the insurer (\"Afgekeurd door VECOZO\"); the   batch may be sent again 
 */

export const BillingBatchVecozoSendStatus = {
    Pending: 'PENDING',
    Delivered: 'DELIVERED',
    Refused: 'REFUSED',
    Failed: 'FAILED',
    Abandoned: 'ABANDONED',
    Rejected: 'REJECTED'
} as const;

export type BillingBatchVecozoSendStatus = typeof BillingBatchVecozoSendStatus[keyof typeof BillingBatchVecozoSendStatus];


/**
 * The batch\'s latest send over the VECOZO berichtenservice (ADR-049), or absent when it was never sent that way. VECOZO\'s 204 on submit only means \"received\"; what became of the message after that is `berichtstatus`, asked of Berichtstatus V3 every few minutes. 
 */
export interface BillingBatchVecozoVerzending {
    'status': BillingBatchVecozoSendStatus;
    /**
     * When the send was made (milliseconds since epoch)
     */
    'sentAt': number;
    'berichtstatus'?: Berichtstatus;
    /**
     * When VECOZO\'s status last changed (milliseconds since epoch), by VECOZO\'s clock
     */
    'berichtstatusChangedAt'?: number | null;
    /**
     * The failed checks VECOZO reported with `berichtstatus`; empty when it reported none.
     */
    'meldingen': Array<VecozoMelding>;
}


export interface BillingCalculationRequest {
    /**
     * Service code ID
     */
    'serviceCodeId': string;
    /**
     * Insurer ID
     */
    'insurerId': string;
    /**
     * Date of service
     */
    'serviceDate': string;
    /**
     * Number of quarters (15-min blocks)
     */
    'quarters': number;
    /**
     * Beroep of the practitioner who delivers the consult (`users.profession_code`), which is what a beroep-scoped contract price rule is matched on. Omit it and no beroep rule can apply — the calculation falls through to the behandelsetting rule and then the contract default. 
     */
    'professionCode'?: string | null;
}
export interface BillingCalculationResponse {
    /**
     * Service code ID
     */
    'serviceCodeId': string;
    /**
     * Service code string
     */
    'serviceCode': string;
    /**
     * Service code description
     */
    'serviceDescription'?: string | null;
    /**
     * Insurer ID
     */
    'insurerId': string;
    /**
     * Insurer name
     */
    'insurerName': string;
    /**
     * NZa tariff per quarter
     */
    'nzaTariffEuro': number;
    /**
     * Effective tariff after contract percentage
     */
    'effectiveTariffEuro': number;
    /**
     * Contract percentage applied (if contracted)
     */
    'contractPercentage'?: number | null;
    /**
     * Number of quarters
     */
    'quarters': number;
    /**
     * Total calculated amount
     */
    'amountEuro': number;
    'billingType': BillingType;
    /**
     * Whether there\'s an active contract
     */
    'isContracted': boolean;
    /**
     * Contract ID (if contracted)
     */
    'contractId'?: string | null;
}


/**
 * Billing category defining how an appointment/service is billed: - INSURED: Billed through VECOZO to health insurers - PRIVATE: Billed directly to the client via invoices - NON_BILLABLE: Not billed (internal meetings, admin time) 
 */

export const BillingCategory = {
    Insured: 'INSURED',
    Private: 'PRIVATE',
    NonBillable: 'NON_BILLABLE'
} as const;

export type BillingCategory = typeof BillingCategory[keyof typeof BillingCategory];


/**
 * One regel and every batch it was declared in (#4733)
 */
export interface BillingLineHistoryResponse {
    'line': BillingLineResponse;
    /**
     * Where this regel\'s declaration stands now. Absent for a credit regel, which is a chapter of its original\'s story rather than a declaration of its own. 
     */
    'declarationState'?: DeclarationState | null;
    /**
     * Every batch the regel was declared in, oldest first
     */
    'declarations': Array<LineDeclarationResponse>;
}


export interface BillingLineListResponse {
    /**
     * List of billing lines for current page
     */
    'content': Array<BillingLineResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of billing lines
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
export interface BillingLineResponse {
    /**
     * Billing line unique identifier
     */
    'id': string;
    /**
     * Organization ID
     */
    'organizationId': string;
    /**
     * Source appointment ID
     */
    'appointmentId'?: string | null;
    /**
     * AppointmentClient ID (for group appointments)
     */
    'appointmentClientId'?: string | null;
    /**
     * Client ID
     */
    'clientId': string;
    /**
     * Client display name
     */
    'clientName'?: string;
    /**
     * Practitioner ID
     */
    'practitionerId': string;
    /**
     * Practitioner display name
     */
    'practitionerName'?: string;
    /**
     * Practitioner function ID
     */
    'practitionerFunctionId'?: string | null;
    /**
     * Care trajectory ID
     */
    'careTrajectoryId'?: string | null;
    /**
     * Care typing ID
     */
    'careTypingId'?: string | null;
    /**
     * Service code ID (null for PARTICULIER lines, which carry no ZPM code)
     */
    'serviceCodeId'?: string | null;
    /**
     * Service code string (null for PARTICULIER lines)
     */
    'serviceCode'?: string | null;
    /**
     * Service description
     */
    'serviceDescription'?: string;
    /**
     * Service type of the underlying service code. Determines whether `quarters` is meaningful as a unit count (GROUP) or just encodes the duration the code is for (CONSULTATION, RESIDENTIAL, SURCHARGE, OTHER). 
     */
    'serviceType'?: BillingLineResponseServiceTypeEnum;
    /**
     * Date of service
     */
    'serviceDate': string;
    /**
     * Number of quarters (15-min blocks)
     */
    'quarters': number;
    /**
     * Duration in minutes (quarters * 15)
     */
    'durationMinutes'?: number;
    /**
     * Tariff per unit
     */
    'tariffEuro': number;
    /**
     * Total billed/declared amount
     */
    'amountEuro': number;
    /**
     * NZa max tariff (100%) per unit, snapshotted at line generation. Null for legacy lines where no matching NZa entry could be backfilled.
     */
    'nzaTariffEuro'?: number | null;
    /**
     * NZa line total at 100%, computed from nzaTariffEuro using the same service-type rule as amountEuro. Null when nzaTariffEuro is null.
     */
    'nzaAmountEuro'?: number | null;
    /**
     * Amount approved by insurer (from GDS802)
     */
    'approvedAmount'?: number | null;
    /**
     * Amount actually received in bank account (manual entry)
     */
    'receivedAmount'?: number | null;
    /**
     * Whether there is a discrepancy between billed, approved, and received amounts
     */
    'hasDiscrepancy'?: boolean;
    /**
     * Contract ID (if contracted)
     */
    'contractId'?: string | null;
    /**
     * Whether this is contracted care
     */
    'isContracted': boolean;
    /**
     * Which contract rule priced this line, snapshotted at generation (#4032). `contractId` names the agreement; this names the arm of its precedence that produced `tariffEuro`, which the contract can no longer answer once it has been renegotiated.  Null when no contract rule priced the line — an uncontracted or particulier line, which `isContracted` says — or, for a line generated before #4032, because the attribution was never recorded; `contractId` tells those two apart. Never backfilled. 
     */
    'priceRuleScope'?: ContractPriceScope | null;
    /**
     * The practice\'s own tariff where *it* priced this line, snapshotted at generation (#4392). Never set together with `priceRuleScope`; both null means the line is at the full NZa maximum.  It is also what tells an eigen-tarief line apart from a pre-#3822 uncontracted row: both are `isContracted` false with a `tariffEuro` below their NZa maximum, and only the first declares and invoices that lower amount. 
     */
    'ownTariffPercentage'?: number | null;
    /**
     * Insurer ID
     */
    'insurerId'?: string | null;
    /**
     * Insurer name
     */
    'insurerName'?: string | null;
    /**
     * Client\'s policy number
     */
    'policyNumber'?: string | null;
    'billingCategory': BillingCategory;
    'status': BillingLineStatus;
    /**
     * Batch ID (when in a batch)
     */
    'batchId'?: string | null;
    /**
     * Rejection reason (if rejected). What the insurer wrote itself when the GDS802 carried free text, and otherwise the COD954-VEKT `Betekenis` of `rejectionCode` — so this is a sentence a practice can read, not necessarily the code list\'s.
     */
    'rejectionReason'?: string | null;
    /**
     * Rejection code (if rejected). The bare 4-digit Vektis retourcode as the insurer sent it; `rejectionCodeBetekenis` and `rejectionCodeToelichting` are what it means.
     */
    'rejectionCode'?: string | null;
    /**
     * The COD954-VEKT `Betekenis` of `rejectionCode`, resolved per response rather than stored. Null when no code was returned, or when the code is not in the list Scrivio ships. Distinct from `rejectionReason`, which is the insurer\'s own words where it supplied any (#4412).
     */
    'rejectionCodeBetekenis'?: string | null;
    /**
     * The COD954-VEKT `Toelichting1` of `rejectionCode` — the longer clarification, which the code list publishes for only some codes. Null when absent, unknown or no code was returned.
     */
    'rejectionCodeToelichting'?: string | null;
    /**
     * Original line ID (for credits)
     */
    'originalLineId'?: string | null;
    /**
     * Whether this is a credit/reversal
     */
    'isCredit'?: boolean;
    /**
     * Whether the practice has written this regel off — the shortfall of a partial grant, or the whole of a rejection. The write-off is a separate `WRITTEN_OFF` credit on no batch, so this is how the regel\'s own batch says it is dealt with. 
     */
    'writtenOff'?: boolean;
    /**
     * The toewijzing a gemeente prestatie declares against (#4820); null for a Zvw or particuliere regel. A regel that names one is credited per toewijzing-month, through `creditGemeenteMonth` (#4823). 
     */
    'toewijzingId'?: string | null;
    /**
     * Start time of the billing slice for group session billing. Present when this billing line represents a 15-minute time slice from a multi-client appointment. Null for regular consultations and residential stays. 
     */
    'sliceStartTime'?: string | null;
    /**
     * End time of the billing slice for group session billing. Present when this billing line represents a 15-minute time slice from a multi-client appointment. Null for regular consultations and residential stays. 
     */
    'sliceEndTime'?: string | null;
    /**
     * Notes
     */
    'notes'?: string | null;
    /**
     * Whether this line is excluded from batch creation
     */
    'excludedFromBilling'?: boolean;
    /**
     * Timestamp when line was excluded
     */
    'excludedAt'?: number | null;
    /**
     * User ID who excluded the line
     */
    'excludedById'?: string | null;
    /**
     * Reason for exclusion
     */
    'exclusionReason'?: string | null;
    /**
     * Creation timestamp
     */
    'createdAt': number;
    /**
     * Last update timestamp
     */
    'updatedAt': number;
}

export const BillingLineResponseServiceTypeEnum = {
    Consultation: 'CONSULTATION',
    Group: 'GROUP',
    Residential: 'RESIDENTIAL',
    Surcharge: 'SURCHARGE',
    Other: 'OTHER'
} as const;

export type BillingLineResponseServiceTypeEnum = typeof BillingLineResponseServiceTypeEnum[keyof typeof BillingLineResponseServiceTypeEnum];

/**
 * Billing line lifecycle status: - DRAFT: Initial state, can be edited - READY: Validated, ready for batching - IN_BATCH: Added to a batch - SUBMITTED: Batch submitted to insurer - APPROVED: Approved by insurer - REJECTED: Rejected by insurer - CREDITED: Reversed/credited - WRITTEN_OFF: A write-off credit — the practice\'s own booking of money the insurer   did not pay. Terminal from creation and never declared or batched. 
 */

export const BillingLineStatus = {
    Draft: 'DRAFT',
    Ready: 'READY',
    InBatch: 'IN_BATCH',
    Submitted: 'SUBMITTED',
    Approved: 'APPROVED',
    Rejected: 'REJECTED',
    Credited: 'CREDITED',
    WrittenOff: 'WRITTEN_OFF'
} as const;

export type BillingLineStatus = typeof BillingLineStatus[keyof typeof BillingLineStatus];


/**
 * Billing type based on contract status: - CONTRACTED: Has active contract, use negotiated rates - NON_CONTRACTED: No contract, priced and declared at the full NZa maximum (100%) 
 */

export const BillingType = {
    Contracted: 'CONTRACTED',
    NonContracted: 'NON_CONTRACTED'
} as const;

export type BillingType = typeof BillingType[keyof typeof BillingType];


/**
 * One signal definition and the share of the openstaand backlog it accounts for. The id arrays are both the routing identity — what the practice has to fix — and the distinct counts: their size is the number of clients / behandelaars / trajectories involved.  `blocking` says which of the summary\'s two lists this came from, so a caller that merges them keeps each entry\'s own answer. 
 */
export interface BlockingSignalCauseDto {
    /**
     * Key of the signal definition; filters the per-consult list to this cause
     */
    'definitionKey': string;
    /**
     * Human-readable label of the signal definition
     */
    'name': string;
    'severity': BlockingSignalSeverity;
    'rootEntityType': BlockingSignalRootEntity;
    /**
     * Whether this cause holds its consults back. `true` for every entry of `causes`, `false` for every entry of `advisoryCauses` — an advisory cause\'s consults can be declared today. 
     */
    'blocking': boolean;
    /**
     * Consults this cause is raised on (one per appointment-client billing line). For a blocking cause these are the consults it blocks; for an advisory one they are the consults it applies to, counted over the whole filtered set. 
     */
    'blockedConsults': number;
    /**
     * Distinct appointments behind those consults
     */
    'blockedAppointments': number;
    /**
     * Distinct clients whose consults this cause is raised on
     */
    'clientIds': Array<string>;
    /**
     * Distinct behandelaars whose consults this cause is raised on
     */
    'practitionerIds': Array<string>;
    /**
     * Distinct zorgtrajecten behind those consults (a consult without one is omitted)
     */
    'careTrajectoryIds': Array<string>;
}


/**
 * A signal that blocks invoicing for one or more appointment facts. Either rooted directly on a fact or on one of its environmental dependencies (organization, location, practitioner, contract, insurer). 
 */
export interface BlockingSignalDto {
    /**
     * Unique identifier for this signal instance
     */
    'id': string;
    /**
     * The key of the signal definition that triggered this signal
     */
    'definitionKey': string;
    /**
     * Human-readable name of the signal
     */
    'name': string;
    /**
     * Rendered message with placeholders filled in
     */
    'message': string;
    'severity': BlockingSignalSeverity;
    'rootEntityType': BlockingSignalRootEntity;
    /**
     * ID of the root entity this signal relates to
     */
    'rootEntityId': string;
    /**
     * Subset of the requested factIds that this signal blocks
     */
    'blockedFactIds': Array<string>;
    /**
     * Unix timestamp (milliseconds) when this signal was created
     */
    'createdAt': number;
}


/**
 * The type of entity that is the root of a blocking signal
 */

export const BlockingSignalRootEntity = {
    Client: 'CLIENT',
    Appointment: 'APPOINTMENT',
    CareTrajectory: 'CARE_TRAJECTORY',
    Document: 'DOCUMENT',
    FormSubmission: 'FORM_SUBMISSION',
    Contract: 'CONTRACT',
    AppointmentFact: 'APPOINTMENT_FACT',
    Organization: 'ORGANIZATION',
    Location: 'LOCATION',
    Practitioner: 'PRACTITIONER',
    Insurer: 'INSURER'
} as const;

export type BlockingSignalRootEntity = typeof BlockingSignalRootEntity[keyof typeof BlockingSignalRootEntity];


/**
 * The severity level of a blocking signal
 */

export const BlockingSignalSeverity = {
    Info: 'INFO',
    Warning: 'WARNING',
    Urgent: 'URGENT'
} as const;

export type BlockingSignalSeverity = typeof BlockingSignalSeverity[keyof typeof BlockingSignalSeverity];


/**
 * The openstaand consult list counted per cause instead of per consult.  `totalConsults` = `blockedConsults` + `readyConsults`, and every blocked consult appears under at least one cause — so the causes account for the whole blocked backlog. Summing `blockedConsults` over the causes gives a larger number when consults carry more than one cause; `consultsWithMultipleCauses` is how many of them do.  `advisoryCauses` is a separate list and deliberately *not* part of that arithmetic: an advisory cause holds nothing back, so its consults are mostly ready ones and counting them here would move `blockedConsults` / `readyConsults` away from the overview\'s own `blockedCount` / `readyCount`.  The `ready*` fields describe the other half of the same split, so the summary bar over the queue can state both halves from one answer. They are the same arithmetic `findBillableClients` states per cliënt on `GET /api/v1/billing/billable-clients?status=READY`, summed — which is what stops the bar and the list disagreeing about a money figure (#3909, #4175). 
 */
export interface BlockingSignalSummaryResponse {
    /**
     * Consults in scope (one per appointment-client billing line)
     */
    'totalConsults': number;
    /**
     * Consults blocked by at least one cause, counted once each. The `blockedCount` of `GET /api/v1/billing/billable-appointments` for the same filters. 
     */
    'blockedConsults': number;
    /**
     * Consults in scope that no cause blocks — \"Factureerbaar\". The `readyCount` of `GET /api/v1/billing/billable-appointments` for the same filters. 
     */
    'readyConsults': number;
    /**
     * Distinct cliënten behind `readyConsults`
     */
    'readyClients': number;
    /**
     * Sum of the resolvable estimated amounts of `readyConsults` — what the practice can declare today. Consults with no resolvable amount contribute nothing and are counted by `readyUnpricedConsults`. 
     */
    'readyAmount': number;
    /**
     * How many of `readyConsults` have no resolvable amount, and so contribute nothing to `readyAmount`. Zero for a complete total. 
     */
    'readyUnpricedConsults': number;
    /**
     * Blocked consults carrying more than one cause
     */
    'consultsWithMultipleCauses': number;
    /**
     * Blocking causes, most consults blocked first (empty = nothing blocks)
     */
    'causes': Array<BlockingSignalCauseDto>;
    /**
     * Causes worth knowing that block nothing — signal definitions with `blocksBilling = false` firing on consults in scope, most consults first. `billing.missing-dsm5-diagnose` is the clearest example: it does not stop the declaration but does leave the restitutienota short. Their consults are counted over the **whole** filtered set rather than over the blocked half, since they are mostly ready to declare. 
     */
    'advisoryCauses': Array<BlockingSignalCauseDto>;
}
/**
 * Request listing the appointment facts to check for blocking signals
 */
export interface BlockingSignalsRequest {
    /**
     * Appointment fact IDs to evaluate
     */
    'factIds': Array<string>;
}
/**
 * Signals blocking invoicing for the requested appointment facts
 */
export interface BlockingSignalsResponse {
    /**
     * Blocking signals (empty list = nothing blocks invoicing)
     */
    'signals': Array<BlockingSignalDto>;
}
/**
 * How a send to the boekhouding delivers (#4727): `EMAIL` mails one invoice per message to the practice\'s boekhouding address; `SNELSTART` books one verkoopboeking per invoice through the SnelStart API. As a request property, what the confirmation told the practice — a run that would do the other is refused with `412`. 
 */

export const BoekhoudingChannel = {
    Email: 'EMAIL',
    Snelstart: 'SNELSTART'
} as const;

export type BoekhoudingChannel = typeof BoekhoudingChannel[keyof typeof BoekhoudingChannel];


export interface BoekhoudingDestinationResponse {
    'channel': BoekhoudingChannel;
    /**
     * The address an `EMAIL` run sends to; absent when none is configured, and on `SNELSTART`
     */
    'recipientEmail'?: string | null;
    /**
     * The grootboekrekening a `SNELSTART` run books on, as SnelStart numbers it
     */
    'grootboekNummer'?: number | null;
    'grootboekOmschrijving'?: string | null;
    /**
     * Whether a `SNELSTART` run has a grootboekrekening to book on; one without is refused
     */
    'grootboekChosen'?: boolean;
}


export interface BoekhoudingSendBatchResult {
    'billingBatchId': string;
    /**
     * The batch as the practice knows it
     */
    'batchNumber': string;
    'outcome': BoekhoudingSendOutcome;
    /**
     * Mails this run sent for the batch — one per verzekeraarfactuur
     */
    'invoicesSent': number;
    /**
     * The batch\'s verzekeraarfacturen an earlier run had already mailed
     */
    'invoicesAlreadySent': number;
    /**
     * Why a `FAILED` batch was not sent; absent for every other outcome
     */
    'failureReason'?: string | null;
    /**
     * A SnelStart run only — invoices skipped because they already went by mail
     */
    'invoicesAlreadyMailed'?: number;
    /**
     * When the last of `invoicesAlreadyMailed` went by mail
     */
    'mailedAt'?: number | null;
    /**
     * A SnelStart run only — invoices SnelStart did not book; each says why on the invoice
     */
    'invoicesFailed'?: number;
}


/**
 * What became of one batch of a send to the boekhouding (#4621):  - `PENDING` — not reached yet by the background worker. - `SENT` — at least one of its verzekeraarfacturen was mailed (or booked) by this run. - `ALREADY_SENT` — every one of its verzekeraarfacturen had been mailed (or booked) before;   nothing sent. - `ALREADY_MAILED` — a SnelStart run only (#4727): nothing booked, because its invoices had   already gone by mail and the batch was not named in `bookDespiteMailedBatchIds`. - `NOT_SUBMITTED` — the batch has not been submitted to the insurer; skipped, never sent. - `FAILED` — it could not be sent; `failureReason` says why. What it mailed before failing   stays mailed. 
 */

export const BoekhoudingSendOutcome = {
    Pending: 'PENDING',
    Sent: 'SENT',
    AlreadySent: 'ALREADY_SENT',
    AlreadyMailed: 'ALREADY_MAILED',
    NotSubmitted: 'NOT_SUBMITTED',
    Failed: 'FAILED'
} as const;

export type BoekhoudingSendOutcome = typeof BoekhoudingSendOutcome[keyof typeof BoekhoudingSendOutcome];


export interface BoekhoudingSendRunResponse {
    'id': string;
    'channel': BoekhoudingChannel;
    /**
     * The address a mail run sends to, fixed when it was requested; absent on a SnelStart run
     */
    'recipientEmail'?: string | null;
    /**
     * When the send was requested (milliseconds since epoch)
     */
    'requestedAt': number;
    /**
     * When the last batch was answered, or `null` while any is still `PENDING`
     */
    'finishedAt'?: number | null;
    /**
     * One entry per selected batch, in the order they were selected
     */
    'batches': Array<BoekhoudingSendBatchResult>;
}


/**
 * Summary of billing-relevant client information for quick-view tooltips. Displayed when hovering over client names in billing tables. 
 */
export interface ClientBillingSummaryResponse {
    /**
     * Client ID
     */
    'clientId': string;
    /**
     * Client full name
     */
    'clientName': string;
    /**
     * Masked BSN (e.g., \"***-***-123\")
     */
    'maskedBsn': string;
    /**
     * Name of the client\'s insurer
     */
    'insurerName'?: string | null;
    /**
     * UZOVI code of the insurer
     */
    'uzoviCode'?: string | null;
    /**
     * Client\'s policy number
     */
    'policyNumber'?: string | null;
    /**
     * Whether a referral letter is present
     */
    'verwijsbriefStatus': ClientBillingSummaryResponseVerwijsbriefStatusEnum;
    /**
     * Current zorgvraagtypering code
     */
    'zorgvraagtyperingCode'?: string | null;
    /**
     * Number of active care trajectories
     */
    'activeZorgtrajectCount': number;
}

export const ClientBillingSummaryResponseVerwijsbriefStatusEnum = {
    Aanwezig: 'AANWEZIG',
    Ontbreekt: 'ONTBREEKT'
} as const;

export type ClientBillingSummaryResponseVerwijsbriefStatusEnum = typeof ClientBillingSummaryResponseVerwijsbriefStatusEnum[keyof typeof ClientBillingSummaryResponseVerwijsbriefStatusEnum];

/**
 * Which file a consult export writes. Both carry the same columns in the same order, from one definition — the difference is only what opens them.  XLSX is what a bookkeeper reconciles in: amounts arrive as numbers rather than as text, so a column sums without being re-typed first.  Spelled in upper case because it is a **query parameter**: Spring binds one through `StringToEnumConverterFactory`, which is `Enum.valueOf` on the constant\'s own name and never the generated `fromValue`, so a lower-case value would 400 before any controller saw it. `BillableAppointmentStatus` beside it is upper case for the same reason. 
 */

export const ConsultExportFormat = {
    Csv: 'CSV',
    Xlsx: 'XLSX'
} as const;

export type ConsultExportFormat = typeof ConsultExportFormat[keyof typeof ConsultExportFormat];


/**
 * Which arm of a contract\'s price precedence produced a tariff (#4032): a rule scoped to the prestatiecode, to the beroep, to the behandelsetting, or the contract\'s own default. Most specific first, which is the order `contract_price_rule_for()` resolves in.  Never sent for a line no contract rule priced: that is an uncontracted line, billed and declared at the full 100% NZa maximum (#3822), and it is the absence of a rule rather than one of them — `isContracted` is what says so. 
 */

export const ContractPriceScope = {
    ServiceCode: 'SERVICE_CODE',
    Profession: 'PROFESSION',
    Setting: 'SETTING',
    ContractDefault: 'CONTRACT_DEFAULT'
} as const;

export type ContractPriceScope = typeof ContractPriceScope[keyof typeof ContractPriceScope];


/**
 * Whether the current organization can perform a VECOZO COV verification. Scrivio authenticates with the deployment\'s system certificate and authorizes per AGB code (ADR-031 Decision 1), so no certificate of the organization\'s own takes part. 
 */
export interface CovAvailabilityResponse {
    /**
     * Whether *this* organization can verify: this deployment has a usable VECOZO connection (ADR-044), and the organization has an AGB code, which is what a COV request is authorized by (ADR-031), and has confirmed its toestemmingsverklaring. Two organizations in the same deployment can get different answers. A false answer is a neutral state the organization cannot act on, so the frontend hides the verify affordance rather than reporting a misconfiguration. 
     */
    'covAvailable': boolean;
}
/**
 * Request to create billing batches from rows of the \"Te declareren\" list, one per (cliënt, verzekeraar) group (#4370, #4742). The caller states the grouping rather than the server deriving a second one, so what the selection bar promises and what is created cannot come apart (#3821). 
 */
export interface CreateBillingBatchRequest {
    /**
     * The batches to create. Applied atomically: any group that cannot be batched refuses the whole request. 
     */
    'batches': Array<BillingBatchGroup>;
}
/**
 * The batches one create request produced, in the order requested
 */
export interface CreateBillingBatchesResponse {
    'batches': Array<BillingBatchResponse>;
}
/**
 * The batches to create, one per gemeente, applied atomically (#4821)
 */
export interface CreateGemeenteBatchesRequest {
    'batches': Array<GemeenteBatchGroup>;
}
/**
 * The reversal of the original claim, and the corrected regel that replaces it
 */
export interface CreditAndRedeclareLineResponse {
    'creditLine': BillingLineResponse;
    'replacementLine': BillingLineResponse;
}
/**
 * Response containing the GDS801 XML declaration message for a billing line. Includes the XML content and metadata about whether this is a preview or actual submission. 
 */
export interface DeclarationMessageResponse {
    /**
     * The billing line ID
     */
    'lineId': string;
    /**
     * The formatted GDS801 XML content
     */
    'xml': string;
    /**
     * True if this is a preview of what will be sent (line not yet submitted). False if this represents the actual XML that was sent for a submitted line. Either way the message carries the bericht\'s real Factuurnummer and Referentienummer, so a preview differs from the submitted message only in when it was rendered. 
     */
    'isPreview': boolean;
    'lineStatus'?: BillingLineStatus;
    /**
     * The warning-level pre-submission controle violations this message carries. Empty when the message is clean. A warning does not stop the declaration — blocking violations refuse it with a 422 instead. 
     */
    'warnings': Array<Gds801ControleViolation>;
}


/**
 * What kind of thing a row of the \"Te declareren\" list sends to the insurer (#4742): - NIEUW: a prestatie declared for the first time - HERDECLARATIE: a prestatie whose rejected regel is resubmitted in place (herdeclaratiecode 03) - CREDIT: a credit post reversing a declared regel - CORRECTIE: the corrected claim replacing the regel a credit post reverses (herdeclaratiecode 05) 
 */

export const DeclarationSoort = {
    Nieuw: 'NIEUW',
    Herdeclaratie: 'HERDECLARATIE',
    Credit: 'CREDIT',
    Correctie: 'CORRECTIE'
} as const;

export type DeclarationSoort = typeof DeclarationSoort[keyof typeof DeclarationSoort];


/**
 * Where a regel\'s declaration stands — or, for an afspraak, the least advanced of its regels, in this order (#4733). Derived on every read, never stored.  - `REJECTED` — the insurer refused it, and it has not been released for redeclaration - `NOT_DECLARED` — on no batch or nota: openstaand, or a replacement waiting to go out - `IN_BATCH` — on a batch that has not been sent - `DECLARED` — sent, and no retour has judged it yet - `PARTIALLY_APPROVED` — granted for less than declared, shortfall still open - `WRITTEN_OFF` — granted for less, and the shortfall was written off - `CREDITED` — reversed in full, with nothing declared in its place - `INVOICED` — a particuliere regel on a nota to the cliënt - `APPROVED` — granted in full 
 */

export const DeclarationState = {
    Rejected: 'REJECTED',
    NotDeclared: 'NOT_DECLARED',
    InBatch: 'IN_BATCH',
    Declared: 'DECLARED',
    PartiallyApproved: 'PARTIALLY_APPROVED',
    WrittenOff: 'WRITTEN_OFF',
    Credited: 'CREDITED',
    Invoiced: 'INVOICED',
    Approved: 'APPROVED'
} as const;

export type DeclarationState = typeof DeclarationState[keyof typeof DeclarationState];


/**
 * Scope of a discard operation. At least one of `batchId` or `appointmentIds` must be provided. 
 */
export interface DiscardBillingRequest {
    /**
     * Batch to discard in full, together with all of its lines
     */
    'batchId'?: string;
    /**
     * Appointments whose billing lines should be discarded. A line that belongs to a batch pulls that whole batch into the discard. 
     */
    'appointmentIds'?: Array<string>;
}
/**
 * Summary of what a discard operation removed
 */
export interface DiscardBillingResponse {
    /**
     * Number of billing batches deleted
     */
    'discardedBatchCount': number;
    /**
     * Number of billing lines hard-deleted
     */
    'discardedBillingLineCount': number;
    /**
     * Number of DRAFT invoices deleted
     */
    'discardedInvoiceCount': number;
}
/**
 * Request to exclude a billing line from batch creation
 */
export interface ExcludeBillingLineRequest {
    /**
     * Optional reason for excluding the billing line
     */
    'reason'?: string | null;
}
/**
 * One pre-submission controle (RBC u29) violation on a built GDS801 message, as the practice sees it. The same shape carries the blocking violations in a `gds801-controles-failed` Problem Details `errors` array. 
 */
export interface Gds801ControleViolation {
    /**
     * The RBC u29 identifier, e.g. `RC032`
     */
    'controle': string;
    /**
     * What the rule requires, in the practice\'s language
     */
    'omschrijving': string;
    /**
     * Where in the message it failed, e.g. `DebetPrestatie[referentienummer=…]`
     */
    'locatie': string;
    /**
     * The patiëntnummer (PatientIdentificatienummer) the prestatie belongs to. Absent for message-level controles such as the Declarant AGB code. 
     */
    'verzekerde'?: string;
    /**
     * The concrete reason (expected vs actual)
     */
    'detail': string;
    /**
     * The whole violation as one localized sentence, ready to render
     */
    'message': string;
}
/**
 * What one bulk retour upload contained and what it did. Every entry of the upload has a row here, whatever became of it — the counts a screen shows are derived from `files` rather than reported separately, so nothing can disagree with the list it summarises. 
 */
export interface Gds802BulkImportReport {
    /**
     * The name of the uploaded archive, so a report read later says which download it was
     */
    'fileName': string;
    /**
     * One row per entry of the upload, in the order they were read
     */
    'files': Array<Gds802ReturnFileResult>;
}
/**
 * Where in a retourbericht a `Feedback` block was attached. Only the three message levels appear here: per-prestatie feedback lands on the billing line it answers. 
 */

export const Gds802FeedbackNiveau = {
    Header: 'HEADER',
    DeclaratieContext: 'DECLARATIE_CONTEXT',
    Overzicht: 'OVERZICHT'
} as const;

export type Gds802FeedbackNiveau = typeof Gds802FeedbackNiveau[keyof typeof Gds802FeedbackNiveau];


/**
 * Who sent a GDS802 retour pulled from the VECOZO berichtenservice
 */

export const Gds802RetourAfzender = {
    Zorgverzekeraar: 'ZORGVERZEKERAAR',
    Vecozo: 'VECOZO'
} as const;

export type Gds802RetourAfzender = typeof Gds802RetourAfzender[keyof typeof Gds802RetourAfzender];


/**
 * What became of one retourbericht — a report line in a bulk upload, and the outcome stored with the retour itself (`BatchRetourResponse.outcome`).  Only `FAILED` is a defect. `NOT_A_RETOUR` is the ordinary majority of a portaal download (573 copies and PDFs), and `UNKNOWN_BATCH` is ordinary too — a declaration from a previous system that this organisation never issued a batch number for. `NO_BILLING_LINES` is a batch whose declaratieregels were released after it was sent (#3955), so there is nothing left to match the answer against.  `NOT_A_RETOUR`, `NO_FACTUURNUMMER` and `UNKNOWN_BATCH` name no batch, so they are report lines only and never appear on `BatchRetourResponse`. 
 */

export const Gds802ReturnFileOutcome = {
    Applied: 'APPLIED',
    Superseded: 'SUPERSEDED',
    UnknownBatch: 'UNKNOWN_BATCH',
    BatchNotDeclared: 'BATCH_NOT_DECLARED',
    NoBillingLines: 'NO_BILLING_LINES',
    NotARetour: 'NOT_A_RETOUR',
    NoFactuurnummer: 'NO_FACTUURNUMMER',
    Failed: 'FAILED'
} as const;

export type Gds802ReturnFileOutcome = typeof Gds802ReturnFileOutcome[keyof typeof Gds802ReturnFileOutcome];


/**
 * What became of one file in a bulk retour upload, and — where it reached a batch — what that batch now says. The counts are absent rather than zero for every outcome that never reconciled anything: \"no lines were matched\" and \"matching never happened\" are different answers. 
 */
export interface Gds802ReturnFileResult {
    /**
     * The entry\'s name inside the archive
     */
    'entryName': string;
    'outcome': Gds802ReturnFileOutcome;
    /**
     * The `Factuurnummer` the message named. Present even for `UNKNOWN_BATCH`, where it is the whole of what the practice needs to recognise the declaration. 
     */
    'batchNumber'?: string | null;
    /**
     * The batch the message was routed to, when this organisation has one
     */
    'batchId'?: string | null;
    /**
     * The batch\'s status after the message was applied
     */
    'batchStatus'?: BillingBatchStatus | null;
    /**
     * Response lines in the message
     */
    'totalLinesProcessed'?: number | null;
    /**
     * Lines approved by the insurer
     */
    'approvedCount'?: number | null;
    /**
     * Lines rejected by the insurer
     */
    'rejectedCount'?: number | null;
    /**
     * Response lines that could not be matched to a billing line
     */
    'unmatchedCount'?: number | null;
}


/**
 * One batch to create — the selected prestaties of one gemeente
 */
export interface GemeenteBatchGroup {
    /**
     * The gemeente the batch declares to; every prestatie must be its
     */
    'gemeentecode': string;
    'regels': Array<GemeenteDeclaratieregelRef>;
}
/**
 * One gemeente and the prestaties declared, or to be declared, to it
 */
export interface GemeenteDeclaratieGemeente {
    /**
     * 4-digit CBS gemeentecode
     */
    'gemeentecode': string;
    'gemeentenaam': string;
    /**
     * The CBS jeugdregio the gemeente belongs to today; null when it is in none
     */
    'jeugdregioCode'?: string | null;
    'jeugdregioNaam'?: string | null;
    /**
     * Ordered by cliënt, then period, then productcode
     */
    'regels': Array<GemeenteDeclaratieregelResponse>;
}
/**
 * The undeclared gemeente prestaties as of `peildatum`, per gemeente (#4821)
 */
export interface GemeenteDeclaratieOverzicht {
    /**
     * The day the prestaties were derived on; its month is never listed (TR137)
     */
    'peildatum': string;
    /**
     * One entry per gemeente with at least one prestatie, ordered by gemeentenaam
     */
    'gemeenten': Array<GemeenteDeclaratieGemeente>;
}
/**
 * A gemeente prestatie as `listGemeenteDeclaraties` lists it
 */
export interface GemeenteDeclaratieregelRef {
    'toewijzingId': string;
    'van': string;
    'productCode': string;
}
/**
 * One gemeente prestatie: one toewijzing × one calendar month × one productcode, over `van`..`tot` clipped to the toewijzing, or one tariff period of that month (#4820). 
 */
export interface GemeenteDeclaratieregelResponse {
    'toewijzingId': string;
    'zorgtrajectId': string;
    'clientId': string;
    /**
     * Null when the cliënt no longer resolves
     */
    'clientNaam'?: string | null;
    'productCategorie'?: string | null;
    /**
     * Null only on a held prestatie whose product could not be determined
     */
    'productCode'?: string | null;
    'van': string;
    'tot': string;
    /**
     * In `eenheid`
     */
    'volume': number;
    'eenheid': ToewijzingEenheid;
    /**
     * The declarable tariff per `eenheid`, after the IV081 conversion; null on a held prestatie
     */
    'tariefEuro'?: number | null;
    /**
     * volume × tariefEuro; null on a held prestatie
     */
    'bedragEuro'?: number | null;
    'ophouding'?: GemeenteOphoudingResponse | null;
}


/**
 * Why a gemeente prestatie cannot be declared yet; absent on a declarable one
 */
export interface GemeenteOphoudingResponse {
    'reden': GemeenteOphoudreden;
    /**
     * The reason in Dutch, naming what the practice has to supply
     */
    'omschrijving': string;
    'startbericht'?: UitgaandBerichtstand | null;
    /**
     * On `START_NIET_BEVESTIGD`, the codes the gemeente\'s JW306 rejected the start with; empty unless it did. 
     */
    'startberichtRetourcodes'?: Array<string>;
}


/**
 * - BUITEN_LOOPTIJD: registraties dated outside the toewijzing\'s looptijd; move them to the toewijzing the care was delivered under - PRODUCT_NIET_BEPAALD: the toewijzing names no product the contract narrows to one (IV075) - PRODUCT_NIET_GECONTRACTEERD: no live, active contract covering the gemeente holds the product - CONTRACT_NIET_EENDUIDIG: several contracts covering the gemeente hold the product - ANDER_PRODUCT_GEDECLAREERD: the month stands declared under another productcode than the toewijzing now names; credit it to redeclare - NIET_PER_PRESTATIE: a taakgericht product (declaratiemethode GEEN), never declared per prestatie - DECLARATIEMETHODE_NIET_ONDERSTEUND: the product is not declared per volume, per periode, as a trajectprijs or per week - EENHEID_NIET_ONDERSTEUND: a volume product not delivered in minutes or hours, a per-periode one not in stuks output, or a trajectprijs (LUMP_SUM) or weekprijs (WEEKPRIJS) not in euro\'s - EENHEID_WIJKT_AF: the product\'s eenheid is not the one the toewijzing allocates in (9341) - TRAJECTPRIJS_ONBEKEND: a trajectprijs product on a toewijzing that allocates no omvang in euro\'s - WEEKBEDRAG_ONBEKEND: a weekprijs product on a toewijzing that allocates no omvang in euro\'s per week - GEEN_RECHT_OP_SLOTDEEL: care stopped for a reden beëindiging under which the contract pays no slotdeel of the trajectprijs; never declared - BEROEP_NIET_TOEGESTAAN: care by a medewerker whose beroep the product does not allow; only those registraties are held, and the regel shares its toewijzing, period and productcode with the declarable one beside it - START_NIET_BEVESTIGD: the gemeente has not confirmed the start (JW305/JW306) - EERDER_DAN_LAATST_GEDECLAREERD: a later month of the toewijzing is already declared (IV090) - GEEN_TARIEF: no tariff of the product is valid on the dates the prestatie covers - TARIEF_WIJZIGT_IN_MAAND: the product\'s tariff changes inside the month, which one prestatie cannot carry (9387/9388) 
 */

export const GemeenteOphoudreden = {
    BuitenLooptijd: 'BUITEN_LOOPTIJD',
    ProductNietBepaald: 'PRODUCT_NIET_BEPAALD',
    ProductNietGecontracteerd: 'PRODUCT_NIET_GECONTRACTEERD',
    ContractNietEenduidig: 'CONTRACT_NIET_EENDUIDIG',
    AnderProductGedeclareerd: 'ANDER_PRODUCT_GEDECLAREERD',
    NietPerPrestatie: 'NIET_PER_PRESTATIE',
    DeclaratiemethodeNietOndersteund: 'DECLARATIEMETHODE_NIET_ONDERSTEUND',
    EenheidNietOndersteund: 'EENHEID_NIET_ONDERSTEUND',
    EenheidWijktAf: 'EENHEID_WIJKT_AF',
    TrajectprijsOnbekend: 'TRAJECTPRIJS_ONBEKEND',
    WeekbedragOnbekend: 'WEEKBEDRAG_ONBEKEND',
    GeenRechtOpSlotdeel: 'GEEN_RECHT_OP_SLOTDEEL',
    BeroepNietToegestaan: 'BEROEP_NIET_TOEGESTAAN',
    StartNietBevestigd: 'START_NIET_BEVESTIGD',
    EerderDanLaatstGedeclareerd: 'EERDER_DAN_LAATST_GEDECLAREERD',
    GeenTarief: 'GEEN_TARIEF',
    TariefWijzigtInMaand: 'TARIEF_WIJZIGT_IN_MAAND'
} as const;

export type GemeenteOphoudreden = typeof GemeenteOphoudreden[keyof typeof GemeenteOphoudreden];


/**
 * One year\'s verantwoording, per gemeente declared to (#4824)
 */
export interface GemeenteVerantwoording {
    'jaar': number;
    /**
     * Every gemeente something was declared to in `jaar`, by name
     */
    'gemeenten': Array<GemeenteVerantwoordingGemeente>;
}
export interface GemeenteVerantwoordingGemeente {
    /**
     * The CBS gemeentecode
     */
    'gemeentecode': string;
    'gemeentenaam': string;
    /**
     * The sum of `producten[].bedrag`, in euro
     */
    'bedrag': number;
    'producten': Array<GemeenteVerantwoordingProduct>;
}
/**
 * One product\'s declared year, credits netted
 */
export interface GemeenteVerantwoordingProduct {
    'productCategorie': string;
    'productCode': string;
    'eenheid': ToewijzingEenheid;
    /**
     * The declared volume in `eenheid`, credits subtracted
     */
    'volume': number;
    /**
     * The declared amount in euro, credits subtracted
     */
    'bedrag': number;
    /**
     * How many declared and credit lines the figures are the sum of
     */
    'regels': number;
}


export interface GenerateBillingLinesRequest {
    /**
     * List of appointment IDs to generate billing lines from
     */
    'appointmentIds': Array<string>;
    /**
     * Optional refinement to specific appointment-client rows (#3321). When present, lines are generated/returned only for these rows; rows of a multi-client appointment that are not listed stay unbilled and remain in the openstaand list. Group-session slice pricing keeps its group size based on who was *present*, not on which rows are billed. Rows without an appointmentClientId (legacy single-client data) cannot be refined and are always included for their appointment. 
     */
    'appointmentClientIds'?: Array<string>;
    /**
     * Optional refinement to specific prestaties (#4741). A consult several behandelaren declare — a groepsconsult, or one cliënt seen by two or more — is one prestatie per behandelaar, and the declaratie list shows each as a row of its own. A row named here is billed for exactly the behandelaren named for it, and is selected as if it were listed in `appointmentClientIds`; a row named only there is billed whole. 
     */
    'prestaties'?: Array<BillablePrestatieRef>;
}
export interface GenerateBillingLinesResponse {
    /**
     * Number of billing lines returned (newly generated plus reusable un-invoiced lines)
     */
    'generated': number;
    /**
     * Number of appointments skipped (already billed with no reusable lines, or invalid)
     */
    'skipped': number;
    /**
     * Billing lines ready for invoicing or batching (newly generated plus reusable un-invoiced lines)
     */
    'lines': Array<BillingLineResponse>;
    /**
     * Errors encountered during generation
     */
    'errors'?: Array<GenerationError>;
}
export interface GenerationError {
    /**
     * Appointment ID that failed
     */
    'appointmentId': string;
    'reasonCode': GenerationErrorReasonCode;
    /**
     * Free-text English detail behind `reasonCode` — the appointment status, the unresolved service-code criteria, the exception message. Meant for support and logs; clients must key their behaviour off `reasonCode` and never show this verbatim to a user. 
     */
    'reason': string;
}


/**
 * Why an appointment produced no billing lines. Closed set, so both sides of the contract generate the same type instead of matching the English `reason` prose by substring (#3425): - ALREADY_BILLED: The appointment has billing lines and none can be reused - NOT_COMPLETED: The appointment has not reached status COMPLETED - NOT_BILLABLE: Nothing is ever declared for the appointment — a   niet-declarabel afspraaktype, or an MDO, which under the   zorgprestatiemodel is not a consult (#4740) - NO_SELECTED_ROWS: The per-row refinement matched no billable client row - CLIENT_INACTIVE: Every client row this run could bill belongs to an   archived or removed cliënt. Such consults leave the openstaand overview   entirely (#3905), so this reaches a caller acting on a stale selection   or calling the API directly - GEMEENTE_FUNDED: Every client row this run could bill is a consult a   gemeente funds — the zorgtraject covering its date is Jeugdwet or Wmo.   That care is declared through iJw/iWmo, never as a ZPM declaratie, so   such consults leave the openstaand overview entirely (#4840) - NO_PRIVATE_PRICE: PARTICULIER appointment whose type has no private rate - NO_INVOICED_CLIENT: The appointment\'s type books a prestatiecode that is   invoiced to the cliënt, and the cliënt it was booked for is not a present   participant — so there is nobody to address its one regel to - SERVICE_CODE_UNRESOLVED: No ZPM code matches the appointment\'s context - TARIFF_UNRESOLVED: The tariff for the resolved ZPM code could not be   calculated (unknown insurer or service code, lookup failure); the   appointment is reported rather than billed at €0.00 (#3573) - NOT_FOUND: No appointment with that id in this organization - OTHER: Any other failure; `reason` carries the detail 
 */

export const GenerationErrorReasonCode = {
    AlreadyBilled: 'ALREADY_BILLED',
    NotCompleted: 'NOT_COMPLETED',
    NotBillable: 'NOT_BILLABLE',
    NoSelectedRows: 'NO_SELECTED_ROWS',
    ClientInactive: 'CLIENT_INACTIVE',
    GemeenteFunded: 'GEMEENTE_FUNDED',
    NoPrivatePrice: 'NO_PRIVATE_PRICE',
    NoInvoicedClient: 'NO_INVOICED_CLIENT',
    ServiceCodeUnresolved: 'SERVICE_CODE_UNRESOLVED',
    TariffUnresolved: 'TARIFF_UNRESOLVED',
    NotFound: 'NOT_FOUND',
    Other: 'OTHER'
} as const;

export type GenerationErrorReasonCode = typeof GenerationErrorReasonCode[keyof typeof GenerationErrorReasonCode];


/**
 * The declaratiebatch an outgoing JW323 carries, as the bericht names it; absent on every other bericht
 */
export interface IjwBerichtBatch {
    'id': string;
    'batchNumber': string;
}
/**
 * A cliënt one or more listed berichten concern
 */
export interface IjwBerichtClient {
    'clientId': string;
    'clientNaam': string;
}
/**
 * `INBOUND`: received from a gemeente. `OUTBOUND`: built by the practice. 
 */

export const IjwBerichtDirection = {
    Outbound: 'OUTBOUND',
    Inbound: 'INBOUND'
} as const;

export type IjwBerichtDirection = typeof IjwBerichtDirection[keyof typeof IjwBerichtDirection];


/**
 * What the Berichten list of one direction can be narrowed to: the berichtcodes, the gemeenten and the cliënten of its berichten, of those the caller may see. 
 */
export interface IjwBerichtFilters {
    /**
     * Ordered by code
     */
    'berichtcodes': Array<IjwBerichtcode>;
    /**
     * Ordered by name, a code without one last
     */
    'gemeenten': Array<IjwGemeente>;
    /**
     * Ordered by name
     */
    'clienten': Array<IjwBerichtClient>;
}
export interface IjwBerichtPage {
    'content': Array<IjwBerichtSummary>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of berichten per page
     */
    'size': number;
    /**
     * Every bericht of the list the caller may see
     */
    'totalElements': number;
    'totalPages': number;
}
/**
 * Where a logged iJw bericht stands. Outgoing: `AANGEMAAKT` (queued in the outbox), `GEDOWNLOAD`, `VERZONDEN`, then `GEACCEPTEERD` or `AFGEKEURD` once the gemeente\'s retour is applied. Incoming: `ONTVANGEN`, `WACHT_OP_INTAKE` for a JW301 naming no cliënt yet, or `NIET_GEKOPPELD` for a retour or JW319 answering nothing sent through Scrivio — most often a bericht from the practice\'s previous system, stored and reported rather than refused. 
 */

export const IjwBerichtStatus = {
    Aangemaakt: 'AANGEMAAKT',
    Gedownload: 'GEDOWNLOAD',
    Verzonden: 'VERZONDEN',
    Geaccepteerd: 'GEACCEPTEERD',
    Afgekeurd: 'AFGEKEURD',
    Ontvangen: 'ONTVANGEN',
    WachtOpIntake: 'WACHT_OP_INTAKE',
    NietGekoppeld: 'NIET_GEKOPPELD'
} as const;

export type IjwBerichtStatus = typeof IjwBerichtStatus[keyof typeof IjwBerichtStatus];


/**
 * One logged iJw bericht as the Berichten screen lists it (#4811).
 */
export interface IjwBerichtSummary {
    'id': string;
    'berichtcode': IjwBerichtcode;
    'direction': IjwBerichtDirection;
    /**
     * The CBS code of the gemeente on the other side, as the header names it
     */
    'gemeentecode': string;
    /**
     * The gemeente\'s name; absent for a code Scrivio\'s gemeentelijst does not hold
     */
    'gemeentenaam'?: string | null;
    /**
     * `Header/BerichtIdentificatie/Identificatie` — the sender\'s own reference
     */
    'identificatie': string;
    /**
     * `Header/BerichtIdentificatie/Dagtekening`
     */
    'dagtekening': string;
    'status': IjwBerichtStatus;
    /**
     * The cliënt the bericht is linked to, where it names one the organisation records
     */
    'clientId'?: string | null;
    /**
     * That cliënt\'s name
     */
    'clientNaam'?: string | null;
    /**
     * Whether a received JW301 has been processed into the dossier (#4812); false on every other bericht 
     */
    'verwerkt': boolean;
    /**
     * The retourcodes per berichtklasse (#4813). On a received retour, its own; on an outgoing bericht, those of the retour that answered it — where an `AFGEKEURD` says why. Empty otherwise. 
     */
    'retourregels': Array<IjwRetourregel>;
    /**
     * On a received JW319, its answer; on an outgoing JW315 or JW317, the latest JW319 that answered it. Absent otherwise. A toekenning is not a JW319: it arrives as a JW301. 
     */
    'verzoekAntwoord'?: IjwVerzoekAntwoord | null;
    'batch'?: IjwBerichtBatch;
}


/**
 * What became of one file of an upload.  - `STORED` — stored in the message log as received. - `DUPLICATE` — the organisation already holds this bericht; nothing was stored. The   ordinary case when a download overlaps the previous one. - `INVALID` — an iJw 3.2 bericht its XSD refuses; not stored. - `SENT_BY_PRACTICE` — a bericht type a practice sends rather than receives (a JW315, a   JW323); not stored as received. - `NOT_IJW` — not an iJw 3.2 bericht at all: a PDF, another standard\'s message, anything   that is not XML. 
 */

export const IjwBerichtUploadOutcome = {
    Stored: 'STORED',
    Duplicate: 'DUPLICATE',
    Invalid: 'INVALID',
    SentByPractice: 'SENT_BY_PRACTICE',
    NotIjw: 'NOT_IJW'
} as const;

export type IjwBerichtUploadOutcome = typeof IjwBerichtUploadOutcome[keyof typeof IjwBerichtUploadOutcome];


/**
 * What one upload of a VECOZO-portal download held and did (#4811). Every file has a row, whatever became of it; counts are derived from `files`, so nothing can disagree with the list it summarises. 
 */
export interface IjwBerichtUploadReport {
    /**
     * The name of the upload itself
     */
    'fileName': string;
    /**
     * One row per file of the upload, in the order they were read
     */
    'files': Array<IjwBerichtUploadResult>;
}
export interface IjwBerichtUploadResult {
    /**
     * The file\'s name inside the archive, or the upload\'s own for a single XML
     */
    'fileName': string;
    'outcome': IjwBerichtUploadOutcome;
    /**
     * What the file is, wherever that could be read; absent for `NOT_IJW`
     */
    'berichtcode'?: IjwBerichtcode | null;
    /**
     * The stored bericht; present for `STORED` only
     */
    'berichtId'?: string | null;
}


/**
 * One logged iJw 3.2 bericht as it was exchanged (#4704).
 */
export interface IjwBerichtXmlResponse {
    'id': string;
    /**
     * The iJw message, e.g. `JW315`
     */
    'berichtcode': string;
    /**
     * The filename the download carries: `<gemeentecode>_<berichtcode>_<dagtekening>_<identificatie>.xml`. Neither VECOZO nor the gemeenten prescribe one; this follows the shape the berichtenloketten recommend (gemeente, berichttype, datum) and adds the identificatie so two berichten of one day do not collide. 
     */
    'bestandsnaam': string;
    /**
     * The stored document, unchanged
     */
    'xml': string;
}
/**
 * An iJw 3.2 bericht, named as the standard names it
 */

export const IjwBerichtcode = {
    Jw301: 'JW301',
    Jw302: 'JW302',
    Jw305: 'JW305',
    Jw306: 'JW306',
    Jw307: 'JW307',
    Jw308: 'JW308',
    Jw315: 'JW315',
    Jw316: 'JW316',
    Jw317: 'JW317',
    Jw318: 'JW318',
    Jw319: 'JW319',
    Jw320: 'JW320',
    Jw323: 'JW323',
    Jw325: 'JW325'
} as const;

export type IjwBerichtcode = typeof IjwBerichtcode[keyof typeof IjwBerichtcode];


/**
 * A gemeente as a list names it
 */
export interface IjwGemeente {
    /**
     * 4-digit CBS gemeentecode
     */
    'gemeentecode': string;
    /**
     * The gemeente\'s name; absent for a code Scrivio\'s gemeentelijst does not hold
     */
    'gemeentenaam'?: string | null;
}
/**
 * One outgoing iJw bericht in the outbox (#4814).
 */
export interface IjwOutboxBericht {
    'id': string;
    /**
     * The iJw message, e.g. `JW315`
     */
    'berichtcode': string;
    /**
     * The CBS code of the gemeente it goes to
     */
    'gemeentecode': string;
    /**
     * The bericht\'s own `BerichtIdentificatie/Identificatie`
     */
    'identificatie': string;
    'dagtekening': string;
    'clientId'?: string | null;
    /**
     * The cliënt\'s display name; null when the bericht names none Scrivio records
     */
    'clientNaam'?: string | null;
    'status': IjwBerichtStatus;
    /**
     * The name of its file in the ZIP (see `IjwBerichtXmlResponse.bestandsnaam`)
     */
    'bestandsnaam': string;
    /**
     * When it was queued (epoch millis)
     */
    'createdAt': number;
    /**
     * When it was first downloaded (epoch millis)
     */
    'gedownloadAt'?: number | null;
    /**
     * What keeps it from its XSD; empty when it may be downloaded
     */
    'violations': Array<string>;
    /**
     * What keeps it from the VECOZO berichtenservice; null when `verzendIjwOutbox` can send it
     */
    'verzendBeletsel'?: IjwVerzendBeletsel | null;
    /**
     * The gemeente\'s name; absent for a code Scrivio\'s gemeentelijst does not hold
     */
    'gemeentenaam'?: string | null;
    'batch'?: IjwBerichtBatch;
}


/**
 * The outgoing berichten an outbox action applies to (#4814).
 */
export interface IjwOutboxSelectie {
    /**
     * A repeated id counts once. Deliberately not `uniqueItems`: the TypeScript generator types that as a `Set`, which serialises to `{}`, so the frontend could not send it. 
     */
    'berichtIds': Array<string>;
}
/**
 * What became of one bericht `verzendIjwOutbox` submitted.
 */
export interface IjwOutboxVerzending {
    'berichtId': string;
    'uitkomst': IjwVerzendUitkomst;
    /**
     * VECOZO\'s own words on a `GEWEIGERD` submission
     */
    'melding'?: string | null;
}


/**
 * Why a JW317 asks for a change (iJw `LDT_RedenVerzoek`). - `VERANDERING_CLIENTSITUATIE` — the cliënt\'s situation changed (1) - `WIJZIGING_ZORGPLAN` — the zorgplan changed (2) 
 */

export const IjwRedenVerzoek = {
    VeranderingClientsituatie: 'VERANDERING_CLIENTSITUATIE',
    WijzigingZorgplan: 'WIJZIGING_ZORGPLAN'
} as const;

export type IjwRedenVerzoek = typeof IjwRedenVerzoek[keyof typeof IjwRedenVerzoek];


/**
 * The retourcodes on one berichtklasse of a retour, as received (codelijst WJ001). `0200`, `0233` and `8001` reject nothing; every other code is an afkeur on a named rule. 
 */
export interface IjwRetourregel {
    /**
     * The berichtklasse, e.g. `Header`, `Client`, `StartProduct`, `Prestatie`
     */
    'klasse': string;
    /**
     * What identifies the line within its klasse — a toewijzingnummer, the practice\'s referentie, a declaratienummer. Absent on the header and on the cliënt, whose key is a BSN. 
     */
    'sleutel'?: string | null;
    'retourcodes': Array<string>;
    /**
     * Whether a code on this line rejects it — any code but `0200`, `0233` and `8001`
     */
    'afgekeurd': boolean;
}
/**
 * Why the outbox cannot send a bericht over the VECOZO berichtenservice; it can still be downloaded. - `PORTAALKANAAL` — the organisation\'s iJw stream goes by the VECOZO portal - `AGB_REGISTRATIE_ONBEKEND` — the organisation\'s AGB code, or how it is registered, is not recorded - `HEENBERICHT_VIA_PORTAAL` — a JW302 or JW320 whose JW301 or JW319 arrived by portal upload 
 */

export const IjwVerzendBeletsel = {
    Portaalkanaal: 'PORTAALKANAAL',
    AgbRegistratieOnbekend: 'AGB_REGISTRATIE_ONBEKEND',
    HeenberichtViaPortaal: 'HEENBERICHT_VIA_PORTAAL'
} as const;

export type IjwVerzendBeletsel = typeof IjwVerzendBeletsel[keyof typeof IjwVerzendBeletsel];


/**
 * What became of one bericht sent over the VECOZO berichtenservice. - `VERZONDEN` — VECOZO accepted it; it is `VERZONDEN` - `AL_VERZONDEN` — it was sent by someone else in the meantime; nothing was submitted - `GEWEIGERD` — VECOZO refused the submission; `melding` says why - `NIET_VERBONDEN` — VECOZO refused Scrivio\'s certificate, or there is no connection - `NIET_BEREIKBAAR` — VECOZO could not be reached; send it again later 
 */

export const IjwVerzendUitkomst = {
    Verzonden: 'VERZONDEN',
    AlVerzonden: 'AL_VERZONDEN',
    Geweigerd: 'GEWEIGERD',
    NietVerbonden: 'NIET_VERBONDEN',
    NietBereikbaar: 'NIET_BEREIKBAAR'
} as const;

export type IjwVerzendUitkomst = typeof IjwVerzendUitkomst[keyof typeof IjwVerzendUitkomst];


/**
 * `LDT_VerzoekAntwoord`, what a JW319 answers a verzoek om toewijzing or wijziging: `AFGEWEZEN` (1) or `IN_ONDERZOEK` (2). 
 */

export const IjwVerzoekAntwoord = {
    Afgewezen: 'AFGEWEZEN',
    InOnderzoek: 'IN_ONDERZOEK'
} as const;

export type IjwVerzoekAntwoord = typeof IjwVerzoekAntwoord[keyof typeof IjwVerzoekAntwoord];


/**
 * Result of processing a GDS802 return message
 */
export interface ImportReturnMessageResponse {
    /**
     * The batch that was processed
     */
    'batchId': string;
    'batchStatus': BillingBatchStatus;
    /**
     * Whether this message\'s verdicts are what the batch\'s lines now carry. `false` means it was stored as history because a newer return message has already answered the batch — the counts are then all zero and the batch is untouched. 
     */
    'applied': boolean;
    /**
     * Total number of response lines in the GDS802 message
     */
    'totalLinesProcessed': number;
    /**
     * Number of lines approved by the insurer
     */
    'approvedCount': number;
    /**
     * Number of lines rejected by the insurer
     */
    'rejectedCount': number;
    /**
     * Number of response lines that could not be matched to billing lines
     */
    'unmatchedCount': number;
    /**
     * Details of rejected lines
     */
    'rejectedLines'?: Array<RejectedLineDetail>;
    /**
     * References from the GDS802 that could not be matched
     */
    'unmatchedReferences'?: Array<string>;
}


/**
 * Insurance information returned from VECOZO COV verification. Contains policy details including insurer, coverage dates, and package info. 
 */
export interface InsuranceInfoDto {
    /**
     * UZOVI code identifying the insurer (4-digit code)
     */
    'uzoviCode': string;
    /**
     * Name of the insurer, resolved from the Insurer table using the UZOVI code
     */
    'insurerName': string;
    /**
     * The patient\'s insurance policy number
     */
    'verzekerdenNummer': string;
    /**
     * Type of insurance (e.g., \"Basisverzekering\", \"Aanvullend\")
     */
    'soortVerzekering': string;
    /**
     * Package code identifying the specific insurance product
     */
    'pakketCode'?: string | null;
    /**
     * Human-readable package name
     */
    'pakketNaam'?: string | null;
    /**
     * Label code for grouped insurer brands
     */
    'labelCode'?: string | null;
    /**
     * Human-readable label name
     */
    'labelNaam'?: string | null;
    /**
     * Start date of the insurance coverage
     */
    'ingangsDatum': string;
    /**
     * End date of the insurance coverage (null if open-ended)
     */
    'eindDatum'?: string | null;
    /**
     * Whether this insurance is currently active based on today\'s date
     */
    'isActive': boolean;
}
/**
 * Status of insurance verification via VECOZO COV: - FOUND: Insurance found for the given BSN and date - NOT_FOUND: No active insurance found - BSN_UNKNOWN: BSN not found in the population register - BSN_INVALID: BSN failed checksum validation - BSN_DATE_MISMATCH: BSN and birth date do not match - MULTIPLE_FOUND: Multiple active policies found (rare edge case) - ERROR: Technical error during verification 
 */

export const InsuranceVerificationStatus = {
    Found: 'FOUND',
    NotFound: 'NOT_FOUND',
    BsnUnknown: 'BSN_UNKNOWN',
    BsnInvalid: 'BSN_INVALID',
    BsnDateMismatch: 'BSN_DATE_MISMATCH',
    MultipleFound: 'MULTIPLE_FOUND',
    Error: 'ERROR'
} as const;

export type InsuranceVerificationStatus = typeof InsuranceVerificationStatus[keyof typeof InsuranceVerificationStatus];


/**
 * Request to verify multiple patients\' insurance status in batch
 */
export interface InsuranceVerifyBatchRequest {
    /**
     * List of patients to verify (maximum 20 per batch)
     */
    'patients': Array<InsuranceVerifyRequest>;
}
/**
 * Response from batch insurance verification
 */
export interface InsuranceVerifyBatchResponse {
    /**
     * Verification results for each patient in the same order as requested
     */
    'results': Array<InsuranceVerifyBatchResultItem>;
}
/**
 * Single result in a batch verification response
 */
export interface InsuranceVerifyBatchResultItem {
    /**
     * BSN that was verified. It passes the elfproef (11-check) by construction — a BSN that does not is refused before the COV request leaves — which OpenAPI cannot express (#3585). 
     */
    'bsn': string;
    'status': InsuranceVerificationStatus;
    /**
     * List of insurance policies found (only present when status is \'found\')
     */
    'insurances'?: Array<InsuranceInfoDto>;
    /**
     * Human-readable message with additional details
     */
    'message'?: string | null;
}


/**
 * Request to verify patient insurance status via VECOZO COV
 */
export interface InsuranceVerifyRequest {
    /**
     * Dutch citizen service number (BSN), exactly 9 digits.  It must also pass the elfproef (11-check), which OpenAPI cannot express. A BSN that does not is refused before the COV request leaves, since no register can resolve it (#3585): a `400` with `code` `invalid-bsn` and an `errors` array naming `bsn`, the body every BSN refusal in the platform answers with (#4271). In a batch that refuses the **whole** request — the item-level `BSN_INVALID` status means something else, namely a BSN this side accepted that COV then rejected. 
     */
    'bsn': string;
    /**
     * Patient\'s date of birth for verification
     */
    'geboortedatum': string;
}
/**
 * Response from insurance verification via VECOZO COV
 */
export interface InsuranceVerifyResponse {
    'status': InsuranceVerificationStatus;
    /**
     * List of insurance policies found (only present when status is \'found\')
     */
    'insurances'?: Array<InsuranceInfoDto>;
    /**
     * Human-readable message with additional details (for errors or edge cases)
     */
    'message'?: string | null;
}


/**
 * The cliënt as the JW301 names them
 */
export interface Jw301Client {
    'bsn': string;
    /**
     * The full name, composed from the message\'s name parts
     */
    'naam': string;
    'geboortedatum': string;
    /**
     * The BRP address, or the first address the message carries
     */
    'adres'?: string | null;
    'telefoon'?: string | null;
    'email'?: string | null;
}
/**
 * One `ToegewezenProduct`, and whether its toewijzing is already held
 */
export interface Jw301Product {
    'toewijzingNummer': number;
    'productCategorie'?: string | null;
    'productCode'?: string | null;
    'ingangsdatum': string;
    'einddatum'?: string | null;
    'volume'?: number | null;
    'eenheid'?: ToewijzingEenheid | null;
    'frequentie'?: ToewijzingFrequentie | null;
    /**
     * In eurocents
     */
    'budget'?: number | null;
    'redenWijziging'?: ToewijzingRedenWijziging | null;
    /**
     * The organisation already holds this `ToewijzingNummer` for the gemeente, so processing updates that toewijzing rather than adding one 
     */
    'bestaand': boolean;
    /**
     * That toewijzing belongs to another cliënt than the one this JW301 names; processing is refused until it is resolved 
     */
    'andereClient': boolean;
}


export interface Jw301Relatie {
    /**
     * `Relatie/Nummer`, the gemeente\'s own id for the person — what `gezag` names
     */
    'nummer': string;
    /**
     * The gemeente\'s order of contact, 1 first
     */
    'volgorde'?: number | null;
    /**
     * What the person is to the cliënt; absent for a code the dossier does not model
     */
    'soortRelatie'?: SoortRelatie | null;
    'naam': string;
    /**
     * Address, telephone number and e-mail address, as one line
     */
    'contact'?: string | null;
}


export interface Jw301VerwerkRequest {
    /**
     * The cliënt\'s Jeugdwet zorgtraject that new toewijzingen go on. Absent: one is opened for the cliënt. Not read when every product updates a toewijzing already held. 
     */
    'zorgtrajectId'?: string | null;
    /**
     * The `Relatie/Nummer`s of the relaties that hold gezag, as the person confirmed it; empty when none does. Read only when the cliënt is created; a relatie not named here holds none. 
     */
    'gezag': Array<string>;
}
export interface Jw301VerwerkResultaat {
    'clientId': string;
    /**
     * Whether the cliënt was created by this processing
     */
    'clientAangemaakt': boolean;
    /**
     * The zorgtraject new toewijzingen were placed on; absent when every product was an update
     */
    'zorgtrajectId'?: string | null;
    /**
     * The toewijzingen written, one per product, in the message\'s order
     */
    'toewijzingIds': Array<string>;
    /**
     * The JW302 queued in the outbox
     */
    'retourBerichtId': string;
}
/**
 * A received JW301 read for processing (#4812), and what the organisation already holds that processing it would touch. 
 */
export interface Jw301Verwerking {
    'berichtId': string;
    /**
     * The CBS code of the gemeente that sent it
     */
    'gemeentecode': string;
    /**
     * The gemeente\'s name; absent for a code Scrivio\'s gemeentelijst does not hold
     */
    'gemeentenaam'?: string | null;
    'dagtekening': string;
    /**
     * Whether it has been processed already; a processed JW301 is not processed again
     */
    'verwerkt': boolean;
    'client': Jw301Client;
    /**
     * The organisation\'s cliënt with the message\'s BSN; absent when processing creates one
     */
    'bestaandeClientId'?: string | null;
    /**
     * That cliënt\'s name as the dossier records it
     */
    'bestaandeClientNaam'?: string | null;
    /**
     * Every `Relatie`, in the gemeente\'s order
     */
    'relaties': Array<Jw301Relatie>;
    'producten': Array<Jw301Product>;
    /**
     * The existing cliënt\'s Jeugdwet zorgtrajecten, newest first; empty for a new cliënt
     */
    'zorgtrajecten': Array<Jw301Zorgtraject>;
}
export interface Jw301Zorgtraject {
    'id': string;
    'naam'?: string | null;
    'startdatum': string;
    'einddatum'?: string | null;
}
/**
 * One declaration of a regel: the batch it went out in and what the insurer said about it, as `billing_line_declarations` keeps it (#4624). 
 */
export interface LineDeclarationResponse {
    /**
     * The batch this declaration went out in
     */
    'batchId': string;
    /**
     * That batch\'s number
     */
    'batchNumber': string;
    'batchStatus': BillingBatchStatus;
    /**
     * When the batch was made up, which is when the regel was placed in it (milliseconds since epoch). Read off the batch rather than the declaration row, because the rows #4624 backfilled all carry the moment of that migration. 
     */
    'batchedAt': number;
    /**
     * When the batch was sent (milliseconds since epoch); absent while it has not been
     */
    'batchSubmittedAt'?: number | null;
    /**
     * The `Referentienummer` this batch declared the regel under
     */
    'referentienummer'?: string | null;
    /**
     * What this batch declared for the regel
     */
    'declaredAmountEuro': number;
    /**
     * `APPROVED` or `REJECTED` as the retour judged it; absent while none has
     */
    'verdictStatus'?: BillingLineStatus | null;
    /**
     * What the insurer granted for this declaration
     */
    'approvedAmount'?: number | null;
    /**
     * The retourcode (COD954-VEKT) the insurer gave this declaration
     */
    'rejectionCode'?: string | null;
    /**
     * The insurer\'s own words, or the code\'s meaning where it sent none
     */
    'rejectionReason'?: string | null;
    /**
     * The COD954-VEKT `Betekenis` of `rejectionCode`, resolved per response
     */
    'rejectionCodeBetekenis'?: string | null;
}


/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * What a redeclare released, and the batch it was released from
 */
export interface RedeclareBillingBatchResponse {
    'batch': BillingBatchResponse;
    /**
     * Number of billing lines returned to READY and marked as a resubmission. Lines excluded from billing, or already credited, are not counted. On a partially approved batch this is the number of *rejected* lines and can be fewer than the batch holds — say \"3 van 12 regels vrijgegeven\" rather than implying the whole batch moved. 
     */
    'releasedLineCount': number;
}
/**
 * Details of a rejected billing line from GDS802 response
 */
export interface RejectedLineDetail {
    /**
     * ID of the rejected billing line
     */
    'billingLineId': string;
    /**
     * Insurer rejection code
     */
    'rejectionCode': string;
    /**
     * Human-readable rejection reason
     */
    'rejectionReason'?: string | null;
}
export interface SendToBoekhoudingRequest {
    /**
     * The batches whose verzekeraarfacturen to send, in the order they are sent and reported. The same 250 the bulk downloads take, and for the same reason: the selection is per page, and 250 is the largest page the batch list offers. 
     */
    'batchIds': Array<string>;
    /**
     * A SnelStart run only (#4727): the batches whose invoices are to be booked although they already went to the boekhouding by mail — \"Toch boeken in SnelStart\" in the confirmation. Each must be among `batchIds`; each is recorded in the audit log. Absent or empty books none of them. Ignored on a mail run. 
     */
    'bookDespiteMailedBatchIds'?: Array<string>;
    'expectedChannel'?: BoekhoudingChannel;
}


/**
 * Treatment setting (behandelsetting) reference data. Provides 4 settings per NZA tariff structure: AMBULANT_MONO, AMBULANT_MULTI, KLINISCH, PRAKTIJK. The `code` field is the single source of truth for billing dimension lookups. 
 */
export interface SettingResponse {
    /**
     * Setting unique identifier
     */
    'id': string;
    /**
     * Unique code identifier and NZA billing dimension for tariff lookup. One of: AMBULANT_MONO, AMBULANT_MULTI, KLINISCH, PRAKTIJK 
     */
    'code': string;
    /**
     * Human-readable name
     */
    'name': string;
    /**
     * Detailed description
     */
    'description'?: string | null;
    /**
     * Whether this setting is currently active
     */
    'isActive': boolean;
    /**
     * Display order for UI dropdowns
     */
    'displayOrder'?: number | null;
}
/**
 * Where a batch\'s verzekeraarfacturen stand in SnelStart (#4727): `BOOKED` every one, `PARTIAL` some, `FAILED` attempted and none booked, `NOT_BOOKED` not offered yet. Absent where there is nothing to say: no SnelStart, a batch not submitted, or one whose every invoice went by mail. 
 */

export const SnelStartBatchBookingStatus = {
    Booked: 'BOOKED',
    Partial: 'PARTIAL',
    Failed: 'FAILED',
    NotBooked: 'NOT_BOOKED'
} as const;

export type SnelStartBatchBookingStatus = typeof SnelStartBatchBookingStatus[keyof typeof SnelStartBatchBookingStatus];


/**
 * What kind of refusal a `FAILED` verzekeraarfactuur met (#4727), so the screen can point at where it is fixed: `NO_RELATIE` the relatie mappings, `CONNECTION` and `GROOTBOEK` the SnelStart tab, `REJECTED` SnelStart\'s own validation message, `FACTUURNUMMER` a number longer than SnelStart accepts. 
 */

export const SnelStartBookingFailure = {
    NoRelatie: 'NO_RELATIE',
    Connection: 'CONNECTION',
    Grootboek: 'GROOTBOEK',
    Rejected: 'REJECTED',
    Factuurnummer: 'FACTUURNUMMER'
} as const;

export type SnelStartBookingFailure = typeof SnelStartBookingFailure[keyof typeof SnelStartBookingFailure];


/**
 * Where one verzekeraarfactuur stands in SnelStart (#4727): `BOOKED` with SnelStart\'s boeking id; `FAILED` with the reason; `UNCERTAIN` when the booking was sent and no answer came back — the next attempt looks it up in SnelStart before booking anything. 
 */

export const SnelStartSyncState = {
    Booked: 'BOOKED',
    Failed: 'FAILED',
    Uncertain: 'UNCERTAIN'
} as const;

export type SnelStartSyncState = typeof SnelStartSyncState[keyof typeof SnelStartSyncState];


/**
 * What the contact is to the cliënt, in iJW\'s terms (`LDT_SoortRelatie`, JW301 `Relatie/Soort`). Only the codes that occur around a minor; the Vektis code is in brackets: - HULPVERLENER: Hulpverlener (07) - ANDERS: Anders (09) - OUDER: Ouder (10) - VOOGD: Voogd (11) - PLEEGOUDER: Pleegouder (13) - FAMILIELID: Familielid (17) - GEZINSLID: Gezinslid (18) — also a parent\'s partner, for whom iJW has no code 
 */

export const SoortRelatie = {
    Hulpverlener: 'HULPVERLENER',
    Anders: 'ANDERS',
    Ouder: 'OUDER',
    Voogd: 'VOOGD',
    Pleegouder: 'PLEEGOUDER',
    Familielid: 'FAMILIELID',
    Gezinslid: 'GEZINSLID'
} as const;

export type SoortRelatie = typeof SoortRelatie[keyof typeof SoortRelatie];


/**
 * iJw `LDT_Eenheid`: MINUUT (01), UUR (04), ETMAAL (14), DAGDEEL (16, four hours), STUKS_OUTPUT (82), EURO (83), STUKS_INSPANNING (84) 
 */

export const ToewijzingEenheid = {
    Minuut: 'MINUUT',
    Uur: 'UUR',
    Etmaal: 'ETMAAL',
    Dagdeel: 'DAGDEEL',
    StuksOutput: 'STUKS_OUTPUT',
    Euro: 'EURO',
    StuksInspanning: 'STUKS_INSPANNING'
} as const;

export type ToewijzingEenheid = typeof ToewijzingEenheid[keyof typeof ToewijzingEenheid];


/**
 * iJw `LDT_Frequentie`: PER_DAG (1), PER_WEEK (2), PER_MAAND (4), TOTAAL (6, within the toewijzing\'s period) 
 */

export const ToewijzingFrequentie = {
    PerDag: 'PER_DAG',
    PerWeek: 'PER_WEEK',
    PerMaand: 'PER_MAAND',
    Totaal: 'TOTAAL'
} as const;

export type ToewijzingFrequentie = typeof ToewijzingFrequentie[keyof typeof ToewijzingFrequentie];


/**
 * iJw `LDT_RedenWijziging`, in code order 01–12: administratieve correctie, cliënt overleden, contractwijziging, herbeoordeling verlenging, herbeoordeling verkorting, uitstroom naar ander domein, verhuizing naar andere gemeente, wijziging leveringsvorm, overstap naar andere aanbieder, nieuwe bekostigingssystematiek, gemeentelijke herindeling, geïnitieerd door de aanbieder 
 */

export const ToewijzingRedenWijziging = {
    AdministratieveCorrectie: 'ADMINISTRATIEVE_CORRECTIE',
    ClientOverleden: 'CLIENT_OVERLEDEN',
    Contractwijziging: 'CONTRACTWIJZIGING',
    Verlenging: 'VERLENGING',
    Verkorting: 'VERKORTING',
    UitstroomAnderDomein: 'UITSTROOM_ANDER_DOMEIN',
    Verhuizing: 'VERHUIZING',
    WijzigingLeveringsvorm: 'WIJZIGING_LEVERINGSVORM',
    OverstapAndereAanbieder: 'OVERSTAP_ANDERE_AANBIEDER',
    NieuweBekostigingssystematiek: 'NIEUWE_BEKOSTIGINGSSYSTEMATIEK',
    GemeentelijkeHerindeling: 'GEMEENTELIJKE_HERINDELING',
    GeinitieerdDoorAanbieder: 'GEINITIEERD_DOOR_AANBIEDER'
} as const;

export type ToewijzingRedenWijziging = typeof ToewijzingRedenWijziging[keyof typeof ToewijzingRedenWijziging];


/**
 * A toewijzing as it should become from `gewensteIngangsdatum`. `einddatum`, `omvang` and `budget` are the requested values, not differences: send the current one for what stays. 
 */
export interface ToewijzingWijzigingsverzoekRequest {
    'redenVerzoek': IjwRedenVerzoek;
    'gewensteIngangsdatum': string;
    'einddatum'?: string | null;
    'omvang'?: ToewijzingsverzoekOmvang;
    /**
     * In eurocent (iJw `LDT_Budget`)
     */
    'budget'?: number | null;
    /**
     * Another product in the toewijzing\'s categorie; absent or the current one keeps the product
     */
    'productCode'?: string | null;
}


export interface ToewijzingsverzoekCategorie {
    'productCategorie': string;
    /**
     * The contract\'s budget for this categorie on the ingangsdatum, in eurocent; null when it states none
     */
    'budget'?: number | null;
}
/**
 * A Jeugdwet contract covering the gemeente, with what a request may name under it.
 */
export interface ToewijzingsverzoekContract {
    'contractId': string;
    'contractNumber'?: string | null;
    'producten': Array<ToewijzingsverzoekProduct>;
    /**
     * Every categorie the contract holds a product or a budget for, ascending
     */
    'categorieen': Array<ToewijzingsverzoekCategorie>;
}
export interface ToewijzingsverzoekOmvang {
    'volume': number;
    'eenheid': ToewijzingEenheid;
    'frequentie': ToewijzingFrequentie;
}


/**
 * What a toewijzing request for one zorgtraject can be made of (#4810).
 */
export interface ToewijzingsverzoekOpties {
    /**
     * The gemeente the JW315 goes to; null when none is confirmed
     */
    'gemeentecode'?: string | null;
    'gemeentenaam'?: string | null;
    /**
     * The day the cliënt turns 18; null when the geboortedatum is not recorded
     */
    'wordtAchttienOp'?: string | null;
    'contracten': Array<ToewijzingsverzoekContract>;
    'waarschuwingen': Array<ToewijzingsverzoekWaarschuwing>;
}
/**
 * A contract product a specifiek request may name. `maxOmvang` with its eenheid and frequentie, and `maxDuurMaanden`, are the product\'s request defaults — what the dialog pre-fills, never a limit. 
 */
export interface ToewijzingsverzoekProduct {
    'productCategorie': string;
    'productCode': string;
    'omschrijving'?: string | null;
    'maxOmvang'?: number | null;
    'maxOmvangEenheid'?: ToewijzingEenheid | null;
    'maxOmvangFrequentie'?: ToewijzingFrequentie | null;
    'maxDuurMaanden'?: number | null;
}


/**
 * One requested product. `productCode` and `omvang` are required for SPECIFIEK and ignored for ASPECIFIEK; `budget` is required for ASPECIFIEK and ignored for SPECIFIEK. 
 */
export interface ToewijzingsverzoekRequest {
    'soort': ToewijzingsverzoekSoort;
    /**
     * The contract the product is chosen from; required whenever one covers the gemeente
     */
    'contractId'?: string | null;
    'productCategorie': string;
    'productCode'?: string | null;
    'ingangsdatum': string;
    'einddatum'?: string | null;
    'omvang'?: ToewijzingsverzoekOmvang;
    /**
     * In eurocent (iJw `LDT_Budget`)
     */
    'budget'?: number | null;
    'commentaar'?: string | null;
}


/**
 * The JW315 or JW317 as queued in the outbox.
 */
export interface ToewijzingsverzoekResponse {
    'berichtId': string;
    'identificatie': string;
    'gemeentecode': string;
}
/**
 * SPECIFIEK names a product and its omvang; ASPECIFIEK names only a productcategorie and a budget (iJw CD077/CD078). 
 */

export const ToewijzingsverzoekSoort = {
    Specifiek: 'SPECIFIEK',
    Aspecifiek: 'ASPECIFIEK'
} as const;

export type ToewijzingsverzoekSoort = typeof ToewijzingsverzoekSoort[keyof typeof ToewijzingsverzoekSoort];


/**
 * GEMEENTE_NIET_VASTGELEGD: the cliënt has no confirmed Jeugdwet verantwoordelijke gemeente today, so there is no gemeente to address a JW315 to. GEEN_CONTRACT: no live, active Jeugdwet contract covers the gemeente on the ingangsdatum. 
 */

export const ToewijzingsverzoekWaarschuwing = {
    GemeenteNietVastgelegd: 'GEMEENTE_NIET_VASTGELEGD',
    GeenContract: 'GEEN_CONTRACT'
} as const;

export type ToewijzingsverzoekWaarschuwing = typeof ToewijzingsverzoekWaarschuwing[keyof typeof ToewijzingsverzoekWaarschuwing];


/**
 * An outgoing iJw bericht and how far it got. On a gemeente batch (`jw323Stand`) its latest JW323, absent on any other batch and before the batch is locked; on a regel held on `START_NIET_BEVESTIGD` (`startbericht`) the toewijzing\'s latest JW305, absent while none was queued yet and on every other reden. 
 */
export interface UitgaandBerichtstand {
    /**
     * The bericht in the Berichten outbox
     */
    'berichtId': string;
    'status': IjwBerichtStatus;
    /**
     * When it was queued in the outbox
     */
    'klaargezetAt': number;
    /**
     * When it was sent to VECOZO; null while it waits in the outbox
     */
    'verzondenAt'?: number | null;
    /**
     * When the gemeente\'s retour on it arrived; null while none has
     */
    'beantwoordAt'?: number | null;
}


export interface UpdateBatchStatusRequest {
    'status': BillingBatchStatus;
}


/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateBillingLineRequest {
    /**
     * Service code ID
     */
    'serviceCodeId'?: string | null;
    /**
     * Number of quarters
     */
    'quarters'?: number | null;
    /**
     * Amount approved by insurer (from GDS802)
     */
    'approvedAmount'?: number | null;
    /**
     * Amount actually received in bank account (manual entry)
     */
    'receivedAmount'?: number | null;
    /**
     * Notes
     */
    'notes'?: string | null;
}
/**
 * Summary counts of validation issues for billable appointments. Used to display validation badges in the billing \"Openstaand\" screen. 
 */
export interface ValidationSummaryDto {
    /**
     * Number of appointments ready to process (no validation errors)
     */
    'teVerwerken': number;
    /**
     * Number of appointments missing referral letter
     */
    'verwijsbriefOntbreekt': number;
    /**
     * Number of appointments missing care typing
     */
    'zorgvraagtyperingOntbreekt': number;
    /**
     * Number of appointments where practitioner AGB code is missing
     */
    'agbCodeOnbekend': number;
    /**
     * Number of appointments where client BSN is missing
     */
    'bsnOntbreekt': number;
    /**
     * Number of appointments with unknown/invalid setting
     */
    'settingOnbekend': number;
    /**
     * Number of appointments where insurer is missing
     */
    'insurerOntbreekt': number;
}
export interface VecozoMelding {
    /**
     * VECOZO\'s check code, e.g. `BRS03` (no toestemmingsverklaring) or `BRS13` (XSD)
     */
    'code': string;
    /**
     * VECOZO\'s description of the failed check, in its own words
     */
    'omschrijving': string;
    /**
     * VECOZO\'s further explanation, when it gives one
     */
    'toelichting'?: string | null;
}

/**
 * BillableAppointmentsApi - axios parameter creator
 */
export const BillableAppointmentsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * The rows `findBillableAppointments` lists, as a file — every row of the filtered set rather than one page of it (#4145).  It takes exactly the set-defining parameters of `findBillableAppointments` and none of the ones that describe how a *screen* reads that set: there is no `page` and no `size`, because the whole filtered set is the point, and no `sort`, because a file is a document rather than a view — the rows come out oldest consult first, by cliënt, so two exports of an unchanged set are byte-identical.  The set is the screen\'s: openstaand consults only. A consult that has been batched, moved past DRAFT/READY or invoiced is not in `billable_overview_row_v` and so is not in the file. An export spanning already-declared consults is a different question over a different source.  The file names cliënten, so it is PHI leaving the product: one `audit_log` EXPORT row per distinct cliënt in the exported set is written **before** the bytes are rendered, and an export whose audit rows cannot be written fails with a `500` and delivers nothing (ADR-024). 
         * @summary Export the filtered INSURED openstaand set
         * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
         * @param {BillableAppointmentStatus} [status] Filter by declaration readiness, exactly as &#x60;findBillableAppointments&#x60; applies it. READY keeps the rows with no blocking signals, BLOCKED keeps the rest. Omitted exports both.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
         * @param {string} [definitionKey] Keep only the consults a blocking signal of this definition reaches — the cause drilldown, applied exactly as &#x60;findBillableAppointments&#x60; applies it.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportBillableAppointments: async (format: ConsultExportFormat, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, definitionKey?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'format' is not null or undefined
            assertParamExists('exportBillableAppointments', 'format', format)
            const localVarPath = `/api/v1/billing/billable-appointments/export`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (format !== undefined) {
                localVarQueryParameter['format'] = format;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (periodStart !== undefined) {
                localVarQueryParameter['periodStart'] = (periodStart as any instanceof Date) ?
                    (periodStart as any).toISOString().substring(0,10) :
                    periodStart;
            }

            if (periodEnd !== undefined) {
                localVarQueryParameter['periodEnd'] = (periodEnd as any instanceof Date) ?
                    (periodEnd as any).toISOString().substring(0,10) :
                    periodEnd;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (insurerIds) {
                localVarQueryParameter['insurerIds'] = insurerIds;
            }

            if (practitionerId !== undefined) {
                localVarQueryParameter['practitionerId'] = practitionerId;
            }

            if (regiebehandelaarId !== undefined) {
                localVarQueryParameter['regiebehandelaarId'] = regiebehandelaarId;
            }

            if (locationId !== undefined) {
                localVarQueryParameter['locationId'] = locationId;
            }

            if (soort) {
                localVarQueryParameter['soort'] = soort;
            }

            if (definitionKey !== undefined) {
                localVarQueryParameter['definitionKey'] = definitionKey;
            }

            localVarHeaderParameter['Accept'] = 'text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Writes one row per appointment x cliënt for a period, covering **both halves** of the declaratie administration: the consults that are still openstaand and the ones the insurer or the cliënt already has.  `findBillableAppointments` answers the openstaand set only — `billable_overview_row_v` anti-joins `settled_consult_v`, so a consult that has been batched, moved past DRAFT/READY or invoiced has left it. A practice reconciling a period needs the other half beside it, with the tariff each consult actually carries.  **`isSettled` says which half a row belongs to**, and the two tariff blocks populate accordingly — they are complementary by construction rather than by a rule stated here:  - `open*` — what the declaratie screen shows for a consult that is still openstaand,   taken from the same view the screen renders. - `billed*` — the billing lines of a settled consult, **summed per consult**, with   `billedLineCount` so a multi-line consult is visible as one. The amounts come off the   lines\' own snapshots and are never re-derived: `billing_lines` snapshots   `tariff_euro` / `nza_tariff_euro` at generation (#2405) and the NZa tariff tables move   year on year, so a fresh lookup would report today\'s price for last year\'s declaration.  A row with **neither** block is a consult that is not billable at all — a cancelled afspraak, an afspraaktype that declares nothing, a cliënt with no covering polis whose consult never entered the queue. Those are deliberately in the file: an unexplained gap between the agenda and the declaraties is exactly what a reconciliation is looking for.  **The contracted amount and the NZa maximum are separate columns on both halves** (#2405) — never one figure that could be read as either.  **No cliënt naam, BSN, e-mailadres or geboortedatum appears in the output.** What is in it is what a declaration is decided, priced or defended on, which includes several fields that read as dossier content and are billing determinants: the GGZ-type with its basis-GGZ-profiel / DSM-hoofdgroep (these pick the Diagnosecodelijst the nota and the GDS801 diagnoseblok carry, #3518/#3572), the verzorgingsgraad (which selects the VERBLIJF prestatiecode) and the verwijzer block (which the nota and RC032 read, #3679). `clientId` stays, because a reconciliation file that cannot say which cliënt a line belongs to cannot be reconciled.  The whole filtered set is written — this is not paged.  **Permission required:** `billing.read` 
         * @summary Export every consult in a period, billed and unbilled together
         * @param {string} periodStart First service date in the export (inclusive).
         * @param {string} periodEnd Last service date in the export (inclusive).
         * @param {ConsultExportFormat} [format] Which file to write. Absent is &#x60;CSV&#x60;.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Filter by the verzekeraar the consult\&#39;s own facts name. That is the polis in force on the service date, which is what the declaratie queue filters on too — not the insurer a settled line happened to be generated for, though the two agree wherever nothing has changed since.  Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so the file covers the same set of verzekeraars the screen that asked for it is showing (#4520). Omitted, or repeated zero times, is every verzekeraar. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the cliënt\&#39;s zorgtraject. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportPeriodConsults: async (periodStart: string, periodEnd: string, format?: ConsultExportFormat, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'periodStart' is not null or undefined
            assertParamExists('exportPeriodConsults', 'periodStart', periodStart)
            // verify required parameter 'periodEnd' is not null or undefined
            assertParamExists('exportPeriodConsults', 'periodEnd', periodEnd)
            const localVarPath = `/api/v1/billing/period-consults/export`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (periodStart !== undefined) {
                localVarQueryParameter['periodStart'] = (periodStart as any instanceof Date) ?
                    (periodStart as any).toISOString().substring(0,10) :
                    periodStart;
            }

            if (periodEnd !== undefined) {
                localVarQueryParameter['periodEnd'] = (periodEnd as any instanceof Date) ?
                    (periodEnd as any).toISOString().substring(0,10) :
                    periodEnd;
            }

            if (format !== undefined) {
                localVarQueryParameter['format'] = format;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (insurerIds) {
                localVarQueryParameter['insurerIds'] = insurerIds;
            }

            if (practitionerId !== undefined) {
                localVarQueryParameter['practitionerId'] = practitionerId;
            }

            if (regiebehandelaarId !== undefined) {
                localVarQueryParameter['regiebehandelaarId'] = regiebehandelaarId;
            }

            if (locationId !== undefined) {
                localVarQueryParameter['locationId'] = locationId;
            }

            localVarHeaderParameter['Accept'] = 'text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The particulier half of `exportBillableAppointments` (#4145): the rows `findPrivateBillableAppointments` lists, as a file, filtered exactly as that endpoint filters and carrying the same columns as the insured export.  It is a second operation rather than a `billingCategory` parameter on the first for the reason the two list endpoints are two: a private row carries no insurer and no blocking signals, so `insurerIds`, `status` and `definitionKey` would be parameters this half accepts and cannot honestly answer.  The same audit rule applies — one `audit_log` EXPORT row per distinct cliënt, written before the bytes are rendered (ADR-024). 
         * @summary Export the filtered PRIVATE openstaand set
         * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportPrivateBillableAppointments: async (format: ConsultExportFormat, periodStart?: string, periodEnd?: string, clientId?: string, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'format' is not null or undefined
            assertParamExists('exportPrivateBillableAppointments', 'format', format)
            const localVarPath = `/api/v1/billing/private-billable-appointments/export`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (format !== undefined) {
                localVarQueryParameter['format'] = format;
            }

            if (periodStart !== undefined) {
                localVarQueryParameter['periodStart'] = (periodStart as any instanceof Date) ?
                    (periodStart as any).toISOString().substring(0,10) :
                    periodStart;
            }

            if (periodEnd !== undefined) {
                localVarQueryParameter['periodEnd'] = (periodEnd as any instanceof Date) ?
                    (periodEnd as any).toISOString().substring(0,10) :
                    periodEnd;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (practitionerId !== undefined) {
                localVarQueryParameter['practitionerId'] = practitionerId;
            }

            if (regiebehandelaarId !== undefined) {
                localVarQueryParameter['regiebehandelaarId'] = regiebehandelaarId;
            }

            if (locationId !== undefined) {
                localVarQueryParameter['locationId'] = locationId;
            }

            localVarHeaderParameter['Accept'] = 'text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Find completed appointments that are ready for INSURED billing (VECOZO/insurers). Returns appointments that: - Have status COMPLETED - Are CLIENT category - Have billingCategory = INSURED on their appointment type - Are not billed at a `clientInvoiced` service code (OV0165): that prestatie is   invoiced to the cliënt and never declared, so it is listed by   `findPrivateBillableAppointments` instead - Have not yet been billed (no billing line exists)  By default (no date filters), returns all unprocessed completed appointments to show the full backlog of billable work. Date filters can be used to narrow down the results to a specific period.  The result is paginated and ordered server-side (#3812): every row of the filtered set is resolved, ordered by `sort`, and only the requested page is returned. `totalElements` therefore counts the whole filtered set, not the page.  This is the \"Te declareren\" list (#4742): beside the prestaties still to declare it lists the credit and correctie regels whose prestatie the insurer already has — one row per thing that goes to the insurer, read from `declaration_queue_v`. Such a row carries `billingLineId`, its `soort`, and the `linkedEntry` of the other half of a credit-and-correct pair. It is never blocked: readiness is about a prestatie still to be priced. 
         * @summary Find INSURED billable appointments
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page.  The maximum is above the 100 most lists here allow: the queue\&#39;s selection is **per page**, so the page size is how a practice acts on more of it at once. 500 is the largest choice the screen\&#39;s page-size control offers (&#x60;BILLABLE_PAGE_SIZE_OPTIONS&#x60; in &#x60;scrivio-fe/src/constants/billing.ts&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers. 
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: clientName, practitionerName, regiebehandelaarName, insurerName, serviceDate, status, soort. Any other field falls back to serviceDate. Ascending &#x60;status&#x60; puts the rows with no blocking signals first; ascending &#x60;soort&#x60; orders NIEUW, HERDECLARATIE, CREDIT, CORRECTIE.
         * @param {BillableAppointmentStatus} [status] Filter by declaration readiness. READY keeps the rows with no blocking signals, BLOCKED keeps the rest. Omitted returns both. &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; on the response are counted before this filter is applied, so they stay stable while it is switched.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
         * @param {string} [definitionKey] Keep only the consults a **blocking** signal of this definition reaches — the drilldown of one cause reported by &#x60;/billing/blocking-signals/summary&#x60;.  A line-wide blocker (organisatie, locatie, behandelaar, contract, afspraak) reaches every client\&#39;s line of its appointment; a client-attributable one (cliënt, verzekeraar, de afspraak-feiten van één cliënt) reaches only that client\&#39;s line.  Advisory causes are deliberately not selectable: they fire on consults that are ready to declare and are not what holds the backlog back. An unknown or misspelled key therefore matches nothing and returns an empty page — a filter that matches nothing never reads as no filter.  &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are unaffected by this parameter, for the same reason &#x60;status&#x60; does not move them: they label the panes, and a cause drilldown must not collapse them to its own size. &#x60;totalElements&#x60; does count the cause\&#39;s whole filtered set, so it can be paged. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findBillableAppointments: async (page?: number, size?: number, sort?: string, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, definitionKey?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/billing/billable-appointments`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (periodStart !== undefined) {
                localVarQueryParameter['periodStart'] = (periodStart as any instanceof Date) ?
                    (periodStart as any).toISOString().substring(0,10) :
                    periodStart;
            }

            if (periodEnd !== undefined) {
                localVarQueryParameter['periodEnd'] = (periodEnd as any instanceof Date) ?
                    (periodEnd as any).toISOString().substring(0,10) :
                    periodEnd;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (insurerIds) {
                localVarQueryParameter['insurerIds'] = insurerIds;
            }

            if (practitionerId !== undefined) {
                localVarQueryParameter['practitionerId'] = practitionerId;
            }

            if (regiebehandelaarId !== undefined) {
                localVarQueryParameter['regiebehandelaarId'] = regiebehandelaarId;
            }

            if (locationId !== undefined) {
                localVarQueryParameter['locationId'] = locationId;
            }

            if (soort) {
                localVarQueryParameter['soort'] = soort;
            }

            if (definitionKey !== undefined) {
                localVarQueryParameter['definitionKey'] = definitionKey;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * One page of **clients** in the zorgverzekeraar declaratie queue (#4175), over exactly the set `findBillableAppointments` lists with the same filters — the same view, the same predicates, one row per distinct client instead of one per consult.  It is a second endpoint rather than a second spelling because the grain is the only difference: the queue grouped by client shows clients, and paging it over consults put the same client on as many pages as they had consults — the ordering is by date, so a client\'s consults are scattered across the whole set — while the pager counted consults for a screen listing clients (2.000 consults across 150 clients gave 100 pages for a 150-row list). Ask this endpoint for the clients and `findBillableAppointments?clientId=` for one client\'s consults; each client then appears on exactly one page and `totalElements` counts what the screen shows.  Ordered by client name, case-insensitively, then by id — never by the queue\'s own `sort`, which orders consults. There is deliberately no `sort` parameter: a list of clients has one useful order and a second one would be a second answer to \"which page is this client on\".  There is no `definitionKey` either. That is the \"Actie nodig\" drilldown, which lists the consults of one blocking cause, and that pane groups by oorzaak or not at all — never by client — so a cause narrowing here would be a parameter with no screen behind it.  `consultCount`, `totalAmount` and `unpricedCount` are aggregates over that client\'s **whole filtered set** and are unaffected by which page the client lands on (#3814). Paging the list of clients and computing each row\'s figures over the whole set are independent, and both hold. 
         * @summary Find the clients with INSURED billable consults
         * @param {number} [page] Page number (0-indexed), over distinct clients
         * @param {number} [size] Number of clients per page.  The maximum is above the 100 most lists here allow: the queue\&#39;s selection is **per page**, so the page size is how a practice acts on more of it at once. 500 is the largest choice the screen\&#39;s page-size control offers (&#x60;BILLABLE_PAGE_SIZE_OPTIONS&#x60; in &#x60;scrivio-fe/src/constants/billing.ts&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers.  A page counts cliënten and each brings all its consults, so the response grows with the size even though the query does not. 
         * @param {BillableAppointmentStatus} [status] Filter by declaration readiness, exactly as on &#x60;findBillableAppointments&#x60;: READY keeps the consults with no blocking signals, BLOCKED keeps the rest, omitted counts both. A client with no consult left in the filtered set is not listed at all.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findBillableClients: async (page?: number, size?: number, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/billing/billable-clients`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (periodStart !== undefined) {
                localVarQueryParameter['periodStart'] = (periodStart as any instanceof Date) ?
                    (periodStart as any).toISOString().substring(0,10) :
                    periodStart;
            }

            if (periodEnd !== undefined) {
                localVarQueryParameter['periodEnd'] = (periodEnd as any instanceof Date) ?
                    (periodEnd as any).toISOString().substring(0,10) :
                    periodEnd;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (insurerIds) {
                localVarQueryParameter['insurerIds'] = insurerIds;
            }

            if (practitionerId !== undefined) {
                localVarQueryParameter['practitionerId'] = practitionerId;
            }

            if (regiebehandelaarId !== undefined) {
                localVarQueryParameter['regiebehandelaarId'] = regiebehandelaarId;
            }

            if (locationId !== undefined) {
                localVarQueryParameter['locationId'] = locationId;
            }

            if (soort) {
                localVarQueryParameter['soort'] = soort;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Find completed appointments that are ready to be invoiced to the cliënt. Returns appointments that: - Have status COMPLETED - Are CLIENT category - Have billingCategory = PRIVATE on their appointment type, or are INSURED and   billed at a `clientInvoiced` service code (OV0165) — insured care that is   invoiced to the cliënt and never declared. Such a row carries   `clientInvoiced`, its `serviceCode`, `quarters` and `estimatedAmount`, and   becomes a nota of its own per afspraak - Have not yet been billed (no billing line exists)  By default (no date filters), returns all unprocessed completed appointments to show the full backlog of billable work. Date filters can be used to narrow down the results to a specific period.  The result is paginated and ordered server-side (#3812): every row of the filtered set is resolved, ordered by `sort`, and only the requested page is returned. `totalElements` therefore counts the whole filtered set, not the page.  Private rows carry neither an insurer nor blocking signals, so this endpoint sorts on neither, and takes no `status` or `definitionKey` — see `findBillableAppointments` for those. 
         * @summary Find PRIVATE billable appointments
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: clientName, practitionerName, regiebehandelaarName, serviceDate. Any other field falls back to serviceDate.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findPrivateBillableAppointments: async (page?: number, size?: number, sort?: string, periodStart?: string, periodEnd?: string, clientId?: string, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/billing/private-billable-appointments`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (periodStart !== undefined) {
                localVarQueryParameter['periodStart'] = (periodStart as any instanceof Date) ?
                    (periodStart as any).toISOString().substring(0,10) :
                    periodStart;
            }

            if (periodEnd !== undefined) {
                localVarQueryParameter['periodEnd'] = (periodEnd as any instanceof Date) ?
                    (periodEnd as any).toISOString().substring(0,10) :
                    periodEnd;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (practitionerId !== undefined) {
                localVarQueryParameter['practitionerId'] = practitionerId;
            }

            if (regiebehandelaarId !== undefined) {
                localVarQueryParameter['regiebehandelaarId'] = regiebehandelaarId;
            }

            if (locationId !== undefined) {
                localVarQueryParameter['locationId'] = locationId;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Generates billing lines from the specified appointments. Each appointment generates one billing line per client. Group appointments generate multiple lines.  Appointments that were billed earlier but whose draft lines were never invoiced or batched (e.g. invoice creation failed after generation) return those existing lines instead of being skipped, so invoicing can be retried. 
         * @summary Generate billing lines from appointments
         * @param {GenerateBillingLinesRequest} generateBillingLinesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateBillingLines: async (generateBillingLinesRequest: GenerateBillingLinesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'generateBillingLinesRequest' is not null or undefined
            assertParamExists('generateBillingLines', 'generateBillingLinesRequest', generateBillingLinesRequest)
            const localVarPath = `/api/v1/billing/generate`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(generateBillingLinesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * BillableAppointmentsApi - functional programming interface
 */
export const BillableAppointmentsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = BillableAppointmentsApiAxiosParamCreator(configuration)
    return {
        /**
         * The rows `findBillableAppointments` lists, as a file — every row of the filtered set rather than one page of it (#4145).  It takes exactly the set-defining parameters of `findBillableAppointments` and none of the ones that describe how a *screen* reads that set: there is no `page` and no `size`, because the whole filtered set is the point, and no `sort`, because a file is a document rather than a view — the rows come out oldest consult first, by cliënt, so two exports of an unchanged set are byte-identical.  The set is the screen\'s: openstaand consults only. A consult that has been batched, moved past DRAFT/READY or invoiced is not in `billable_overview_row_v` and so is not in the file. An export spanning already-declared consults is a different question over a different source.  The file names cliënten, so it is PHI leaving the product: one `audit_log` EXPORT row per distinct cliënt in the exported set is written **before** the bytes are rendered, and an export whose audit rows cannot be written fails with a `500` and delivers nothing (ADR-024). 
         * @summary Export the filtered INSURED openstaand set
         * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
         * @param {BillableAppointmentStatus} [status] Filter by declaration readiness, exactly as &#x60;findBillableAppointments&#x60; applies it. READY keeps the rows with no blocking signals, BLOCKED keeps the rest. Omitted exports both.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
         * @param {string} [definitionKey] Keep only the consults a blocking signal of this definition reaches — the cause drilldown, applied exactly as &#x60;findBillableAppointments&#x60; applies it.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async exportBillableAppointments(format: ConsultExportFormat, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, definitionKey?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.exportBillableAppointments(format, status, periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, soort, definitionKey, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillableAppointmentsApi.exportBillableAppointments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Writes one row per appointment x cliënt for a period, covering **both halves** of the declaratie administration: the consults that are still openstaand and the ones the insurer or the cliënt already has.  `findBillableAppointments` answers the openstaand set only — `billable_overview_row_v` anti-joins `settled_consult_v`, so a consult that has been batched, moved past DRAFT/READY or invoiced has left it. A practice reconciling a period needs the other half beside it, with the tariff each consult actually carries.  **`isSettled` says which half a row belongs to**, and the two tariff blocks populate accordingly — they are complementary by construction rather than by a rule stated here:  - `open*` — what the declaratie screen shows for a consult that is still openstaand,   taken from the same view the screen renders. - `billed*` — the billing lines of a settled consult, **summed per consult**, with   `billedLineCount` so a multi-line consult is visible as one. The amounts come off the   lines\' own snapshots and are never re-derived: `billing_lines` snapshots   `tariff_euro` / `nza_tariff_euro` at generation (#2405) and the NZa tariff tables move   year on year, so a fresh lookup would report today\'s price for last year\'s declaration.  A row with **neither** block is a consult that is not billable at all — a cancelled afspraak, an afspraaktype that declares nothing, a cliënt with no covering polis whose consult never entered the queue. Those are deliberately in the file: an unexplained gap between the agenda and the declaraties is exactly what a reconciliation is looking for.  **The contracted amount and the NZa maximum are separate columns on both halves** (#2405) — never one figure that could be read as either.  **No cliënt naam, BSN, e-mailadres or geboortedatum appears in the output.** What is in it is what a declaration is decided, priced or defended on, which includes several fields that read as dossier content and are billing determinants: the GGZ-type with its basis-GGZ-profiel / DSM-hoofdgroep (these pick the Diagnosecodelijst the nota and the GDS801 diagnoseblok carry, #3518/#3572), the verzorgingsgraad (which selects the VERBLIJF prestatiecode) and the verwijzer block (which the nota and RC032 read, #3679). `clientId` stays, because a reconciliation file that cannot say which cliënt a line belongs to cannot be reconciled.  The whole filtered set is written — this is not paged.  **Permission required:** `billing.read` 
         * @summary Export every consult in a period, billed and unbilled together
         * @param {string} periodStart First service date in the export (inclusive).
         * @param {string} periodEnd Last service date in the export (inclusive).
         * @param {ConsultExportFormat} [format] Which file to write. Absent is &#x60;CSV&#x60;.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Filter by the verzekeraar the consult\&#39;s own facts name. That is the polis in force on the service date, which is what the declaratie queue filters on too — not the insurer a settled line happened to be generated for, though the two agree wherever nothing has changed since.  Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so the file covers the same set of verzekeraars the screen that asked for it is showing (#4520). Omitted, or repeated zero times, is every verzekeraar. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the cliënt\&#39;s zorgtraject. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async exportPeriodConsults(periodStart: string, periodEnd: string, format?: ConsultExportFormat, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.exportPeriodConsults(periodStart, periodEnd, format, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillableAppointmentsApi.exportPeriodConsults']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The particulier half of `exportBillableAppointments` (#4145): the rows `findPrivateBillableAppointments` lists, as a file, filtered exactly as that endpoint filters and carrying the same columns as the insured export.  It is a second operation rather than a `billingCategory` parameter on the first for the reason the two list endpoints are two: a private row carries no insurer and no blocking signals, so `insurerIds`, `status` and `definitionKey` would be parameters this half accepts and cannot honestly answer.  The same audit rule applies — one `audit_log` EXPORT row per distinct cliënt, written before the bytes are rendered (ADR-024). 
         * @summary Export the filtered PRIVATE openstaand set
         * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async exportPrivateBillableAppointments(format: ConsultExportFormat, periodStart?: string, periodEnd?: string, clientId?: string, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.exportPrivateBillableAppointments(format, periodStart, periodEnd, clientId, practitionerId, regiebehandelaarId, locationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillableAppointmentsApi.exportPrivateBillableAppointments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Find completed appointments that are ready for INSURED billing (VECOZO/insurers). Returns appointments that: - Have status COMPLETED - Are CLIENT category - Have billingCategory = INSURED on their appointment type - Are not billed at a `clientInvoiced` service code (OV0165): that prestatie is   invoiced to the cliënt and never declared, so it is listed by   `findPrivateBillableAppointments` instead - Have not yet been billed (no billing line exists)  By default (no date filters), returns all unprocessed completed appointments to show the full backlog of billable work. Date filters can be used to narrow down the results to a specific period.  The result is paginated and ordered server-side (#3812): every row of the filtered set is resolved, ordered by `sort`, and only the requested page is returned. `totalElements` therefore counts the whole filtered set, not the page.  This is the \"Te declareren\" list (#4742): beside the prestaties still to declare it lists the credit and correctie regels whose prestatie the insurer already has — one row per thing that goes to the insurer, read from `declaration_queue_v`. Such a row carries `billingLineId`, its `soort`, and the `linkedEntry` of the other half of a credit-and-correct pair. It is never blocked: readiness is about a prestatie still to be priced. 
         * @summary Find INSURED billable appointments
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page.  The maximum is above the 100 most lists here allow: the queue\&#39;s selection is **per page**, so the page size is how a practice acts on more of it at once. 500 is the largest choice the screen\&#39;s page-size control offers (&#x60;BILLABLE_PAGE_SIZE_OPTIONS&#x60; in &#x60;scrivio-fe/src/constants/billing.ts&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers. 
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: clientName, practitionerName, regiebehandelaarName, insurerName, serviceDate, status, soort. Any other field falls back to serviceDate. Ascending &#x60;status&#x60; puts the rows with no blocking signals first; ascending &#x60;soort&#x60; orders NIEUW, HERDECLARATIE, CREDIT, CORRECTIE.
         * @param {BillableAppointmentStatus} [status] Filter by declaration readiness. READY keeps the rows with no blocking signals, BLOCKED keeps the rest. Omitted returns both. &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; on the response are counted before this filter is applied, so they stay stable while it is switched.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
         * @param {string} [definitionKey] Keep only the consults a **blocking** signal of this definition reaches — the drilldown of one cause reported by &#x60;/billing/blocking-signals/summary&#x60;.  A line-wide blocker (organisatie, locatie, behandelaar, contract, afspraak) reaches every client\&#39;s line of its appointment; a client-attributable one (cliënt, verzekeraar, de afspraak-feiten van één cliënt) reaches only that client\&#39;s line.  Advisory causes are deliberately not selectable: they fire on consults that are ready to declare and are not what holds the backlog back. An unknown or misspelled key therefore matches nothing and returns an empty page — a filter that matches nothing never reads as no filter.  &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are unaffected by this parameter, for the same reason &#x60;status&#x60; does not move them: they label the panes, and a cause drilldown must not collapse them to its own size. &#x60;totalElements&#x60; does count the cause\&#39;s whole filtered set, so it can be paged. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findBillableAppointments(page?: number, size?: number, sort?: string, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, definitionKey?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillableAppointmentPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findBillableAppointments(page, size, sort, status, periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, soort, definitionKey, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillableAppointmentsApi.findBillableAppointments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * One page of **clients** in the zorgverzekeraar declaratie queue (#4175), over exactly the set `findBillableAppointments` lists with the same filters — the same view, the same predicates, one row per distinct client instead of one per consult.  It is a second endpoint rather than a second spelling because the grain is the only difference: the queue grouped by client shows clients, and paging it over consults put the same client on as many pages as they had consults — the ordering is by date, so a client\'s consults are scattered across the whole set — while the pager counted consults for a screen listing clients (2.000 consults across 150 clients gave 100 pages for a 150-row list). Ask this endpoint for the clients and `findBillableAppointments?clientId=` for one client\'s consults; each client then appears on exactly one page and `totalElements` counts what the screen shows.  Ordered by client name, case-insensitively, then by id — never by the queue\'s own `sort`, which orders consults. There is deliberately no `sort` parameter: a list of clients has one useful order and a second one would be a second answer to \"which page is this client on\".  There is no `definitionKey` either. That is the \"Actie nodig\" drilldown, which lists the consults of one blocking cause, and that pane groups by oorzaak or not at all — never by client — so a cause narrowing here would be a parameter with no screen behind it.  `consultCount`, `totalAmount` and `unpricedCount` are aggregates over that client\'s **whole filtered set** and are unaffected by which page the client lands on (#3814). Paging the list of clients and computing each row\'s figures over the whole set are independent, and both hold. 
         * @summary Find the clients with INSURED billable consults
         * @param {number} [page] Page number (0-indexed), over distinct clients
         * @param {number} [size] Number of clients per page.  The maximum is above the 100 most lists here allow: the queue\&#39;s selection is **per page**, so the page size is how a practice acts on more of it at once. 500 is the largest choice the screen\&#39;s page-size control offers (&#x60;BILLABLE_PAGE_SIZE_OPTIONS&#x60; in &#x60;scrivio-fe/src/constants/billing.ts&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers.  A page counts cliënten and each brings all its consults, so the response grows with the size even though the query does not. 
         * @param {BillableAppointmentStatus} [status] Filter by declaration readiness, exactly as on &#x60;findBillableAppointments&#x60;: READY keeps the consults with no blocking signals, BLOCKED keeps the rest, omitted counts both. A client with no consult left in the filtered set is not listed at all.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findBillableClients(page?: number, size?: number, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillableClientPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findBillableClients(page, size, status, periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, soort, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillableAppointmentsApi.findBillableClients']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Find completed appointments that are ready to be invoiced to the cliënt. Returns appointments that: - Have status COMPLETED - Are CLIENT category - Have billingCategory = PRIVATE on their appointment type, or are INSURED and   billed at a `clientInvoiced` service code (OV0165) — insured care that is   invoiced to the cliënt and never declared. Such a row carries   `clientInvoiced`, its `serviceCode`, `quarters` and `estimatedAmount`, and   becomes a nota of its own per afspraak - Have not yet been billed (no billing line exists)  By default (no date filters), returns all unprocessed completed appointments to show the full backlog of billable work. Date filters can be used to narrow down the results to a specific period.  The result is paginated and ordered server-side (#3812): every row of the filtered set is resolved, ordered by `sort`, and only the requested page is returned. `totalElements` therefore counts the whole filtered set, not the page.  Private rows carry neither an insurer nor blocking signals, so this endpoint sorts on neither, and takes no `status` or `definitionKey` — see `findBillableAppointments` for those. 
         * @summary Find PRIVATE billable appointments
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: clientName, practitionerName, regiebehandelaarName, serviceDate. Any other field falls back to serviceDate.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findPrivateBillableAppointments(page?: number, size?: number, sort?: string, periodStart?: string, periodEnd?: string, clientId?: string, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillableAppointmentPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findPrivateBillableAppointments(page, size, sort, periodStart, periodEnd, clientId, practitionerId, regiebehandelaarId, locationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillableAppointmentsApi.findPrivateBillableAppointments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Generates billing lines from the specified appointments. Each appointment generates one billing line per client. Group appointments generate multiple lines.  Appointments that were billed earlier but whose draft lines were never invoiced or batched (e.g. invoice creation failed after generation) return those existing lines instead of being skipped, so invoicing can be retried. 
         * @summary Generate billing lines from appointments
         * @param {GenerateBillingLinesRequest} generateBillingLinesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async generateBillingLines(generateBillingLinesRequest: GenerateBillingLinesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GenerateBillingLinesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.generateBillingLines(generateBillingLinesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillableAppointmentsApi.generateBillingLines']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * BillableAppointmentsApi - factory interface
 */
export const BillableAppointmentsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = BillableAppointmentsApiFp(configuration)
    return {
        /**
         * The rows `findBillableAppointments` lists, as a file — every row of the filtered set rather than one page of it (#4145).  It takes exactly the set-defining parameters of `findBillableAppointments` and none of the ones that describe how a *screen* reads that set: there is no `page` and no `size`, because the whole filtered set is the point, and no `sort`, because a file is a document rather than a view — the rows come out oldest consult first, by cliënt, so two exports of an unchanged set are byte-identical.  The set is the screen\'s: openstaand consults only. A consult that has been batched, moved past DRAFT/READY or invoiced is not in `billable_overview_row_v` and so is not in the file. An export spanning already-declared consults is a different question over a different source.  The file names cliënten, so it is PHI leaving the product: one `audit_log` EXPORT row per distinct cliënt in the exported set is written **before** the bytes are rendered, and an export whose audit rows cannot be written fails with a `500` and delivers nothing (ADR-024). 
         * @summary Export the filtered INSURED openstaand set
         * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
         * @param {BillableAppointmentStatus} [status] Filter by declaration readiness, exactly as &#x60;findBillableAppointments&#x60; applies it. READY keeps the rows with no blocking signals, BLOCKED keeps the rest. Omitted exports both.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
         * @param {string} [definitionKey] Keep only the consults a blocking signal of this definition reaches — the cause drilldown, applied exactly as &#x60;findBillableAppointments&#x60; applies it.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportBillableAppointments(format: ConsultExportFormat, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, definitionKey?: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.exportBillableAppointments(format, status, periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, soort, definitionKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Writes one row per appointment x cliënt for a period, covering **both halves** of the declaratie administration: the consults that are still openstaand and the ones the insurer or the cliënt already has.  `findBillableAppointments` answers the openstaand set only — `billable_overview_row_v` anti-joins `settled_consult_v`, so a consult that has been batched, moved past DRAFT/READY or invoiced has left it. A practice reconciling a period needs the other half beside it, with the tariff each consult actually carries.  **`isSettled` says which half a row belongs to**, and the two tariff blocks populate accordingly — they are complementary by construction rather than by a rule stated here:  - `open*` — what the declaratie screen shows for a consult that is still openstaand,   taken from the same view the screen renders. - `billed*` — the billing lines of a settled consult, **summed per consult**, with   `billedLineCount` so a multi-line consult is visible as one. The amounts come off the   lines\' own snapshots and are never re-derived: `billing_lines` snapshots   `tariff_euro` / `nza_tariff_euro` at generation (#2405) and the NZa tariff tables move   year on year, so a fresh lookup would report today\'s price for last year\'s declaration.  A row with **neither** block is a consult that is not billable at all — a cancelled afspraak, an afspraaktype that declares nothing, a cliënt with no covering polis whose consult never entered the queue. Those are deliberately in the file: an unexplained gap between the agenda and the declaraties is exactly what a reconciliation is looking for.  **The contracted amount and the NZa maximum are separate columns on both halves** (#2405) — never one figure that could be read as either.  **No cliënt naam, BSN, e-mailadres or geboortedatum appears in the output.** What is in it is what a declaration is decided, priced or defended on, which includes several fields that read as dossier content and are billing determinants: the GGZ-type with its basis-GGZ-profiel / DSM-hoofdgroep (these pick the Diagnosecodelijst the nota and the GDS801 diagnoseblok carry, #3518/#3572), the verzorgingsgraad (which selects the VERBLIJF prestatiecode) and the verwijzer block (which the nota and RC032 read, #3679). `clientId` stays, because a reconciliation file that cannot say which cliënt a line belongs to cannot be reconciled.  The whole filtered set is written — this is not paged.  **Permission required:** `billing.read` 
         * @summary Export every consult in a period, billed and unbilled together
         * @param {string} periodStart First service date in the export (inclusive).
         * @param {string} periodEnd Last service date in the export (inclusive).
         * @param {ConsultExportFormat} [format] Which file to write. Absent is &#x60;CSV&#x60;.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Filter by the verzekeraar the consult\&#39;s own facts name. That is the polis in force on the service date, which is what the declaratie queue filters on too — not the insurer a settled line happened to be generated for, though the two agree wherever nothing has changed since.  Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so the file covers the same set of verzekeraars the screen that asked for it is showing (#4520). Omitted, or repeated zero times, is every verzekeraar. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the cliënt\&#39;s zorgtraject. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportPeriodConsults(periodStart: string, periodEnd: string, format?: ConsultExportFormat, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.exportPeriodConsults(periodStart, periodEnd, format, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, options).then((request) => request(axios, basePath));
        },
        /**
         * The particulier half of `exportBillableAppointments` (#4145): the rows `findPrivateBillableAppointments` lists, as a file, filtered exactly as that endpoint filters and carrying the same columns as the insured export.  It is a second operation rather than a `billingCategory` parameter on the first for the reason the two list endpoints are two: a private row carries no insurer and no blocking signals, so `insurerIds`, `status` and `definitionKey` would be parameters this half accepts and cannot honestly answer.  The same audit rule applies — one `audit_log` EXPORT row per distinct cliënt, written before the bytes are rendered (ADR-024). 
         * @summary Export the filtered PRIVATE openstaand set
         * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportPrivateBillableAppointments(format: ConsultExportFormat, periodStart?: string, periodEnd?: string, clientId?: string, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.exportPrivateBillableAppointments(format, periodStart, periodEnd, clientId, practitionerId, regiebehandelaarId, locationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Find completed appointments that are ready for INSURED billing (VECOZO/insurers). Returns appointments that: - Have status COMPLETED - Are CLIENT category - Have billingCategory = INSURED on their appointment type - Are not billed at a `clientInvoiced` service code (OV0165): that prestatie is   invoiced to the cliënt and never declared, so it is listed by   `findPrivateBillableAppointments` instead - Have not yet been billed (no billing line exists)  By default (no date filters), returns all unprocessed completed appointments to show the full backlog of billable work. Date filters can be used to narrow down the results to a specific period.  The result is paginated and ordered server-side (#3812): every row of the filtered set is resolved, ordered by `sort`, and only the requested page is returned. `totalElements` therefore counts the whole filtered set, not the page.  This is the \"Te declareren\" list (#4742): beside the prestaties still to declare it lists the credit and correctie regels whose prestatie the insurer already has — one row per thing that goes to the insurer, read from `declaration_queue_v`. Such a row carries `billingLineId`, its `soort`, and the `linkedEntry` of the other half of a credit-and-correct pair. It is never blocked: readiness is about a prestatie still to be priced. 
         * @summary Find INSURED billable appointments
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page.  The maximum is above the 100 most lists here allow: the queue\&#39;s selection is **per page**, so the page size is how a practice acts on more of it at once. 500 is the largest choice the screen\&#39;s page-size control offers (&#x60;BILLABLE_PAGE_SIZE_OPTIONS&#x60; in &#x60;scrivio-fe/src/constants/billing.ts&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers. 
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: clientName, practitionerName, regiebehandelaarName, insurerName, serviceDate, status, soort. Any other field falls back to serviceDate. Ascending &#x60;status&#x60; puts the rows with no blocking signals first; ascending &#x60;soort&#x60; orders NIEUW, HERDECLARATIE, CREDIT, CORRECTIE.
         * @param {BillableAppointmentStatus} [status] Filter by declaration readiness. READY keeps the rows with no blocking signals, BLOCKED keeps the rest. Omitted returns both. &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; on the response are counted before this filter is applied, so they stay stable while it is switched.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
         * @param {string} [definitionKey] Keep only the consults a **blocking** signal of this definition reaches — the drilldown of one cause reported by &#x60;/billing/blocking-signals/summary&#x60;.  A line-wide blocker (organisatie, locatie, behandelaar, contract, afspraak) reaches every client\&#39;s line of its appointment; a client-attributable one (cliënt, verzekeraar, de afspraak-feiten van één cliënt) reaches only that client\&#39;s line.  Advisory causes are deliberately not selectable: they fire on consults that are ready to declare and are not what holds the backlog back. An unknown or misspelled key therefore matches nothing and returns an empty page — a filter that matches nothing never reads as no filter.  &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are unaffected by this parameter, for the same reason &#x60;status&#x60; does not move them: they label the panes, and a cause drilldown must not collapse them to its own size. &#x60;totalElements&#x60; does count the cause\&#39;s whole filtered set, so it can be paged. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findBillableAppointments(page?: number, size?: number, sort?: string, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, definitionKey?: string, options?: RawAxiosRequestConfig): AxiosPromise<BillableAppointmentPage> {
            return localVarFp.findBillableAppointments(page, size, sort, status, periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, soort, definitionKey, options).then((request) => request(axios, basePath));
        },
        /**
         * One page of **clients** in the zorgverzekeraar declaratie queue (#4175), over exactly the set `findBillableAppointments` lists with the same filters — the same view, the same predicates, one row per distinct client instead of one per consult.  It is a second endpoint rather than a second spelling because the grain is the only difference: the queue grouped by client shows clients, and paging it over consults put the same client on as many pages as they had consults — the ordering is by date, so a client\'s consults are scattered across the whole set — while the pager counted consults for a screen listing clients (2.000 consults across 150 clients gave 100 pages for a 150-row list). Ask this endpoint for the clients and `findBillableAppointments?clientId=` for one client\'s consults; each client then appears on exactly one page and `totalElements` counts what the screen shows.  Ordered by client name, case-insensitively, then by id — never by the queue\'s own `sort`, which orders consults. There is deliberately no `sort` parameter: a list of clients has one useful order and a second one would be a second answer to \"which page is this client on\".  There is no `definitionKey` either. That is the \"Actie nodig\" drilldown, which lists the consults of one blocking cause, and that pane groups by oorzaak or not at all — never by client — so a cause narrowing here would be a parameter with no screen behind it.  `consultCount`, `totalAmount` and `unpricedCount` are aggregates over that client\'s **whole filtered set** and are unaffected by which page the client lands on (#3814). Paging the list of clients and computing each row\'s figures over the whole set are independent, and both hold. 
         * @summary Find the clients with INSURED billable consults
         * @param {number} [page] Page number (0-indexed), over distinct clients
         * @param {number} [size] Number of clients per page.  The maximum is above the 100 most lists here allow: the queue\&#39;s selection is **per page**, so the page size is how a practice acts on more of it at once. 500 is the largest choice the screen\&#39;s page-size control offers (&#x60;BILLABLE_PAGE_SIZE_OPTIONS&#x60; in &#x60;scrivio-fe/src/constants/billing.ts&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers.  A page counts cliënten and each brings all its consults, so the response grows with the size even though the query does not. 
         * @param {BillableAppointmentStatus} [status] Filter by declaration readiness, exactly as on &#x60;findBillableAppointments&#x60;: READY keeps the consults with no blocking signals, BLOCKED keeps the rest, omitted counts both. A client with no consult left in the filtered set is not listed at all.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findBillableClients(page?: number, size?: number, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, options?: RawAxiosRequestConfig): AxiosPromise<BillableClientPage> {
            return localVarFp.findBillableClients(page, size, status, periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, soort, options).then((request) => request(axios, basePath));
        },
        /**
         * Find completed appointments that are ready to be invoiced to the cliënt. Returns appointments that: - Have status COMPLETED - Are CLIENT category - Have billingCategory = PRIVATE on their appointment type, or are INSURED and   billed at a `clientInvoiced` service code (OV0165) — insured care that is   invoiced to the cliënt and never declared. Such a row carries   `clientInvoiced`, its `serviceCode`, `quarters` and `estimatedAmount`, and   becomes a nota of its own per afspraak - Have not yet been billed (no billing line exists)  By default (no date filters), returns all unprocessed completed appointments to show the full backlog of billable work. Date filters can be used to narrow down the results to a specific period.  The result is paginated and ordered server-side (#3812): every row of the filtered set is resolved, ordered by `sort`, and only the requested page is returned. `totalElements` therefore counts the whole filtered set, not the page.  Private rows carry neither an insurer nor blocking signals, so this endpoint sorts on neither, and takes no `status` or `definitionKey` — see `findBillableAppointments` for those. 
         * @summary Find PRIVATE billable appointments
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: clientName, practitionerName, regiebehandelaarName, serviceDate. Any other field falls back to serviceDate.
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findPrivateBillableAppointments(page?: number, size?: number, sort?: string, periodStart?: string, periodEnd?: string, clientId?: string, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig): AxiosPromise<BillableAppointmentPage> {
            return localVarFp.findPrivateBillableAppointments(page, size, sort, periodStart, periodEnd, clientId, practitionerId, regiebehandelaarId, locationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Generates billing lines from the specified appointments. Each appointment generates one billing line per client. Group appointments generate multiple lines.  Appointments that were billed earlier but whose draft lines were never invoiced or batched (e.g. invoice creation failed after generation) return those existing lines instead of being skipped, so invoicing can be retried. 
         * @summary Generate billing lines from appointments
         * @param {GenerateBillingLinesRequest} generateBillingLinesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateBillingLines(generateBillingLinesRequest: GenerateBillingLinesRequest, options?: RawAxiosRequestConfig): AxiosPromise<GenerateBillingLinesResponse> {
            return localVarFp.generateBillingLines(generateBillingLinesRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * BillableAppointmentsApi - object-oriented interface
 */
export class BillableAppointmentsApi extends BaseAPI {
    /**
     * The rows `findBillableAppointments` lists, as a file — every row of the filtered set rather than one page of it (#4145).  It takes exactly the set-defining parameters of `findBillableAppointments` and none of the ones that describe how a *screen* reads that set: there is no `page` and no `size`, because the whole filtered set is the point, and no `sort`, because a file is a document rather than a view — the rows come out oldest consult first, by cliënt, so two exports of an unchanged set are byte-identical.  The set is the screen\'s: openstaand consults only. A consult that has been batched, moved past DRAFT/READY or invoiced is not in `billable_overview_row_v` and so is not in the file. An export spanning already-declared consults is a different question over a different source.  The file names cliënten, so it is PHI leaving the product: one `audit_log` EXPORT row per distinct cliënt in the exported set is written **before** the bytes are rendered, and an export whose audit rows cannot be written fails with a `500` and delivers nothing (ADR-024). 
     * @summary Export the filtered INSURED openstaand set
     * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
     * @param {BillableAppointmentStatus} [status] Filter by declaration readiness, exactly as &#x60;findBillableAppointments&#x60; applies it. READY keeps the rows with no blocking signals, BLOCKED keeps the rest. Omitted exports both.
     * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
     * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
     * @param {string} [clientId] Filter by specific client
     * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
     * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
     * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
     * @param {string} [locationId] Filter by specific location
     * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
     * @param {string} [definitionKey] Keep only the consults a blocking signal of this definition reaches — the cause drilldown, applied exactly as &#x60;findBillableAppointments&#x60; applies it.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public exportBillableAppointments(format: ConsultExportFormat, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, definitionKey?: string, options?: RawAxiosRequestConfig) {
        return BillableAppointmentsApiFp(this.configuration).exportBillableAppointments(format, status, periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, soort, definitionKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Writes one row per appointment x cliënt for a period, covering **both halves** of the declaratie administration: the consults that are still openstaand and the ones the insurer or the cliënt already has.  `findBillableAppointments` answers the openstaand set only — `billable_overview_row_v` anti-joins `settled_consult_v`, so a consult that has been batched, moved past DRAFT/READY or invoiced has left it. A practice reconciling a period needs the other half beside it, with the tariff each consult actually carries.  **`isSettled` says which half a row belongs to**, and the two tariff blocks populate accordingly — they are complementary by construction rather than by a rule stated here:  - `open*` — what the declaratie screen shows for a consult that is still openstaand,   taken from the same view the screen renders. - `billed*` — the billing lines of a settled consult, **summed per consult**, with   `billedLineCount` so a multi-line consult is visible as one. The amounts come off the   lines\' own snapshots and are never re-derived: `billing_lines` snapshots   `tariff_euro` / `nza_tariff_euro` at generation (#2405) and the NZa tariff tables move   year on year, so a fresh lookup would report today\'s price for last year\'s declaration.  A row with **neither** block is a consult that is not billable at all — a cancelled afspraak, an afspraaktype that declares nothing, a cliënt with no covering polis whose consult never entered the queue. Those are deliberately in the file: an unexplained gap between the agenda and the declaraties is exactly what a reconciliation is looking for.  **The contracted amount and the NZa maximum are separate columns on both halves** (#2405) — never one figure that could be read as either.  **No cliënt naam, BSN, e-mailadres or geboortedatum appears in the output.** What is in it is what a declaration is decided, priced or defended on, which includes several fields that read as dossier content and are billing determinants: the GGZ-type with its basis-GGZ-profiel / DSM-hoofdgroep (these pick the Diagnosecodelijst the nota and the GDS801 diagnoseblok carry, #3518/#3572), the verzorgingsgraad (which selects the VERBLIJF prestatiecode) and the verwijzer block (which the nota and RC032 read, #3679). `clientId` stays, because a reconciliation file that cannot say which cliënt a line belongs to cannot be reconciled.  The whole filtered set is written — this is not paged.  **Permission required:** `billing.read` 
     * @summary Export every consult in a period, billed and unbilled together
     * @param {string} periodStart First service date in the export (inclusive).
     * @param {string} periodEnd Last service date in the export (inclusive).
     * @param {ConsultExportFormat} [format] Which file to write. Absent is &#x60;CSV&#x60;.
     * @param {string} [clientId] Filter by specific client
     * @param {Array<string>} [insurerIds] Filter by the verzekeraar the consult\&#39;s own facts name. That is the polis in force on the service date, which is what the declaratie queue filters on too — not the insurer a settled line happened to be generated for, though the two agree wherever nothing has changed since.  Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so the file covers the same set of verzekeraars the screen that asked for it is showing (#4520). Omitted, or repeated zero times, is every verzekeraar. 
     * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
     * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the cliënt\&#39;s zorgtraject. Rows whose trajectory names no regiebehandelaar are excluded. 
     * @param {string} [locationId] Filter by specific location
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public exportPeriodConsults(periodStart: string, periodEnd: string, format?: ConsultExportFormat, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig) {
        return BillableAppointmentsApiFp(this.configuration).exportPeriodConsults(periodStart, periodEnd, format, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The particulier half of `exportBillableAppointments` (#4145): the rows `findPrivateBillableAppointments` lists, as a file, filtered exactly as that endpoint filters and carrying the same columns as the insured export.  It is a second operation rather than a `billingCategory` parameter on the first for the reason the two list endpoints are two: a private row carries no insurer and no blocking signals, so `insurerIds`, `status` and `definitionKey` would be parameters this half accepts and cannot honestly answer.  The same audit rule applies — one `audit_log` EXPORT row per distinct cliënt, written before the bytes are rendered (ADR-024). 
     * @summary Export the filtered PRIVATE openstaand set
     * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
     * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
     * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
     * @param {string} [clientId] Filter by specific client
     * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
     * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
     * @param {string} [locationId] Filter by specific location
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public exportPrivateBillableAppointments(format: ConsultExportFormat, periodStart?: string, periodEnd?: string, clientId?: string, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig) {
        return BillableAppointmentsApiFp(this.configuration).exportPrivateBillableAppointments(format, periodStart, periodEnd, clientId, practitionerId, regiebehandelaarId, locationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Find completed appointments that are ready for INSURED billing (VECOZO/insurers). Returns appointments that: - Have status COMPLETED - Are CLIENT category - Have billingCategory = INSURED on their appointment type - Are not billed at a `clientInvoiced` service code (OV0165): that prestatie is   invoiced to the cliënt and never declared, so it is listed by   `findPrivateBillableAppointments` instead - Have not yet been billed (no billing line exists)  By default (no date filters), returns all unprocessed completed appointments to show the full backlog of billable work. Date filters can be used to narrow down the results to a specific period.  The result is paginated and ordered server-side (#3812): every row of the filtered set is resolved, ordered by `sort`, and only the requested page is returned. `totalElements` therefore counts the whole filtered set, not the page.  This is the \"Te declareren\" list (#4742): beside the prestaties still to declare it lists the credit and correctie regels whose prestatie the insurer already has — one row per thing that goes to the insurer, read from `declaration_queue_v`. Such a row carries `billingLineId`, its `soort`, and the `linkedEntry` of the other half of a credit-and-correct pair. It is never blocked: readiness is about a prestatie still to be priced. 
     * @summary Find INSURED billable appointments
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page.  The maximum is above the 100 most lists here allow: the queue\&#39;s selection is **per page**, so the page size is how a practice acts on more of it at once. 500 is the largest choice the screen\&#39;s page-size control offers (&#x60;BILLABLE_PAGE_SIZE_OPTIONS&#x60; in &#x60;scrivio-fe/src/constants/billing.ts&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers. 
     * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: clientName, practitionerName, regiebehandelaarName, insurerName, serviceDate, status, soort. Any other field falls back to serviceDate. Ascending &#x60;status&#x60; puts the rows with no blocking signals first; ascending &#x60;soort&#x60; orders NIEUW, HERDECLARATIE, CREDIT, CORRECTIE.
     * @param {BillableAppointmentStatus} [status] Filter by declaration readiness. READY keeps the rows with no blocking signals, BLOCKED keeps the rest. Omitted returns both. &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; on the response are counted before this filter is applied, so they stay stable while it is switched.
     * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
     * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
     * @param {string} [clientId] Filter by specific client
     * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
     * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
     * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
     * @param {string} [locationId] Filter by specific location
     * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
     * @param {string} [definitionKey] Keep only the consults a **blocking** signal of this definition reaches — the drilldown of one cause reported by &#x60;/billing/blocking-signals/summary&#x60;.  A line-wide blocker (organisatie, locatie, behandelaar, contract, afspraak) reaches every client\&#39;s line of its appointment; a client-attributable one (cliënt, verzekeraar, de afspraak-feiten van één cliënt) reaches only that client\&#39;s line.  Advisory causes are deliberately not selectable: they fire on consults that are ready to declare and are not what holds the backlog back. An unknown or misspelled key therefore matches nothing and returns an empty page — a filter that matches nothing never reads as no filter.  &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are unaffected by this parameter, for the same reason &#x60;status&#x60; does not move them: they label the panes, and a cause drilldown must not collapse them to its own size. &#x60;totalElements&#x60; does count the cause\&#39;s whole filtered set, so it can be paged. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findBillableAppointments(page?: number, size?: number, sort?: string, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, definitionKey?: string, options?: RawAxiosRequestConfig) {
        return BillableAppointmentsApiFp(this.configuration).findBillableAppointments(page, size, sort, status, periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, soort, definitionKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * One page of **clients** in the zorgverzekeraar declaratie queue (#4175), over exactly the set `findBillableAppointments` lists with the same filters — the same view, the same predicates, one row per distinct client instead of one per consult.  It is a second endpoint rather than a second spelling because the grain is the only difference: the queue grouped by client shows clients, and paging it over consults put the same client on as many pages as they had consults — the ordering is by date, so a client\'s consults are scattered across the whole set — while the pager counted consults for a screen listing clients (2.000 consults across 150 clients gave 100 pages for a 150-row list). Ask this endpoint for the clients and `findBillableAppointments?clientId=` for one client\'s consults; each client then appears on exactly one page and `totalElements` counts what the screen shows.  Ordered by client name, case-insensitively, then by id — never by the queue\'s own `sort`, which orders consults. There is deliberately no `sort` parameter: a list of clients has one useful order and a second one would be a second answer to \"which page is this client on\".  There is no `definitionKey` either. That is the \"Actie nodig\" drilldown, which lists the consults of one blocking cause, and that pane groups by oorzaak or not at all — never by client — so a cause narrowing here would be a parameter with no screen behind it.  `consultCount`, `totalAmount` and `unpricedCount` are aggregates over that client\'s **whole filtered set** and are unaffected by which page the client lands on (#3814). Paging the list of clients and computing each row\'s figures over the whole set are independent, and both hold. 
     * @summary Find the clients with INSURED billable consults
     * @param {number} [page] Page number (0-indexed), over distinct clients
     * @param {number} [size] Number of clients per page.  The maximum is above the 100 most lists here allow: the queue\&#39;s selection is **per page**, so the page size is how a practice acts on more of it at once. 500 is the largest choice the screen\&#39;s page-size control offers (&#x60;BILLABLE_PAGE_SIZE_OPTIONS&#x60; in &#x60;scrivio-fe/src/constants/billing.ts&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers.  A page counts cliënten and each brings all its consults, so the response grows with the size even though the query does not. 
     * @param {BillableAppointmentStatus} [status] Filter by declaration readiness, exactly as on &#x60;findBillableAppointments&#x60;: READY keeps the consults with no blocking signals, BLOCKED keeps the rest, omitted counts both. A client with no consult left in the filtered set is not listed at all.
     * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
     * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
     * @param {string} [clientId] Filter by specific client
     * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
     * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
     * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
     * @param {string} [locationId] Filter by specific location
     * @param {Array<DeclarationSoort>} [soort] Narrow the \&quot;Te declareren\&quot; list to these kinds of row (#4742). Repeatable; values combine as OR (&#x60;?soort&#x3D;CREDIT&amp;soort&#x3D;CORRECTIE&#x60;), which is where a credit toast points. Omitted, or repeated zero times, is **every** kind.  Like &#x60;status&#x60;, it narrows what is listed and not what the panes count: &#x60;readyCount&#x60; and &#x60;blockedCount&#x60; are taken without it. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findBillableClients(page?: number, size?: number, status?: BillableAppointmentStatus, periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, soort?: Array<DeclarationSoort>, options?: RawAxiosRequestConfig) {
        return BillableAppointmentsApiFp(this.configuration).findBillableClients(page, size, status, periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, soort, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Find completed appointments that are ready to be invoiced to the cliënt. Returns appointments that: - Have status COMPLETED - Are CLIENT category - Have billingCategory = PRIVATE on their appointment type, or are INSURED and   billed at a `clientInvoiced` service code (OV0165) — insured care that is   invoiced to the cliënt and never declared. Such a row carries   `clientInvoiced`, its `serviceCode`, `quarters` and `estimatedAmount`, and   becomes a nota of its own per afspraak - Have not yet been billed (no billing line exists)  By default (no date filters), returns all unprocessed completed appointments to show the full backlog of billable work. Date filters can be used to narrow down the results to a specific period.  The result is paginated and ordered server-side (#3812): every row of the filtered set is resolved, ordered by `sort`, and only the requested page is returned. `totalElements` therefore counts the whole filtered set, not the page.  Private rows carry neither an insurer nor blocking signals, so this endpoint sorts on neither, and takes no `status` or `definitionKey` — see `findBillableAppointments` for those. 
     * @summary Find PRIVATE billable appointments
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: clientName, practitionerName, regiebehandelaarName, serviceDate. Any other field falls back to serviceDate.
     * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
     * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
     * @param {string} [clientId] Filter by specific client
     * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
     * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
     * @param {string} [locationId] Filter by specific location
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findPrivateBillableAppointments(page?: number, size?: number, sort?: string, periodStart?: string, periodEnd?: string, clientId?: string, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig) {
        return BillableAppointmentsApiFp(this.configuration).findPrivateBillableAppointments(page, size, sort, periodStart, periodEnd, clientId, practitionerId, regiebehandelaarId, locationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Generates billing lines from the specified appointments. Each appointment generates one billing line per client. Group appointments generate multiple lines.  Appointments that were billed earlier but whose draft lines were never invoiced or batched (e.g. invoice creation failed after generation) return those existing lines instead of being skipped, so invoicing can be retried. 
     * @summary Generate billing lines from appointments
     * @param {GenerateBillingLinesRequest} generateBillingLinesRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public generateBillingLines(generateBillingLinesRequest: GenerateBillingLinesRequest, options?: RawAxiosRequestConfig) {
        return BillableAppointmentsApiFp(this.configuration).generateBillingLines(generateBillingLinesRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * BillingBatchesApi - axios parameter creator
 */
export const BillingBatchesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Sets or clears the administrative \"verwerkt\" flag on a billing batch.  This is orthogonal to the batch status lifecycle: an archived batch keeps its status, its totals and its billing lines, and any status can be archived — the flag only decides whether the batch shows up in the default batches list or in the archive. The operation is idempotent and reversible. 
         * @summary Mark a billing batch as verwerkt (or undo)
         * @param {string} batchId Billing batch UUID
         * @param {ArchiveBillingBatchRequest} archiveBillingBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveBillingBatch: async (batchId: string, archiveBillingBatchRequest: ArchiveBillingBatchRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('archiveBillingBatch', 'batchId', batchId)
            // verify required parameter 'archiveBillingBatchRequest' is not null or undefined
            assertParamExists('archiveBillingBatch', 'archiveBillingBatchRequest', archiveBillingBatchRequest)
            const localVarPath = `/api/v1/billing/batches/{batchId}/archive`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(archiveBillingBatchRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates one billing batch per (cliënt, verzekeraar) group in the request, from the rows the practice selected in the \"Te declareren\" list (#4742). A group names its rows by their `entry`, exactly as `BillableAppointmentDto.entry` and `BillableSelectableConsultDto.entry` state it, and may mix both kinds:  - `prestatie:<appointmentClientId>:<practitionerId>` — a prestatie still to   declare. Its billing line is generated if it does not exist yet, priced,   marked READY and batched. - `line:<billingLineId>` — a credit or correctie regel whose prestatie the   payer already has. It must be the organisation\'s, unbatched, READY and of   the group\'s cliënt and verzekeraar. A corrected replacement goes only   together with the credit post that reverses its original, or after that   credit post is in a batch: herdeclaratiecode `05` describes a debet   *beside* a credit.  Two entries resolving to the same billing line are batched once. A group\'s period is the span of its lines\' own service dates.  Every refusal is a 400 in Dutch, and the whole request is applied or none of it is: a partial run that created three of five batches would leave the lines it moved out of READY behind while the practice was told the selection failed. 
         * @summary Create billing batches from rows of the \"Te declareren\" list
         * @param {CreateBillingBatchRequest} createBillingBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createBillingBatches: async (createBillingBatchRequest: CreateBillingBatchRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createBillingBatchRequest' is not null or undefined
            assertParamExists('createBillingBatches', 'createBillingBatchRequest', createBillingBatchRequest)
            const localVarPath = `/api/v1/billing/batches`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createBillingBatchRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates one batch per gemeente the request names (#4821), declared to that gemeente — `payerType` MUNICIPALITY on the existing batch, no verzekeraar and no single cliënt. The caller states the grouping, as `createBillingBatches` has it state the (cliënt, verzekeraar) one, so what the screen promised and what is created cannot come apart.  A prestatie is named by its `toewijzingId`, `van` and `productCode`, exactly as `listGemeenteDeclaraties` lists it. The organisation\'s DRAFT gemeente lines are first made what the registraties say now, and each selected prestatie\'s line is re-derived once more as it leaves DRAFT, so a batch never declares a month at what it said when the screen was loaded. A batch\'s period is the span of its lines\' own periods.  Every refusal is a 400 in Dutch — a prestatie that is held, already in a batch, excluded from billing, no longer yielded by the registraties, or of another gemeente than its group — and the whole request is applied or none of it is. 
         * @summary Create one billing batch per gemeente from selected gemeente prestaties
         * @param {CreateGemeenteBatchesRequest} createGemeenteBatchesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createGemeenteBillingBatches: async (createGemeenteBatchesRequest: CreateGemeenteBatchesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createGemeenteBatchesRequest' is not null or undefined
            assertParamExists('createGemeenteBillingBatches', 'createGemeenteBatchesRequest', createGemeenteBatchesRequest)
            const localVarPath = `/api/v1/billing/batches/gemeente`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createGemeenteBatchesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Credits the toewijzing-month the named regel declares (#4823) — every regel that declares that toewijzing × month, never one alone, because a JW323 declares the month and a month half credited is declared half. What a practice does when a session was declared on the wrong toewijzing: credit the month, move the session, declare the month anew.  Each regel is reversed in the ledger exactly as a Zvw regel is (a credit regel with the amount negated and `originalLineId` set; the original becomes CREDITED). The credits are put in one batch to the original\'s gemeente and that batch is locked at once, which builds its **JW323** — each prestatie `DebetCredit` C with the values of the prestatie it reverses and its `VorigReferentieNummer` — and queues it in the Berichten outbox, from where the practice sends it like any other bericht.  The month is free from that moment: its registraties can move to another toewijzing, and `listGemeenteDeclaraties` lists it again to be declared in a batch of its own.  Only a month the gemeente granted in full is credited — a regel it has not answered yet may still be rejected, and a rejected one was never granted. A gemeente prestatie is not credited through `createCreditLine`. 
         * @summary Credit the toewijzing-month a gemeente prestatie declares, and queue the credit\'s JW323
         * @param {string} lineId A gemeente prestatie of the toewijzing-month to credit
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        creditGemeenteMonth: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('creditGemeenteMonth', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}/credit-gemeente-month`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deletes a billing batch and returns its billing lines to unbatched state. Only batches in DRAFT status can be deleted. 
         * @summary Delete billing batch
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteBillingBatch: async (batchId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('deleteBillingBatch', 'batchId', batchId)
            const localVarPath = `/api/v1/billing/batches/{batchId}`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Hard-deletes billing that has not yet been issued, scoped to a batch and/or a set of appointments. Records are removed in dependency order: invoice lines, DRAFT invoices, billing lines, then any batch whose lines were all discarded.  Unlike `deleteBillingBatch` (which only accepts DRAFT batches), this also discards `READY` and `EXPORTED` (never-submitted) batches. It is the cleanup tool for incorrectly-created billing from test appointments.  Refuses with `409` (\"credit instead\") on truly committed records: issued (SENT/PAID/OVERDUE) invoices, billing lines already submitted to an insurer, or batches that are SUBMITTED or beyond. 
         * @summary Discard not-yet-issued billing
         * @param {DiscardBillingRequest} discardBillingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        discardBilling: async (discardBillingRequest: DiscardBillingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'discardBillingRequest' is not null or undefined
            assertParamExists('discardBilling', 'discardBillingRequest', discardBillingRequest)
            const localVarPath = `/api/v1/billing/batches/discard`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(discardBillingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Downloads one file an insurer sent as aanvullende retourinformatie about this batch\'s declaration, pulled from the VECOZO berichtenservice (ADR-049) and scanned for malware before it was stored. The batch lists its files in `retourBijlagen`.  Served as an attachment under the insurer\'s own file name and recorded as PHI egress (ADR-024).  **Permission required:** `billing.read` 
         * @summary Download one file of the aanvullende retourinformatie on a batch
         * @param {string} batchId Billing batch UUID
         * @param {string} bijlageId The file\&#39;s id, as &#x60;retourBijlagen&#x60; lists it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadBatchRetourBijlage: async (batchId: string, bijlageId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('downloadBatchRetourBijlage', 'batchId', batchId)
            // verify required parameter 'bijlageId' is not null or undefined
            assertParamExists('downloadBatchRetourBijlage', 'bijlageId', bijlageId)
            const localVarPath = `/api/v1/billing/batches/{batchId}/retour-bijlagen/{bijlageId}`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)))
                .replace(`{${"bijlageId"}}`, encodeURIComponent(String(bijlageId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/octet-stream,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Downloads the **verzekeraarfactuur** for one declared batch: the invoice a VECOZO organisation\'s bookkeeper books the receivable from.  The document is addressed to the **verzekeraar** the batch was declared to (naam and UZOVI-nummer; a gemeente by name), names the patiënt only as the verzekerde it concerns, and carries the declared prestaties as factuurregels. A batch created before per-cliënt batches (#4370) declares for several cliënten, so it has **one verzekeraarfactuur per cliënt** and the response is those invoices merged into a single PDF, in patiëntnummer order. Such an invoice is numbered `<batchNummer>-<n>`; a batch declaring for one cliënt keeps the bare batch number.  It is deliberately not the ZPM restitutienota served by `GET /api/v1/invoices/{invoiceId}/pdf`: it carries **no BSN, diagnose, zorgtrajectnummer, AGB-code or verwijzergegeven**, because an external bookkeeper does not need them to book a receivable and a cliënt\'s verzekeraar does.  Whether it reads as a debetfactuur or a creditfactuur follows the **sign of the total**.  The invoices are produced on the first download and reused on every one after it — asking twice serves the same document under the same numbers rather than producing a second receivable. The batch number *is* the factuurnummer; nothing is drawn from the practice\'s cliëntnota-reeks, and the invoice amounts sum to the batch total, which is what reconciles against the single payment the verzekeraar makes for the declaration.  Every download is recorded as PHI egress (ADR-024), exactly as the GDS801 message download is.  **Permission required:** `billing.read` 
         * @summary Download the batch\'s verzekeraarfactuur as PDF
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadBatchVerkoopfactuur: async (batchId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('downloadBatchVerkoopfactuur', 'batchId', batchId)
            const localVarPath = `/api/v1/billing/batches/{batchId}/verkoopfactuur`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/pdf,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Zips up the **verzekeraarfactuur** of each named batch, one PDF per batch, named exactly as `downloadBatchVerkoopfactuur` names its file. This is what makes the document usable for a practice\'s boekhouding at all: a month is on the order of 137 batches, and one click per batch is not something a bookkeeper does.  It is `downloadBatchVerkoopfactuur` per batch and nothing more, so a batch declaring for several cliënten is still one entry — the merged PDF that endpoint serves. Each batch produces its invoices if it has none and reuses them otherwise, so asking twice serves the same documents rather than a second set of receivables, and each batch produces its own `EXPORT` access-audit record (ADR-024) — this is bulk PHI egress, so the trail is per batch rather than per archive.  **A batch that cannot produce an invoice does not abandon the rest.** Its entry is left out, its number is named on the `Scrivio-Bulk-Export-Failures` header, and the reason is written into the archive\'s own `mislukte-facturen.txt` report — the header cannot carry an unbounded sentence per batch, and the report is the copy that survives the download. A batch that has not been declared yet, one holding no regels, and one whose regels are already on a cliëntnota each refuse on exactly those terms. When **nothing** could be produced there is no archive to serve, so the request fails with a 422 listing every reason instead.  A batch id that names no batch of this organisation is a different thing entirely and fails the whole request with a 404: the caller asked for a batch it cannot have, and answering with a partial archive would hide that.  **Permission required:** `billing.read` 
         * @summary Download several batches\' verzekeraarfacturen as one ZIP archive
         * @param {Array<string>} batchIds The batches to invoice. Comma-separated list of billing batch UUIDs.  The upper bound is the same 250 &#x60;exportBillingBatches&#x60; takes, and for the same reason — the selection this serves is per page, so it is the largest page the batch list offers. A different bound would mean a full page of batches is accepted by one of the two bulk downloads on that screen and refused by the other. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportBatchVerkoopfacturen: async (batchIds: Array<string>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchIds' is not null or undefined
            assertParamExists('exportBatchVerkoopfacturen', 'batchIds', batchIds)
            const localVarPath = `/api/v1/billing/batches/verkoopfacturen`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (batchIds) {
                localVarQueryParameter['batchIds'] = batchIds.join(COLLECTION_FORMATS.csv);
            }

            localVarHeaderParameter['Accept'] = 'application/zip,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Generates and downloads a GDS801 XML file for the billing batch.  Available in **every** batch status — \"what is about to be sent\" is a question a practice asks before afronden, not after. Only what the download means differs:  - DRAFT — a concept of the message as it stands. Nothing is registered, the batch   does not move, and the filename carries a `-concept` suffix. - READY — the message that will be sent. The export is registered: status becomes   EXPORTED, `exported_at` is set and `exported_by_id` is the current user. - EXPORTED and beyond — a re-download of the same message; nothing is written.  The GDS801 format is the Dutch standard for healthcare claim submissions to insurers via Vecozo.  **The bytes are either the stored message or a fresh render, and the two can differ.** A batch whose declaration message was stored when it was submitted is served exactly those bytes — what the insurer received, unaffected by anything edited since. Every other batch is rendered from **current** data on each request: the message is assembled from the live billing lines, the client, the zorgtraject, the insurer and the organisation, so a corrected tarief, an edited verwijzer or a changed regiebehandelaar silently rewrite what a given batch number says. There is deliberately **no backfill**, so a batch submitted before message storage existed will never have a stored message and its download is always a re-render.  Which of the two this response is comes back on the `Scrivio-Declaration-Message-Source` header. `declarationMessageStoredAt` on the batch itself answers the same question *before* the download, so a UI can label the action as the sent message or as a re-render without calling this endpoint first.  The response body is the file itself, so the pre-submission controle warnings the message carries are read separately, from `GET /api/v1/billing/batches/{batchId}/controles`.  **Permission required:** `billing.read` 
         * @summary Export billing batch as GDS801 XML
         * @param {string} batchId Billing batch UUID
         * @param {ExportBillingBatchFormatEnum} [format] Export format (currently only \&#39;xml\&#39; for GDS801 is supported)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportBillingBatch: async (batchId: string, format?: ExportBillingBatchFormatEnum, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('exportBillingBatch', 'batchId', batchId)
            const localVarPath = `/api/v1/billing/batches/{batchId}/export`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (format !== undefined) {
                localVarQueryParameter['format'] = format;
            }

            localVarHeaderParameter['Accept'] = 'application/xml,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Zips up the GDS801 message of each named batch, one entry per batch, named exactly as the single-batch download names its file — `-concept` suffix for a DRAFT included.  Available in **every** batch status, for the same reason the single export is (`exportBillingBatch`): what differs per status is only what the download *means*. Each **READY** batch in the selection is registered as exported — status becomes EXPORTED, `exported_at` is set and `exported_by_id` is the current user — exactly as downloading it on its own would. Every other status moves nothing. One selection can therefore be several status transitions, which is why the UI confirms it first.  **A batch that cannot be rendered does not abandon the rest.** Its entry is left out, its number is named on the `Scrivio-Bulk-Export-Failures` header, and the reason is written into the archive\'s own `mislukte-declaraties.txt` report — the header cannot carry an unbounded sentence per batch, and the report is the copy that survives the download. When **nothing** could be rendered there is no archive to serve, so the request fails with a 422 listing every reason instead.  A batch id that names no batch of this organisation is a different thing entirely and fails the whole request with a 404: the caller asked for a batch it cannot have, and answering with a partial archive would hide that.  Each exported batch produces its own `EXPORT` access-audit record (ADR-024) — this is bulk PHI egress, so the trail is per batch rather than per archive.  The archive itself is recorded as one numbered export of kind `ZIP` (#4781), naming every selected batch with whether the archive holds it — a refused batch is on the record too, with its reason. The number is in the file name; see `listBillingBatchExports`.  **Permission required:** `billing.read` 
         * @summary Export several billing batches as one ZIP archive
         * @param {Array<string>} batchIds The batches to archive. Comma-separated list of billing batch UUIDs.  The upper bound is the largest page the batch list offers, because the selection this serves is per page — the same 250 as &#x60;listBillingBatches&#x60;\&#39; &#x60;size&#x60; maximum and &#x60;PAGE_SIZE_OPTIONS&#x60; in &#x60;BatchesView.vue&#x60;. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportBillingBatches: async (batchIds: Array<string>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchIds' is not null or undefined
            assertParamExists('exportBillingBatches', 'batchIds', batchIds)
            const localVarPath = `/api/v1/billing/batches/export`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (batchIds) {
                localVarQueryParameter['batchIds'] = batchIds.join(COLLECTION_FORMATS.csv);
            }

            localVarHeaderParameter['Accept'] = 'application/zip,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves a billing batch by ID including its billing lines. Lines are grouped by client for easier display. 
         * @summary Get billing batch by ID
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingBatchById: async (batchId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('getBillingBatchById', 'batchId', batchId)
            const localVarPath = `/api/v1/billing/batches/{batchId}`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Builds the batch\'s GDS801 message and returns the **warning-level** violations the pre-submission controles (RBC u29) found in it. Nothing is exported and the batch is not touched — this only reports.  A warning does not stop a declaration but is worth acting on before it is submitted: RC032 (an AGB code the AGB-register does not know) is the one the engines raise today. The export endpoint returns the file itself and cannot carry them, so the declaratie UI reads them here.  A batch that breaks a *blocking* controle answers `422` exactly as the export does, so this doubles as a pre-export check. 
         * @summary Report the pre-submission controle warnings for a billing batch
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingBatchControles: async (batchId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('getBillingBatchControles', 'batchId', batchId)
            const localVarPath = `/api/v1/billing/batches/{batchId}/controles`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * A numbered export (#4781) with every batch it was asked to hold: the included ones first, in batch-number order, then the ones it left out with the reason it gave — the same words the archive\'s `mislukte-declaraties.txt` carries. \"What was in EXP-2026-0042\" is this call.  **Permission required:** `billing.read` 
         * @summary One declaratiebericht export and the batches it held
         * @param {string} exportId The export\&#39;s id, as &#x60;listBillingBatchExports&#x60; answers it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingBatchExport: async (exportId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'exportId' is not null or undefined
            assertParamExists('getBillingBatchExport', 'exportId', exportId)
            const localVarPath = `/api/v1/billing/batch-exports/{exportId}`
                .replace(`{${"exportId"}}`, encodeURIComponent(String(exportId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The two batch figures above the declaratie queue — **In batch** and **Wacht op retour** — over the organisation\'s whole non-archived backlog.  It exists because summing them in the browser could not be made correct. The bar read one page of `listBillingBatches` and counted what it happened to hold, so every batch past the hundredth fell out of both figures; and because that page is every status newest-first, most of its hundred slots go to batches the insurer has already answered, which belong to neither stage. Since one batch per cliënt (#4370) a practice passes a hundred batches within days, and the oldest SUBMITTED ones — exactly what \"oudste … dagen\" is meant to surface — were the first to be dropped (#4518).  The stage → status mapping is the server\'s alone: `inBatch` is DRAFT, READY and EXPORTED, `awaitingReturn` is SUBMITTED, and a batch the insurer has answered (APPROVED, REJECTED, PARTIALLY_APPROVED) is in neither — it has left the queue these figures describe, whether or not it has been marked verwerkt. A caller restating that mapping would be a second list of statuses, which is how the two would come to disagree.  Archived (\"verwerkt\") batches are excluded, as they are from the list\'s default view.  There is deliberately no `page` or `size`: every figure is over the whole set, whatever its size, so there is no cap left to disclose. 
         * @summary Aggregate the batch backlog per stage
         * @param {Array<string>} [insurerIds] Narrow both stages to the batches carrying these insurers\&#39; lines — the same filter, resolved the same way, as &#x60;listBillingBatches&#x60;. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so these figures narrow to the same set of verzekeraars the queue\&#39;s consult figures do (#4520). Omitted, or repeated zero times, is every verzekeraar.  It is the only filter here. A batch is per insurer per period and spans many consults, so the queue\&#39;s period, cliënt, behandelaar and regiebehandelaar filters have no counterpart: these figures describe the practice\&#39;s batch backlog rather than a slice of the consult list. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingBatchStageSummary: async (insurerIds?: Array<string>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/billing/batches/summary`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (insurerIds) {
                localVarQueryParameter['insurerIds'] = insurerIds;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * SnelStart when the organisation has it connected, its boekhouding mailbox otherwise (#4727), so the confirmation can say which of the two `sendBatchVerkoopfacturenToBoekhouding` will do before it is asked. Readable with `billing.read`, which the SnelStart settings themselves are not.  **Permission required:** `billing.read` 
         * @summary Where \"Naar boekhouding\" delivers for this organisation right now
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBoekhoudingDestination: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/billing/batches/boekhouding-bestemming`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The run `sendBatchVerkoopfacturenToBoekhouding` staged, with the outcome per batch in the order the batches were selected. A batch reads `PENDING` until the background worker has reached it; `finishedAt` is set once none is left.  **Permission required:** `billing.read` 
         * @summary What a send to the boekhouding has done so far
         * @param {string} runId The run\&#39;s id, as the send answered it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBoekhoudingSendRun: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getBoekhoudingSendRun', 'runId', runId)
            const localVarPath = `/api/v1/billing/batches/boekhouding-verzendingen/{runId}`
                .replace(`{${"runId"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Imports a GDS802 return message XML and matches response lines to the original billing lines in the batch. Updates billing line statuses based on insurer approval/rejection.  The batch must **have been declared** — `EXPORTED`, `SUBMITTED`, `APPROVED`, `REJECTED` or `PARTIALLY_APPROVED`. `EXPORTED` is included because the VECOZO connection is not live, so the export is the send and `SUBMITTED` is recorded by hand afterwards, sometimes not at all; a retour is itself proof the declaration went out. The three retour statuses are included because they are exactly the batches a *second* retour answers — including a verdict set by hand before that was withdrawn (#4622), which a retour overrides the same way. `DRAFT` and `READY` are refused: nothing was sent, so no insurer can be answering them. An archived batch is accepted — a retour can arrive days after a batch was closed off.  After processing: - Matched lines are updated to APPROVED or REJECTED - Rejection codes and reasons are stored on rejected lines - Batch status is updated to APPROVED, REJECTED, or PARTIALLY_APPROVED - Unknown response lines are logged as warnings  **Repeating an upload is safe.** Every accepted message is stored, and only the newest for a batch — by `Verzenddatum`, ties resolved by import time — is applied; applying overwrites the previous verdicts wholesale rather than accumulating on top of them. A message older than the one already applied is kept as history and `applied` is `false`. 
         * @summary Import GDS802 return message for a batch
         * @param {string} batchId Billing batch UUID
         * @param {File} body 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        importReturnMessage: async (batchId: string, body: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('importReturnMessage', 'batchId', batchId)
            // verify required parameter 'body' is not null or undefined
            assertParamExists('importReturnMessage', 'body', body)
            const localVarPath = `/api/v1/billing/batches/{batchId}/import-return`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/xml';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(body, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Uploads the retourinformatie a practice downloads from the VECOZO-portaal — a ZIP, or a single retourbericht — and applies every GDS802 (574) message in it to the batch it names. Requires the `billing.update` permission.  Each entry is classified on its own `Berichtcode`, never on its filename: a portaal download mixes 574 retouren with 573 copies of what was sent and with PDFs, and the 574s themselves arrive under more than one naming convention. Every entry is then routed by its `DeclaratieContext/Factuurnummer`, which is the batch number the GDS801 export sent.  **A file that cannot be placed is reported, never fatal.** A retour naming a batch this organisation does not have — a declaration from a previous system, typically — is a normal outcome of a good download, and refusing the whole upload over it would throw away every batch the archive *could* answer. The per-file `outcome` says which case each entry fell into; the response is `200` whatever the mix.  Each applied message is reconciled exactly as `importReturnMessage` does for one batch, including the supersede rule: every accepted retour is stored, and only the newest one for a batch is applied. 
         * @summary Import a bulk download of GDS802 return messages
         * @param {File} file A ZIP of retourberichten from the VECOZO-portaal, or a single 574 XML
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        importReturnMessages: async (file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'file' is not null or undefined
            assertParamExists('importReturnMessages', 'file', file)
            const localVarPath = `/api/v1/billing/batches/import-returns`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every numbered export this batch was asked to be in — a bulk ZIP, its own download, or one of its regels\' — newest first (#4781). A batch downloaded twice is in two exports, and two ZIPs that overlap both list it: that is what lets a practice holding several downloaded files tell which one held which batch.  An export is recorded for every declaratiebericht file the API serves, from the day this shipped; nothing before it is backfilled. A verzekeraarfactuur download is not an export.  **Permission required:** `billing.read` 
         * @summary The declaratiebericht downloads a batch was part of
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBillingBatchExports: async (batchId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('listBillingBatchExports', 'batchId', batchId)
            const localVarPath = `/api/v1/billing/batches/{batchId}/exports`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List billing batches with pagination and filtering.
         * @summary List billing batches
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page.  The maximum is deliberately above the 100 most lists here allow: the batch list carries row selection, the selection is **per page**, and raising the page size is the whole answer to acting on more batches at once (#4185). 250 is what the screen\&#39;s own page-size control offers as its largest choice (&#x60;PAGE_SIZE_OPTIONS&#x60; in &#x60;BatchesView.vue&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers. A batch row is a flat summary of about ten scalars with no joins behind it, which is what makes the larger page cheap; the default stays 20. 
         * @param {Array<BillingBatchStatus>} [status] Filter by batch status. Repeatable; values combine as OR (&#x60;?status&#x3D;APPROVED&amp;status&#x3D;REJECTED&amp;status&#x3D;PARTIALLY_APPROVED&#x60;), which is what the list\&#39;s \&quot;Beantwoord\&quot; quick filter sends: the three retour statuses answer one question between them — which batches the insurer has answered — and clicking each in turn is not that question (#4422).  A single &#x60;status&#x3D;APPROVED&#x60; is the same request it always was, so an existing caller or bookmark keeps working.  Omitted means every status; an empty repetition is the same as omitted. 
         * @param {Array<string>} [insurerIds] Filter by the verzekeraar a batch was assembled for. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so the declaratiescherm\&#39;s batch figures narrow to the same set of verzekeraars its consult figures do (#4520). Omitted, or repeated zero times, is every verzekeraar. 
         * @param {boolean} [archived] Filter on the administrative archive (\&quot;verwerkt\&quot;). Defaults to &#x60;false&#x60;, so the list shows active batches only; pass &#x60;true&#x60; for the archive view. 
         * @param {boolean} [openOnly] Keep only the batches with declared money nobody has settled yet (#4625) — what the list\&#39;s \&quot;Gedeeltelijk goedgekeurd\&quot; and \&quot;Afgewezen\&quot; filters send beside their status, because a practice uses those two to find the rejected or cut money that still needs something from it, and the batch status alone cannot say that.  Per regel the batch declared, the money is settled when it was **granted** in full, granted short with the shortfall **written off**, **credited**, or **redeclared** — released and placed in a later batch. Anything else is open: a rejected regel still on the batch, one released and not batched again, a shortfall not written off, and a regel no retour has answered. A batch with nothing open is afgehandeld and drops out; its status is unchanged, and it is still listed without this parameter. &#x60;openAmountEuro&#x60; on each batch is the same rule\&#39;s figure.  ANDs with every other filter. Omitted or &#x60;false&#x60; is no narrowing. 
         * @param {string} [batchNumber] Narrow to the batches whose &#x60;batchNumber&#x60; — the **factuurnummer** an insurer quotes back when it answers a declaration — contains this text. A practice looks a returned declaration up by that number and had nowhere to type it (#4564).  The match is a case-insensitive substring and is **separator-insensitive on both sides**: a number is stored as &#x60;2026-00417&#x60; and is typed as &#x60;202600417&#x60; on a numeric keypad, so every character that is not a letter or a digit is dropped from the term and the hyphen from the stored number before they are compared. Both widths of number occur (&#x60;BillingBatch.batchNumber&#x60;), which is another reason this is a substring match rather than an equality one — &#x60;417&#x60; finds the batch whichever width it was written at.  Deliberately **not** a free-text search: it reads the batch number and nothing else. A cliënt is found through the cliëntenlijst, and a parameter named &#x60;search&#x60; here would let that scope widen without anyone noticing.  ANDs with &#x60;status&#x60;, &#x60;insurerIds&#x60; and &#x60;archived&#x60; rather than replacing them — which also means a batch already marked verwerkt is found from the archive tab only. Omitted, empty, or holding no letter or digit at all is no narrowing. 
         * @param {string} [exportNumber] Narrow to the batches one declaratiebericht export was asked to hold — the number in a downloaded file\&#39;s name, e.g. &#x60;EXP-2026-0042&#x60; (#4781). Included and refused batches alike, so the list shows the same selection the export\&#39;s own detail (&#x60;getBillingBatchExport&#x60;) does.  An exact match on the number, case-insensitive and with surrounding space ignored; a term typed without its &#x60;EXP-&#x60; prefix is given one. ANDs with every other filter — which also means a batch already marked verwerkt is found from the archive tab only. Omitted or empty is no narrowing. 
         * @param {string} [startDate] Keep only the batches **created** on or after this date (#4778) — the batch\&#39;s own &#x60;createdAt&#x60;, not the consult dates of the regels it holds. A practice reads the periode on a list of batches as \&quot;when was this batch made\&quot;; answered through the regels, asking for the batches made on 2 October listed the ones made on 5 October that held consults of 1–3 October.  The date is an **Amsterdam calendar day**: &#x60;createdAt&#x60; is an instant, and a batch made at 00:30 Dutch time counts for that day, not for the previous UTC one.  ANDs with every other filter, &#x60;status&#x60; included — switching status keeps the periode and narrows within it. Omitted is no lower bound. 
         * @param {string} [endDate] Keep only the batches **created** on or before this date, the whole Amsterdam day included, resolved exactly as &#x60;startDate&#x60; is (#4778). Omitted is no upper bound. 
         * @param {string} [clientId] Keep only the batches holding a regel of this cliënt (#4597). Resolved through the batch\&#39;s regels together with &#x60;insurerIds&#x60;, in one &#x60;EXISTS&#x60;: a batch matches when **one** of its regels satisfies both at once. A regel released from its batch carries &#x60;batchId &#x3D; NULL&#x60; and no longer makes that batch match.  This is what a practice\&#39;s administratiekantoor could not ask at all: knowing whose regels it was looking for said nothing about which batch held them. A cliënt of another organisation matches nothing, since the regels are organisation-scoped with the batches. Omitted is every cliënt. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBillingBatches: async (page?: number, size?: number, status?: Array<BillingBatchStatus>, insurerIds?: Array<string>, archived?: boolean, openOnly?: boolean, batchNumber?: string, exportNumber?: string, startDate?: string, endDate?: string, clientId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/billing/batches`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (status) {
                localVarQueryParameter['status'] = status;
            }

            if (insurerIds) {
                localVarQueryParameter['insurerIds'] = insurerIds;
            }

            if (archived !== undefined) {
                localVarQueryParameter['archived'] = archived;
            }

            if (openOnly !== undefined) {
                localVarQueryParameter['openOnly'] = openOnly;
            }

            if (batchNumber !== undefined) {
                localVarQueryParameter['batchNumber'] = batchNumber;
            }

            if (exportNumber !== undefined) {
                localVarQueryParameter['exportNumber'] = exportNumber;
            }

            if (startDate !== undefined) {
                localVarQueryParameter['startDate'] = (startDate as any instanceof Date) ?
                    (startDate as any).toISOString().substring(0,10) :
                    startDate;
            }

            if (endDate !== undefined) {
                localVarQueryParameter['endDate'] = (endDate as any instanceof Date) ?
                    (endDate as any).toISOString().substring(0,10) :
                    endDate;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Releases the billing lines of a batch that has been sent to the insurer back to `READY` with no batch, and marks them as a resubmission (COD651-VEKT Herdeclaratiecode `03`, \"opnieuw ingediende declaratie na afwijzing bij zelfde zorgverzekeraar\"). They can then be put into a **new** batch, which gets the next batch number — the factuurkenmerk an insurer asks a resubmission to carry.  The batch itself is left exactly as it is: same number, same status, not deleted. It is the record of what was sent, and stays resolvable when the insurer refers to it.  Accepted in `REJECTED` and `PARTIALLY_APPROVED`, and in no other status:  - `PARTIALLY_APPROVED` releases **only the lines the insurer rejected**. The   insurer answered this declaration per regel, so releasing the whole batch would   resend work that was already approved and paid. Lines that are `APPROVED` stay   exactly where they are, on this batch, and are never redeclared — including a   line granted at a lower amount than declared, which is an approval with a   retourcode rather than a rejection. So is a line the retour did not answer at   all, which stays `SUBMITTED`. A partially approved batch on which no line is   rejected is therefore refused with `409` rather than answered with a release of   zero lines. - `APPROVED` is refused: an approved declaration is paid, and declaring it again   would bill the same care twice. A correction there is a creditering, not a   resubmission. - `DRAFT`, `READY` and `EXPORTED` are refused: nothing has been sent, so there is   nothing to redeclare. A `DRAFT` batch releases its lines by being deleted   (`DELETE /api/v1/billing/batches/{batchId}`), which marks nothing as a   resubmission; a `READY` batch has to go back to `DRAFT` first, and an `EXPORTED`   one has to be recorded as `SUBMITTED` if it was in fact sent. - `SUBMITTED` is refused: nobody has answered it, so nothing in it was rejected.   A verdict comes only from an imported GDS802 retour, so the retour is imported   first and the batch redeclared once it reads `REJECTED` or `PARTIALLY_APPROVED`.  A `REJECTED` batch releases every releasable line, and deliberately not only the ones reading `REJECTED`: its lines may still read `SUBMITTED` when the retour refused the invoice as a whole without judging each regel, or when the batch was marked rejected by hand before that was withdrawn.  Lines that are excluded from billing, or already credited, stay where they are and are not counted as released.  A gemeentebatch is refused with `409` in every status: a month the gemeente rejected is derived again from its registraties into a new line by itself.  Nothing here credits anything. A rejected line was never paid, so there is nothing to reverse financially — the released lines carry Herdeclaratiecode `03`, which is what the insurer expects on a resend. 
         * @summary Release a declared batch\'s lines so they can be declared again
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        redeclareBillingBatch: async (batchId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('redeclareBillingBatch', 'batchId', batchId)
            const localVarPath = `/api/v1/billing/batches/{batchId}/redeclare`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * **Mail or SnelStart, decided by the organisation (#4727).** When the organisation has SnelStart connected, this books each verzekeraarfactuur as a SnelStart **verkoopboeking** through the API instead of mailing it: on the relatie mapped to its verzekeraar, on the chosen grootboekrekening, btw vrijgesteld, at the invoice\'s declared total, with an omschrijving carrying no cliënt data. `getBoekhoudingDestination` says which of the two a request made now would do, and the run\'s `channel` says which it did. On a SnelStart run:  - An invoice already booked is never booked again, and every booking is preceded by a lookup   of its factuurnummer in SnelStart, so a booking whose answer was lost is recorded rather   than made twice. - An invoice that already went to the boekhouding **by mail** is skipped and reported   (`ALREADY_MAILED`), unless its batch is named in `bookDespiteMailedBatchIds` — the   practice\'s explicit choice, recorded in the audit log. - An invoice SnelStart refuses (no relatie for its verzekeraar, a refused connection key,   SnelStart\'s own validation message) is recorded on the invoice in Dutch and does not stop   the others; its batch is answered `FAILED`. A SnelStart outage fails nothing: the batch   stays `PENDING` and is tried again later. - No `EXPORT` access-audit record is written: a booking carries no cliënt data.  The rest of this description is the mail route (#4621).  Sends the **verzekeraarfactuur** of each named batch to the organisation\'s `boekhoudingEmail` — an inlees-mailbox such as SnelStart\'s — **one invoice per mail**, each carrying that invoice\'s own PDF as its only attachment (#4621).  One invoice per mail is the load-bearing half. An inlees-mailbox books every mail as one booking, so a batch declaring for several cliënten (#4382) is sent as one mail per cliënt invoice, never as the merged PDF `downloadBatchVerkoopfactuur` serves.  **Asynchronous.** The request only stages the work and answers `202` with the run; the mails go out in the background, and `getBoekhoudingSendRun` reports what became of each batch until `finishedAt` is set.  - Only a batch in `SUBMITTED`, `APPROVED`, `REJECTED` or `PARTIALLY_APPROVED` is sent. Any   other is answered `NOT_SUBMITTED` at once and never sent — an `EXPORTED` batch included. - Invoices are produced exactly as `exportBatchVerkoopfacturen` produces them: made if the   batch has none, reused otherwise. A batch that cannot produce one is answered `FAILED`   with its reason, and does not stop the rest. - **Sending twice books nothing twice.** Each invoice records that it went to the   boekhouding, and one that did is never mailed again: a repeated selection answers every   batch `ALREADY_SENT`. A mail that fails leaves its invoice unrecorded, so the next run   sends only what did not go. - Every batch that will be sent gets one `EXPORT` access-audit record (ADR-024), because   this is bulk PHI egress, as the ZIP is.  A batch id naming no batch fails the whole request with a `404`, and one naming another organisation\'s batch with a `403`, exactly as the single-batch endpoints answer.  **Permission required:** `billing.update` 
         * @summary Send the selected batches\' verzekeraarfacturen to the practice\'s boekhouding
         * @param {SendToBoekhoudingRequest} sendToBoekhoudingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendBatchVerkoopfacturenToBoekhouding: async (sendToBoekhoudingRequest: SendToBoekhoudingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'sendToBoekhoudingRequest' is not null or undefined
            assertParamExists('sendBatchVerkoopfacturenToBoekhouding', 'sendToBoekhoudingRequest', sendToBoekhoudingRequest)
            const localVarPath = `/api/v1/billing/batches/boekhouding-verzendingen`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(sendToBoekhoudingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Submits the batch\'s GDS801 to VECOZO directly (ADR-049), for an organisation whose GDS801 stream is on the berichtenservice channel (`vecozoKanalen.gds801` on `GET /api/v1/organizations/me`). The message is the one `GET .../export` serves, zipped as the single entry of the payload. The download stays available on both channels.  Only a `DRAFT` or `READY` batch is sent, or a `SUBMITTED` one whose latest send VECOZO did not forward (`vecozoVerzending.status` `REJECTED`) — that one again, with the message it was stored with. On VECOZO\'s acceptance a concept moves to `SUBMITTED`, through the same transitions as a download followed by \"ingediend\"; on any other answer the batch is unchanged. Every attempt is recorded with its `conversatieId`, `traceerId` and the exact payload; a resend keeps the `conversatieId`.  A batch is sent at most once: while an attempt is out or once one has been delivered and not rejected, another is refused with `409`.  **Permission required:** `billing.update` 
         * @summary Send a billing batch to VECOZO over the berichtenservice
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendBillingBatchToVecozo: async (batchId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('sendBillingBatchToVecozo', 'batchId', batchId)
            const localVarPath = `/api/v1/billing/batches/{batchId}/vecozo-verzending`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates the status of a billing batch.  Valid transitions: - DRAFT → READY - READY → DRAFT (revert to editing) - READY → EXPORTED (use export endpoint instead for full export) - EXPORTED → SUBMITTED (records that the practice uploaded the declaration)  `APPROVED`, `REJECTED` and `PARTIALLY_APPROVED` are the insurer\'s verdict and are refused here with `409`, from every status. A verdict comes only from an imported GDS802 retour (`POST /api/v1/billing/batches/import-returns`, or `/{batchId}/import-return` for one batch), which also records what the insurer said per regel — a status set by hand recorded nothing of the kind. 
         * @summary Update billing batch status
         * @param {string} batchId Billing batch UUID
         * @param {UpdateBatchStatusRequest} updateBatchStatusRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateBillingBatchStatus: async (batchId: string, updateBatchStatusRequest: UpdateBatchStatusRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchId' is not null or undefined
            assertParamExists('updateBillingBatchStatus', 'batchId', batchId)
            // verify required parameter 'updateBatchStatusRequest' is not null or undefined
            assertParamExists('updateBillingBatchStatus', 'updateBatchStatusRequest', updateBatchStatusRequest)
            const localVarPath = `/api/v1/billing/batches/{batchId}/status`
                .replace(`{${"batchId"}}`, encodeURIComponent(String(batchId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateBatchStatusRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * BillingBatchesApi - functional programming interface
 */
export const BillingBatchesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = BillingBatchesApiAxiosParamCreator(configuration)
    return {
        /**
         * Sets or clears the administrative \"verwerkt\" flag on a billing batch.  This is orthogonal to the batch status lifecycle: an archived batch keeps its status, its totals and its billing lines, and any status can be archived — the flag only decides whether the batch shows up in the default batches list or in the archive. The operation is idempotent and reversible. 
         * @summary Mark a billing batch as verwerkt (or undo)
         * @param {string} batchId Billing batch UUID
         * @param {ArchiveBillingBatchRequest} archiveBillingBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async archiveBillingBatch(batchId: string, archiveBillingBatchRequest: ArchiveBillingBatchRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingBatchResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.archiveBillingBatch(batchId, archiveBillingBatchRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.archiveBillingBatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates one billing batch per (cliënt, verzekeraar) group in the request, from the rows the practice selected in the \"Te declareren\" list (#4742). A group names its rows by their `entry`, exactly as `BillableAppointmentDto.entry` and `BillableSelectableConsultDto.entry` state it, and may mix both kinds:  - `prestatie:<appointmentClientId>:<practitionerId>` — a prestatie still to   declare. Its billing line is generated if it does not exist yet, priced,   marked READY and batched. - `line:<billingLineId>` — a credit or correctie regel whose prestatie the   payer already has. It must be the organisation\'s, unbatched, READY and of   the group\'s cliënt and verzekeraar. A corrected replacement goes only   together with the credit post that reverses its original, or after that   credit post is in a batch: herdeclaratiecode `05` describes a debet   *beside* a credit.  Two entries resolving to the same billing line are batched once. A group\'s period is the span of its lines\' own service dates.  Every refusal is a 400 in Dutch, and the whole request is applied or none of it is: a partial run that created three of five batches would leave the lines it moved out of READY behind while the practice was told the selection failed. 
         * @summary Create billing batches from rows of the \"Te declareren\" list
         * @param {CreateBillingBatchRequest} createBillingBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createBillingBatches(createBillingBatchRequest: CreateBillingBatchRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CreateBillingBatchesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createBillingBatches(createBillingBatchRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.createBillingBatches']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates one batch per gemeente the request names (#4821), declared to that gemeente — `payerType` MUNICIPALITY on the existing batch, no verzekeraar and no single cliënt. The caller states the grouping, as `createBillingBatches` has it state the (cliënt, verzekeraar) one, so what the screen promised and what is created cannot come apart.  A prestatie is named by its `toewijzingId`, `van` and `productCode`, exactly as `listGemeenteDeclaraties` lists it. The organisation\'s DRAFT gemeente lines are first made what the registraties say now, and each selected prestatie\'s line is re-derived once more as it leaves DRAFT, so a batch never declares a month at what it said when the screen was loaded. A batch\'s period is the span of its lines\' own periods.  Every refusal is a 400 in Dutch — a prestatie that is held, already in a batch, excluded from billing, no longer yielded by the registraties, or of another gemeente than its group — and the whole request is applied or none of it is. 
         * @summary Create one billing batch per gemeente from selected gemeente prestaties
         * @param {CreateGemeenteBatchesRequest} createGemeenteBatchesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createGemeenteBillingBatches(createGemeenteBatchesRequest: CreateGemeenteBatchesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CreateBillingBatchesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createGemeenteBillingBatches(createGemeenteBatchesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.createGemeenteBillingBatches']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Credits the toewijzing-month the named regel declares (#4823) — every regel that declares that toewijzing × month, never one alone, because a JW323 declares the month and a month half credited is declared half. What a practice does when a session was declared on the wrong toewijzing: credit the month, move the session, declare the month anew.  Each regel is reversed in the ledger exactly as a Zvw regel is (a credit regel with the amount negated and `originalLineId` set; the original becomes CREDITED). The credits are put in one batch to the original\'s gemeente and that batch is locked at once, which builds its **JW323** — each prestatie `DebetCredit` C with the values of the prestatie it reverses and its `VorigReferentieNummer` — and queues it in the Berichten outbox, from where the practice sends it like any other bericht.  The month is free from that moment: its registraties can move to another toewijzing, and `listGemeenteDeclaraties` lists it again to be declared in a batch of its own.  Only a month the gemeente granted in full is credited — a regel it has not answered yet may still be rejected, and a rejected one was never granted. A gemeente prestatie is not credited through `createCreditLine`. 
         * @summary Credit the toewijzing-month a gemeente prestatie declares, and queue the credit\'s JW323
         * @param {string} lineId A gemeente prestatie of the toewijzing-month to credit
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async creditGemeenteMonth(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingBatchResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.creditGemeenteMonth(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.creditGemeenteMonth']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deletes a billing batch and returns its billing lines to unbatched state. Only batches in DRAFT status can be deleted. 
         * @summary Delete billing batch
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteBillingBatch(batchId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteBillingBatch(batchId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.deleteBillingBatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Hard-deletes billing that has not yet been issued, scoped to a batch and/or a set of appointments. Records are removed in dependency order: invoice lines, DRAFT invoices, billing lines, then any batch whose lines were all discarded.  Unlike `deleteBillingBatch` (which only accepts DRAFT batches), this also discards `READY` and `EXPORTED` (never-submitted) batches. It is the cleanup tool for incorrectly-created billing from test appointments.  Refuses with `409` (\"credit instead\") on truly committed records: issued (SENT/PAID/OVERDUE) invoices, billing lines already submitted to an insurer, or batches that are SUBMITTED or beyond. 
         * @summary Discard not-yet-issued billing
         * @param {DiscardBillingRequest} discardBillingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async discardBilling(discardBillingRequest: DiscardBillingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DiscardBillingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.discardBilling(discardBillingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.discardBilling']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Downloads one file an insurer sent as aanvullende retourinformatie about this batch\'s declaration, pulled from the VECOZO berichtenservice (ADR-049) and scanned for malware before it was stored. The batch lists its files in `retourBijlagen`.  Served as an attachment under the insurer\'s own file name and recorded as PHI egress (ADR-024).  **Permission required:** `billing.read` 
         * @summary Download one file of the aanvullende retourinformatie on a batch
         * @param {string} batchId Billing batch UUID
         * @param {string} bijlageId The file\&#39;s id, as &#x60;retourBijlagen&#x60; lists it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadBatchRetourBijlage(batchId: string, bijlageId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadBatchRetourBijlage(batchId, bijlageId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.downloadBatchRetourBijlage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Downloads the **verzekeraarfactuur** for one declared batch: the invoice a VECOZO organisation\'s bookkeeper books the receivable from.  The document is addressed to the **verzekeraar** the batch was declared to (naam and UZOVI-nummer; a gemeente by name), names the patiënt only as the verzekerde it concerns, and carries the declared prestaties as factuurregels. A batch created before per-cliënt batches (#4370) declares for several cliënten, so it has **one verzekeraarfactuur per cliënt** and the response is those invoices merged into a single PDF, in patiëntnummer order. Such an invoice is numbered `<batchNummer>-<n>`; a batch declaring for one cliënt keeps the bare batch number.  It is deliberately not the ZPM restitutienota served by `GET /api/v1/invoices/{invoiceId}/pdf`: it carries **no BSN, diagnose, zorgtrajectnummer, AGB-code or verwijzergegeven**, because an external bookkeeper does not need them to book a receivable and a cliënt\'s verzekeraar does.  Whether it reads as a debetfactuur or a creditfactuur follows the **sign of the total**.  The invoices are produced on the first download and reused on every one after it — asking twice serves the same document under the same numbers rather than producing a second receivable. The batch number *is* the factuurnummer; nothing is drawn from the practice\'s cliëntnota-reeks, and the invoice amounts sum to the batch total, which is what reconciles against the single payment the verzekeraar makes for the declaration.  Every download is recorded as PHI egress (ADR-024), exactly as the GDS801 message download is.  **Permission required:** `billing.read` 
         * @summary Download the batch\'s verzekeraarfactuur as PDF
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadBatchVerkoopfactuur(batchId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadBatchVerkoopfactuur(batchId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.downloadBatchVerkoopfactuur']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Zips up the **verzekeraarfactuur** of each named batch, one PDF per batch, named exactly as `downloadBatchVerkoopfactuur` names its file. This is what makes the document usable for a practice\'s boekhouding at all: a month is on the order of 137 batches, and one click per batch is not something a bookkeeper does.  It is `downloadBatchVerkoopfactuur` per batch and nothing more, so a batch declaring for several cliënten is still one entry — the merged PDF that endpoint serves. Each batch produces its invoices if it has none and reuses them otherwise, so asking twice serves the same documents rather than a second set of receivables, and each batch produces its own `EXPORT` access-audit record (ADR-024) — this is bulk PHI egress, so the trail is per batch rather than per archive.  **A batch that cannot produce an invoice does not abandon the rest.** Its entry is left out, its number is named on the `Scrivio-Bulk-Export-Failures` header, and the reason is written into the archive\'s own `mislukte-facturen.txt` report — the header cannot carry an unbounded sentence per batch, and the report is the copy that survives the download. A batch that has not been declared yet, one holding no regels, and one whose regels are already on a cliëntnota each refuse on exactly those terms. When **nothing** could be produced there is no archive to serve, so the request fails with a 422 listing every reason instead.  A batch id that names no batch of this organisation is a different thing entirely and fails the whole request with a 404: the caller asked for a batch it cannot have, and answering with a partial archive would hide that.  **Permission required:** `billing.read` 
         * @summary Download several batches\' verzekeraarfacturen as one ZIP archive
         * @param {Array<string>} batchIds The batches to invoice. Comma-separated list of billing batch UUIDs.  The upper bound is the same 250 &#x60;exportBillingBatches&#x60; takes, and for the same reason — the selection this serves is per page, so it is the largest page the batch list offers. A different bound would mean a full page of batches is accepted by one of the two bulk downloads on that screen and refused by the other. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async exportBatchVerkoopfacturen(batchIds: Array<string>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.exportBatchVerkoopfacturen(batchIds, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.exportBatchVerkoopfacturen']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Generates and downloads a GDS801 XML file for the billing batch.  Available in **every** batch status — \"what is about to be sent\" is a question a practice asks before afronden, not after. Only what the download means differs:  - DRAFT — a concept of the message as it stands. Nothing is registered, the batch   does not move, and the filename carries a `-concept` suffix. - READY — the message that will be sent. The export is registered: status becomes   EXPORTED, `exported_at` is set and `exported_by_id` is the current user. - EXPORTED and beyond — a re-download of the same message; nothing is written.  The GDS801 format is the Dutch standard for healthcare claim submissions to insurers via Vecozo.  **The bytes are either the stored message or a fresh render, and the two can differ.** A batch whose declaration message was stored when it was submitted is served exactly those bytes — what the insurer received, unaffected by anything edited since. Every other batch is rendered from **current** data on each request: the message is assembled from the live billing lines, the client, the zorgtraject, the insurer and the organisation, so a corrected tarief, an edited verwijzer or a changed regiebehandelaar silently rewrite what a given batch number says. There is deliberately **no backfill**, so a batch submitted before message storage existed will never have a stored message and its download is always a re-render.  Which of the two this response is comes back on the `Scrivio-Declaration-Message-Source` header. `declarationMessageStoredAt` on the batch itself answers the same question *before* the download, so a UI can label the action as the sent message or as a re-render without calling this endpoint first.  The response body is the file itself, so the pre-submission controle warnings the message carries are read separately, from `GET /api/v1/billing/batches/{batchId}/controles`.  **Permission required:** `billing.read` 
         * @summary Export billing batch as GDS801 XML
         * @param {string} batchId Billing batch UUID
         * @param {ExportBillingBatchFormatEnum} [format] Export format (currently only \&#39;xml\&#39; for GDS801 is supported)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async exportBillingBatch(batchId: string, format?: ExportBillingBatchFormatEnum, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.exportBillingBatch(batchId, format, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.exportBillingBatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Zips up the GDS801 message of each named batch, one entry per batch, named exactly as the single-batch download names its file — `-concept` suffix for a DRAFT included.  Available in **every** batch status, for the same reason the single export is (`exportBillingBatch`): what differs per status is only what the download *means*. Each **READY** batch in the selection is registered as exported — status becomes EXPORTED, `exported_at` is set and `exported_by_id` is the current user — exactly as downloading it on its own would. Every other status moves nothing. One selection can therefore be several status transitions, which is why the UI confirms it first.  **A batch that cannot be rendered does not abandon the rest.** Its entry is left out, its number is named on the `Scrivio-Bulk-Export-Failures` header, and the reason is written into the archive\'s own `mislukte-declaraties.txt` report — the header cannot carry an unbounded sentence per batch, and the report is the copy that survives the download. When **nothing** could be rendered there is no archive to serve, so the request fails with a 422 listing every reason instead.  A batch id that names no batch of this organisation is a different thing entirely and fails the whole request with a 404: the caller asked for a batch it cannot have, and answering with a partial archive would hide that.  Each exported batch produces its own `EXPORT` access-audit record (ADR-024) — this is bulk PHI egress, so the trail is per batch rather than per archive.  The archive itself is recorded as one numbered export of kind `ZIP` (#4781), naming every selected batch with whether the archive holds it — a refused batch is on the record too, with its reason. The number is in the file name; see `listBillingBatchExports`.  **Permission required:** `billing.read` 
         * @summary Export several billing batches as one ZIP archive
         * @param {Array<string>} batchIds The batches to archive. Comma-separated list of billing batch UUIDs.  The upper bound is the largest page the batch list offers, because the selection this serves is per page — the same 250 as &#x60;listBillingBatches&#x60;\&#39; &#x60;size&#x60; maximum and &#x60;PAGE_SIZE_OPTIONS&#x60; in &#x60;BatchesView.vue&#x60;. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async exportBillingBatches(batchIds: Array<string>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.exportBillingBatches(batchIds, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.exportBillingBatches']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves a billing batch by ID including its billing lines. Lines are grouped by client for easier display. 
         * @summary Get billing batch by ID
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBillingBatchById(batchId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingBatchDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBillingBatchById(batchId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.getBillingBatchById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Builds the batch\'s GDS801 message and returns the **warning-level** violations the pre-submission controles (RBC u29) found in it. Nothing is exported and the batch is not touched — this only reports.  A warning does not stop a declaration but is worth acting on before it is submitted: RC032 (an AGB code the AGB-register does not know) is the one the engines raise today. The export endpoint returns the file itself and cannot carry them, so the declaratie UI reads them here.  A batch that breaks a *blocking* controle answers `422` exactly as the export does, so this doubles as a pre-export check. 
         * @summary Report the pre-submission controle warnings for a billing batch
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBillingBatchControles(batchId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BatchControleReportResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBillingBatchControles(batchId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.getBillingBatchControles']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * A numbered export (#4781) with every batch it was asked to hold: the included ones first, in batch-number order, then the ones it left out with the reason it gave — the same words the archive\'s `mislukte-declaraties.txt` carries. \"What was in EXP-2026-0042\" is this call.  **Permission required:** `billing.read` 
         * @summary One declaratiebericht export and the batches it held
         * @param {string} exportId The export\&#39;s id, as &#x60;listBillingBatchExports&#x60; answers it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBillingBatchExport(exportId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingBatchExportDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBillingBatchExport(exportId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.getBillingBatchExport']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The two batch figures above the declaratie queue — **In batch** and **Wacht op retour** — over the organisation\'s whole non-archived backlog.  It exists because summing them in the browser could not be made correct. The bar read one page of `listBillingBatches` and counted what it happened to hold, so every batch past the hundredth fell out of both figures; and because that page is every status newest-first, most of its hundred slots go to batches the insurer has already answered, which belong to neither stage. Since one batch per cliënt (#4370) a practice passes a hundred batches within days, and the oldest SUBMITTED ones — exactly what \"oudste … dagen\" is meant to surface — were the first to be dropped (#4518).  The stage → status mapping is the server\'s alone: `inBatch` is DRAFT, READY and EXPORTED, `awaitingReturn` is SUBMITTED, and a batch the insurer has answered (APPROVED, REJECTED, PARTIALLY_APPROVED) is in neither — it has left the queue these figures describe, whether or not it has been marked verwerkt. A caller restating that mapping would be a second list of statuses, which is how the two would come to disagree.  Archived (\"verwerkt\") batches are excluded, as they are from the list\'s default view.  There is deliberately no `page` or `size`: every figure is over the whole set, whatever its size, so there is no cap left to disclose. 
         * @summary Aggregate the batch backlog per stage
         * @param {Array<string>} [insurerIds] Narrow both stages to the batches carrying these insurers\&#39; lines — the same filter, resolved the same way, as &#x60;listBillingBatches&#x60;. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so these figures narrow to the same set of verzekeraars the queue\&#39;s consult figures do (#4520). Omitted, or repeated zero times, is every verzekeraar.  It is the only filter here. A batch is per insurer per period and spans many consults, so the queue\&#39;s period, cliënt, behandelaar and regiebehandelaar filters have no counterpart: these figures describe the practice\&#39;s batch backlog rather than a slice of the consult list. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBillingBatchStageSummary(insurerIds?: Array<string>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingBatchStageSummaryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBillingBatchStageSummary(insurerIds, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.getBillingBatchStageSummary']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * SnelStart when the organisation has it connected, its boekhouding mailbox otherwise (#4727), so the confirmation can say which of the two `sendBatchVerkoopfacturenToBoekhouding` will do before it is asked. Readable with `billing.read`, which the SnelStart settings themselves are not.  **Permission required:** `billing.read` 
         * @summary Where \"Naar boekhouding\" delivers for this organisation right now
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBoekhoudingDestination(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BoekhoudingDestinationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBoekhoudingDestination(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.getBoekhoudingDestination']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The run `sendBatchVerkoopfacturenToBoekhouding` staged, with the outcome per batch in the order the batches were selected. A batch reads `PENDING` until the background worker has reached it; `finishedAt` is set once none is left.  **Permission required:** `billing.read` 
         * @summary What a send to the boekhouding has done so far
         * @param {string} runId The run\&#39;s id, as the send answered it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBoekhoudingSendRun(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BoekhoudingSendRunResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBoekhoudingSendRun(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.getBoekhoudingSendRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Imports a GDS802 return message XML and matches response lines to the original billing lines in the batch. Updates billing line statuses based on insurer approval/rejection.  The batch must **have been declared** — `EXPORTED`, `SUBMITTED`, `APPROVED`, `REJECTED` or `PARTIALLY_APPROVED`. `EXPORTED` is included because the VECOZO connection is not live, so the export is the send and `SUBMITTED` is recorded by hand afterwards, sometimes not at all; a retour is itself proof the declaration went out. The three retour statuses are included because they are exactly the batches a *second* retour answers — including a verdict set by hand before that was withdrawn (#4622), which a retour overrides the same way. `DRAFT` and `READY` are refused: nothing was sent, so no insurer can be answering them. An archived batch is accepted — a retour can arrive days after a batch was closed off.  After processing: - Matched lines are updated to APPROVED or REJECTED - Rejection codes and reasons are stored on rejected lines - Batch status is updated to APPROVED, REJECTED, or PARTIALLY_APPROVED - Unknown response lines are logged as warnings  **Repeating an upload is safe.** Every accepted message is stored, and only the newest for a batch — by `Verzenddatum`, ties resolved by import time — is applied; applying overwrites the previous verdicts wholesale rather than accumulating on top of them. A message older than the one already applied is kept as history and `applied` is `false`. 
         * @summary Import GDS802 return message for a batch
         * @param {string} batchId Billing batch UUID
         * @param {File} body 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async importReturnMessage(batchId: string, body: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImportReturnMessageResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.importReturnMessage(batchId, body, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.importReturnMessage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Uploads the retourinformatie a practice downloads from the VECOZO-portaal — a ZIP, or a single retourbericht — and applies every GDS802 (574) message in it to the batch it names. Requires the `billing.update` permission.  Each entry is classified on its own `Berichtcode`, never on its filename: a portaal download mixes 574 retouren with 573 copies of what was sent and with PDFs, and the 574s themselves arrive under more than one naming convention. Every entry is then routed by its `DeclaratieContext/Factuurnummer`, which is the batch number the GDS801 export sent.  **A file that cannot be placed is reported, never fatal.** A retour naming a batch this organisation does not have — a declaration from a previous system, typically — is a normal outcome of a good download, and refusing the whole upload over it would throw away every batch the archive *could* answer. The per-file `outcome` says which case each entry fell into; the response is `200` whatever the mix.  Each applied message is reconciled exactly as `importReturnMessage` does for one batch, including the supersede rule: every accepted retour is stored, and only the newest one for a batch is applied. 
         * @summary Import a bulk download of GDS802 return messages
         * @param {File} file A ZIP of retourberichten from the VECOZO-portaal, or a single 574 XML
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async importReturnMessages(file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Gds802BulkImportReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.importReturnMessages(file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.importReturnMessages']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every numbered export this batch was asked to be in — a bulk ZIP, its own download, or one of its regels\' — newest first (#4781). A batch downloaded twice is in two exports, and two ZIPs that overlap both list it: that is what lets a practice holding several downloaded files tell which one held which batch.  An export is recorded for every declaratiebericht file the API serves, from the day this shipped; nothing before it is backfilled. A verzekeraarfactuur download is not an export.  **Permission required:** `billing.read` 
         * @summary The declaratiebericht downloads a batch was part of
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listBillingBatchExports(batchId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingBatchExportListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listBillingBatchExports(batchId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.listBillingBatchExports']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List billing batches with pagination and filtering.
         * @summary List billing batches
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page.  The maximum is deliberately above the 100 most lists here allow: the batch list carries row selection, the selection is **per page**, and raising the page size is the whole answer to acting on more batches at once (#4185). 250 is what the screen\&#39;s own page-size control offers as its largest choice (&#x60;PAGE_SIZE_OPTIONS&#x60; in &#x60;BatchesView.vue&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers. A batch row is a flat summary of about ten scalars with no joins behind it, which is what makes the larger page cheap; the default stays 20. 
         * @param {Array<BillingBatchStatus>} [status] Filter by batch status. Repeatable; values combine as OR (&#x60;?status&#x3D;APPROVED&amp;status&#x3D;REJECTED&amp;status&#x3D;PARTIALLY_APPROVED&#x60;), which is what the list\&#39;s \&quot;Beantwoord\&quot; quick filter sends: the three retour statuses answer one question between them — which batches the insurer has answered — and clicking each in turn is not that question (#4422).  A single &#x60;status&#x3D;APPROVED&#x60; is the same request it always was, so an existing caller or bookmark keeps working.  Omitted means every status; an empty repetition is the same as omitted. 
         * @param {Array<string>} [insurerIds] Filter by the verzekeraar a batch was assembled for. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so the declaratiescherm\&#39;s batch figures narrow to the same set of verzekeraars its consult figures do (#4520). Omitted, or repeated zero times, is every verzekeraar. 
         * @param {boolean} [archived] Filter on the administrative archive (\&quot;verwerkt\&quot;). Defaults to &#x60;false&#x60;, so the list shows active batches only; pass &#x60;true&#x60; for the archive view. 
         * @param {boolean} [openOnly] Keep only the batches with declared money nobody has settled yet (#4625) — what the list\&#39;s \&quot;Gedeeltelijk goedgekeurd\&quot; and \&quot;Afgewezen\&quot; filters send beside their status, because a practice uses those two to find the rejected or cut money that still needs something from it, and the batch status alone cannot say that.  Per regel the batch declared, the money is settled when it was **granted** in full, granted short with the shortfall **written off**, **credited**, or **redeclared** — released and placed in a later batch. Anything else is open: a rejected regel still on the batch, one released and not batched again, a shortfall not written off, and a regel no retour has answered. A batch with nothing open is afgehandeld and drops out; its status is unchanged, and it is still listed without this parameter. &#x60;openAmountEuro&#x60; on each batch is the same rule\&#39;s figure.  ANDs with every other filter. Omitted or &#x60;false&#x60; is no narrowing. 
         * @param {string} [batchNumber] Narrow to the batches whose &#x60;batchNumber&#x60; — the **factuurnummer** an insurer quotes back when it answers a declaration — contains this text. A practice looks a returned declaration up by that number and had nowhere to type it (#4564).  The match is a case-insensitive substring and is **separator-insensitive on both sides**: a number is stored as &#x60;2026-00417&#x60; and is typed as &#x60;202600417&#x60; on a numeric keypad, so every character that is not a letter or a digit is dropped from the term and the hyphen from the stored number before they are compared. Both widths of number occur (&#x60;BillingBatch.batchNumber&#x60;), which is another reason this is a substring match rather than an equality one — &#x60;417&#x60; finds the batch whichever width it was written at.  Deliberately **not** a free-text search: it reads the batch number and nothing else. A cliënt is found through the cliëntenlijst, and a parameter named &#x60;search&#x60; here would let that scope widen without anyone noticing.  ANDs with &#x60;status&#x60;, &#x60;insurerIds&#x60; and &#x60;archived&#x60; rather than replacing them — which also means a batch already marked verwerkt is found from the archive tab only. Omitted, empty, or holding no letter or digit at all is no narrowing. 
         * @param {string} [exportNumber] Narrow to the batches one declaratiebericht export was asked to hold — the number in a downloaded file\&#39;s name, e.g. &#x60;EXP-2026-0042&#x60; (#4781). Included and refused batches alike, so the list shows the same selection the export\&#39;s own detail (&#x60;getBillingBatchExport&#x60;) does.  An exact match on the number, case-insensitive and with surrounding space ignored; a term typed without its &#x60;EXP-&#x60; prefix is given one. ANDs with every other filter — which also means a batch already marked verwerkt is found from the archive tab only. Omitted or empty is no narrowing. 
         * @param {string} [startDate] Keep only the batches **created** on or after this date (#4778) — the batch\&#39;s own &#x60;createdAt&#x60;, not the consult dates of the regels it holds. A practice reads the periode on a list of batches as \&quot;when was this batch made\&quot;; answered through the regels, asking for the batches made on 2 October listed the ones made on 5 October that held consults of 1–3 October.  The date is an **Amsterdam calendar day**: &#x60;createdAt&#x60; is an instant, and a batch made at 00:30 Dutch time counts for that day, not for the previous UTC one.  ANDs with every other filter, &#x60;status&#x60; included — switching status keeps the periode and narrows within it. Omitted is no lower bound. 
         * @param {string} [endDate] Keep only the batches **created** on or before this date, the whole Amsterdam day included, resolved exactly as &#x60;startDate&#x60; is (#4778). Omitted is no upper bound. 
         * @param {string} [clientId] Keep only the batches holding a regel of this cliënt (#4597). Resolved through the batch\&#39;s regels together with &#x60;insurerIds&#x60;, in one &#x60;EXISTS&#x60;: a batch matches when **one** of its regels satisfies both at once. A regel released from its batch carries &#x60;batchId &#x3D; NULL&#x60; and no longer makes that batch match.  This is what a practice\&#39;s administratiekantoor could not ask at all: knowing whose regels it was looking for said nothing about which batch held them. A cliënt of another organisation matches nothing, since the regels are organisation-scoped with the batches. Omitted is every cliënt. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listBillingBatches(page?: number, size?: number, status?: Array<BillingBatchStatus>, insurerIds?: Array<string>, archived?: boolean, openOnly?: boolean, batchNumber?: string, exportNumber?: string, startDate?: string, endDate?: string, clientId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingBatchListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listBillingBatches(page, size, status, insurerIds, archived, openOnly, batchNumber, exportNumber, startDate, endDate, clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.listBillingBatches']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Releases the billing lines of a batch that has been sent to the insurer back to `READY` with no batch, and marks them as a resubmission (COD651-VEKT Herdeclaratiecode `03`, \"opnieuw ingediende declaratie na afwijzing bij zelfde zorgverzekeraar\"). They can then be put into a **new** batch, which gets the next batch number — the factuurkenmerk an insurer asks a resubmission to carry.  The batch itself is left exactly as it is: same number, same status, not deleted. It is the record of what was sent, and stays resolvable when the insurer refers to it.  Accepted in `REJECTED` and `PARTIALLY_APPROVED`, and in no other status:  - `PARTIALLY_APPROVED` releases **only the lines the insurer rejected**. The   insurer answered this declaration per regel, so releasing the whole batch would   resend work that was already approved and paid. Lines that are `APPROVED` stay   exactly where they are, on this batch, and are never redeclared — including a   line granted at a lower amount than declared, which is an approval with a   retourcode rather than a rejection. So is a line the retour did not answer at   all, which stays `SUBMITTED`. A partially approved batch on which no line is   rejected is therefore refused with `409` rather than answered with a release of   zero lines. - `APPROVED` is refused: an approved declaration is paid, and declaring it again   would bill the same care twice. A correction there is a creditering, not a   resubmission. - `DRAFT`, `READY` and `EXPORTED` are refused: nothing has been sent, so there is   nothing to redeclare. A `DRAFT` batch releases its lines by being deleted   (`DELETE /api/v1/billing/batches/{batchId}`), which marks nothing as a   resubmission; a `READY` batch has to go back to `DRAFT` first, and an `EXPORTED`   one has to be recorded as `SUBMITTED` if it was in fact sent. - `SUBMITTED` is refused: nobody has answered it, so nothing in it was rejected.   A verdict comes only from an imported GDS802 retour, so the retour is imported   first and the batch redeclared once it reads `REJECTED` or `PARTIALLY_APPROVED`.  A `REJECTED` batch releases every releasable line, and deliberately not only the ones reading `REJECTED`: its lines may still read `SUBMITTED` when the retour refused the invoice as a whole without judging each regel, or when the batch was marked rejected by hand before that was withdrawn.  Lines that are excluded from billing, or already credited, stay where they are and are not counted as released.  A gemeentebatch is refused with `409` in every status: a month the gemeente rejected is derived again from its registraties into a new line by itself.  Nothing here credits anything. A rejected line was never paid, so there is nothing to reverse financially — the released lines carry Herdeclaratiecode `03`, which is what the insurer expects on a resend. 
         * @summary Release a declared batch\'s lines so they can be declared again
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async redeclareBillingBatch(batchId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RedeclareBillingBatchResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.redeclareBillingBatch(batchId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.redeclareBillingBatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * **Mail or SnelStart, decided by the organisation (#4727).** When the organisation has SnelStart connected, this books each verzekeraarfactuur as a SnelStart **verkoopboeking** through the API instead of mailing it: on the relatie mapped to its verzekeraar, on the chosen grootboekrekening, btw vrijgesteld, at the invoice\'s declared total, with an omschrijving carrying no cliënt data. `getBoekhoudingDestination` says which of the two a request made now would do, and the run\'s `channel` says which it did. On a SnelStart run:  - An invoice already booked is never booked again, and every booking is preceded by a lookup   of its factuurnummer in SnelStart, so a booking whose answer was lost is recorded rather   than made twice. - An invoice that already went to the boekhouding **by mail** is skipped and reported   (`ALREADY_MAILED`), unless its batch is named in `bookDespiteMailedBatchIds` — the   practice\'s explicit choice, recorded in the audit log. - An invoice SnelStart refuses (no relatie for its verzekeraar, a refused connection key,   SnelStart\'s own validation message) is recorded on the invoice in Dutch and does not stop   the others; its batch is answered `FAILED`. A SnelStart outage fails nothing: the batch   stays `PENDING` and is tried again later. - No `EXPORT` access-audit record is written: a booking carries no cliënt data.  The rest of this description is the mail route (#4621).  Sends the **verzekeraarfactuur** of each named batch to the organisation\'s `boekhoudingEmail` — an inlees-mailbox such as SnelStart\'s — **one invoice per mail**, each carrying that invoice\'s own PDF as its only attachment (#4621).  One invoice per mail is the load-bearing half. An inlees-mailbox books every mail as one booking, so a batch declaring for several cliënten (#4382) is sent as one mail per cliënt invoice, never as the merged PDF `downloadBatchVerkoopfactuur` serves.  **Asynchronous.** The request only stages the work and answers `202` with the run; the mails go out in the background, and `getBoekhoudingSendRun` reports what became of each batch until `finishedAt` is set.  - Only a batch in `SUBMITTED`, `APPROVED`, `REJECTED` or `PARTIALLY_APPROVED` is sent. Any   other is answered `NOT_SUBMITTED` at once and never sent — an `EXPORTED` batch included. - Invoices are produced exactly as `exportBatchVerkoopfacturen` produces them: made if the   batch has none, reused otherwise. A batch that cannot produce one is answered `FAILED`   with its reason, and does not stop the rest. - **Sending twice books nothing twice.** Each invoice records that it went to the   boekhouding, and one that did is never mailed again: a repeated selection answers every   batch `ALREADY_SENT`. A mail that fails leaves its invoice unrecorded, so the next run   sends only what did not go. - Every batch that will be sent gets one `EXPORT` access-audit record (ADR-024), because   this is bulk PHI egress, as the ZIP is.  A batch id naming no batch fails the whole request with a `404`, and one naming another organisation\'s batch with a `403`, exactly as the single-batch endpoints answer.  **Permission required:** `billing.update` 
         * @summary Send the selected batches\' verzekeraarfacturen to the practice\'s boekhouding
         * @param {SendToBoekhoudingRequest} sendToBoekhoudingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendBatchVerkoopfacturenToBoekhouding(sendToBoekhoudingRequest: SendToBoekhoudingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BoekhoudingSendRunResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendBatchVerkoopfacturenToBoekhouding(sendToBoekhoudingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.sendBatchVerkoopfacturenToBoekhouding']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Submits the batch\'s GDS801 to VECOZO directly (ADR-049), for an organisation whose GDS801 stream is on the berichtenservice channel (`vecozoKanalen.gds801` on `GET /api/v1/organizations/me`). The message is the one `GET .../export` serves, zipped as the single entry of the payload. The download stays available on both channels.  Only a `DRAFT` or `READY` batch is sent, or a `SUBMITTED` one whose latest send VECOZO did not forward (`vecozoVerzending.status` `REJECTED`) — that one again, with the message it was stored with. On VECOZO\'s acceptance a concept moves to `SUBMITTED`, through the same transitions as a download followed by \"ingediend\"; on any other answer the batch is unchanged. Every attempt is recorded with its `conversatieId`, `traceerId` and the exact payload; a resend keeps the `conversatieId`.  A batch is sent at most once: while an attempt is out or once one has been delivered and not rejected, another is refused with `409`.  **Permission required:** `billing.update` 
         * @summary Send a billing batch to VECOZO over the berichtenservice
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendBillingBatchToVecozo(batchId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingBatchResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendBillingBatchToVecozo(batchId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.sendBillingBatchToVecozo']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates the status of a billing batch.  Valid transitions: - DRAFT → READY - READY → DRAFT (revert to editing) - READY → EXPORTED (use export endpoint instead for full export) - EXPORTED → SUBMITTED (records that the practice uploaded the declaration)  `APPROVED`, `REJECTED` and `PARTIALLY_APPROVED` are the insurer\'s verdict and are refused here with `409`, from every status. A verdict comes only from an imported GDS802 retour (`POST /api/v1/billing/batches/import-returns`, or `/{batchId}/import-return` for one batch), which also records what the insurer said per regel — a status set by hand recorded nothing of the kind. 
         * @summary Update billing batch status
         * @param {string} batchId Billing batch UUID
         * @param {UpdateBatchStatusRequest} updateBatchStatusRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateBillingBatchStatus(batchId: string, updateBatchStatusRequest: UpdateBatchStatusRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingBatchResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateBillingBatchStatus(batchId, updateBatchStatusRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingBatchesApi.updateBillingBatchStatus']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * BillingBatchesApi - factory interface
 */
export const BillingBatchesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = BillingBatchesApiFp(configuration)
    return {
        /**
         * Sets or clears the administrative \"verwerkt\" flag on a billing batch.  This is orthogonal to the batch status lifecycle: an archived batch keeps its status, its totals and its billing lines, and any status can be archived — the flag only decides whether the batch shows up in the default batches list or in the archive. The operation is idempotent and reversible. 
         * @summary Mark a billing batch as verwerkt (or undo)
         * @param {string} batchId Billing batch UUID
         * @param {ArchiveBillingBatchRequest} archiveBillingBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveBillingBatch(batchId: string, archiveBillingBatchRequest: ArchiveBillingBatchRequest, options?: RawAxiosRequestConfig): AxiosPromise<BillingBatchResponse> {
            return localVarFp.archiveBillingBatch(batchId, archiveBillingBatchRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates one billing batch per (cliënt, verzekeraar) group in the request, from the rows the practice selected in the \"Te declareren\" list (#4742). A group names its rows by their `entry`, exactly as `BillableAppointmentDto.entry` and `BillableSelectableConsultDto.entry` state it, and may mix both kinds:  - `prestatie:<appointmentClientId>:<practitionerId>` — a prestatie still to   declare. Its billing line is generated if it does not exist yet, priced,   marked READY and batched. - `line:<billingLineId>` — a credit or correctie regel whose prestatie the   payer already has. It must be the organisation\'s, unbatched, READY and of   the group\'s cliënt and verzekeraar. A corrected replacement goes only   together with the credit post that reverses its original, or after that   credit post is in a batch: herdeclaratiecode `05` describes a debet   *beside* a credit.  Two entries resolving to the same billing line are batched once. A group\'s period is the span of its lines\' own service dates.  Every refusal is a 400 in Dutch, and the whole request is applied or none of it is: a partial run that created three of five batches would leave the lines it moved out of READY behind while the practice was told the selection failed. 
         * @summary Create billing batches from rows of the \"Te declareren\" list
         * @param {CreateBillingBatchRequest} createBillingBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createBillingBatches(createBillingBatchRequest: CreateBillingBatchRequest, options?: RawAxiosRequestConfig): AxiosPromise<CreateBillingBatchesResponse> {
            return localVarFp.createBillingBatches(createBillingBatchRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates one batch per gemeente the request names (#4821), declared to that gemeente — `payerType` MUNICIPALITY on the existing batch, no verzekeraar and no single cliënt. The caller states the grouping, as `createBillingBatches` has it state the (cliënt, verzekeraar) one, so what the screen promised and what is created cannot come apart.  A prestatie is named by its `toewijzingId`, `van` and `productCode`, exactly as `listGemeenteDeclaraties` lists it. The organisation\'s DRAFT gemeente lines are first made what the registraties say now, and each selected prestatie\'s line is re-derived once more as it leaves DRAFT, so a batch never declares a month at what it said when the screen was loaded. A batch\'s period is the span of its lines\' own periods.  Every refusal is a 400 in Dutch — a prestatie that is held, already in a batch, excluded from billing, no longer yielded by the registraties, or of another gemeente than its group — and the whole request is applied or none of it is. 
         * @summary Create one billing batch per gemeente from selected gemeente prestaties
         * @param {CreateGemeenteBatchesRequest} createGemeenteBatchesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createGemeenteBillingBatches(createGemeenteBatchesRequest: CreateGemeenteBatchesRequest, options?: RawAxiosRequestConfig): AxiosPromise<CreateBillingBatchesResponse> {
            return localVarFp.createGemeenteBillingBatches(createGemeenteBatchesRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Credits the toewijzing-month the named regel declares (#4823) — every regel that declares that toewijzing × month, never one alone, because a JW323 declares the month and a month half credited is declared half. What a practice does when a session was declared on the wrong toewijzing: credit the month, move the session, declare the month anew.  Each regel is reversed in the ledger exactly as a Zvw regel is (a credit regel with the amount negated and `originalLineId` set; the original becomes CREDITED). The credits are put in one batch to the original\'s gemeente and that batch is locked at once, which builds its **JW323** — each prestatie `DebetCredit` C with the values of the prestatie it reverses and its `VorigReferentieNummer` — and queues it in the Berichten outbox, from where the practice sends it like any other bericht.  The month is free from that moment: its registraties can move to another toewijzing, and `listGemeenteDeclaraties` lists it again to be declared in a batch of its own.  Only a month the gemeente granted in full is credited — a regel it has not answered yet may still be rejected, and a rejected one was never granted. A gemeente prestatie is not credited through `createCreditLine`. 
         * @summary Credit the toewijzing-month a gemeente prestatie declares, and queue the credit\'s JW323
         * @param {string} lineId A gemeente prestatie of the toewijzing-month to credit
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        creditGemeenteMonth(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingBatchResponse> {
            return localVarFp.creditGemeenteMonth(lineId, options).then((request) => request(axios, basePath));
        },
        /**
         * Deletes a billing batch and returns its billing lines to unbatched state. Only batches in DRAFT status can be deleted. 
         * @summary Delete billing batch
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteBillingBatch(batchId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteBillingBatch(batchId, options).then((request) => request(axios, basePath));
        },
        /**
         * Hard-deletes billing that has not yet been issued, scoped to a batch and/or a set of appointments. Records are removed in dependency order: invoice lines, DRAFT invoices, billing lines, then any batch whose lines were all discarded.  Unlike `deleteBillingBatch` (which only accepts DRAFT batches), this also discards `READY` and `EXPORTED` (never-submitted) batches. It is the cleanup tool for incorrectly-created billing from test appointments.  Refuses with `409` (\"credit instead\") on truly committed records: issued (SENT/PAID/OVERDUE) invoices, billing lines already submitted to an insurer, or batches that are SUBMITTED or beyond. 
         * @summary Discard not-yet-issued billing
         * @param {DiscardBillingRequest} discardBillingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        discardBilling(discardBillingRequest: DiscardBillingRequest, options?: RawAxiosRequestConfig): AxiosPromise<DiscardBillingResponse> {
            return localVarFp.discardBilling(discardBillingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Downloads one file an insurer sent as aanvullende retourinformatie about this batch\'s declaration, pulled from the VECOZO berichtenservice (ADR-049) and scanned for malware before it was stored. The batch lists its files in `retourBijlagen`.  Served as an attachment under the insurer\'s own file name and recorded as PHI egress (ADR-024).  **Permission required:** `billing.read` 
         * @summary Download one file of the aanvullende retourinformatie on a batch
         * @param {string} batchId Billing batch UUID
         * @param {string} bijlageId The file\&#39;s id, as &#x60;retourBijlagen&#x60; lists it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadBatchRetourBijlage(batchId: string, bijlageId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadBatchRetourBijlage(batchId, bijlageId, options).then((request) => request(axios, basePath));
        },
        /**
         * Downloads the **verzekeraarfactuur** for one declared batch: the invoice a VECOZO organisation\'s bookkeeper books the receivable from.  The document is addressed to the **verzekeraar** the batch was declared to (naam and UZOVI-nummer; a gemeente by name), names the patiënt only as the verzekerde it concerns, and carries the declared prestaties as factuurregels. A batch created before per-cliënt batches (#4370) declares for several cliënten, so it has **one verzekeraarfactuur per cliënt** and the response is those invoices merged into a single PDF, in patiëntnummer order. Such an invoice is numbered `<batchNummer>-<n>`; a batch declaring for one cliënt keeps the bare batch number.  It is deliberately not the ZPM restitutienota served by `GET /api/v1/invoices/{invoiceId}/pdf`: it carries **no BSN, diagnose, zorgtrajectnummer, AGB-code or verwijzergegeven**, because an external bookkeeper does not need them to book a receivable and a cliënt\'s verzekeraar does.  Whether it reads as a debetfactuur or a creditfactuur follows the **sign of the total**.  The invoices are produced on the first download and reused on every one after it — asking twice serves the same document under the same numbers rather than producing a second receivable. The batch number *is* the factuurnummer; nothing is drawn from the practice\'s cliëntnota-reeks, and the invoice amounts sum to the batch total, which is what reconciles against the single payment the verzekeraar makes for the declaration.  Every download is recorded as PHI egress (ADR-024), exactly as the GDS801 message download is.  **Permission required:** `billing.read` 
         * @summary Download the batch\'s verzekeraarfactuur as PDF
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadBatchVerkoopfactuur(batchId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadBatchVerkoopfactuur(batchId, options).then((request) => request(axios, basePath));
        },
        /**
         * Zips up the **verzekeraarfactuur** of each named batch, one PDF per batch, named exactly as `downloadBatchVerkoopfactuur` names its file. This is what makes the document usable for a practice\'s boekhouding at all: a month is on the order of 137 batches, and one click per batch is not something a bookkeeper does.  It is `downloadBatchVerkoopfactuur` per batch and nothing more, so a batch declaring for several cliënten is still one entry — the merged PDF that endpoint serves. Each batch produces its invoices if it has none and reuses them otherwise, so asking twice serves the same documents rather than a second set of receivables, and each batch produces its own `EXPORT` access-audit record (ADR-024) — this is bulk PHI egress, so the trail is per batch rather than per archive.  **A batch that cannot produce an invoice does not abandon the rest.** Its entry is left out, its number is named on the `Scrivio-Bulk-Export-Failures` header, and the reason is written into the archive\'s own `mislukte-facturen.txt` report — the header cannot carry an unbounded sentence per batch, and the report is the copy that survives the download. A batch that has not been declared yet, one holding no regels, and one whose regels are already on a cliëntnota each refuse on exactly those terms. When **nothing** could be produced there is no archive to serve, so the request fails with a 422 listing every reason instead.  A batch id that names no batch of this organisation is a different thing entirely and fails the whole request with a 404: the caller asked for a batch it cannot have, and answering with a partial archive would hide that.  **Permission required:** `billing.read` 
         * @summary Download several batches\' verzekeraarfacturen as one ZIP archive
         * @param {Array<string>} batchIds The batches to invoice. Comma-separated list of billing batch UUIDs.  The upper bound is the same 250 &#x60;exportBillingBatches&#x60; takes, and for the same reason — the selection this serves is per page, so it is the largest page the batch list offers. A different bound would mean a full page of batches is accepted by one of the two bulk downloads on that screen and refused by the other. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportBatchVerkoopfacturen(batchIds: Array<string>, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.exportBatchVerkoopfacturen(batchIds, options).then((request) => request(axios, basePath));
        },
        /**
         * Generates and downloads a GDS801 XML file for the billing batch.  Available in **every** batch status — \"what is about to be sent\" is a question a practice asks before afronden, not after. Only what the download means differs:  - DRAFT — a concept of the message as it stands. Nothing is registered, the batch   does not move, and the filename carries a `-concept` suffix. - READY — the message that will be sent. The export is registered: status becomes   EXPORTED, `exported_at` is set and `exported_by_id` is the current user. - EXPORTED and beyond — a re-download of the same message; nothing is written.  The GDS801 format is the Dutch standard for healthcare claim submissions to insurers via Vecozo.  **The bytes are either the stored message or a fresh render, and the two can differ.** A batch whose declaration message was stored when it was submitted is served exactly those bytes — what the insurer received, unaffected by anything edited since. Every other batch is rendered from **current** data on each request: the message is assembled from the live billing lines, the client, the zorgtraject, the insurer and the organisation, so a corrected tarief, an edited verwijzer or a changed regiebehandelaar silently rewrite what a given batch number says. There is deliberately **no backfill**, so a batch submitted before message storage existed will never have a stored message and its download is always a re-render.  Which of the two this response is comes back on the `Scrivio-Declaration-Message-Source` header. `declarationMessageStoredAt` on the batch itself answers the same question *before* the download, so a UI can label the action as the sent message or as a re-render without calling this endpoint first.  The response body is the file itself, so the pre-submission controle warnings the message carries are read separately, from `GET /api/v1/billing/batches/{batchId}/controles`.  **Permission required:** `billing.read` 
         * @summary Export billing batch as GDS801 XML
         * @param {string} batchId Billing batch UUID
         * @param {ExportBillingBatchFormatEnum} [format] Export format (currently only \&#39;xml\&#39; for GDS801 is supported)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportBillingBatch(batchId: string, format?: ExportBillingBatchFormatEnum, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.exportBillingBatch(batchId, format, options).then((request) => request(axios, basePath));
        },
        /**
         * Zips up the GDS801 message of each named batch, one entry per batch, named exactly as the single-batch download names its file — `-concept` suffix for a DRAFT included.  Available in **every** batch status, for the same reason the single export is (`exportBillingBatch`): what differs per status is only what the download *means*. Each **READY** batch in the selection is registered as exported — status becomes EXPORTED, `exported_at` is set and `exported_by_id` is the current user — exactly as downloading it on its own would. Every other status moves nothing. One selection can therefore be several status transitions, which is why the UI confirms it first.  **A batch that cannot be rendered does not abandon the rest.** Its entry is left out, its number is named on the `Scrivio-Bulk-Export-Failures` header, and the reason is written into the archive\'s own `mislukte-declaraties.txt` report — the header cannot carry an unbounded sentence per batch, and the report is the copy that survives the download. When **nothing** could be rendered there is no archive to serve, so the request fails with a 422 listing every reason instead.  A batch id that names no batch of this organisation is a different thing entirely and fails the whole request with a 404: the caller asked for a batch it cannot have, and answering with a partial archive would hide that.  Each exported batch produces its own `EXPORT` access-audit record (ADR-024) — this is bulk PHI egress, so the trail is per batch rather than per archive.  The archive itself is recorded as one numbered export of kind `ZIP` (#4781), naming every selected batch with whether the archive holds it — a refused batch is on the record too, with its reason. The number is in the file name; see `listBillingBatchExports`.  **Permission required:** `billing.read` 
         * @summary Export several billing batches as one ZIP archive
         * @param {Array<string>} batchIds The batches to archive. Comma-separated list of billing batch UUIDs.  The upper bound is the largest page the batch list offers, because the selection this serves is per page — the same 250 as &#x60;listBillingBatches&#x60;\&#39; &#x60;size&#x60; maximum and &#x60;PAGE_SIZE_OPTIONS&#x60; in &#x60;BatchesView.vue&#x60;. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportBillingBatches(batchIds: Array<string>, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.exportBillingBatches(batchIds, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves a billing batch by ID including its billing lines. Lines are grouped by client for easier display. 
         * @summary Get billing batch by ID
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingBatchById(batchId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingBatchDetailResponse> {
            return localVarFp.getBillingBatchById(batchId, options).then((request) => request(axios, basePath));
        },
        /**
         * Builds the batch\'s GDS801 message and returns the **warning-level** violations the pre-submission controles (RBC u29) found in it. Nothing is exported and the batch is not touched — this only reports.  A warning does not stop a declaration but is worth acting on before it is submitted: RC032 (an AGB code the AGB-register does not know) is the one the engines raise today. The export endpoint returns the file itself and cannot carry them, so the declaratie UI reads them here.  A batch that breaks a *blocking* controle answers `422` exactly as the export does, so this doubles as a pre-export check. 
         * @summary Report the pre-submission controle warnings for a billing batch
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingBatchControles(batchId: string, options?: RawAxiosRequestConfig): AxiosPromise<BatchControleReportResponse> {
            return localVarFp.getBillingBatchControles(batchId, options).then((request) => request(axios, basePath));
        },
        /**
         * A numbered export (#4781) with every batch it was asked to hold: the included ones first, in batch-number order, then the ones it left out with the reason it gave — the same words the archive\'s `mislukte-declaraties.txt` carries. \"What was in EXP-2026-0042\" is this call.  **Permission required:** `billing.read` 
         * @summary One declaratiebericht export and the batches it held
         * @param {string} exportId The export\&#39;s id, as &#x60;listBillingBatchExports&#x60; answers it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingBatchExport(exportId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingBatchExportDetailResponse> {
            return localVarFp.getBillingBatchExport(exportId, options).then((request) => request(axios, basePath));
        },
        /**
         * The two batch figures above the declaratie queue — **In batch** and **Wacht op retour** — over the organisation\'s whole non-archived backlog.  It exists because summing them in the browser could not be made correct. The bar read one page of `listBillingBatches` and counted what it happened to hold, so every batch past the hundredth fell out of both figures; and because that page is every status newest-first, most of its hundred slots go to batches the insurer has already answered, which belong to neither stage. Since one batch per cliënt (#4370) a practice passes a hundred batches within days, and the oldest SUBMITTED ones — exactly what \"oudste … dagen\" is meant to surface — were the first to be dropped (#4518).  The stage → status mapping is the server\'s alone: `inBatch` is DRAFT, READY and EXPORTED, `awaitingReturn` is SUBMITTED, and a batch the insurer has answered (APPROVED, REJECTED, PARTIALLY_APPROVED) is in neither — it has left the queue these figures describe, whether or not it has been marked verwerkt. A caller restating that mapping would be a second list of statuses, which is how the two would come to disagree.  Archived (\"verwerkt\") batches are excluded, as they are from the list\'s default view.  There is deliberately no `page` or `size`: every figure is over the whole set, whatever its size, so there is no cap left to disclose. 
         * @summary Aggregate the batch backlog per stage
         * @param {Array<string>} [insurerIds] Narrow both stages to the batches carrying these insurers\&#39; lines — the same filter, resolved the same way, as &#x60;listBillingBatches&#x60;. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so these figures narrow to the same set of verzekeraars the queue\&#39;s consult figures do (#4520). Omitted, or repeated zero times, is every verzekeraar.  It is the only filter here. A batch is per insurer per period and spans many consults, so the queue\&#39;s period, cliënt, behandelaar and regiebehandelaar filters have no counterpart: these figures describe the practice\&#39;s batch backlog rather than a slice of the consult list. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingBatchStageSummary(insurerIds?: Array<string>, options?: RawAxiosRequestConfig): AxiosPromise<BillingBatchStageSummaryResponse> {
            return localVarFp.getBillingBatchStageSummary(insurerIds, options).then((request) => request(axios, basePath));
        },
        /**
         * SnelStart when the organisation has it connected, its boekhouding mailbox otherwise (#4727), so the confirmation can say which of the two `sendBatchVerkoopfacturenToBoekhouding` will do before it is asked. Readable with `billing.read`, which the SnelStart settings themselves are not.  **Permission required:** `billing.read` 
         * @summary Where \"Naar boekhouding\" delivers for this organisation right now
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBoekhoudingDestination(options?: RawAxiosRequestConfig): AxiosPromise<BoekhoudingDestinationResponse> {
            return localVarFp.getBoekhoudingDestination(options).then((request) => request(axios, basePath));
        },
        /**
         * The run `sendBatchVerkoopfacturenToBoekhouding` staged, with the outcome per batch in the order the batches were selected. A batch reads `PENDING` until the background worker has reached it; `finishedAt` is set once none is left.  **Permission required:** `billing.read` 
         * @summary What a send to the boekhouding has done so far
         * @param {string} runId The run\&#39;s id, as the send answered it
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBoekhoudingSendRun(runId: string, options?: RawAxiosRequestConfig): AxiosPromise<BoekhoudingSendRunResponse> {
            return localVarFp.getBoekhoudingSendRun(runId, options).then((request) => request(axios, basePath));
        },
        /**
         * Imports a GDS802 return message XML and matches response lines to the original billing lines in the batch. Updates billing line statuses based on insurer approval/rejection.  The batch must **have been declared** — `EXPORTED`, `SUBMITTED`, `APPROVED`, `REJECTED` or `PARTIALLY_APPROVED`. `EXPORTED` is included because the VECOZO connection is not live, so the export is the send and `SUBMITTED` is recorded by hand afterwards, sometimes not at all; a retour is itself proof the declaration went out. The three retour statuses are included because they are exactly the batches a *second* retour answers — including a verdict set by hand before that was withdrawn (#4622), which a retour overrides the same way. `DRAFT` and `READY` are refused: nothing was sent, so no insurer can be answering them. An archived batch is accepted — a retour can arrive days after a batch was closed off.  After processing: - Matched lines are updated to APPROVED or REJECTED - Rejection codes and reasons are stored on rejected lines - Batch status is updated to APPROVED, REJECTED, or PARTIALLY_APPROVED - Unknown response lines are logged as warnings  **Repeating an upload is safe.** Every accepted message is stored, and only the newest for a batch — by `Verzenddatum`, ties resolved by import time — is applied; applying overwrites the previous verdicts wholesale rather than accumulating on top of them. A message older than the one already applied is kept as history and `applied` is `false`. 
         * @summary Import GDS802 return message for a batch
         * @param {string} batchId Billing batch UUID
         * @param {File} body 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        importReturnMessage(batchId: string, body: File, options?: RawAxiosRequestConfig): AxiosPromise<ImportReturnMessageResponse> {
            return localVarFp.importReturnMessage(batchId, body, options).then((request) => request(axios, basePath));
        },
        /**
         * Uploads the retourinformatie a practice downloads from the VECOZO-portaal — a ZIP, or a single retourbericht — and applies every GDS802 (574) message in it to the batch it names. Requires the `billing.update` permission.  Each entry is classified on its own `Berichtcode`, never on its filename: a portaal download mixes 574 retouren with 573 copies of what was sent and with PDFs, and the 574s themselves arrive under more than one naming convention. Every entry is then routed by its `DeclaratieContext/Factuurnummer`, which is the batch number the GDS801 export sent.  **A file that cannot be placed is reported, never fatal.** A retour naming a batch this organisation does not have — a declaration from a previous system, typically — is a normal outcome of a good download, and refusing the whole upload over it would throw away every batch the archive *could* answer. The per-file `outcome` says which case each entry fell into; the response is `200` whatever the mix.  Each applied message is reconciled exactly as `importReturnMessage` does for one batch, including the supersede rule: every accepted retour is stored, and only the newest one for a batch is applied. 
         * @summary Import a bulk download of GDS802 return messages
         * @param {File} file A ZIP of retourberichten from the VECOZO-portaal, or a single 574 XML
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        importReturnMessages(file: File, options?: RawAxiosRequestConfig): AxiosPromise<Gds802BulkImportReport> {
            return localVarFp.importReturnMessages(file, options).then((request) => request(axios, basePath));
        },
        /**
         * Every numbered export this batch was asked to be in — a bulk ZIP, its own download, or one of its regels\' — newest first (#4781). A batch downloaded twice is in two exports, and two ZIPs that overlap both list it: that is what lets a practice holding several downloaded files tell which one held which batch.  An export is recorded for every declaratiebericht file the API serves, from the day this shipped; nothing before it is backfilled. A verzekeraarfactuur download is not an export.  **Permission required:** `billing.read` 
         * @summary The declaratiebericht downloads a batch was part of
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBillingBatchExports(batchId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingBatchExportListResponse> {
            return localVarFp.listBillingBatchExports(batchId, options).then((request) => request(axios, basePath));
        },
        /**
         * List billing batches with pagination and filtering.
         * @summary List billing batches
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page.  The maximum is deliberately above the 100 most lists here allow: the batch list carries row selection, the selection is **per page**, and raising the page size is the whole answer to acting on more batches at once (#4185). 250 is what the screen\&#39;s own page-size control offers as its largest choice (&#x60;PAGE_SIZE_OPTIONS&#x60; in &#x60;BatchesView.vue&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers. A batch row is a flat summary of about ten scalars with no joins behind it, which is what makes the larger page cheap; the default stays 20. 
         * @param {Array<BillingBatchStatus>} [status] Filter by batch status. Repeatable; values combine as OR (&#x60;?status&#x3D;APPROVED&amp;status&#x3D;REJECTED&amp;status&#x3D;PARTIALLY_APPROVED&#x60;), which is what the list\&#39;s \&quot;Beantwoord\&quot; quick filter sends: the three retour statuses answer one question between them — which batches the insurer has answered — and clicking each in turn is not that question (#4422).  A single &#x60;status&#x3D;APPROVED&#x60; is the same request it always was, so an existing caller or bookmark keeps working.  Omitted means every status; an empty repetition is the same as omitted. 
         * @param {Array<string>} [insurerIds] Filter by the verzekeraar a batch was assembled for. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so the declaratiescherm\&#39;s batch figures narrow to the same set of verzekeraars its consult figures do (#4520). Omitted, or repeated zero times, is every verzekeraar. 
         * @param {boolean} [archived] Filter on the administrative archive (\&quot;verwerkt\&quot;). Defaults to &#x60;false&#x60;, so the list shows active batches only; pass &#x60;true&#x60; for the archive view. 
         * @param {boolean} [openOnly] Keep only the batches with declared money nobody has settled yet (#4625) — what the list\&#39;s \&quot;Gedeeltelijk goedgekeurd\&quot; and \&quot;Afgewezen\&quot; filters send beside their status, because a practice uses those two to find the rejected or cut money that still needs something from it, and the batch status alone cannot say that.  Per regel the batch declared, the money is settled when it was **granted** in full, granted short with the shortfall **written off**, **credited**, or **redeclared** — released and placed in a later batch. Anything else is open: a rejected regel still on the batch, one released and not batched again, a shortfall not written off, and a regel no retour has answered. A batch with nothing open is afgehandeld and drops out; its status is unchanged, and it is still listed without this parameter. &#x60;openAmountEuro&#x60; on each batch is the same rule\&#39;s figure.  ANDs with every other filter. Omitted or &#x60;false&#x60; is no narrowing. 
         * @param {string} [batchNumber] Narrow to the batches whose &#x60;batchNumber&#x60; — the **factuurnummer** an insurer quotes back when it answers a declaration — contains this text. A practice looks a returned declaration up by that number and had nowhere to type it (#4564).  The match is a case-insensitive substring and is **separator-insensitive on both sides**: a number is stored as &#x60;2026-00417&#x60; and is typed as &#x60;202600417&#x60; on a numeric keypad, so every character that is not a letter or a digit is dropped from the term and the hyphen from the stored number before they are compared. Both widths of number occur (&#x60;BillingBatch.batchNumber&#x60;), which is another reason this is a substring match rather than an equality one — &#x60;417&#x60; finds the batch whichever width it was written at.  Deliberately **not** a free-text search: it reads the batch number and nothing else. A cliënt is found through the cliëntenlijst, and a parameter named &#x60;search&#x60; here would let that scope widen without anyone noticing.  ANDs with &#x60;status&#x60;, &#x60;insurerIds&#x60; and &#x60;archived&#x60; rather than replacing them — which also means a batch already marked verwerkt is found from the archive tab only. Omitted, empty, or holding no letter or digit at all is no narrowing. 
         * @param {string} [exportNumber] Narrow to the batches one declaratiebericht export was asked to hold — the number in a downloaded file\&#39;s name, e.g. &#x60;EXP-2026-0042&#x60; (#4781). Included and refused batches alike, so the list shows the same selection the export\&#39;s own detail (&#x60;getBillingBatchExport&#x60;) does.  An exact match on the number, case-insensitive and with surrounding space ignored; a term typed without its &#x60;EXP-&#x60; prefix is given one. ANDs with every other filter — which also means a batch already marked verwerkt is found from the archive tab only. Omitted or empty is no narrowing. 
         * @param {string} [startDate] Keep only the batches **created** on or after this date (#4778) — the batch\&#39;s own &#x60;createdAt&#x60;, not the consult dates of the regels it holds. A practice reads the periode on a list of batches as \&quot;when was this batch made\&quot;; answered through the regels, asking for the batches made on 2 October listed the ones made on 5 October that held consults of 1–3 October.  The date is an **Amsterdam calendar day**: &#x60;createdAt&#x60; is an instant, and a batch made at 00:30 Dutch time counts for that day, not for the previous UTC one.  ANDs with every other filter, &#x60;status&#x60; included — switching status keeps the periode and narrows within it. Omitted is no lower bound. 
         * @param {string} [endDate] Keep only the batches **created** on or before this date, the whole Amsterdam day included, resolved exactly as &#x60;startDate&#x60; is (#4778). Omitted is no upper bound. 
         * @param {string} [clientId] Keep only the batches holding a regel of this cliënt (#4597). Resolved through the batch\&#39;s regels together with &#x60;insurerIds&#x60;, in one &#x60;EXISTS&#x60;: a batch matches when **one** of its regels satisfies both at once. A regel released from its batch carries &#x60;batchId &#x3D; NULL&#x60; and no longer makes that batch match.  This is what a practice\&#39;s administratiekantoor could not ask at all: knowing whose regels it was looking for said nothing about which batch held them. A cliënt of another organisation matches nothing, since the regels are organisation-scoped with the batches. Omitted is every cliënt. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBillingBatches(page?: number, size?: number, status?: Array<BillingBatchStatus>, insurerIds?: Array<string>, archived?: boolean, openOnly?: boolean, batchNumber?: string, exportNumber?: string, startDate?: string, endDate?: string, clientId?: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingBatchListResponse> {
            return localVarFp.listBillingBatches(page, size, status, insurerIds, archived, openOnly, batchNumber, exportNumber, startDate, endDate, clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Releases the billing lines of a batch that has been sent to the insurer back to `READY` with no batch, and marks them as a resubmission (COD651-VEKT Herdeclaratiecode `03`, \"opnieuw ingediende declaratie na afwijzing bij zelfde zorgverzekeraar\"). They can then be put into a **new** batch, which gets the next batch number — the factuurkenmerk an insurer asks a resubmission to carry.  The batch itself is left exactly as it is: same number, same status, not deleted. It is the record of what was sent, and stays resolvable when the insurer refers to it.  Accepted in `REJECTED` and `PARTIALLY_APPROVED`, and in no other status:  - `PARTIALLY_APPROVED` releases **only the lines the insurer rejected**. The   insurer answered this declaration per regel, so releasing the whole batch would   resend work that was already approved and paid. Lines that are `APPROVED` stay   exactly where they are, on this batch, and are never redeclared — including a   line granted at a lower amount than declared, which is an approval with a   retourcode rather than a rejection. So is a line the retour did not answer at   all, which stays `SUBMITTED`. A partially approved batch on which no line is   rejected is therefore refused with `409` rather than answered with a release of   zero lines. - `APPROVED` is refused: an approved declaration is paid, and declaring it again   would bill the same care twice. A correction there is a creditering, not a   resubmission. - `DRAFT`, `READY` and `EXPORTED` are refused: nothing has been sent, so there is   nothing to redeclare. A `DRAFT` batch releases its lines by being deleted   (`DELETE /api/v1/billing/batches/{batchId}`), which marks nothing as a   resubmission; a `READY` batch has to go back to `DRAFT` first, and an `EXPORTED`   one has to be recorded as `SUBMITTED` if it was in fact sent. - `SUBMITTED` is refused: nobody has answered it, so nothing in it was rejected.   A verdict comes only from an imported GDS802 retour, so the retour is imported   first and the batch redeclared once it reads `REJECTED` or `PARTIALLY_APPROVED`.  A `REJECTED` batch releases every releasable line, and deliberately not only the ones reading `REJECTED`: its lines may still read `SUBMITTED` when the retour refused the invoice as a whole without judging each regel, or when the batch was marked rejected by hand before that was withdrawn.  Lines that are excluded from billing, or already credited, stay where they are and are not counted as released.  A gemeentebatch is refused with `409` in every status: a month the gemeente rejected is derived again from its registraties into a new line by itself.  Nothing here credits anything. A rejected line was never paid, so there is nothing to reverse financially — the released lines carry Herdeclaratiecode `03`, which is what the insurer expects on a resend. 
         * @summary Release a declared batch\'s lines so they can be declared again
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        redeclareBillingBatch(batchId: string, options?: RawAxiosRequestConfig): AxiosPromise<RedeclareBillingBatchResponse> {
            return localVarFp.redeclareBillingBatch(batchId, options).then((request) => request(axios, basePath));
        },
        /**
         * **Mail or SnelStart, decided by the organisation (#4727).** When the organisation has SnelStart connected, this books each verzekeraarfactuur as a SnelStart **verkoopboeking** through the API instead of mailing it: on the relatie mapped to its verzekeraar, on the chosen grootboekrekening, btw vrijgesteld, at the invoice\'s declared total, with an omschrijving carrying no cliënt data. `getBoekhoudingDestination` says which of the two a request made now would do, and the run\'s `channel` says which it did. On a SnelStart run:  - An invoice already booked is never booked again, and every booking is preceded by a lookup   of its factuurnummer in SnelStart, so a booking whose answer was lost is recorded rather   than made twice. - An invoice that already went to the boekhouding **by mail** is skipped and reported   (`ALREADY_MAILED`), unless its batch is named in `bookDespiteMailedBatchIds` — the   practice\'s explicit choice, recorded in the audit log. - An invoice SnelStart refuses (no relatie for its verzekeraar, a refused connection key,   SnelStart\'s own validation message) is recorded on the invoice in Dutch and does not stop   the others; its batch is answered `FAILED`. A SnelStart outage fails nothing: the batch   stays `PENDING` and is tried again later. - No `EXPORT` access-audit record is written: a booking carries no cliënt data.  The rest of this description is the mail route (#4621).  Sends the **verzekeraarfactuur** of each named batch to the organisation\'s `boekhoudingEmail` — an inlees-mailbox such as SnelStart\'s — **one invoice per mail**, each carrying that invoice\'s own PDF as its only attachment (#4621).  One invoice per mail is the load-bearing half. An inlees-mailbox books every mail as one booking, so a batch declaring for several cliënten (#4382) is sent as one mail per cliënt invoice, never as the merged PDF `downloadBatchVerkoopfactuur` serves.  **Asynchronous.** The request only stages the work and answers `202` with the run; the mails go out in the background, and `getBoekhoudingSendRun` reports what became of each batch until `finishedAt` is set.  - Only a batch in `SUBMITTED`, `APPROVED`, `REJECTED` or `PARTIALLY_APPROVED` is sent. Any   other is answered `NOT_SUBMITTED` at once and never sent — an `EXPORTED` batch included. - Invoices are produced exactly as `exportBatchVerkoopfacturen` produces them: made if the   batch has none, reused otherwise. A batch that cannot produce one is answered `FAILED`   with its reason, and does not stop the rest. - **Sending twice books nothing twice.** Each invoice records that it went to the   boekhouding, and one that did is never mailed again: a repeated selection answers every   batch `ALREADY_SENT`. A mail that fails leaves its invoice unrecorded, so the next run   sends only what did not go. - Every batch that will be sent gets one `EXPORT` access-audit record (ADR-024), because   this is bulk PHI egress, as the ZIP is.  A batch id naming no batch fails the whole request with a `404`, and one naming another organisation\'s batch with a `403`, exactly as the single-batch endpoints answer.  **Permission required:** `billing.update` 
         * @summary Send the selected batches\' verzekeraarfacturen to the practice\'s boekhouding
         * @param {SendToBoekhoudingRequest} sendToBoekhoudingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendBatchVerkoopfacturenToBoekhouding(sendToBoekhoudingRequest: SendToBoekhoudingRequest, options?: RawAxiosRequestConfig): AxiosPromise<BoekhoudingSendRunResponse> {
            return localVarFp.sendBatchVerkoopfacturenToBoekhouding(sendToBoekhoudingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Submits the batch\'s GDS801 to VECOZO directly (ADR-049), for an organisation whose GDS801 stream is on the berichtenservice channel (`vecozoKanalen.gds801` on `GET /api/v1/organizations/me`). The message is the one `GET .../export` serves, zipped as the single entry of the payload. The download stays available on both channels.  Only a `DRAFT` or `READY` batch is sent, or a `SUBMITTED` one whose latest send VECOZO did not forward (`vecozoVerzending.status` `REJECTED`) — that one again, with the message it was stored with. On VECOZO\'s acceptance a concept moves to `SUBMITTED`, through the same transitions as a download followed by \"ingediend\"; on any other answer the batch is unchanged. Every attempt is recorded with its `conversatieId`, `traceerId` and the exact payload; a resend keeps the `conversatieId`.  A batch is sent at most once: while an attempt is out or once one has been delivered and not rejected, another is refused with `409`.  **Permission required:** `billing.update` 
         * @summary Send a billing batch to VECOZO over the berichtenservice
         * @param {string} batchId Billing batch UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendBillingBatchToVecozo(batchId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingBatchResponse> {
            return localVarFp.sendBillingBatchToVecozo(batchId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates the status of a billing batch.  Valid transitions: - DRAFT → READY - READY → DRAFT (revert to editing) - READY → EXPORTED (use export endpoint instead for full export) - EXPORTED → SUBMITTED (records that the practice uploaded the declaration)  `APPROVED`, `REJECTED` and `PARTIALLY_APPROVED` are the insurer\'s verdict and are refused here with `409`, from every status. A verdict comes only from an imported GDS802 retour (`POST /api/v1/billing/batches/import-returns`, or `/{batchId}/import-return` for one batch), which also records what the insurer said per regel — a status set by hand recorded nothing of the kind. 
         * @summary Update billing batch status
         * @param {string} batchId Billing batch UUID
         * @param {UpdateBatchStatusRequest} updateBatchStatusRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateBillingBatchStatus(batchId: string, updateBatchStatusRequest: UpdateBatchStatusRequest, options?: RawAxiosRequestConfig): AxiosPromise<BillingBatchResponse> {
            return localVarFp.updateBillingBatchStatus(batchId, updateBatchStatusRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * BillingBatchesApi - object-oriented interface
 */
export class BillingBatchesApi extends BaseAPI {
    /**
     * Sets or clears the administrative \"verwerkt\" flag on a billing batch.  This is orthogonal to the batch status lifecycle: an archived batch keeps its status, its totals and its billing lines, and any status can be archived — the flag only decides whether the batch shows up in the default batches list or in the archive. The operation is idempotent and reversible. 
     * @summary Mark a billing batch as verwerkt (or undo)
     * @param {string} batchId Billing batch UUID
     * @param {ArchiveBillingBatchRequest} archiveBillingBatchRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public archiveBillingBatch(batchId: string, archiveBillingBatchRequest: ArchiveBillingBatchRequest, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).archiveBillingBatch(batchId, archiveBillingBatchRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates one billing batch per (cliënt, verzekeraar) group in the request, from the rows the practice selected in the \"Te declareren\" list (#4742). A group names its rows by their `entry`, exactly as `BillableAppointmentDto.entry` and `BillableSelectableConsultDto.entry` state it, and may mix both kinds:  - `prestatie:<appointmentClientId>:<practitionerId>` — a prestatie still to   declare. Its billing line is generated if it does not exist yet, priced,   marked READY and batched. - `line:<billingLineId>` — a credit or correctie regel whose prestatie the   payer already has. It must be the organisation\'s, unbatched, READY and of   the group\'s cliënt and verzekeraar. A corrected replacement goes only   together with the credit post that reverses its original, or after that   credit post is in a batch: herdeclaratiecode `05` describes a debet   *beside* a credit.  Two entries resolving to the same billing line are batched once. A group\'s period is the span of its lines\' own service dates.  Every refusal is a 400 in Dutch, and the whole request is applied or none of it is: a partial run that created three of five batches would leave the lines it moved out of READY behind while the practice was told the selection failed. 
     * @summary Create billing batches from rows of the \"Te declareren\" list
     * @param {CreateBillingBatchRequest} createBillingBatchRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createBillingBatches(createBillingBatchRequest: CreateBillingBatchRequest, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).createBillingBatches(createBillingBatchRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates one batch per gemeente the request names (#4821), declared to that gemeente — `payerType` MUNICIPALITY on the existing batch, no verzekeraar and no single cliënt. The caller states the grouping, as `createBillingBatches` has it state the (cliënt, verzekeraar) one, so what the screen promised and what is created cannot come apart.  A prestatie is named by its `toewijzingId`, `van` and `productCode`, exactly as `listGemeenteDeclaraties` lists it. The organisation\'s DRAFT gemeente lines are first made what the registraties say now, and each selected prestatie\'s line is re-derived once more as it leaves DRAFT, so a batch never declares a month at what it said when the screen was loaded. A batch\'s period is the span of its lines\' own periods.  Every refusal is a 400 in Dutch — a prestatie that is held, already in a batch, excluded from billing, no longer yielded by the registraties, or of another gemeente than its group — and the whole request is applied or none of it is. 
     * @summary Create one billing batch per gemeente from selected gemeente prestaties
     * @param {CreateGemeenteBatchesRequest} createGemeenteBatchesRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createGemeenteBillingBatches(createGemeenteBatchesRequest: CreateGemeenteBatchesRequest, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).createGemeenteBillingBatches(createGemeenteBatchesRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Credits the toewijzing-month the named regel declares (#4823) — every regel that declares that toewijzing × month, never one alone, because a JW323 declares the month and a month half credited is declared half. What a practice does when a session was declared on the wrong toewijzing: credit the month, move the session, declare the month anew.  Each regel is reversed in the ledger exactly as a Zvw regel is (a credit regel with the amount negated and `originalLineId` set; the original becomes CREDITED). The credits are put in one batch to the original\'s gemeente and that batch is locked at once, which builds its **JW323** — each prestatie `DebetCredit` C with the values of the prestatie it reverses and its `VorigReferentieNummer` — and queues it in the Berichten outbox, from where the practice sends it like any other bericht.  The month is free from that moment: its registraties can move to another toewijzing, and `listGemeenteDeclaraties` lists it again to be declared in a batch of its own.  Only a month the gemeente granted in full is credited — a regel it has not answered yet may still be rejected, and a rejected one was never granted. A gemeente prestatie is not credited through `createCreditLine`. 
     * @summary Credit the toewijzing-month a gemeente prestatie declares, and queue the credit\'s JW323
     * @param {string} lineId A gemeente prestatie of the toewijzing-month to credit
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public creditGemeenteMonth(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).creditGemeenteMonth(lineId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deletes a billing batch and returns its billing lines to unbatched state. Only batches in DRAFT status can be deleted. 
     * @summary Delete billing batch
     * @param {string} batchId Billing batch UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteBillingBatch(batchId: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).deleteBillingBatch(batchId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Hard-deletes billing that has not yet been issued, scoped to a batch and/or a set of appointments. Records are removed in dependency order: invoice lines, DRAFT invoices, billing lines, then any batch whose lines were all discarded.  Unlike `deleteBillingBatch` (which only accepts DRAFT batches), this also discards `READY` and `EXPORTED` (never-submitted) batches. It is the cleanup tool for incorrectly-created billing from test appointments.  Refuses with `409` (\"credit instead\") on truly committed records: issued (SENT/PAID/OVERDUE) invoices, billing lines already submitted to an insurer, or batches that are SUBMITTED or beyond. 
     * @summary Discard not-yet-issued billing
     * @param {DiscardBillingRequest} discardBillingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public discardBilling(discardBillingRequest: DiscardBillingRequest, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).discardBilling(discardBillingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Downloads one file an insurer sent as aanvullende retourinformatie about this batch\'s declaration, pulled from the VECOZO berichtenservice (ADR-049) and scanned for malware before it was stored. The batch lists its files in `retourBijlagen`.  Served as an attachment under the insurer\'s own file name and recorded as PHI egress (ADR-024).  **Permission required:** `billing.read` 
     * @summary Download one file of the aanvullende retourinformatie on a batch
     * @param {string} batchId Billing batch UUID
     * @param {string} bijlageId The file\&#39;s id, as &#x60;retourBijlagen&#x60; lists it
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadBatchRetourBijlage(batchId: string, bijlageId: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).downloadBatchRetourBijlage(batchId, bijlageId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Downloads the **verzekeraarfactuur** for one declared batch: the invoice a VECOZO organisation\'s bookkeeper books the receivable from.  The document is addressed to the **verzekeraar** the batch was declared to (naam and UZOVI-nummer; a gemeente by name), names the patiënt only as the verzekerde it concerns, and carries the declared prestaties as factuurregels. A batch created before per-cliënt batches (#4370) declares for several cliënten, so it has **one verzekeraarfactuur per cliënt** and the response is those invoices merged into a single PDF, in patiëntnummer order. Such an invoice is numbered `<batchNummer>-<n>`; a batch declaring for one cliënt keeps the bare batch number.  It is deliberately not the ZPM restitutienota served by `GET /api/v1/invoices/{invoiceId}/pdf`: it carries **no BSN, diagnose, zorgtrajectnummer, AGB-code or verwijzergegeven**, because an external bookkeeper does not need them to book a receivable and a cliënt\'s verzekeraar does.  Whether it reads as a debetfactuur or a creditfactuur follows the **sign of the total**.  The invoices are produced on the first download and reused on every one after it — asking twice serves the same document under the same numbers rather than producing a second receivable. The batch number *is* the factuurnummer; nothing is drawn from the practice\'s cliëntnota-reeks, and the invoice amounts sum to the batch total, which is what reconciles against the single payment the verzekeraar makes for the declaration.  Every download is recorded as PHI egress (ADR-024), exactly as the GDS801 message download is.  **Permission required:** `billing.read` 
     * @summary Download the batch\'s verzekeraarfactuur as PDF
     * @param {string} batchId Billing batch UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadBatchVerkoopfactuur(batchId: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).downloadBatchVerkoopfactuur(batchId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Zips up the **verzekeraarfactuur** of each named batch, one PDF per batch, named exactly as `downloadBatchVerkoopfactuur` names its file. This is what makes the document usable for a practice\'s boekhouding at all: a month is on the order of 137 batches, and one click per batch is not something a bookkeeper does.  It is `downloadBatchVerkoopfactuur` per batch and nothing more, so a batch declaring for several cliënten is still one entry — the merged PDF that endpoint serves. Each batch produces its invoices if it has none and reuses them otherwise, so asking twice serves the same documents rather than a second set of receivables, and each batch produces its own `EXPORT` access-audit record (ADR-024) — this is bulk PHI egress, so the trail is per batch rather than per archive.  **A batch that cannot produce an invoice does not abandon the rest.** Its entry is left out, its number is named on the `Scrivio-Bulk-Export-Failures` header, and the reason is written into the archive\'s own `mislukte-facturen.txt` report — the header cannot carry an unbounded sentence per batch, and the report is the copy that survives the download. A batch that has not been declared yet, one holding no regels, and one whose regels are already on a cliëntnota each refuse on exactly those terms. When **nothing** could be produced there is no archive to serve, so the request fails with a 422 listing every reason instead.  A batch id that names no batch of this organisation is a different thing entirely and fails the whole request with a 404: the caller asked for a batch it cannot have, and answering with a partial archive would hide that.  **Permission required:** `billing.read` 
     * @summary Download several batches\' verzekeraarfacturen as one ZIP archive
     * @param {Array<string>} batchIds The batches to invoice. Comma-separated list of billing batch UUIDs.  The upper bound is the same 250 &#x60;exportBillingBatches&#x60; takes, and for the same reason — the selection this serves is per page, so it is the largest page the batch list offers. A different bound would mean a full page of batches is accepted by one of the two bulk downloads on that screen and refused by the other. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public exportBatchVerkoopfacturen(batchIds: Array<string>, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).exportBatchVerkoopfacturen(batchIds, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Generates and downloads a GDS801 XML file for the billing batch.  Available in **every** batch status — \"what is about to be sent\" is a question a practice asks before afronden, not after. Only what the download means differs:  - DRAFT — a concept of the message as it stands. Nothing is registered, the batch   does not move, and the filename carries a `-concept` suffix. - READY — the message that will be sent. The export is registered: status becomes   EXPORTED, `exported_at` is set and `exported_by_id` is the current user. - EXPORTED and beyond — a re-download of the same message; nothing is written.  The GDS801 format is the Dutch standard for healthcare claim submissions to insurers via Vecozo.  **The bytes are either the stored message or a fresh render, and the two can differ.** A batch whose declaration message was stored when it was submitted is served exactly those bytes — what the insurer received, unaffected by anything edited since. Every other batch is rendered from **current** data on each request: the message is assembled from the live billing lines, the client, the zorgtraject, the insurer and the organisation, so a corrected tarief, an edited verwijzer or a changed regiebehandelaar silently rewrite what a given batch number says. There is deliberately **no backfill**, so a batch submitted before message storage existed will never have a stored message and its download is always a re-render.  Which of the two this response is comes back on the `Scrivio-Declaration-Message-Source` header. `declarationMessageStoredAt` on the batch itself answers the same question *before* the download, so a UI can label the action as the sent message or as a re-render without calling this endpoint first.  The response body is the file itself, so the pre-submission controle warnings the message carries are read separately, from `GET /api/v1/billing/batches/{batchId}/controles`.  **Permission required:** `billing.read` 
     * @summary Export billing batch as GDS801 XML
     * @param {string} batchId Billing batch UUID
     * @param {ExportBillingBatchFormatEnum} [format] Export format (currently only \&#39;xml\&#39; for GDS801 is supported)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public exportBillingBatch(batchId: string, format?: ExportBillingBatchFormatEnum, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).exportBillingBatch(batchId, format, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Zips up the GDS801 message of each named batch, one entry per batch, named exactly as the single-batch download names its file — `-concept` suffix for a DRAFT included.  Available in **every** batch status, for the same reason the single export is (`exportBillingBatch`): what differs per status is only what the download *means*. Each **READY** batch in the selection is registered as exported — status becomes EXPORTED, `exported_at` is set and `exported_by_id` is the current user — exactly as downloading it on its own would. Every other status moves nothing. One selection can therefore be several status transitions, which is why the UI confirms it first.  **A batch that cannot be rendered does not abandon the rest.** Its entry is left out, its number is named on the `Scrivio-Bulk-Export-Failures` header, and the reason is written into the archive\'s own `mislukte-declaraties.txt` report — the header cannot carry an unbounded sentence per batch, and the report is the copy that survives the download. When **nothing** could be rendered there is no archive to serve, so the request fails with a 422 listing every reason instead.  A batch id that names no batch of this organisation is a different thing entirely and fails the whole request with a 404: the caller asked for a batch it cannot have, and answering with a partial archive would hide that.  Each exported batch produces its own `EXPORT` access-audit record (ADR-024) — this is bulk PHI egress, so the trail is per batch rather than per archive.  The archive itself is recorded as one numbered export of kind `ZIP` (#4781), naming every selected batch with whether the archive holds it — a refused batch is on the record too, with its reason. The number is in the file name; see `listBillingBatchExports`.  **Permission required:** `billing.read` 
     * @summary Export several billing batches as one ZIP archive
     * @param {Array<string>} batchIds The batches to archive. Comma-separated list of billing batch UUIDs.  The upper bound is the largest page the batch list offers, because the selection this serves is per page — the same 250 as &#x60;listBillingBatches&#x60;\&#39; &#x60;size&#x60; maximum and &#x60;PAGE_SIZE_OPTIONS&#x60; in &#x60;BatchesView.vue&#x60;. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public exportBillingBatches(batchIds: Array<string>, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).exportBillingBatches(batchIds, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves a billing batch by ID including its billing lines. Lines are grouped by client for easier display. 
     * @summary Get billing batch by ID
     * @param {string} batchId Billing batch UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBillingBatchById(batchId: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).getBillingBatchById(batchId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Builds the batch\'s GDS801 message and returns the **warning-level** violations the pre-submission controles (RBC u29) found in it. Nothing is exported and the batch is not touched — this only reports.  A warning does not stop a declaration but is worth acting on before it is submitted: RC032 (an AGB code the AGB-register does not know) is the one the engines raise today. The export endpoint returns the file itself and cannot carry them, so the declaratie UI reads them here.  A batch that breaks a *blocking* controle answers `422` exactly as the export does, so this doubles as a pre-export check. 
     * @summary Report the pre-submission controle warnings for a billing batch
     * @param {string} batchId Billing batch UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBillingBatchControles(batchId: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).getBillingBatchControles(batchId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * A numbered export (#4781) with every batch it was asked to hold: the included ones first, in batch-number order, then the ones it left out with the reason it gave — the same words the archive\'s `mislukte-declaraties.txt` carries. \"What was in EXP-2026-0042\" is this call.  **Permission required:** `billing.read` 
     * @summary One declaratiebericht export and the batches it held
     * @param {string} exportId The export\&#39;s id, as &#x60;listBillingBatchExports&#x60; answers it
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBillingBatchExport(exportId: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).getBillingBatchExport(exportId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The two batch figures above the declaratie queue — **In batch** and **Wacht op retour** — over the organisation\'s whole non-archived backlog.  It exists because summing them in the browser could not be made correct. The bar read one page of `listBillingBatches` and counted what it happened to hold, so every batch past the hundredth fell out of both figures; and because that page is every status newest-first, most of its hundred slots go to batches the insurer has already answered, which belong to neither stage. Since one batch per cliënt (#4370) a practice passes a hundred batches within days, and the oldest SUBMITTED ones — exactly what \"oudste … dagen\" is meant to surface — were the first to be dropped (#4518).  The stage → status mapping is the server\'s alone: `inBatch` is DRAFT, READY and EXPORTED, `awaitingReturn` is SUBMITTED, and a batch the insurer has answered (APPROVED, REJECTED, PARTIALLY_APPROVED) is in neither — it has left the queue these figures describe, whether or not it has been marked verwerkt. A caller restating that mapping would be a second list of statuses, which is how the two would come to disagree.  Archived (\"verwerkt\") batches are excluded, as they are from the list\'s default view.  There is deliberately no `page` or `size`: every figure is over the whole set, whatever its size, so there is no cap left to disclose. 
     * @summary Aggregate the batch backlog per stage
     * @param {Array<string>} [insurerIds] Narrow both stages to the batches carrying these insurers\&#39; lines — the same filter, resolved the same way, as &#x60;listBillingBatches&#x60;. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so these figures narrow to the same set of verzekeraars the queue\&#39;s consult figures do (#4520). Omitted, or repeated zero times, is every verzekeraar.  It is the only filter here. A batch is per insurer per period and spans many consults, so the queue\&#39;s period, cliënt, behandelaar and regiebehandelaar filters have no counterpart: these figures describe the practice\&#39;s batch backlog rather than a slice of the consult list. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBillingBatchStageSummary(insurerIds?: Array<string>, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).getBillingBatchStageSummary(insurerIds, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * SnelStart when the organisation has it connected, its boekhouding mailbox otherwise (#4727), so the confirmation can say which of the two `sendBatchVerkoopfacturenToBoekhouding` will do before it is asked. Readable with `billing.read`, which the SnelStart settings themselves are not.  **Permission required:** `billing.read` 
     * @summary Where \"Naar boekhouding\" delivers for this organisation right now
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBoekhoudingDestination(options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).getBoekhoudingDestination(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The run `sendBatchVerkoopfacturenToBoekhouding` staged, with the outcome per batch in the order the batches were selected. A batch reads `PENDING` until the background worker has reached it; `finishedAt` is set once none is left.  **Permission required:** `billing.read` 
     * @summary What a send to the boekhouding has done so far
     * @param {string} runId The run\&#39;s id, as the send answered it
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBoekhoudingSendRun(runId: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).getBoekhoudingSendRun(runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Imports a GDS802 return message XML and matches response lines to the original billing lines in the batch. Updates billing line statuses based on insurer approval/rejection.  The batch must **have been declared** — `EXPORTED`, `SUBMITTED`, `APPROVED`, `REJECTED` or `PARTIALLY_APPROVED`. `EXPORTED` is included because the VECOZO connection is not live, so the export is the send and `SUBMITTED` is recorded by hand afterwards, sometimes not at all; a retour is itself proof the declaration went out. The three retour statuses are included because they are exactly the batches a *second* retour answers — including a verdict set by hand before that was withdrawn (#4622), which a retour overrides the same way. `DRAFT` and `READY` are refused: nothing was sent, so no insurer can be answering them. An archived batch is accepted — a retour can arrive days after a batch was closed off.  After processing: - Matched lines are updated to APPROVED or REJECTED - Rejection codes and reasons are stored on rejected lines - Batch status is updated to APPROVED, REJECTED, or PARTIALLY_APPROVED - Unknown response lines are logged as warnings  **Repeating an upload is safe.** Every accepted message is stored, and only the newest for a batch — by `Verzenddatum`, ties resolved by import time — is applied; applying overwrites the previous verdicts wholesale rather than accumulating on top of them. A message older than the one already applied is kept as history and `applied` is `false`. 
     * @summary Import GDS802 return message for a batch
     * @param {string} batchId Billing batch UUID
     * @param {File} body 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public importReturnMessage(batchId: string, body: File, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).importReturnMessage(batchId, body, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Uploads the retourinformatie a practice downloads from the VECOZO-portaal — a ZIP, or a single retourbericht — and applies every GDS802 (574) message in it to the batch it names. Requires the `billing.update` permission.  Each entry is classified on its own `Berichtcode`, never on its filename: a portaal download mixes 574 retouren with 573 copies of what was sent and with PDFs, and the 574s themselves arrive under more than one naming convention. Every entry is then routed by its `DeclaratieContext/Factuurnummer`, which is the batch number the GDS801 export sent.  **A file that cannot be placed is reported, never fatal.** A retour naming a batch this organisation does not have — a declaration from a previous system, typically — is a normal outcome of a good download, and refusing the whole upload over it would throw away every batch the archive *could* answer. The per-file `outcome` says which case each entry fell into; the response is `200` whatever the mix.  Each applied message is reconciled exactly as `importReturnMessage` does for one batch, including the supersede rule: every accepted retour is stored, and only the newest one for a batch is applied. 
     * @summary Import a bulk download of GDS802 return messages
     * @param {File} file A ZIP of retourberichten from the VECOZO-portaal, or a single 574 XML
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public importReturnMessages(file: File, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).importReturnMessages(file, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every numbered export this batch was asked to be in — a bulk ZIP, its own download, or one of its regels\' — newest first (#4781). A batch downloaded twice is in two exports, and two ZIPs that overlap both list it: that is what lets a practice holding several downloaded files tell which one held which batch.  An export is recorded for every declaratiebericht file the API serves, from the day this shipped; nothing before it is backfilled. A verzekeraarfactuur download is not an export.  **Permission required:** `billing.read` 
     * @summary The declaratiebericht downloads a batch was part of
     * @param {string} batchId Billing batch UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listBillingBatchExports(batchId: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).listBillingBatchExports(batchId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List billing batches with pagination and filtering.
     * @summary List billing batches
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page.  The maximum is deliberately above the 100 most lists here allow: the batch list carries row selection, the selection is **per page**, and raising the page size is the whole answer to acting on more batches at once (#4185). 250 is what the screen\&#39;s own page-size control offers as its largest choice (&#x60;PAGE_SIZE_OPTIONS&#x60; in &#x60;BatchesView.vue&#x60;) — the two are written twice and must agree, or the list accepts a size nothing asks for or refuses one the control offers. A batch row is a flat summary of about ten scalars with no joins behind it, which is what makes the larger page cheap; the default stays 20. 
     * @param {Array<BillingBatchStatus>} [status] Filter by batch status. Repeatable; values combine as OR (&#x60;?status&#x3D;APPROVED&amp;status&#x3D;REJECTED&amp;status&#x3D;PARTIALLY_APPROVED&#x60;), which is what the list\&#39;s \&quot;Beantwoord\&quot; quick filter sends: the three retour statuses answer one question between them — which batches the insurer has answered — and clicking each in turn is not that question (#4422).  A single &#x60;status&#x3D;APPROVED&#x60; is the same request it always was, so an existing caller or bookmark keeps working.  Omitted means every status; an empty repetition is the same as omitted. 
     * @param {Array<string>} [insurerIds] Filter by the verzekeraar a batch was assembled for. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), so the declaratiescherm\&#39;s batch figures narrow to the same set of verzekeraars its consult figures do (#4520). Omitted, or repeated zero times, is every verzekeraar. 
     * @param {boolean} [archived] Filter on the administrative archive (\&quot;verwerkt\&quot;). Defaults to &#x60;false&#x60;, so the list shows active batches only; pass &#x60;true&#x60; for the archive view. 
     * @param {boolean} [openOnly] Keep only the batches with declared money nobody has settled yet (#4625) — what the list\&#39;s \&quot;Gedeeltelijk goedgekeurd\&quot; and \&quot;Afgewezen\&quot; filters send beside their status, because a practice uses those two to find the rejected or cut money that still needs something from it, and the batch status alone cannot say that.  Per regel the batch declared, the money is settled when it was **granted** in full, granted short with the shortfall **written off**, **credited**, or **redeclared** — released and placed in a later batch. Anything else is open: a rejected regel still on the batch, one released and not batched again, a shortfall not written off, and a regel no retour has answered. A batch with nothing open is afgehandeld and drops out; its status is unchanged, and it is still listed without this parameter. &#x60;openAmountEuro&#x60; on each batch is the same rule\&#39;s figure.  ANDs with every other filter. Omitted or &#x60;false&#x60; is no narrowing. 
     * @param {string} [batchNumber] Narrow to the batches whose &#x60;batchNumber&#x60; — the **factuurnummer** an insurer quotes back when it answers a declaration — contains this text. A practice looks a returned declaration up by that number and had nowhere to type it (#4564).  The match is a case-insensitive substring and is **separator-insensitive on both sides**: a number is stored as &#x60;2026-00417&#x60; and is typed as &#x60;202600417&#x60; on a numeric keypad, so every character that is not a letter or a digit is dropped from the term and the hyphen from the stored number before they are compared. Both widths of number occur (&#x60;BillingBatch.batchNumber&#x60;), which is another reason this is a substring match rather than an equality one — &#x60;417&#x60; finds the batch whichever width it was written at.  Deliberately **not** a free-text search: it reads the batch number and nothing else. A cliënt is found through the cliëntenlijst, and a parameter named &#x60;search&#x60; here would let that scope widen without anyone noticing.  ANDs with &#x60;status&#x60;, &#x60;insurerIds&#x60; and &#x60;archived&#x60; rather than replacing them — which also means a batch already marked verwerkt is found from the archive tab only. Omitted, empty, or holding no letter or digit at all is no narrowing. 
     * @param {string} [exportNumber] Narrow to the batches one declaratiebericht export was asked to hold — the number in a downloaded file\&#39;s name, e.g. &#x60;EXP-2026-0042&#x60; (#4781). Included and refused batches alike, so the list shows the same selection the export\&#39;s own detail (&#x60;getBillingBatchExport&#x60;) does.  An exact match on the number, case-insensitive and with surrounding space ignored; a term typed without its &#x60;EXP-&#x60; prefix is given one. ANDs with every other filter — which also means a batch already marked verwerkt is found from the archive tab only. Omitted or empty is no narrowing. 
     * @param {string} [startDate] Keep only the batches **created** on or after this date (#4778) — the batch\&#39;s own &#x60;createdAt&#x60;, not the consult dates of the regels it holds. A practice reads the periode on a list of batches as \&quot;when was this batch made\&quot;; answered through the regels, asking for the batches made on 2 October listed the ones made on 5 October that held consults of 1–3 October.  The date is an **Amsterdam calendar day**: &#x60;createdAt&#x60; is an instant, and a batch made at 00:30 Dutch time counts for that day, not for the previous UTC one.  ANDs with every other filter, &#x60;status&#x60; included — switching status keeps the periode and narrows within it. Omitted is no lower bound. 
     * @param {string} [endDate] Keep only the batches **created** on or before this date, the whole Amsterdam day included, resolved exactly as &#x60;startDate&#x60; is (#4778). Omitted is no upper bound. 
     * @param {string} [clientId] Keep only the batches holding a regel of this cliënt (#4597). Resolved through the batch\&#39;s regels together with &#x60;insurerIds&#x60;, in one &#x60;EXISTS&#x60;: a batch matches when **one** of its regels satisfies both at once. A regel released from its batch carries &#x60;batchId &#x3D; NULL&#x60; and no longer makes that batch match.  This is what a practice\&#39;s administratiekantoor could not ask at all: knowing whose regels it was looking for said nothing about which batch held them. A cliënt of another organisation matches nothing, since the regels are organisation-scoped with the batches. Omitted is every cliënt. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listBillingBatches(page?: number, size?: number, status?: Array<BillingBatchStatus>, insurerIds?: Array<string>, archived?: boolean, openOnly?: boolean, batchNumber?: string, exportNumber?: string, startDate?: string, endDate?: string, clientId?: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).listBillingBatches(page, size, status, insurerIds, archived, openOnly, batchNumber, exportNumber, startDate, endDate, clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Releases the billing lines of a batch that has been sent to the insurer back to `READY` with no batch, and marks them as a resubmission (COD651-VEKT Herdeclaratiecode `03`, \"opnieuw ingediende declaratie na afwijzing bij zelfde zorgverzekeraar\"). They can then be put into a **new** batch, which gets the next batch number — the factuurkenmerk an insurer asks a resubmission to carry.  The batch itself is left exactly as it is: same number, same status, not deleted. It is the record of what was sent, and stays resolvable when the insurer refers to it.  Accepted in `REJECTED` and `PARTIALLY_APPROVED`, and in no other status:  - `PARTIALLY_APPROVED` releases **only the lines the insurer rejected**. The   insurer answered this declaration per regel, so releasing the whole batch would   resend work that was already approved and paid. Lines that are `APPROVED` stay   exactly where they are, on this batch, and are never redeclared — including a   line granted at a lower amount than declared, which is an approval with a   retourcode rather than a rejection. So is a line the retour did not answer at   all, which stays `SUBMITTED`. A partially approved batch on which no line is   rejected is therefore refused with `409` rather than answered with a release of   zero lines. - `APPROVED` is refused: an approved declaration is paid, and declaring it again   would bill the same care twice. A correction there is a creditering, not a   resubmission. - `DRAFT`, `READY` and `EXPORTED` are refused: nothing has been sent, so there is   nothing to redeclare. A `DRAFT` batch releases its lines by being deleted   (`DELETE /api/v1/billing/batches/{batchId}`), which marks nothing as a   resubmission; a `READY` batch has to go back to `DRAFT` first, and an `EXPORTED`   one has to be recorded as `SUBMITTED` if it was in fact sent. - `SUBMITTED` is refused: nobody has answered it, so nothing in it was rejected.   A verdict comes only from an imported GDS802 retour, so the retour is imported   first and the batch redeclared once it reads `REJECTED` or `PARTIALLY_APPROVED`.  A `REJECTED` batch releases every releasable line, and deliberately not only the ones reading `REJECTED`: its lines may still read `SUBMITTED` when the retour refused the invoice as a whole without judging each regel, or when the batch was marked rejected by hand before that was withdrawn.  Lines that are excluded from billing, or already credited, stay where they are and are not counted as released.  A gemeentebatch is refused with `409` in every status: a month the gemeente rejected is derived again from its registraties into a new line by itself.  Nothing here credits anything. A rejected line was never paid, so there is nothing to reverse financially — the released lines carry Herdeclaratiecode `03`, which is what the insurer expects on a resend. 
     * @summary Release a declared batch\'s lines so they can be declared again
     * @param {string} batchId Billing batch UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public redeclareBillingBatch(batchId: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).redeclareBillingBatch(batchId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * **Mail or SnelStart, decided by the organisation (#4727).** When the organisation has SnelStart connected, this books each verzekeraarfactuur as a SnelStart **verkoopboeking** through the API instead of mailing it: on the relatie mapped to its verzekeraar, on the chosen grootboekrekening, btw vrijgesteld, at the invoice\'s declared total, with an omschrijving carrying no cliënt data. `getBoekhoudingDestination` says which of the two a request made now would do, and the run\'s `channel` says which it did. On a SnelStart run:  - An invoice already booked is never booked again, and every booking is preceded by a lookup   of its factuurnummer in SnelStart, so a booking whose answer was lost is recorded rather   than made twice. - An invoice that already went to the boekhouding **by mail** is skipped and reported   (`ALREADY_MAILED`), unless its batch is named in `bookDespiteMailedBatchIds` — the   practice\'s explicit choice, recorded in the audit log. - An invoice SnelStart refuses (no relatie for its verzekeraar, a refused connection key,   SnelStart\'s own validation message) is recorded on the invoice in Dutch and does not stop   the others; its batch is answered `FAILED`. A SnelStart outage fails nothing: the batch   stays `PENDING` and is tried again later. - No `EXPORT` access-audit record is written: a booking carries no cliënt data.  The rest of this description is the mail route (#4621).  Sends the **verzekeraarfactuur** of each named batch to the organisation\'s `boekhoudingEmail` — an inlees-mailbox such as SnelStart\'s — **one invoice per mail**, each carrying that invoice\'s own PDF as its only attachment (#4621).  One invoice per mail is the load-bearing half. An inlees-mailbox books every mail as one booking, so a batch declaring for several cliënten (#4382) is sent as one mail per cliënt invoice, never as the merged PDF `downloadBatchVerkoopfactuur` serves.  **Asynchronous.** The request only stages the work and answers `202` with the run; the mails go out in the background, and `getBoekhoudingSendRun` reports what became of each batch until `finishedAt` is set.  - Only a batch in `SUBMITTED`, `APPROVED`, `REJECTED` or `PARTIALLY_APPROVED` is sent. Any   other is answered `NOT_SUBMITTED` at once and never sent — an `EXPORTED` batch included. - Invoices are produced exactly as `exportBatchVerkoopfacturen` produces them: made if the   batch has none, reused otherwise. A batch that cannot produce one is answered `FAILED`   with its reason, and does not stop the rest. - **Sending twice books nothing twice.** Each invoice records that it went to the   boekhouding, and one that did is never mailed again: a repeated selection answers every   batch `ALREADY_SENT`. A mail that fails leaves its invoice unrecorded, so the next run   sends only what did not go. - Every batch that will be sent gets one `EXPORT` access-audit record (ADR-024), because   this is bulk PHI egress, as the ZIP is.  A batch id naming no batch fails the whole request with a `404`, and one naming another organisation\'s batch with a `403`, exactly as the single-batch endpoints answer.  **Permission required:** `billing.update` 
     * @summary Send the selected batches\' verzekeraarfacturen to the practice\'s boekhouding
     * @param {SendToBoekhoudingRequest} sendToBoekhoudingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendBatchVerkoopfacturenToBoekhouding(sendToBoekhoudingRequest: SendToBoekhoudingRequest, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).sendBatchVerkoopfacturenToBoekhouding(sendToBoekhoudingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Submits the batch\'s GDS801 to VECOZO directly (ADR-049), for an organisation whose GDS801 stream is on the berichtenservice channel (`vecozoKanalen.gds801` on `GET /api/v1/organizations/me`). The message is the one `GET .../export` serves, zipped as the single entry of the payload. The download stays available on both channels.  Only a `DRAFT` or `READY` batch is sent, or a `SUBMITTED` one whose latest send VECOZO did not forward (`vecozoVerzending.status` `REJECTED`) — that one again, with the message it was stored with. On VECOZO\'s acceptance a concept moves to `SUBMITTED`, through the same transitions as a download followed by \"ingediend\"; on any other answer the batch is unchanged. Every attempt is recorded with its `conversatieId`, `traceerId` and the exact payload; a resend keeps the `conversatieId`.  A batch is sent at most once: while an attempt is out or once one has been delivered and not rejected, another is refused with `409`.  **Permission required:** `billing.update` 
     * @summary Send a billing batch to VECOZO over the berichtenservice
     * @param {string} batchId Billing batch UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendBillingBatchToVecozo(batchId: string, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).sendBillingBatchToVecozo(batchId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates the status of a billing batch.  Valid transitions: - DRAFT → READY - READY → DRAFT (revert to editing) - READY → EXPORTED (use export endpoint instead for full export) - EXPORTED → SUBMITTED (records that the practice uploaded the declaration)  `APPROVED`, `REJECTED` and `PARTIALLY_APPROVED` are the insurer\'s verdict and are refused here with `409`, from every status. A verdict comes only from an imported GDS802 retour (`POST /api/v1/billing/batches/import-returns`, or `/{batchId}/import-return` for one batch), which also records what the insurer said per regel — a status set by hand recorded nothing of the kind. 
     * @summary Update billing batch status
     * @param {string} batchId Billing batch UUID
     * @param {UpdateBatchStatusRequest} updateBatchStatusRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateBillingBatchStatus(batchId: string, updateBatchStatusRequest: UpdateBatchStatusRequest, options?: RawAxiosRequestConfig) {
        return BillingBatchesApiFp(this.configuration).updateBillingBatchStatus(batchId, updateBatchStatusRequest, options).then((request) => request(this.axios, this.basePath));
    }
}

export const ExportBillingBatchFormatEnum = {
    Xml: 'xml'
} as const;
export type ExportBillingBatchFormatEnum = typeof ExportBillingBatchFormatEnum[keyof typeof ExportBillingBatchFormatEnum];


/**
 * BillingLinesApi - axios parameter creator
 */
export const BillingLinesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Calculates the amount for a potential billing line without creating it. Useful for previewing billing calculations. 
         * @summary Calculate billing amount
         * @param {BillingCalculationRequest} billingCalculationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        calculateBillingAmount: async (billingCalculationRequest: BillingCalculationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'billingCalculationRequest' is not null or undefined
            assertParamExists('calculateBillingAmount', 'billingCalculationRequest', billingCalculationRequest)
            const localVarPath = `/api/v1/billing/calculate`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(billingCalculationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create a reversing credit line for a billing line that has been committed to an insurer. The credit line copies the original\'s context with negated amounts (isCredit = true, originalLineId set) and is created in READY status so it can flow into a future correction batch or onto a credit invoice. The original line is moved to CREDITED.  Only submitted lines (SUBMITTED, APPROVED, REJECTED) can be credited; an unbatched DRAFT/READY line is discarded by deletion, not credited. 
         * @summary Credit (reverse) a committed billing line
         * @param {string} lineId Billing line UUID to credit
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createCreditLine: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('createCreditLine', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}/credit`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reverses a declared billing line completely and creates the corrected regel that replaces it, in one transaction — the resubmission of a regel the insurer granted less than was claimed for. Something was paid, so it has to be credited before the claim can go out again.  The original moves to CREDITED and is reversed for its **full** amount, whatever was granted: a partial grant still withdraws the whole original claim. The replacement copies the original\'s pricing context verbatim — so a corrected resend is not silently repriced at the NZa maximum — carries herdeclaratiecode `05` (\"Herdeclaratie na creditering bij zelfde zorgverzekeraar\", COD651-VEKT) and belongs to no batch. Both lines are READY and enter the next batch together, the credit as a CreditPrestatie naming the original\'s referentienummer and the replacement as a DebetPrestatie.  The replacement is editable so the practice can correct what the insurer objected to before it is batched; correcting the prestatiecode or the duration re-prices it.  Accepts an APPROVED line with an approved amount below what was claimed. Refuses a REJECTED line (#4738): nothing was paid, so a credit would reverse a claim the insurer never granted — `releaseBillingLineForRedeclaration` is the resubmission of a rejection. Also refuses a credit line, an already-credited line, and a line that was never in a declared batch — including an imported record of a declaration another system sent, which nothing here can reverse.  Undone, while neither regel has been batched, by `undoCreditAndRedeclareLine`.  Deliberately separate from `createCreditLine`: a caller asking for a credit must not get a replacement regel as a side effect.  Its sibling is `writeOffLineShortfall`, which answers the other half of a partial grant: where the insurer is right and the practice accepts the granted amount, only the shortfall is credited and nothing is declared again. 
         * @summary Credit a declared line in full and produce a corrected replacement
         * @param {string} lineId Billing line UUID to credit and declare again
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        creditAndRedeclareLine: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('creditAndRedeclareLine', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}/credit-and-redeclare`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deletes a billing line.  Only lines in DRAFT status can be deleted — narrower than what may be edited, because a READY line may already hang off an invoice that has been totalled and sent. 
         * @summary Delete billing line
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteBillingLine: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('deleteBillingLine', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Downloads the GDS801 XML declaration message for a specific billing line as an XML file.  The file carries the real Factuurnummer and Referentienummer of the bericht the line belongs to — never placeholders. A line that is not in a declaration batch has no such numbers and is refused with 422.  It registers nothing on the line\'s batch (#4779), but it is recorded as an export of kind `LINE` naming the regel, and is listed among its batch\'s exports (#4781). 
         * @summary Download declaration message XML as a file
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadBillingLineDeclarationMessage: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('downloadBillingLineDeclarationMessage', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}/declaration-message/download`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/xml,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Excludes a billing line from being included in batch creation. Only lines in DRAFT or READY status that are not already in a batch can be excluded. An optional reason can be provided for audit purposes. 
         * @summary Exclude billing line from billing
         * @param {string} lineId Billing line UUID
         * @param {ExcludeBillingLineRequest} [excludeBillingLineRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        excludeBillingLine: async (lineId: string, excludeBillingLineRequest?: ExcludeBillingLineRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('excludeBillingLine', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}/exclude`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(excludeBillingLineRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every billing regel on the afspraak — credits and corrected replacements included — with where its declaration stands now and every batch it was declared in (#4733).  The batch detail lists the regels a batch declared; this is the other axis, the story of one consult across batches. A regel released for redeclaration keeps one declaration per batch it went out in, each with the verdict, granted amount and retourcode that retour gave it, so a consult declared in three batches answers with three declarations in order.  An afspraak with no regel answers with an empty `lines`, not 404: an afgeronde afspraak that has not been declared yet is an ordinary answer. 
         * @summary Declaration history of an afspraak\'s billing regels
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentDeclarationHistory: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('getAppointmentDeclarationHistory', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/billing/appointments/{appointmentId}/declaration-history`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves a single billing line by ID.
         * @summary Get billing line by ID
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingLineById: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('getBillingLineById', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the GDS801 XML declaration message for a specific billing line. - For lines not yet submitted: returns a preview of what will be sent - For submitted lines: returns the actual XML that was sent  The message carries the real Factuurnummer and Referentienummer of the bericht the line belongs to — never placeholders — so the preview and the submitted export state the same values an insurer reads. A line that is not in a declaration batch has no such numbers and is refused with 422 rather than rendered.  The response includes the formatted XML content and a flag indicating whether this is a preview or the actual submitted message. 
         * @summary Get declaration message XML for a billing line
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingLineDeclarationMessage: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('getBillingLineDeclarationMessage', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}/declaration-message`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns aggregate counts of validation issues for billable appointments in the specified period. Used to populate validation badges in the billing \"Openstaand\" screen.  If no date range is provided, returns validation summary for all unprocessed completed appointments. 
         * @summary Get validation summary for a period
         * @param {string} [periodStart] Start of billing period (inclusive). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (inclusive). If not provided, no upper bound is applied.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getValidationSummary: async (periodStart?: string, periodEnd?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/billing/validation-summary`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (periodStart !== undefined) {
                localVarQueryParameter['periodStart'] = (periodStart as any instanceof Date) ?
                    (periodStart as any).toISOString().substring(0,10) :
                    periodStart;
            }

            if (periodEnd !== undefined) {
                localVarQueryParameter['periodEnd'] = (periodEnd as any instanceof Date) ?
                    (periodEnd as any).toISOString().substring(0,10) :
                    periodEnd;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Re-includes a previously excluded billing line so it can be included in batch creation again. Only excluded lines can be included. 
         * @summary Include previously excluded billing line
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        includeBillingLine: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('includeBillingLine', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}/include`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List billing lines with pagination and filtering.
         * @summary List billing lines
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: serviceDate, status, amountEuro, tariffEuro, quarters, createdAt, updatedAt. Any other field falls back to serviceDate.
         * @param {BillingLineStatus} [status] Filter by status
         * @param {string} [clientId] Filter by client
         * @param {string} [startDate] Filter by service date start
         * @param {string} [endDate] Filter by service date end
         * @param {boolean} [hasDiscrepancy] Filter by amount discrepancy (approved or received differs from billed)
         * @param {boolean} [excludedFromBilling] Filter by exclusion status (true &#x3D; only excluded, false &#x3D; only non-excluded)
         * @param {string} [practitionerId] Filter by practitioner (behandelaar)
         * @param {string} [insurerId] Filter by insurer (verzekeraar)
         * @param {BillingCategory} [billingCategory] Filter by billing category (INSURED, PRIVATE, NON_BILLABLE)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBillingLines: async (page?: number, size?: number, sort?: string, status?: BillingLineStatus, clientId?: string, startDate?: string, endDate?: string, hasDiscrepancy?: boolean, excludedFromBilling?: boolean, practitionerId?: string, insurerId?: string, billingCategory?: BillingCategory, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/billing/lines`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (startDate !== undefined) {
                localVarQueryParameter['startDate'] = (startDate as any instanceof Date) ?
                    (startDate as any).toISOString().substring(0,10) :
                    startDate;
            }

            if (endDate !== undefined) {
                localVarQueryParameter['endDate'] = (endDate as any instanceof Date) ?
                    (endDate as any).toISOString().substring(0,10) :
                    endDate;
            }

            if (hasDiscrepancy !== undefined) {
                localVarQueryParameter['hasDiscrepancy'] = hasDiscrepancy;
            }

            if (excludedFromBilling !== undefined) {
                localVarQueryParameter['excludedFromBilling'] = excludedFromBilling;
            }

            if (practitionerId !== undefined) {
                localVarQueryParameter['practitionerId'] = practitionerId;
            }

            if (insurerId !== undefined) {
                localVarQueryParameter['insurerId'] = insurerId;
            }

            if (billingCategory !== undefined) {
                localVarQueryParameter['billingCategory'] = billingCategory;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Release a single line the insurer rejected so it can go into a new declaration: it returns to READY with no batch and carries Herdeclaratiecode 03 (COD651-VEKT, \"resubmission\"), which is what the next batch puts on the wire. The batch it came from is left exactly as it is - same number, same status, not deleted - because it is the record of what was sent, and the other lines on it keep their own statuses.  This is the per-line entry point to the same release the batch action performs, for a batch whose rejections the practice wants to handle differently from each other. It is a release, not a creditering: a rejected line was never paid, so there is nothing to reverse.  Only a REJECTED line that is not excluded from billing, is not itself a credit line, and sits on a batch that has actually been declared can be released. A line the insurer approved is credited instead; a line on a batch that never left the building is freed by deleting that batch.  A gemeente prestatie is never released: a month the gemeente rejected is derived again from its registraties into a new line beside the rejected one, so a released line would declare that month twice. 
         * @summary Release a rejected billing line for redeclaration
         * @param {string} lineId Billing line UUID to release for redeclaration
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        releaseBillingLineForRedeclaration: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('releaseBillingLineForRedeclaration', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}/release`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Take back a `creditAndRedeclareLine` before anything reached the insurer (#4738). `lineId` is the **original** regel the pair answers.  In one transaction this deletes the credit regel and the corrected replacement, and puts the original back to the status the insurer\'s answer gave it: APPROVED when the insurer granted part of it, REJECTED when it granted nothing. The original\'s `approvedAmount`, `rejectionCode` and `rejectionReason` were never cleared, so they are what decides which. The consult and the original\'s batch read as they did before the credit, and the original offers its answers again. An audit record names the original and both deleted regels.  Refused with `409` when the original is not CREDITED, when it is not answered by exactly one credit regel and one replacement, or once either of them is in a batch or on an invoice — from then on the pair is on its way to the insurer. 
         * @summary Undo a credit-and-correct pair that has not been sent
         * @param {string} lineId Billing line UUID of the credited original the pair answers
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        undoCreditAndRedeclareLine: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('undoCreditAndRedeclareLine', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}/undo-credit-and-redeclare`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates an existing billing line.  The prestatiecode and the number of kwartieren may be changed while the line has not been committed to a payer — DRAFT or READY, the same two statuses that leave a consult openstaand. Changing either re-prices the line. From IN_BATCH on they are refused; the remaining fields (notes, approvedAmount, receivedAmount) are accepted in any status. 
         * @summary Update billing line
         * @param {string} lineId Billing line UUID
         * @param {UpdateBillingLineRequest} updateBillingLineRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateBillingLine: async (lineId: string, updateBillingLineRequest: UpdateBillingLineRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('updateBillingLine', 'lineId', lineId)
            // verify required parameter 'updateBillingLineRequest' is not null or undefined
            assertParamExists('updateBillingLine', 'updateBillingLineRequest', updateBillingLineRequest)
            const localVarPath = `/api/v1/billing/lines/{lineId}`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateBillingLineRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Credit the difference between what a billing line declared and what the insurer granted for it — the practice accepting the insurer\'s amount and writing off the rest. Nothing is redeclared.  The credited amount is **derived** from the line (`amountEuro - approvedAmount`) and cannot be supplied: a caller-supplied amount would let the UI and the retour disagree about what the insurer granted.  The write-off is a credit line in status `WRITTEN_OFF`: the practice\'s own booking, never batched and never sent to the insurer as a CreditPrestatie.  The original line keeps its APPROVED status — it was paid, partly, and CREDITED means fully reversed. The consult stays settled, so it does not re-enter the declaratie queue.  The other answer to the same partial grant is `creditAndRedeclareLine`, for when the claim itself was wrong: there the whole original is withdrawn and a corrected regel takes its place. This one accepts the insurer\'s amount as final.  Refused with `409` when the line is not APPROVED, when there is no shortfall (no answer recorded, the full amount granted, or more granted than declared), or when the line already carries a credit. 
         * @summary Accept a partial grant and credit the shortfall
         * @param {string} lineId Billing line UUID whose shortfall is written off
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        writeOffLineShortfall: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('writeOffLineShortfall', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}/write-off-shortfall`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Write off the whole declared amount of a billing line the insurer rejected — the practice accepting the rejection with nothing more to do, for instance because the care was already paid under another batch (retourcode 0587). Nothing is redeclared.  The write-off is a credit line in status `WRITTEN_OFF`: the practice\'s own booking, never batched and never sent to the insurer. The original keeps its REJECTED status and the consult stays settled. Its amount is derived and cannot be supplied.  The other answer to a rejection is `releaseBillingLineForRedeclaration` (resend it, correcting it first if need be), which refuses a written-off regel.  A rejected gemeente prestatie is written off the same way, and the write-off keeps its toewijzing-month from being derived and declared again.  Refused with `409` when the line is a credit, is not REJECTED, already carries a credit or write-off, or is a gemeente prestatie whose month has since been declared again. 
         * @summary Accept a rejection and write the regel off in full
         * @param {string} lineId Billing line UUID of the rejected regel to write off
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        writeOffRejectedLine: async (lineId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'lineId' is not null or undefined
            assertParamExists('writeOffRejectedLine', 'lineId', lineId)
            const localVarPath = `/api/v1/billing/lines/{lineId}/write-off-rejected`
                .replace(`{${"lineId"}}`, encodeURIComponent(String(lineId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * BillingLinesApi - functional programming interface
 */
export const BillingLinesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = BillingLinesApiAxiosParamCreator(configuration)
    return {
        /**
         * Calculates the amount for a potential billing line without creating it. Useful for previewing billing calculations. 
         * @summary Calculate billing amount
         * @param {BillingCalculationRequest} billingCalculationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async calculateBillingAmount(billingCalculationRequest: BillingCalculationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingCalculationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.calculateBillingAmount(billingCalculationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.calculateBillingAmount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Create a reversing credit line for a billing line that has been committed to an insurer. The credit line copies the original\'s context with negated amounts (isCredit = true, originalLineId set) and is created in READY status so it can flow into a future correction batch or onto a credit invoice. The original line is moved to CREDITED.  Only submitted lines (SUBMITTED, APPROVED, REJECTED) can be credited; an unbatched DRAFT/READY line is discarded by deletion, not credited. 
         * @summary Credit (reverse) a committed billing line
         * @param {string} lineId Billing line UUID to credit
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createCreditLine(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingLineResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createCreditLine(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.createCreditLine']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reverses a declared billing line completely and creates the corrected regel that replaces it, in one transaction — the resubmission of a regel the insurer granted less than was claimed for. Something was paid, so it has to be credited before the claim can go out again.  The original moves to CREDITED and is reversed for its **full** amount, whatever was granted: a partial grant still withdraws the whole original claim. The replacement copies the original\'s pricing context verbatim — so a corrected resend is not silently repriced at the NZa maximum — carries herdeclaratiecode `05` (\"Herdeclaratie na creditering bij zelfde zorgverzekeraar\", COD651-VEKT) and belongs to no batch. Both lines are READY and enter the next batch together, the credit as a CreditPrestatie naming the original\'s referentienummer and the replacement as a DebetPrestatie.  The replacement is editable so the practice can correct what the insurer objected to before it is batched; correcting the prestatiecode or the duration re-prices it.  Accepts an APPROVED line with an approved amount below what was claimed. Refuses a REJECTED line (#4738): nothing was paid, so a credit would reverse a claim the insurer never granted — `releaseBillingLineForRedeclaration` is the resubmission of a rejection. Also refuses a credit line, an already-credited line, and a line that was never in a declared batch — including an imported record of a declaration another system sent, which nothing here can reverse.  Undone, while neither regel has been batched, by `undoCreditAndRedeclareLine`.  Deliberately separate from `createCreditLine`: a caller asking for a credit must not get a replacement regel as a side effect.  Its sibling is `writeOffLineShortfall`, which answers the other half of a partial grant: where the insurer is right and the practice accepts the granted amount, only the shortfall is credited and nothing is declared again. 
         * @summary Credit a declared line in full and produce a corrected replacement
         * @param {string} lineId Billing line UUID to credit and declare again
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async creditAndRedeclareLine(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CreditAndRedeclareLineResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.creditAndRedeclareLine(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.creditAndRedeclareLine']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deletes a billing line.  Only lines in DRAFT status can be deleted — narrower than what may be edited, because a READY line may already hang off an invoice that has been totalled and sent. 
         * @summary Delete billing line
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteBillingLine(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteBillingLine(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.deleteBillingLine']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Downloads the GDS801 XML declaration message for a specific billing line as an XML file.  The file carries the real Factuurnummer and Referentienummer of the bericht the line belongs to — never placeholders. A line that is not in a declaration batch has no such numbers and is refused with 422.  It registers nothing on the line\'s batch (#4779), but it is recorded as an export of kind `LINE` naming the regel, and is listed among its batch\'s exports (#4781). 
         * @summary Download declaration message XML as a file
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadBillingLineDeclarationMessage(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadBillingLineDeclarationMessage(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.downloadBillingLineDeclarationMessage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Excludes a billing line from being included in batch creation. Only lines in DRAFT or READY status that are not already in a batch can be excluded. An optional reason can be provided for audit purposes. 
         * @summary Exclude billing line from billing
         * @param {string} lineId Billing line UUID
         * @param {ExcludeBillingLineRequest} [excludeBillingLineRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async excludeBillingLine(lineId: string, excludeBillingLineRequest?: ExcludeBillingLineRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingLineResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.excludeBillingLine(lineId, excludeBillingLineRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.excludeBillingLine']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every billing regel on the afspraak — credits and corrected replacements included — with where its declaration stands now and every batch it was declared in (#4733).  The batch detail lists the regels a batch declared; this is the other axis, the story of one consult across batches. A regel released for redeclaration keeps one declaration per batch it went out in, each with the verdict, granted amount and retourcode that retour gave it, so a consult declared in three batches answers with three declarations in order.  An afspraak with no regel answers with an empty `lines`, not 404: an afgeronde afspraak that has not been declared yet is an ordinary answer. 
         * @summary Declaration history of an afspraak\'s billing regels
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAppointmentDeclarationHistory(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentDeclarationHistoryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAppointmentDeclarationHistory(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.getAppointmentDeclarationHistory']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves a single billing line by ID.
         * @summary Get billing line by ID
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBillingLineById(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingLineResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBillingLineById(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.getBillingLineById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the GDS801 XML declaration message for a specific billing line. - For lines not yet submitted: returns a preview of what will be sent - For submitted lines: returns the actual XML that was sent  The message carries the real Factuurnummer and Referentienummer of the bericht the line belongs to — never placeholders — so the preview and the submitted export state the same values an insurer reads. A line that is not in a declaration batch has no such numbers and is refused with 422 rather than rendered.  The response includes the formatted XML content and a flag indicating whether this is a preview or the actual submitted message. 
         * @summary Get declaration message XML for a billing line
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBillingLineDeclarationMessage(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DeclarationMessageResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBillingLineDeclarationMessage(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.getBillingLineDeclarationMessage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns aggregate counts of validation issues for billable appointments in the specified period. Used to populate validation badges in the billing \"Openstaand\" screen.  If no date range is provided, returns validation summary for all unprocessed completed appointments. 
         * @summary Get validation summary for a period
         * @param {string} [periodStart] Start of billing period (inclusive). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (inclusive). If not provided, no upper bound is applied.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getValidationSummary(periodStart?: string, periodEnd?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ValidationSummaryDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getValidationSummary(periodStart, periodEnd, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.getValidationSummary']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Re-includes a previously excluded billing line so it can be included in batch creation again. Only excluded lines can be included. 
         * @summary Include previously excluded billing line
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async includeBillingLine(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingLineResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.includeBillingLine(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.includeBillingLine']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List billing lines with pagination and filtering.
         * @summary List billing lines
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: serviceDate, status, amountEuro, tariffEuro, quarters, createdAt, updatedAt. Any other field falls back to serviceDate.
         * @param {BillingLineStatus} [status] Filter by status
         * @param {string} [clientId] Filter by client
         * @param {string} [startDate] Filter by service date start
         * @param {string} [endDate] Filter by service date end
         * @param {boolean} [hasDiscrepancy] Filter by amount discrepancy (approved or received differs from billed)
         * @param {boolean} [excludedFromBilling] Filter by exclusion status (true &#x3D; only excluded, false &#x3D; only non-excluded)
         * @param {string} [practitionerId] Filter by practitioner (behandelaar)
         * @param {string} [insurerId] Filter by insurer (verzekeraar)
         * @param {BillingCategory} [billingCategory] Filter by billing category (INSURED, PRIVATE, NON_BILLABLE)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listBillingLines(page?: number, size?: number, sort?: string, status?: BillingLineStatus, clientId?: string, startDate?: string, endDate?: string, hasDiscrepancy?: boolean, excludedFromBilling?: boolean, practitionerId?: string, insurerId?: string, billingCategory?: BillingCategory, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingLineListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listBillingLines(page, size, sort, status, clientId, startDate, endDate, hasDiscrepancy, excludedFromBilling, practitionerId, insurerId, billingCategory, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.listBillingLines']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Release a single line the insurer rejected so it can go into a new declaration: it returns to READY with no batch and carries Herdeclaratiecode 03 (COD651-VEKT, \"resubmission\"), which is what the next batch puts on the wire. The batch it came from is left exactly as it is - same number, same status, not deleted - because it is the record of what was sent, and the other lines on it keep their own statuses.  This is the per-line entry point to the same release the batch action performs, for a batch whose rejections the practice wants to handle differently from each other. It is a release, not a creditering: a rejected line was never paid, so there is nothing to reverse.  Only a REJECTED line that is not excluded from billing, is not itself a credit line, and sits on a batch that has actually been declared can be released. A line the insurer approved is credited instead; a line on a batch that never left the building is freed by deleting that batch.  A gemeente prestatie is never released: a month the gemeente rejected is derived again from its registraties into a new line beside the rejected one, so a released line would declare that month twice. 
         * @summary Release a rejected billing line for redeclaration
         * @param {string} lineId Billing line UUID to release for redeclaration
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async releaseBillingLineForRedeclaration(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingLineResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.releaseBillingLineForRedeclaration(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.releaseBillingLineForRedeclaration']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Take back a `creditAndRedeclareLine` before anything reached the insurer (#4738). `lineId` is the **original** regel the pair answers.  In one transaction this deletes the credit regel and the corrected replacement, and puts the original back to the status the insurer\'s answer gave it: APPROVED when the insurer granted part of it, REJECTED when it granted nothing. The original\'s `approvedAmount`, `rejectionCode` and `rejectionReason` were never cleared, so they are what decides which. The consult and the original\'s batch read as they did before the credit, and the original offers its answers again. An audit record names the original and both deleted regels.  Refused with `409` when the original is not CREDITED, when it is not answered by exactly one credit regel and one replacement, or once either of them is in a batch or on an invoice — from then on the pair is on its way to the insurer. 
         * @summary Undo a credit-and-correct pair that has not been sent
         * @param {string} lineId Billing line UUID of the credited original the pair answers
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async undoCreditAndRedeclareLine(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingLineResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.undoCreditAndRedeclareLine(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.undoCreditAndRedeclareLine']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates an existing billing line.  The prestatiecode and the number of kwartieren may be changed while the line has not been committed to a payer — DRAFT or READY, the same two statuses that leave a consult openstaand. Changing either re-prices the line. From IN_BATCH on they are refused; the remaining fields (notes, approvedAmount, receivedAmount) are accepted in any status. 
         * @summary Update billing line
         * @param {string} lineId Billing line UUID
         * @param {UpdateBillingLineRequest} updateBillingLineRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateBillingLine(lineId: string, updateBillingLineRequest: UpdateBillingLineRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingLineResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateBillingLine(lineId, updateBillingLineRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.updateBillingLine']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Credit the difference between what a billing line declared and what the insurer granted for it — the practice accepting the insurer\'s amount and writing off the rest. Nothing is redeclared.  The credited amount is **derived** from the line (`amountEuro - approvedAmount`) and cannot be supplied: a caller-supplied amount would let the UI and the retour disagree about what the insurer granted.  The write-off is a credit line in status `WRITTEN_OFF`: the practice\'s own booking, never batched and never sent to the insurer as a CreditPrestatie.  The original line keeps its APPROVED status — it was paid, partly, and CREDITED means fully reversed. The consult stays settled, so it does not re-enter the declaratie queue.  The other answer to the same partial grant is `creditAndRedeclareLine`, for when the claim itself was wrong: there the whole original is withdrawn and a corrected regel takes its place. This one accepts the insurer\'s amount as final.  Refused with `409` when the line is not APPROVED, when there is no shortfall (no answer recorded, the full amount granted, or more granted than declared), or when the line already carries a credit. 
         * @summary Accept a partial grant and credit the shortfall
         * @param {string} lineId Billing line UUID whose shortfall is written off
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async writeOffLineShortfall(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingLineResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.writeOffLineShortfall(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.writeOffLineShortfall']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Write off the whole declared amount of a billing line the insurer rejected — the practice accepting the rejection with nothing more to do, for instance because the care was already paid under another batch (retourcode 0587). Nothing is redeclared.  The write-off is a credit line in status `WRITTEN_OFF`: the practice\'s own booking, never batched and never sent to the insurer. The original keeps its REJECTED status and the consult stays settled. Its amount is derived and cannot be supplied.  The other answer to a rejection is `releaseBillingLineForRedeclaration` (resend it, correcting it first if need be), which refuses a written-off regel.  A rejected gemeente prestatie is written off the same way, and the write-off keeps its toewijzing-month from being derived and declared again.  Refused with `409` when the line is a credit, is not REJECTED, already carries a credit or write-off, or is a gemeente prestatie whose month has since been declared again. 
         * @summary Accept a rejection and write the regel off in full
         * @param {string} lineId Billing line UUID of the rejected regel to write off
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async writeOffRejectedLine(lineId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingLineResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.writeOffRejectedLine(lineId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingLinesApi.writeOffRejectedLine']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * BillingLinesApi - factory interface
 */
export const BillingLinesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = BillingLinesApiFp(configuration)
    return {
        /**
         * Calculates the amount for a potential billing line without creating it. Useful for previewing billing calculations. 
         * @summary Calculate billing amount
         * @param {BillingCalculationRequest} billingCalculationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        calculateBillingAmount(billingCalculationRequest: BillingCalculationRequest, options?: RawAxiosRequestConfig): AxiosPromise<BillingCalculationResponse> {
            return localVarFp.calculateBillingAmount(billingCalculationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Create a reversing credit line for a billing line that has been committed to an insurer. The credit line copies the original\'s context with negated amounts (isCredit = true, originalLineId set) and is created in READY status so it can flow into a future correction batch or onto a credit invoice. The original line is moved to CREDITED.  Only submitted lines (SUBMITTED, APPROVED, REJECTED) can be credited; an unbatched DRAFT/READY line is discarded by deletion, not credited. 
         * @summary Credit (reverse) a committed billing line
         * @param {string} lineId Billing line UUID to credit
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createCreditLine(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingLineResponse> {
            return localVarFp.createCreditLine(lineId, options).then((request) => request(axios, basePath));
        },
        /**
         * Reverses a declared billing line completely and creates the corrected regel that replaces it, in one transaction — the resubmission of a regel the insurer granted less than was claimed for. Something was paid, so it has to be credited before the claim can go out again.  The original moves to CREDITED and is reversed for its **full** amount, whatever was granted: a partial grant still withdraws the whole original claim. The replacement copies the original\'s pricing context verbatim — so a corrected resend is not silently repriced at the NZa maximum — carries herdeclaratiecode `05` (\"Herdeclaratie na creditering bij zelfde zorgverzekeraar\", COD651-VEKT) and belongs to no batch. Both lines are READY and enter the next batch together, the credit as a CreditPrestatie naming the original\'s referentienummer and the replacement as a DebetPrestatie.  The replacement is editable so the practice can correct what the insurer objected to before it is batched; correcting the prestatiecode or the duration re-prices it.  Accepts an APPROVED line with an approved amount below what was claimed. Refuses a REJECTED line (#4738): nothing was paid, so a credit would reverse a claim the insurer never granted — `releaseBillingLineForRedeclaration` is the resubmission of a rejection. Also refuses a credit line, an already-credited line, and a line that was never in a declared batch — including an imported record of a declaration another system sent, which nothing here can reverse.  Undone, while neither regel has been batched, by `undoCreditAndRedeclareLine`.  Deliberately separate from `createCreditLine`: a caller asking for a credit must not get a replacement regel as a side effect.  Its sibling is `writeOffLineShortfall`, which answers the other half of a partial grant: where the insurer is right and the practice accepts the granted amount, only the shortfall is credited and nothing is declared again. 
         * @summary Credit a declared line in full and produce a corrected replacement
         * @param {string} lineId Billing line UUID to credit and declare again
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        creditAndRedeclareLine(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<CreditAndRedeclareLineResponse> {
            return localVarFp.creditAndRedeclareLine(lineId, options).then((request) => request(axios, basePath));
        },
        /**
         * Deletes a billing line.  Only lines in DRAFT status can be deleted — narrower than what may be edited, because a READY line may already hang off an invoice that has been totalled and sent. 
         * @summary Delete billing line
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteBillingLine(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteBillingLine(lineId, options).then((request) => request(axios, basePath));
        },
        /**
         * Downloads the GDS801 XML declaration message for a specific billing line as an XML file.  The file carries the real Factuurnummer and Referentienummer of the bericht the line belongs to — never placeholders. A line that is not in a declaration batch has no such numbers and is refused with 422.  It registers nothing on the line\'s batch (#4779), but it is recorded as an export of kind `LINE` naming the regel, and is listed among its batch\'s exports (#4781). 
         * @summary Download declaration message XML as a file
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadBillingLineDeclarationMessage(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadBillingLineDeclarationMessage(lineId, options).then((request) => request(axios, basePath));
        },
        /**
         * Excludes a billing line from being included in batch creation. Only lines in DRAFT or READY status that are not already in a batch can be excluded. An optional reason can be provided for audit purposes. 
         * @summary Exclude billing line from billing
         * @param {string} lineId Billing line UUID
         * @param {ExcludeBillingLineRequest} [excludeBillingLineRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        excludeBillingLine(lineId: string, excludeBillingLineRequest?: ExcludeBillingLineRequest, options?: RawAxiosRequestConfig): AxiosPromise<BillingLineResponse> {
            return localVarFp.excludeBillingLine(lineId, excludeBillingLineRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Every billing regel on the afspraak — credits and corrected replacements included — with where its declaration stands now and every batch it was declared in (#4733).  The batch detail lists the regels a batch declared; this is the other axis, the story of one consult across batches. A regel released for redeclaration keeps one declaration per batch it went out in, each with the verdict, granted amount and retourcode that retour gave it, so a consult declared in three batches answers with three declarations in order.  An afspraak with no regel answers with an empty `lines`, not 404: an afgeronde afspraak that has not been declared yet is an ordinary answer. 
         * @summary Declaration history of an afspraak\'s billing regels
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentDeclarationHistory(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentDeclarationHistoryResponse> {
            return localVarFp.getAppointmentDeclarationHistory(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves a single billing line by ID.
         * @summary Get billing line by ID
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingLineById(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingLineResponse> {
            return localVarFp.getBillingLineById(lineId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the GDS801 XML declaration message for a specific billing line. - For lines not yet submitted: returns a preview of what will be sent - For submitted lines: returns the actual XML that was sent  The message carries the real Factuurnummer and Referentienummer of the bericht the line belongs to — never placeholders — so the preview and the submitted export state the same values an insurer reads. A line that is not in a declaration batch has no such numbers and is refused with 422 rather than rendered.  The response includes the formatted XML content and a flag indicating whether this is a preview or the actual submitted message. 
         * @summary Get declaration message XML for a billing line
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBillingLineDeclarationMessage(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<DeclarationMessageResponse> {
            return localVarFp.getBillingLineDeclarationMessage(lineId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns aggregate counts of validation issues for billable appointments in the specified period. Used to populate validation badges in the billing \"Openstaand\" screen.  If no date range is provided, returns validation summary for all unprocessed completed appointments. 
         * @summary Get validation summary for a period
         * @param {string} [periodStart] Start of billing period (inclusive). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (inclusive). If not provided, no upper bound is applied.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getValidationSummary(periodStart?: string, periodEnd?: string, options?: RawAxiosRequestConfig): AxiosPromise<ValidationSummaryDto> {
            return localVarFp.getValidationSummary(periodStart, periodEnd, options).then((request) => request(axios, basePath));
        },
        /**
         * Re-includes a previously excluded billing line so it can be included in batch creation again. Only excluded lines can be included. 
         * @summary Include previously excluded billing line
         * @param {string} lineId Billing line UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        includeBillingLine(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingLineResponse> {
            return localVarFp.includeBillingLine(lineId, options).then((request) => request(axios, basePath));
        },
        /**
         * List billing lines with pagination and filtering.
         * @summary List billing lines
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: serviceDate, status, amountEuro, tariffEuro, quarters, createdAt, updatedAt. Any other field falls back to serviceDate.
         * @param {BillingLineStatus} [status] Filter by status
         * @param {string} [clientId] Filter by client
         * @param {string} [startDate] Filter by service date start
         * @param {string} [endDate] Filter by service date end
         * @param {boolean} [hasDiscrepancy] Filter by amount discrepancy (approved or received differs from billed)
         * @param {boolean} [excludedFromBilling] Filter by exclusion status (true &#x3D; only excluded, false &#x3D; only non-excluded)
         * @param {string} [practitionerId] Filter by practitioner (behandelaar)
         * @param {string} [insurerId] Filter by insurer (verzekeraar)
         * @param {BillingCategory} [billingCategory] Filter by billing category (INSURED, PRIVATE, NON_BILLABLE)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBillingLines(page?: number, size?: number, sort?: string, status?: BillingLineStatus, clientId?: string, startDate?: string, endDate?: string, hasDiscrepancy?: boolean, excludedFromBilling?: boolean, practitionerId?: string, insurerId?: string, billingCategory?: BillingCategory, options?: RawAxiosRequestConfig): AxiosPromise<BillingLineListResponse> {
            return localVarFp.listBillingLines(page, size, sort, status, clientId, startDate, endDate, hasDiscrepancy, excludedFromBilling, practitionerId, insurerId, billingCategory, options).then((request) => request(axios, basePath));
        },
        /**
         * Release a single line the insurer rejected so it can go into a new declaration: it returns to READY with no batch and carries Herdeclaratiecode 03 (COD651-VEKT, \"resubmission\"), which is what the next batch puts on the wire. The batch it came from is left exactly as it is - same number, same status, not deleted - because it is the record of what was sent, and the other lines on it keep their own statuses.  This is the per-line entry point to the same release the batch action performs, for a batch whose rejections the practice wants to handle differently from each other. It is a release, not a creditering: a rejected line was never paid, so there is nothing to reverse.  Only a REJECTED line that is not excluded from billing, is not itself a credit line, and sits on a batch that has actually been declared can be released. A line the insurer approved is credited instead; a line on a batch that never left the building is freed by deleting that batch.  A gemeente prestatie is never released: a month the gemeente rejected is derived again from its registraties into a new line beside the rejected one, so a released line would declare that month twice. 
         * @summary Release a rejected billing line for redeclaration
         * @param {string} lineId Billing line UUID to release for redeclaration
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        releaseBillingLineForRedeclaration(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingLineResponse> {
            return localVarFp.releaseBillingLineForRedeclaration(lineId, options).then((request) => request(axios, basePath));
        },
        /**
         * Take back a `creditAndRedeclareLine` before anything reached the insurer (#4738). `lineId` is the **original** regel the pair answers.  In one transaction this deletes the credit regel and the corrected replacement, and puts the original back to the status the insurer\'s answer gave it: APPROVED when the insurer granted part of it, REJECTED when it granted nothing. The original\'s `approvedAmount`, `rejectionCode` and `rejectionReason` were never cleared, so they are what decides which. The consult and the original\'s batch read as they did before the credit, and the original offers its answers again. An audit record names the original and both deleted regels.  Refused with `409` when the original is not CREDITED, when it is not answered by exactly one credit regel and one replacement, or once either of them is in a batch or on an invoice — from then on the pair is on its way to the insurer. 
         * @summary Undo a credit-and-correct pair that has not been sent
         * @param {string} lineId Billing line UUID of the credited original the pair answers
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        undoCreditAndRedeclareLine(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingLineResponse> {
            return localVarFp.undoCreditAndRedeclareLine(lineId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates an existing billing line.  The prestatiecode and the number of kwartieren may be changed while the line has not been committed to a payer — DRAFT or READY, the same two statuses that leave a consult openstaand. Changing either re-prices the line. From IN_BATCH on they are refused; the remaining fields (notes, approvedAmount, receivedAmount) are accepted in any status. 
         * @summary Update billing line
         * @param {string} lineId Billing line UUID
         * @param {UpdateBillingLineRequest} updateBillingLineRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateBillingLine(lineId: string, updateBillingLineRequest: UpdateBillingLineRequest, options?: RawAxiosRequestConfig): AxiosPromise<BillingLineResponse> {
            return localVarFp.updateBillingLine(lineId, updateBillingLineRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Credit the difference between what a billing line declared and what the insurer granted for it — the practice accepting the insurer\'s amount and writing off the rest. Nothing is redeclared.  The credited amount is **derived** from the line (`amountEuro - approvedAmount`) and cannot be supplied: a caller-supplied amount would let the UI and the retour disagree about what the insurer granted.  The write-off is a credit line in status `WRITTEN_OFF`: the practice\'s own booking, never batched and never sent to the insurer as a CreditPrestatie.  The original line keeps its APPROVED status — it was paid, partly, and CREDITED means fully reversed. The consult stays settled, so it does not re-enter the declaratie queue.  The other answer to the same partial grant is `creditAndRedeclareLine`, for when the claim itself was wrong: there the whole original is withdrawn and a corrected regel takes its place. This one accepts the insurer\'s amount as final.  Refused with `409` when the line is not APPROVED, when there is no shortfall (no answer recorded, the full amount granted, or more granted than declared), or when the line already carries a credit. 
         * @summary Accept a partial grant and credit the shortfall
         * @param {string} lineId Billing line UUID whose shortfall is written off
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        writeOffLineShortfall(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingLineResponse> {
            return localVarFp.writeOffLineShortfall(lineId, options).then((request) => request(axios, basePath));
        },
        /**
         * Write off the whole declared amount of a billing line the insurer rejected — the practice accepting the rejection with nothing more to do, for instance because the care was already paid under another batch (retourcode 0587). Nothing is redeclared.  The write-off is a credit line in status `WRITTEN_OFF`: the practice\'s own booking, never batched and never sent to the insurer. The original keeps its REJECTED status and the consult stays settled. Its amount is derived and cannot be supplied.  The other answer to a rejection is `releaseBillingLineForRedeclaration` (resend it, correcting it first if need be), which refuses a written-off regel.  A rejected gemeente prestatie is written off the same way, and the write-off keeps its toewijzing-month from being derived and declared again.  Refused with `409` when the line is a credit, is not REJECTED, already carries a credit or write-off, or is a gemeente prestatie whose month has since been declared again. 
         * @summary Accept a rejection and write the regel off in full
         * @param {string} lineId Billing line UUID of the rejected regel to write off
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        writeOffRejectedLine(lineId: string, options?: RawAxiosRequestConfig): AxiosPromise<BillingLineResponse> {
            return localVarFp.writeOffRejectedLine(lineId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * BillingLinesApi - object-oriented interface
 */
export class BillingLinesApi extends BaseAPI {
    /**
     * Calculates the amount for a potential billing line without creating it. Useful for previewing billing calculations. 
     * @summary Calculate billing amount
     * @param {BillingCalculationRequest} billingCalculationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public calculateBillingAmount(billingCalculationRequest: BillingCalculationRequest, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).calculateBillingAmount(billingCalculationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create a reversing credit line for a billing line that has been committed to an insurer. The credit line copies the original\'s context with negated amounts (isCredit = true, originalLineId set) and is created in READY status so it can flow into a future correction batch or onto a credit invoice. The original line is moved to CREDITED.  Only submitted lines (SUBMITTED, APPROVED, REJECTED) can be credited; an unbatched DRAFT/READY line is discarded by deletion, not credited. 
     * @summary Credit (reverse) a committed billing line
     * @param {string} lineId Billing line UUID to credit
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createCreditLine(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).createCreditLine(lineId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reverses a declared billing line completely and creates the corrected regel that replaces it, in one transaction — the resubmission of a regel the insurer granted less than was claimed for. Something was paid, so it has to be credited before the claim can go out again.  The original moves to CREDITED and is reversed for its **full** amount, whatever was granted: a partial grant still withdraws the whole original claim. The replacement copies the original\'s pricing context verbatim — so a corrected resend is not silently repriced at the NZa maximum — carries herdeclaratiecode `05` (\"Herdeclaratie na creditering bij zelfde zorgverzekeraar\", COD651-VEKT) and belongs to no batch. Both lines are READY and enter the next batch together, the credit as a CreditPrestatie naming the original\'s referentienummer and the replacement as a DebetPrestatie.  The replacement is editable so the practice can correct what the insurer objected to before it is batched; correcting the prestatiecode or the duration re-prices it.  Accepts an APPROVED line with an approved amount below what was claimed. Refuses a REJECTED line (#4738): nothing was paid, so a credit would reverse a claim the insurer never granted — `releaseBillingLineForRedeclaration` is the resubmission of a rejection. Also refuses a credit line, an already-credited line, and a line that was never in a declared batch — including an imported record of a declaration another system sent, which nothing here can reverse.  Undone, while neither regel has been batched, by `undoCreditAndRedeclareLine`.  Deliberately separate from `createCreditLine`: a caller asking for a credit must not get a replacement regel as a side effect.  Its sibling is `writeOffLineShortfall`, which answers the other half of a partial grant: where the insurer is right and the practice accepts the granted amount, only the shortfall is credited and nothing is declared again. 
     * @summary Credit a declared line in full and produce a corrected replacement
     * @param {string} lineId Billing line UUID to credit and declare again
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public creditAndRedeclareLine(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).creditAndRedeclareLine(lineId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deletes a billing line.  Only lines in DRAFT status can be deleted — narrower than what may be edited, because a READY line may already hang off an invoice that has been totalled and sent. 
     * @summary Delete billing line
     * @param {string} lineId Billing line UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteBillingLine(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).deleteBillingLine(lineId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Downloads the GDS801 XML declaration message for a specific billing line as an XML file.  The file carries the real Factuurnummer and Referentienummer of the bericht the line belongs to — never placeholders. A line that is not in a declaration batch has no such numbers and is refused with 422.  It registers nothing on the line\'s batch (#4779), but it is recorded as an export of kind `LINE` naming the regel, and is listed among its batch\'s exports (#4781). 
     * @summary Download declaration message XML as a file
     * @param {string} lineId Billing line UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadBillingLineDeclarationMessage(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).downloadBillingLineDeclarationMessage(lineId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Excludes a billing line from being included in batch creation. Only lines in DRAFT or READY status that are not already in a batch can be excluded. An optional reason can be provided for audit purposes. 
     * @summary Exclude billing line from billing
     * @param {string} lineId Billing line UUID
     * @param {ExcludeBillingLineRequest} [excludeBillingLineRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public excludeBillingLine(lineId: string, excludeBillingLineRequest?: ExcludeBillingLineRequest, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).excludeBillingLine(lineId, excludeBillingLineRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every billing regel on the afspraak — credits and corrected replacements included — with where its declaration stands now and every batch it was declared in (#4733).  The batch detail lists the regels a batch declared; this is the other axis, the story of one consult across batches. A regel released for redeclaration keeps one declaration per batch it went out in, each with the verdict, granted amount and retourcode that retour gave it, so a consult declared in three batches answers with three declarations in order.  An afspraak with no regel answers with an empty `lines`, not 404: an afgeronde afspraak that has not been declared yet is an ordinary answer. 
     * @summary Declaration history of an afspraak\'s billing regels
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAppointmentDeclarationHistory(appointmentId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).getAppointmentDeclarationHistory(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves a single billing line by ID.
     * @summary Get billing line by ID
     * @param {string} lineId Billing line UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBillingLineById(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).getBillingLineById(lineId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the GDS801 XML declaration message for a specific billing line. - For lines not yet submitted: returns a preview of what will be sent - For submitted lines: returns the actual XML that was sent  The message carries the real Factuurnummer and Referentienummer of the bericht the line belongs to — never placeholders — so the preview and the submitted export state the same values an insurer reads. A line that is not in a declaration batch has no such numbers and is refused with 422 rather than rendered.  The response includes the formatted XML content and a flag indicating whether this is a preview or the actual submitted message. 
     * @summary Get declaration message XML for a billing line
     * @param {string} lineId Billing line UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBillingLineDeclarationMessage(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).getBillingLineDeclarationMessage(lineId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns aggregate counts of validation issues for billable appointments in the specified period. Used to populate validation badges in the billing \"Openstaand\" screen.  If no date range is provided, returns validation summary for all unprocessed completed appointments. 
     * @summary Get validation summary for a period
     * @param {string} [periodStart] Start of billing period (inclusive). If not provided, no lower bound is applied.
     * @param {string} [periodEnd] End of billing period (inclusive). If not provided, no upper bound is applied.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getValidationSummary(periodStart?: string, periodEnd?: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).getValidationSummary(periodStart, periodEnd, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Re-includes a previously excluded billing line so it can be included in batch creation again. Only excluded lines can be included. 
     * @summary Include previously excluded billing line
     * @param {string} lineId Billing line UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public includeBillingLine(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).includeBillingLine(lineId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List billing lines with pagination and filtering.
     * @summary List billing lines
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;serviceDate,desc\&quot;). Supported fields: serviceDate, status, amountEuro, tariffEuro, quarters, createdAt, updatedAt. Any other field falls back to serviceDate.
     * @param {BillingLineStatus} [status] Filter by status
     * @param {string} [clientId] Filter by client
     * @param {string} [startDate] Filter by service date start
     * @param {string} [endDate] Filter by service date end
     * @param {boolean} [hasDiscrepancy] Filter by amount discrepancy (approved or received differs from billed)
     * @param {boolean} [excludedFromBilling] Filter by exclusion status (true &#x3D; only excluded, false &#x3D; only non-excluded)
     * @param {string} [practitionerId] Filter by practitioner (behandelaar)
     * @param {string} [insurerId] Filter by insurer (verzekeraar)
     * @param {BillingCategory} [billingCategory] Filter by billing category (INSURED, PRIVATE, NON_BILLABLE)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listBillingLines(page?: number, size?: number, sort?: string, status?: BillingLineStatus, clientId?: string, startDate?: string, endDate?: string, hasDiscrepancy?: boolean, excludedFromBilling?: boolean, practitionerId?: string, insurerId?: string, billingCategory?: BillingCategory, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).listBillingLines(page, size, sort, status, clientId, startDate, endDate, hasDiscrepancy, excludedFromBilling, practitionerId, insurerId, billingCategory, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Release a single line the insurer rejected so it can go into a new declaration: it returns to READY with no batch and carries Herdeclaratiecode 03 (COD651-VEKT, \"resubmission\"), which is what the next batch puts on the wire. The batch it came from is left exactly as it is - same number, same status, not deleted - because it is the record of what was sent, and the other lines on it keep their own statuses.  This is the per-line entry point to the same release the batch action performs, for a batch whose rejections the practice wants to handle differently from each other. It is a release, not a creditering: a rejected line was never paid, so there is nothing to reverse.  Only a REJECTED line that is not excluded from billing, is not itself a credit line, and sits on a batch that has actually been declared can be released. A line the insurer approved is credited instead; a line on a batch that never left the building is freed by deleting that batch.  A gemeente prestatie is never released: a month the gemeente rejected is derived again from its registraties into a new line beside the rejected one, so a released line would declare that month twice. 
     * @summary Release a rejected billing line for redeclaration
     * @param {string} lineId Billing line UUID to release for redeclaration
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public releaseBillingLineForRedeclaration(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).releaseBillingLineForRedeclaration(lineId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Take back a `creditAndRedeclareLine` before anything reached the insurer (#4738). `lineId` is the **original** regel the pair answers.  In one transaction this deletes the credit regel and the corrected replacement, and puts the original back to the status the insurer\'s answer gave it: APPROVED when the insurer granted part of it, REJECTED when it granted nothing. The original\'s `approvedAmount`, `rejectionCode` and `rejectionReason` were never cleared, so they are what decides which. The consult and the original\'s batch read as they did before the credit, and the original offers its answers again. An audit record names the original and both deleted regels.  Refused with `409` when the original is not CREDITED, when it is not answered by exactly one credit regel and one replacement, or once either of them is in a batch or on an invoice — from then on the pair is on its way to the insurer. 
     * @summary Undo a credit-and-correct pair that has not been sent
     * @param {string} lineId Billing line UUID of the credited original the pair answers
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public undoCreditAndRedeclareLine(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).undoCreditAndRedeclareLine(lineId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates an existing billing line.  The prestatiecode and the number of kwartieren may be changed while the line has not been committed to a payer — DRAFT or READY, the same two statuses that leave a consult openstaand. Changing either re-prices the line. From IN_BATCH on they are refused; the remaining fields (notes, approvedAmount, receivedAmount) are accepted in any status. 
     * @summary Update billing line
     * @param {string} lineId Billing line UUID
     * @param {UpdateBillingLineRequest} updateBillingLineRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateBillingLine(lineId: string, updateBillingLineRequest: UpdateBillingLineRequest, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).updateBillingLine(lineId, updateBillingLineRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Credit the difference between what a billing line declared and what the insurer granted for it — the practice accepting the insurer\'s amount and writing off the rest. Nothing is redeclared.  The credited amount is **derived** from the line (`amountEuro - approvedAmount`) and cannot be supplied: a caller-supplied amount would let the UI and the retour disagree about what the insurer granted.  The write-off is a credit line in status `WRITTEN_OFF`: the practice\'s own booking, never batched and never sent to the insurer as a CreditPrestatie.  The original line keeps its APPROVED status — it was paid, partly, and CREDITED means fully reversed. The consult stays settled, so it does not re-enter the declaratie queue.  The other answer to the same partial grant is `creditAndRedeclareLine`, for when the claim itself was wrong: there the whole original is withdrawn and a corrected regel takes its place. This one accepts the insurer\'s amount as final.  Refused with `409` when the line is not APPROVED, when there is no shortfall (no answer recorded, the full amount granted, or more granted than declared), or when the line already carries a credit. 
     * @summary Accept a partial grant and credit the shortfall
     * @param {string} lineId Billing line UUID whose shortfall is written off
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public writeOffLineShortfall(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).writeOffLineShortfall(lineId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Write off the whole declared amount of a billing line the insurer rejected — the practice accepting the rejection with nothing more to do, for instance because the care was already paid under another batch (retourcode 0587). Nothing is redeclared.  The write-off is a credit line in status `WRITTEN_OFF`: the practice\'s own booking, never batched and never sent to the insurer. The original keeps its REJECTED status and the consult stays settled. Its amount is derived and cannot be supplied.  The other answer to a rejection is `releaseBillingLineForRedeclaration` (resend it, correcting it first if need be), which refuses a written-off regel.  A rejected gemeente prestatie is written off the same way, and the write-off keeps its toewijzing-month from being derived and declared again.  Refused with `409` when the line is a credit, is not REJECTED, already carries a credit or write-off, or is a gemeente prestatie whose month has since been declared again. 
     * @summary Accept a rejection and write the regel off in full
     * @param {string} lineId Billing line UUID of the rejected regel to write off
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public writeOffRejectedLine(lineId: string, options?: RawAxiosRequestConfig) {
        return BillingLinesApiFp(this.configuration).writeOffRejectedLine(lineId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * BillingSignalsApi - axios parameter creator
 */
export const BillingSignalsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Aggregates the blocking signals on the openstaand (INSURED) consult list per signal definition, so the practice sees a handful of causes instead of one row per blocked consult.  Scope and filters are identical to `GET /api/v1/billing/billable-appointments`: the summary is computed from exactly the consults that endpoint returns, so `readyConsults` and `blockedConsults` are the `readyCount` and `blockedCount` that endpoint reports for the same filters.  It is also where the *ready* half\'s figures come from — `readyClients`, `readyAmount`, `readyUnpricedConsults`. A caller that wants both halves of the split therefore asks once: reading them off a one-row `?status=READY&size=1` page assembled the whole openstaand list a second time to state the same numbers (#3909).  Its `status`, `page`, `size` and `sort` parameters deliberately have no counterpart here. A summary is over the whole filtered set rather than a page of it, and `status` selects the very split this response states.  A consult blocked by two causes is counted under both. `blockedConsults` counts each blocked consult once, so the difference between it and the sum of the causes is the overlap. 
         * @summary Aggregate blocking signals by cause
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBlockingSignalSummary: async (periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/billing/blocking-signals/summary`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (periodStart !== undefined) {
                localVarQueryParameter['periodStart'] = (periodStart as any instanceof Date) ?
                    (periodStart as any).toISOString().substring(0,10) :
                    periodStart;
            }

            if (periodEnd !== undefined) {
                localVarQueryParameter['periodEnd'] = (periodEnd as any instanceof Date) ?
                    (periodEnd as any).toISOString().substring(0,10) :
                    periodEnd;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (insurerIds) {
                localVarQueryParameter['insurerIds'] = insurerIds;
            }

            if (practitionerId !== undefined) {
                localVarQueryParameter['practitionerId'] = practitionerId;
            }

            if (regiebehandelaarId !== undefined) {
                localVarQueryParameter['regiebehandelaarId'] = regiebehandelaarId;
            }

            if (locationId !== undefined) {
                localVarQueryParameter['locationId'] = locationId;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns signals that block invoicing for the given appointment facts. A signal blocks if its entity is one of the facts itself or one of the facts\' environmental dependencies (organization, location, practitioner, contract, insurer).  Used by the billing UI to render validation warnings on the openstaand screen and to gate batch generation. 
         * @summary List blocking signals for appointment facts
         * @param {BlockingSignalsRequest} blockingSignalsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBlockingSignals: async (blockingSignalsRequest: BlockingSignalsRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'blockingSignalsRequest' is not null or undefined
            assertParamExists('listBlockingSignals', 'blockingSignalsRequest', blockingSignalsRequest)
            const localVarPath = `/api/v1/billing/blocking-signals`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(blockingSignalsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * BillingSignalsApi - functional programming interface
 */
export const BillingSignalsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = BillingSignalsApiAxiosParamCreator(configuration)
    return {
        /**
         * Aggregates the blocking signals on the openstaand (INSURED) consult list per signal definition, so the practice sees a handful of causes instead of one row per blocked consult.  Scope and filters are identical to `GET /api/v1/billing/billable-appointments`: the summary is computed from exactly the consults that endpoint returns, so `readyConsults` and `blockedConsults` are the `readyCount` and `blockedCount` that endpoint reports for the same filters.  It is also where the *ready* half\'s figures come from — `readyClients`, `readyAmount`, `readyUnpricedConsults`. A caller that wants both halves of the split therefore asks once: reading them off a one-row `?status=READY&size=1` page assembled the whole openstaand list a second time to state the same numbers (#3909).  Its `status`, `page`, `size` and `sort` parameters deliberately have no counterpart here. A summary is over the whole filtered set rather than a page of it, and `status` selects the very split this response states.  A consult blocked by two causes is counted under both. `blockedConsults` counts each blocked consult once, so the difference between it and the sum of the causes is the overlap. 
         * @summary Aggregate blocking signals by cause
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBlockingSignalSummary(periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BlockingSignalSummaryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBlockingSignalSummary(periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingSignalsApi.getBlockingSignalSummary']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns signals that block invoicing for the given appointment facts. A signal blocks if its entity is one of the facts itself or one of the facts\' environmental dependencies (organization, location, practitioner, contract, insurer).  Used by the billing UI to render validation warnings on the openstaand screen and to gate batch generation. 
         * @summary List blocking signals for appointment facts
         * @param {BlockingSignalsRequest} blockingSignalsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listBlockingSignals(blockingSignalsRequest: BlockingSignalsRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BlockingSignalsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listBlockingSignals(blockingSignalsRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingSignalsApi.listBlockingSignals']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * BillingSignalsApi - factory interface
 */
export const BillingSignalsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = BillingSignalsApiFp(configuration)
    return {
        /**
         * Aggregates the blocking signals on the openstaand (INSURED) consult list per signal definition, so the practice sees a handful of causes instead of one row per blocked consult.  Scope and filters are identical to `GET /api/v1/billing/billable-appointments`: the summary is computed from exactly the consults that endpoint returns, so `readyConsults` and `blockedConsults` are the `readyCount` and `blockedCount` that endpoint reports for the same filters.  It is also where the *ready* half\'s figures come from — `readyClients`, `readyAmount`, `readyUnpricedConsults`. A caller that wants both halves of the split therefore asks once: reading them off a one-row `?status=READY&size=1` page assembled the whole openstaand list a second time to state the same numbers (#3909).  Its `status`, `page`, `size` and `sort` parameters deliberately have no counterpart here. A summary is over the whole filtered set rather than a page of it, and `status` selects the very split this response states.  A consult blocked by two causes is counted under both. `blockedConsults` counts each blocked consult once, so the difference between it and the sum of the causes is the overlap. 
         * @summary Aggregate blocking signals by cause
         * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
         * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
         * @param {string} [clientId] Filter by specific client
         * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
         * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
         * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
         * @param {string} [locationId] Filter by specific location
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBlockingSignalSummary(periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig): AxiosPromise<BlockingSignalSummaryResponse> {
            return localVarFp.getBlockingSignalSummary(periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns signals that block invoicing for the given appointment facts. A signal blocks if its entity is one of the facts itself or one of the facts\' environmental dependencies (organization, location, practitioner, contract, insurer).  Used by the billing UI to render validation warnings on the openstaand screen and to gate batch generation. 
         * @summary List blocking signals for appointment facts
         * @param {BlockingSignalsRequest} blockingSignalsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBlockingSignals(blockingSignalsRequest: BlockingSignalsRequest, options?: RawAxiosRequestConfig): AxiosPromise<BlockingSignalsResponse> {
            return localVarFp.listBlockingSignals(blockingSignalsRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * BillingSignalsApi - object-oriented interface
 */
export class BillingSignalsApi extends BaseAPI {
    /**
     * Aggregates the blocking signals on the openstaand (INSURED) consult list per signal definition, so the practice sees a handful of causes instead of one row per blocked consult.  Scope and filters are identical to `GET /api/v1/billing/billable-appointments`: the summary is computed from exactly the consults that endpoint returns, so `readyConsults` and `blockedConsults` are the `readyCount` and `blockedCount` that endpoint reports for the same filters.  It is also where the *ready* half\'s figures come from — `readyClients`, `readyAmount`, `readyUnpricedConsults`. A caller that wants both halves of the split therefore asks once: reading them off a one-row `?status=READY&size=1` page assembled the whole openstaand list a second time to state the same numbers (#3909).  Its `status`, `page`, `size` and `sort` parameters deliberately have no counterpart here. A summary is over the whole filtered set rather than a page of it, and `status` selects the very split this response states.  A consult blocked by two causes is counted under both. `blockedConsults` counts each blocked consult once, so the difference between it and the sum of the causes is the overlap. 
     * @summary Aggregate blocking signals by cause
     * @param {string} [periodStart] Start of billing period (optional). If not provided, no lower bound is applied.
     * @param {string} [periodEnd] End of billing period (optional). If not provided, no upper bound is applied.
     * @param {string} [clientId] Filter by specific client
     * @param {Array<string>} [insurerIds] Narrow to these verzekeraars. Repeatable; values combine as OR (&#x60;?insurerIds&#x3D;a&amp;insurerIds&#x3D;b&#x60;), which is what the Zorgverzekeraar queue\&#39;s verzekeraar filter sends — a practice declaring to VGZ and CZ together reads one queue instead of switching a single-valued filter back and forth (#4520).  Omitted, or repeated zero times, is **every** verzekeraar — never none.  A consult whose cliënt has no covering polis on its service date names no verzekeraar at all, so any selection excludes it, exactly as the single-valued filter this replaces excluded it. Those are the consults &#x60;billing.missing-insurer&#x60; blocks. 
     * @param {string} [practitionerId] Filter by specific practitioner (behandelaar)
     * @param {string} [regiebehandelaarId] Filter by the regiebehandelaar of the client\&#39;s care trajectory, resolved on each row\&#39;s service date. Rows whose trajectory names no regiebehandelaar are excluded. 
     * @param {string} [locationId] Filter by specific location
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBlockingSignalSummary(periodStart?: string, periodEnd?: string, clientId?: string, insurerIds?: Array<string>, practitionerId?: string, regiebehandelaarId?: string, locationId?: string, options?: RawAxiosRequestConfig) {
        return BillingSignalsApiFp(this.configuration).getBlockingSignalSummary(periodStart, periodEnd, clientId, insurerIds, practitionerId, regiebehandelaarId, locationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns signals that block invoicing for the given appointment facts. A signal blocks if its entity is one of the facts itself or one of the facts\' environmental dependencies (organization, location, practitioner, contract, insurer).  Used by the billing UI to render validation warnings on the openstaand screen and to gate batch generation. 
     * @summary List blocking signals for appointment facts
     * @param {BlockingSignalsRequest} blockingSignalsRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listBlockingSignals(blockingSignalsRequest: BlockingSignalsRequest, options?: RawAxiosRequestConfig) {
        return BillingSignalsApiFp(this.configuration).listBlockingSignals(blockingSignalsRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientBillingSummaryApi - axios parameter creator
 */
export const ClientBillingSummaryApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns a summary of billing-relevant information for a client. Used for hover tooltips on client names in billing tables. 
         * @summary Get client billing summary
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientBillingSummary: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientBillingSummary', 'clientId', clientId)
            const localVarPath = `/api/v1/billing/clients/{clientId}/summary`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientBillingSummaryApi - functional programming interface
 */
export const ClientBillingSummaryApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientBillingSummaryApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns a summary of billing-relevant information for a client. Used for hover tooltips on client names in billing tables. 
         * @summary Get client billing summary
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientBillingSummary(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientBillingSummaryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientBillingSummary(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientBillingSummaryApi.getClientBillingSummary']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientBillingSummaryApi - factory interface
 */
export const ClientBillingSummaryApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientBillingSummaryApiFp(configuration)
    return {
        /**
         * Returns a summary of billing-relevant information for a client. Used for hover tooltips on client names in billing tables. 
         * @summary Get client billing summary
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientBillingSummary(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientBillingSummaryResponse> {
            return localVarFp.getClientBillingSummary(clientId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientBillingSummaryApi - object-oriented interface
 */
export class ClientBillingSummaryApi extends BaseAPI {
    /**
     * Returns a summary of billing-relevant information for a client. Used for hover tooltips on client names in billing tables. 
     * @summary Get client billing summary
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientBillingSummary(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientBillingSummaryApiFp(this.configuration).getClientBillingSummary(clientId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * GemeenteDeclaratiesApi - axios parameter creator
 */
export const GemeenteDeclaratiesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * The Gemeente tab in Declaraties (#4821): every gemeente prestatie the registraties call for as of today and that no batch holds yet — one toewijzing × one calendar month × one productcode, clipped to the toewijzing (#4820) — grouped per **individual gemeente**, because a declaration goes to the gemeente that allocated the care, never to its region.  A prestatie that cannot be declared yet is listed too, with its `ophouding`: what it waits for (a JW305 the gemeente has not confirmed, a product the toewijzing does not determine, no tariff on its dates, …) and in words the practice can act on. It carries no amount, and `createGemeenteBillingBatches` refuses it. The current month is never listed (TR137).  Each gemeente names the CBS jeugdregio it belongs to today, which is what the region filter reads; a gemeente in none names none. Nothing is written: the DRAFT lines follow at batch creation. There is no paging — the set is one organisation\'s undeclared months. 
         * @summary The gemeente prestaties still to be declared, per gemeente
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listGemeenteDeclaraties: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/billing/gemeente-declaraties`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * GemeenteDeclaratiesApi - functional programming interface
 */
export const GemeenteDeclaratiesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = GemeenteDeclaratiesApiAxiosParamCreator(configuration)
    return {
        /**
         * The Gemeente tab in Declaraties (#4821): every gemeente prestatie the registraties call for as of today and that no batch holds yet — one toewijzing × one calendar month × one productcode, clipped to the toewijzing (#4820) — grouped per **individual gemeente**, because a declaration goes to the gemeente that allocated the care, never to its region.  A prestatie that cannot be declared yet is listed too, with its `ophouding`: what it waits for (a JW305 the gemeente has not confirmed, a product the toewijzing does not determine, no tariff on its dates, …) and in words the practice can act on. It carries no amount, and `createGemeenteBillingBatches` refuses it. The current month is never listed (TR137).  Each gemeente names the CBS jeugdregio it belongs to today, which is what the region filter reads; a gemeente in none names none. Nothing is written: the DRAFT lines follow at batch creation. There is no paging — the set is one organisation\'s undeclared months. 
         * @summary The gemeente prestaties still to be declared, per gemeente
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listGemeenteDeclaraties(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GemeenteDeclaratieOverzicht>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listGemeenteDeclaraties(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GemeenteDeclaratiesApi.listGemeenteDeclaraties']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * GemeenteDeclaratiesApi - factory interface
 */
export const GemeenteDeclaratiesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = GemeenteDeclaratiesApiFp(configuration)
    return {
        /**
         * The Gemeente tab in Declaraties (#4821): every gemeente prestatie the registraties call for as of today and that no batch holds yet — one toewijzing × one calendar month × one productcode, clipped to the toewijzing (#4820) — grouped per **individual gemeente**, because a declaration goes to the gemeente that allocated the care, never to its region.  A prestatie that cannot be declared yet is listed too, with its `ophouding`: what it waits for (a JW305 the gemeente has not confirmed, a product the toewijzing does not determine, no tariff on its dates, …) and in words the practice can act on. It carries no amount, and `createGemeenteBillingBatches` refuses it. The current month is never listed (TR137).  Each gemeente names the CBS jeugdregio it belongs to today, which is what the region filter reads; a gemeente in none names none. Nothing is written: the DRAFT lines follow at batch creation. There is no paging — the set is one organisation\'s undeclared months. 
         * @summary The gemeente prestaties still to be declared, per gemeente
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listGemeenteDeclaraties(options?: RawAxiosRequestConfig): AxiosPromise<GemeenteDeclaratieOverzicht> {
            return localVarFp.listGemeenteDeclaraties(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * GemeenteDeclaratiesApi - object-oriented interface
 */
export class GemeenteDeclaratiesApi extends BaseAPI {
    /**
     * The Gemeente tab in Declaraties (#4821): every gemeente prestatie the registraties call for as of today and that no batch holds yet — one toewijzing × one calendar month × one productcode, clipped to the toewijzing (#4820) — grouped per **individual gemeente**, because a declaration goes to the gemeente that allocated the care, never to its region.  A prestatie that cannot be declared yet is listed too, with its `ophouding`: what it waits for (a JW305 the gemeente has not confirmed, a product the toewijzing does not determine, no tariff on its dates, …) and in words the practice can act on. It carries no amount, and `createGemeenteBillingBatches` refuses it. The current month is never listed (TR137).  Each gemeente names the CBS jeugdregio it belongs to today, which is what the region filter reads; a gemeente in none names none. Nothing is written: the DRAFT lines follow at batch creation. There is no paging — the set is one organisation\'s undeclared months. 
     * @summary The gemeente prestaties still to be declared, per gemeente
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listGemeenteDeclaraties(options?: RawAxiosRequestConfig) {
        return GemeenteDeclaratiesApiFp(this.configuration).listGemeenteDeclaraties(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * GemeenteVerantwoordingApi - axios parameter creator
 */
export const GemeenteVerantwoordingApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * The rows `getGemeenteVerantwoording` answers for one gemeente, as a `;`-separated CSV or an XLSX workbook: one row per product, with its volume, eenheid, amount and the number of declared lines summed. It names no cliënt. 
         * @summary Export one gemeente\'s yearly verantwoording
         * @param {number} jaar The calendar year the verantwoording covers
         * @param {string} gemeentecode The CBS gemeentecode the verantwoording is for
         * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportGemeenteVerantwoording: async (jaar: number, gemeentecode: string, format: ConsultExportFormat, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'jaar' is not null or undefined
            assertParamExists('exportGemeenteVerantwoording', 'jaar', jaar)
            // verify required parameter 'gemeentecode' is not null or undefined
            assertParamExists('exportGemeenteVerantwoording', 'gemeentecode', gemeentecode)
            // verify required parameter 'format' is not null or undefined
            assertParamExists('exportGemeenteVerantwoording', 'format', format)
            const localVarPath = `/api/v1/billing/gemeente-verantwoording/export`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (jaar !== undefined) {
                localVarQueryParameter['jaar'] = jaar;
            }

            if (gemeentecode !== undefined) {
                localVarQueryParameter['gemeentecode'] = gemeentecode;
            }

            if (format !== undefined) {
                localVarQueryParameter['format'] = format;
            }

            localVarHeaderParameter['Accept'] = 'text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Per gemeente and per product, the volume and the amount declared in `jaar`, credits netted (#4824, ADR-030 A11). A read over the declared gemeente lines and nothing else, so it equals what was declared to the minute and the cent.  A line counts when the gemeente has been sent it and has not refused it (`SUBMITTED`, `APPROVED`, `CREDITED`); a credit line that has been sent counts against its original, in volume as in amount. A line belongs to the year its period starts in, and a credit to its original\'s. A gemeente nothing was declared to that year is not listed. 
         * @summary The yearly verantwoording per gemeente
         * @param {number} jaar The calendar year the verantwoording covers
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getGemeenteVerantwoording: async (jaar: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'jaar' is not null or undefined
            assertParamExists('getGemeenteVerantwoording', 'jaar', jaar)
            const localVarPath = `/api/v1/billing/gemeente-verantwoording`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (jaar !== undefined) {
                localVarQueryParameter['jaar'] = jaar;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * GemeenteVerantwoordingApi - functional programming interface
 */
export const GemeenteVerantwoordingApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = GemeenteVerantwoordingApiAxiosParamCreator(configuration)
    return {
        /**
         * The rows `getGemeenteVerantwoording` answers for one gemeente, as a `;`-separated CSV or an XLSX workbook: one row per product, with its volume, eenheid, amount and the number of declared lines summed. It names no cliënt. 
         * @summary Export one gemeente\'s yearly verantwoording
         * @param {number} jaar The calendar year the verantwoording covers
         * @param {string} gemeentecode The CBS gemeentecode the verantwoording is for
         * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async exportGemeenteVerantwoording(jaar: number, gemeentecode: string, format: ConsultExportFormat, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.exportGemeenteVerantwoording(jaar, gemeentecode, format, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GemeenteVerantwoordingApi.exportGemeenteVerantwoording']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Per gemeente and per product, the volume and the amount declared in `jaar`, credits netted (#4824, ADR-030 A11). A read over the declared gemeente lines and nothing else, so it equals what was declared to the minute and the cent.  A line counts when the gemeente has been sent it and has not refused it (`SUBMITTED`, `APPROVED`, `CREDITED`); a credit line that has been sent counts against its original, in volume as in amount. A line belongs to the year its period starts in, and a credit to its original\'s. A gemeente nothing was declared to that year is not listed. 
         * @summary The yearly verantwoording per gemeente
         * @param {number} jaar The calendar year the verantwoording covers
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getGemeenteVerantwoording(jaar: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GemeenteVerantwoording>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getGemeenteVerantwoording(jaar, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GemeenteVerantwoordingApi.getGemeenteVerantwoording']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * GemeenteVerantwoordingApi - factory interface
 */
export const GemeenteVerantwoordingApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = GemeenteVerantwoordingApiFp(configuration)
    return {
        /**
         * The rows `getGemeenteVerantwoording` answers for one gemeente, as a `;`-separated CSV or an XLSX workbook: one row per product, with its volume, eenheid, amount and the number of declared lines summed. It names no cliënt. 
         * @summary Export one gemeente\'s yearly verantwoording
         * @param {number} jaar The calendar year the verantwoording covers
         * @param {string} gemeentecode The CBS gemeentecode the verantwoording is for
         * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportGemeenteVerantwoording(jaar: number, gemeentecode: string, format: ConsultExportFormat, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.exportGemeenteVerantwoording(jaar, gemeentecode, format, options).then((request) => request(axios, basePath));
        },
        /**
         * Per gemeente and per product, the volume and the amount declared in `jaar`, credits netted (#4824, ADR-030 A11). A read over the declared gemeente lines and nothing else, so it equals what was declared to the minute and the cent.  A line counts when the gemeente has been sent it and has not refused it (`SUBMITTED`, `APPROVED`, `CREDITED`); a credit line that has been sent counts against its original, in volume as in amount. A line belongs to the year its period starts in, and a credit to its original\'s. A gemeente nothing was declared to that year is not listed. 
         * @summary The yearly verantwoording per gemeente
         * @param {number} jaar The calendar year the verantwoording covers
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getGemeenteVerantwoording(jaar: number, options?: RawAxiosRequestConfig): AxiosPromise<GemeenteVerantwoording> {
            return localVarFp.getGemeenteVerantwoording(jaar, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * GemeenteVerantwoordingApi - object-oriented interface
 */
export class GemeenteVerantwoordingApi extends BaseAPI {
    /**
     * The rows `getGemeenteVerantwoording` answers for one gemeente, as a `;`-separated CSV or an XLSX workbook: one row per product, with its volume, eenheid, amount and the number of declared lines summed. It names no cliënt. 
     * @summary Export one gemeente\'s yearly verantwoording
     * @param {number} jaar The calendar year the verantwoording covers
     * @param {string} gemeentecode The CBS gemeentecode the verantwoording is for
     * @param {ConsultExportFormat} format Which file to produce. Both carry the same columns in the same order.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public exportGemeenteVerantwoording(jaar: number, gemeentecode: string, format: ConsultExportFormat, options?: RawAxiosRequestConfig) {
        return GemeenteVerantwoordingApiFp(this.configuration).exportGemeenteVerantwoording(jaar, gemeentecode, format, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Per gemeente and per product, the volume and the amount declared in `jaar`, credits netted (#4824, ADR-030 A11). A read over the declared gemeente lines and nothing else, so it equals what was declared to the minute and the cent.  A line counts when the gemeente has been sent it and has not refused it (`SUBMITTED`, `APPROVED`, `CREDITED`); a credit line that has been sent counts against its original, in volume as in amount. A line belongs to the year its period starts in, and a credit to its original\'s. A gemeente nothing was declared to that year is not listed. 
     * @summary The yearly verantwoording per gemeente
     * @param {number} jaar The calendar year the verantwoording covers
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getGemeenteVerantwoording(jaar: number, options?: RawAxiosRequestConfig) {
        return GemeenteVerantwoordingApiFp(this.configuration).getGemeenteVerantwoording(jaar, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * InsuranceVerificationApi - axios parameter creator
 */
export const InsuranceVerificationApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns whether the authenticated user\'s organization can perform a COV verification. Used by the frontend to decide whether to offer the verify affordance at all.  Availability is the deployment\'s VECOZO connection (ADR-044) plus the organization\'s own preconditions: an AGB code and a confirmed toestemmingsverklaring. There is no per-organization VECOZO environment and no per-organization certificate.  **Permission required:** `billing.read` 
         * @summary Check whether the current organization can verify insurance via VECOZO COV
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCovAvailability: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/insurance/cov-availability`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Verifies a patient\'s insurance status via VECOZO COV (Controle op Verzekering).  Uses the BSN and birth date to look up the patient\'s current insurance information. The insurer name is resolved from the organization\'s Insurer table using the UZOVI code.  **Permission required:** `billing.read` or equivalent  **Note:** This endpoint uses POST instead of GET to keep the BSN in the request body rather than in the URL, for privacy and logging considerations. 
         * @summary Verify patient insurance status
         * @param {InsuranceVerifyRequest} insuranceVerifyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verifyInsurance: async (insuranceVerifyRequest: InsuranceVerifyRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'insuranceVerifyRequest' is not null or undefined
            assertParamExists('verifyInsurance', 'insuranceVerifyRequest', insuranceVerifyRequest)
            const localVarPath = `/api/v1/insurance/verify`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(insuranceVerifyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Verifies insurance status for multiple patients in a single request.  Limited to 20 patients per batch to prevent abuse and ensure reasonable response times. Results are returned in the same order as the input.  Each individual verification may have a different status - check the `status` field on each result item.  **Permission required:** `billing.read` or equivalent 
         * @summary Verify insurance for multiple patients
         * @param {InsuranceVerifyBatchRequest} insuranceVerifyBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verifyInsuranceBatch: async (insuranceVerifyBatchRequest: InsuranceVerifyBatchRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'insuranceVerifyBatchRequest' is not null or undefined
            assertParamExists('verifyInsuranceBatch', 'insuranceVerifyBatchRequest', insuranceVerifyBatchRequest)
            const localVarPath = `/api/v1/insurance/verify-batch`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(insuranceVerifyBatchRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * InsuranceVerificationApi - functional programming interface
 */
export const InsuranceVerificationApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = InsuranceVerificationApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns whether the authenticated user\'s organization can perform a COV verification. Used by the frontend to decide whether to offer the verify affordance at all.  Availability is the deployment\'s VECOZO connection (ADR-044) plus the organization\'s own preconditions: an AGB code and a confirmed toestemmingsverklaring. There is no per-organization VECOZO environment and no per-organization certificate.  **Permission required:** `billing.read` 
         * @summary Check whether the current organization can verify insurance via VECOZO COV
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCovAvailability(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CovAvailabilityResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCovAvailability(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InsuranceVerificationApi.getCovAvailability']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Verifies a patient\'s insurance status via VECOZO COV (Controle op Verzekering).  Uses the BSN and birth date to look up the patient\'s current insurance information. The insurer name is resolved from the organization\'s Insurer table using the UZOVI code.  **Permission required:** `billing.read` or equivalent  **Note:** This endpoint uses POST instead of GET to keep the BSN in the request body rather than in the URL, for privacy and logging considerations. 
         * @summary Verify patient insurance status
         * @param {InsuranceVerifyRequest} insuranceVerifyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verifyInsurance(insuranceVerifyRequest: InsuranceVerifyRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InsuranceVerifyResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verifyInsurance(insuranceVerifyRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InsuranceVerificationApi.verifyInsurance']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Verifies insurance status for multiple patients in a single request.  Limited to 20 patients per batch to prevent abuse and ensure reasonable response times. Results are returned in the same order as the input.  Each individual verification may have a different status - check the `status` field on each result item.  **Permission required:** `billing.read` or equivalent 
         * @summary Verify insurance for multiple patients
         * @param {InsuranceVerifyBatchRequest} insuranceVerifyBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verifyInsuranceBatch(insuranceVerifyBatchRequest: InsuranceVerifyBatchRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InsuranceVerifyBatchResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verifyInsuranceBatch(insuranceVerifyBatchRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InsuranceVerificationApi.verifyInsuranceBatch']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * InsuranceVerificationApi - factory interface
 */
export const InsuranceVerificationApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = InsuranceVerificationApiFp(configuration)
    return {
        /**
         * Returns whether the authenticated user\'s organization can perform a COV verification. Used by the frontend to decide whether to offer the verify affordance at all.  Availability is the deployment\'s VECOZO connection (ADR-044) plus the organization\'s own preconditions: an AGB code and a confirmed toestemmingsverklaring. There is no per-organization VECOZO environment and no per-organization certificate.  **Permission required:** `billing.read` 
         * @summary Check whether the current organization can verify insurance via VECOZO COV
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCovAvailability(options?: RawAxiosRequestConfig): AxiosPromise<CovAvailabilityResponse> {
            return localVarFp.getCovAvailability(options).then((request) => request(axios, basePath));
        },
        /**
         * Verifies a patient\'s insurance status via VECOZO COV (Controle op Verzekering).  Uses the BSN and birth date to look up the patient\'s current insurance information. The insurer name is resolved from the organization\'s Insurer table using the UZOVI code.  **Permission required:** `billing.read` or equivalent  **Note:** This endpoint uses POST instead of GET to keep the BSN in the request body rather than in the URL, for privacy and logging considerations. 
         * @summary Verify patient insurance status
         * @param {InsuranceVerifyRequest} insuranceVerifyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verifyInsurance(insuranceVerifyRequest: InsuranceVerifyRequest, options?: RawAxiosRequestConfig): AxiosPromise<InsuranceVerifyResponse> {
            return localVarFp.verifyInsurance(insuranceVerifyRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Verifies insurance status for multiple patients in a single request.  Limited to 20 patients per batch to prevent abuse and ensure reasonable response times. Results are returned in the same order as the input.  Each individual verification may have a different status - check the `status` field on each result item.  **Permission required:** `billing.read` or equivalent 
         * @summary Verify insurance for multiple patients
         * @param {InsuranceVerifyBatchRequest} insuranceVerifyBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verifyInsuranceBatch(insuranceVerifyBatchRequest: InsuranceVerifyBatchRequest, options?: RawAxiosRequestConfig): AxiosPromise<InsuranceVerifyBatchResponse> {
            return localVarFp.verifyInsuranceBatch(insuranceVerifyBatchRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * InsuranceVerificationApi - object-oriented interface
 */
export class InsuranceVerificationApi extends BaseAPI {
    /**
     * Returns whether the authenticated user\'s organization can perform a COV verification. Used by the frontend to decide whether to offer the verify affordance at all.  Availability is the deployment\'s VECOZO connection (ADR-044) plus the organization\'s own preconditions: an AGB code and a confirmed toestemmingsverklaring. There is no per-organization VECOZO environment and no per-organization certificate.  **Permission required:** `billing.read` 
     * @summary Check whether the current organization can verify insurance via VECOZO COV
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCovAvailability(options?: RawAxiosRequestConfig) {
        return InsuranceVerificationApiFp(this.configuration).getCovAvailability(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Verifies a patient\'s insurance status via VECOZO COV (Controle op Verzekering).  Uses the BSN and birth date to look up the patient\'s current insurance information. The insurer name is resolved from the organization\'s Insurer table using the UZOVI code.  **Permission required:** `billing.read` or equivalent  **Note:** This endpoint uses POST instead of GET to keep the BSN in the request body rather than in the URL, for privacy and logging considerations. 
     * @summary Verify patient insurance status
     * @param {InsuranceVerifyRequest} insuranceVerifyRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verifyInsurance(insuranceVerifyRequest: InsuranceVerifyRequest, options?: RawAxiosRequestConfig) {
        return InsuranceVerificationApiFp(this.configuration).verifyInsurance(insuranceVerifyRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Verifies insurance status for multiple patients in a single request.  Limited to 20 patients per batch to prevent abuse and ensure reasonable response times. Results are returned in the same order as the input.  Each individual verification may have a different status - check the `status` field on each result item.  **Permission required:** `billing.read` or equivalent 
     * @summary Verify insurance for multiple patients
     * @param {InsuranceVerifyBatchRequest} insuranceVerifyBatchRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verifyInsuranceBatch(insuranceVerifyBatchRequest: InsuranceVerifyBatchRequest, options?: RawAxiosRequestConfig) {
        return InsuranceVerificationApiFp(this.configuration).verifyInsuranceBatch(insuranceVerifyBatchRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * JeugdwetBerichtenApi - axios parameter creator
 */
export const JeugdwetBerichtenApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * The stored document byte for byte, as an attachment — the file a practice uploads to the gemeente while berichten move through the VECOZO portal by hand (#2757). The filename is the bericht\'s `bestandsnaam` (see `IjwBerichtXmlResponse`).  Visibility is that of `getIjwBericht`, and every download is recorded in the access audit (ADR-024) as a DOWNLOAD of the bericht. 
         * @summary Download one logged iJw bericht as its XML file
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadIjwBericht: async (berichtId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'berichtId' is not null or undefined
            assertParamExists('downloadIjwBericht', 'berichtId', berichtId)
            const localVarPath = `/api/v1/jeugdwet/berichten/{berichtId}/xml`
                .replace(`{${"berichtId"}}`, encodeURIComponent(String(berichtId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/xml,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The selected berichten as one ZIP holding one XML file per bericht, each the stored document byte for byte under its `bestandsnaam`. VECOZO\'s Berichtloket takes several berichten of several types at once as files dropped onto its indienscherm, so the practice unpacks the ZIP and drops the files.  Every queued bericht in the selection moves to `GEDOWNLOAD`; one already downloaded or sent is served again unchanged. Each bericht served is recorded in the access audit (ADR-024) as a DOWNLOAD. 
         * @summary Download outgoing iJw berichten together as a ZIP
         * @param {IjwOutboxSelectie} ijwOutboxSelectie 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadIjwOutbox: async (ijwOutboxSelectie: IjwOutboxSelectie, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'ijwOutboxSelectie' is not null or undefined
            assertParamExists('downloadIjwOutbox', 'ijwOutboxSelectie', ijwOutboxSelectie)
            const localVarPath = `/api/v1/jeugdwet/outbox/download`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/zip,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(ijwOutboxSelectie, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * One iJw 3.2 bericht from the message log (#4703, #4704) as it was exchanged, for the in-app viewer. `xml` is the stored document unchanged; the viewer formats it for reading.  A bericht concerning a cliënt the caller may not view is answered exactly as one that does not exist (404), so the response does not reveal that it does. A bericht naming no cliënt — a JW301 still waiting for intake — is visible to anyone holding `billing.read`.  Every view is recorded in the access audit (ADR-024) as a VIEW of the bericht. 
         * @summary View one logged iJw bericht
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getIjwBericht: async (berichtId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'berichtId' is not null or undefined
            assertParamExists('getIjwBericht', 'berichtId', berichtId)
            const localVarPath = `/api/v1/jeugdwet/berichten/{berichtId}`
                .replace(`{${"berichtId"}}`, encodeURIComponent(String(berichtId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The berichtcodes, gemeenten and cliënten the berichten of one direction carry, as `listIjwBerichten`\'s `berichtcode`, `gemeentecode` and `clientId` take them. Only berichten the caller may see count, by the same rule `listIjwBerichten` applies, so no cliënt is named here that its rows would hide. 
         * @summary What the logged iJw berichten of one direction can be narrowed to
         * @param {IjwBerichtDirection} direction 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getIjwBerichtFilters: async (direction: IjwBerichtDirection, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'direction' is not null or undefined
            assertParamExists('getIjwBerichtFilters', 'direction', direction)
            const localVarPath = `/api/v1/jeugdwet/berichten/filters`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (direction !== undefined) {
                localVarQueryParameter['direction'] = direction;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * A received JW301 read for processing (#4812): what it says about the cliënt and the relaties, each toegewezen product, and what the organisation already holds — the cliënt its BSN names, if any, that cliënt\'s Jeugdwet zorgtrajecten, and for each product whether its `ToewijzingNummer` is already a toewijzing here (it is then updated, never duplicated).  Nothing is written. Reading the document is recorded in the access audit (ADR-024) as a VIEW of the bericht, and visibility is that of `getIjwBericht`. 
         * @summary What processing a received JW301 would do
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getJw301Verwerking: async (berichtId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'berichtId' is not null or undefined
            assertParamExists('getJw301Verwerking', 'berichtId', berichtId)
            const localVarPath = `/api/v1/jeugdwet/berichten/{berichtId}/jw301`
                .replace(`{${"berichtId"}}`, encodeURIComponent(String(berichtId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The cliënt\'s Berichtenverkeer (#4812): every logged iJw bericht linked to them, received and built alike, newest `dagtekening` first. A cliënt the caller may not view is answered as not found, as `getIjwBericht` answers a bericht about them. 
         * @summary List the iJw berichten linked to one cliënt
         * @param {string} clientId 
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of berichten per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientIjwBerichten: async (clientId: string, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listClientIjwBerichten', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/jeugdwet-berichten`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The iJw message log (#4703) as the Berichten screen lists it (#4811): one direction at a time, newest `dagtekening` first, each row naming its gemeente and — where the bericht is linked to one — its cliënt.  A bericht about a cliënt the caller may not view (`clients.read.all`, or the behandelteam) is not listed, exactly as `getIjwBericht` answers it as not found; `totalElements` counts only what the caller may see. A bericht naming no cliënt is listed for anyone holding `billing.read`. A row carries nothing from the document itself: reading it goes through `getIjwBericht`, which records the read. 
         * @summary List the organisation\'s logged iJw berichten
         * @param {IjwBerichtDirection} direction Received (&#x60;INBOUND&#x60;, the inbox) or built by the practice (&#x60;OUTBOUND&#x60;)
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of berichten per page
         * @param {Array<IjwBerichtStatus>} [status] Only berichten in one of these statuses; every status when absent
         * @param {string} [gemeentecode] Only berichten exchanged with this gemeente (4-digit CBS code)
         * @param {string} [clientId] Only berichten concerning this cliënt
         * @param {Array<IjwBerichtcode>} [berichtcode] Only berichten of one of these berichtcodes; every code when absent
         * @param {string} [dagtekeningVanaf] Only berichten whose &#x60;dagtekening&#x60; is on or after this date
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listIjwBerichten: async (direction: IjwBerichtDirection, page?: number, size?: number, status?: Array<IjwBerichtStatus>, gemeentecode?: string, clientId?: string, berichtcode?: Array<IjwBerichtcode>, dagtekeningVanaf?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'direction' is not null or undefined
            assertParamExists('listIjwBerichten', 'direction', direction)
            const localVarPath = `/api/v1/jeugdwet/berichten`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (direction !== undefined) {
                localVarQueryParameter['direction'] = direction;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (status) {
                localVarQueryParameter['status'] = status;
            }

            if (gemeentecode !== undefined) {
                localVarQueryParameter['gemeentecode'] = gemeentecode;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (berichtcode) {
                localVarQueryParameter['berichtcode'] = berichtcode;
            }

            if (dagtekeningVanaf !== undefined) {
                localVarQueryParameter['dagtekeningVanaf'] = (dagtekeningVanaf as any instanceof Date) ?
                    (dagtekeningVanaf as any).toISOString().substring(0,10) :
                    dagtekeningVanaf;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The Berichten outbox (#4814): every outgoing iJw 3.2 bericht that is queued or downloaded and not yet marked sent, oldest first. Each is held to its XSD again when listed; one that does not satisfy it carries its `violations` and cannot be downloaded.  A bericht concerning a cliënt the caller may not view is left out. 
         * @summary List the outgoing iJw berichten waiting to go to VECOZO
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listIjwOutbox: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/jeugdwet/outbox`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Records that the practice uploaded the selected berichten to VECOZO: each moves to `VERZONDEN` with the time and the person. What was sent is the stored document the download served, which never changes afterwards, so a later download serves the same bytes.  Only a `GEDOWNLOAD` bericht can be marked sent; the whole selection is refused otherwise. 
         * @summary Mark downloaded iJw berichten as sent
         * @param {IjwOutboxSelectie} ijwOutboxSelectie 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markIjwOutboxVerzonden: async (ijwOutboxSelectie: IjwOutboxSelectie, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'ijwOutboxSelectie' is not null or undefined
            assertParamExists('markIjwOutboxVerzonden', 'ijwOutboxSelectie', ijwOutboxSelectie)
            const localVarPath = `/api/v1/jeugdwet/outbox/verzonden`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(ijwOutboxSelectie, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Uploads what a practice downloaded from the VECOZO portal — a ZIP, or a single XML — and stores every received iJw 3.2 bericht in it in the message log, as exchanged (#4811). Requires the `billing.update` permission. A retour or JW319 is applied as it is stored (#4813): a retour moves the outgoing bericht it answers to `GEACCEPTEERD` or `AFGEKEURD`, a JW319 is linked to the verzoek it answers and queues the practice\'s JW320 in the outbox, and one answering nothing sent through Scrivio is stored as `NIET_GEKOPPELD`. A JW301 is read and acted on separately.  Each file is classified by its own content — the root namespace of its XSD, which pins its `BerichtCode` — never by its filename, and validated against that XSD. **A file that cannot be stored is reported, never fatal**: the per-file `outcome` says which case each file fell into, and the response is `200` whatever the mix.  **Re-uploading is safe.** A bericht the organisation already holds — the same berichtcode from the same gemeente with the same `BerichtIdentificatie` (identificatie and dagtekening) — is reported as `DUPLICATE` and not stored again, so a download that overlaps the previous one changes nothing it already brought in. 
         * @summary Store the iJw berichten of a VECOZO-portal download
         * @param {File} file A ZIP of berichten from the VECOZO portal, or a single iJw XML
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadIjwBerichten: async (file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadIjwBerichten', 'file', file)
            const localVarPath = `/api/v1/jeugdwet/berichten/upload`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Applies a received JW301 (#4812), once, after a person has confirmed what `getJw301Verwerking` showed them:  - **Cliënt.** The cliënt its BSN names. When it names nobody, a cliënt is created from the   message — name, BSN, geboortedatum, geslacht, address and contact details — with every   `Relatie` as a care-circle contact, its soort and volgorde kept and gezag as `gezag`   states it. That needs `clients.create` as well. Nothing is merged into another cliënt. - **Toewijzingen.** One per `ToegewezenProduct`, source `JW301`. A `ToewijzingNummer` the   organisation already holds for this gemeente **updates** that toewijzing — einddatum,   omvang, status, reden wijziging — wherever it is held; a gemeente\'s follow-up JW301   replaces, it never adds. A new one is placed on `zorgtrajectId`, which must be one of the   cliënt\'s Jeugdwet zorgtrajecten; without one, a Jeugdwet zorgtraject is opened for the   cliënt from the earliest ingangsdatum. - **Retour.** A JW302 accepting the message is queued in the outbox as `AANGEMAAKT`.  The bericht is linked to the cliënt, and a JW301 that was waiting for intake becomes `ONTVANGEN`. One from another gemeente than the cliënt\'s verantwoordelijke gemeente is processed all the same, and raises the correction signal on the cliënt (#4749). 
         * @summary Process a received JW301 into the dossier
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {Jw301VerwerkRequest} jw301VerwerkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwerkJw301: async (berichtId: string, jw301VerwerkRequest: Jw301VerwerkRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'berichtId' is not null or undefined
            assertParamExists('verwerkJw301', 'berichtId', berichtId)
            // verify required parameter 'jw301VerwerkRequest' is not null or undefined
            assertParamExists('verwerkJw301', 'jw301VerwerkRequest', jw301VerwerkRequest)
            const localVarPath = `/api/v1/jeugdwet/berichten/{berichtId}/verwerken`
                .replace(`{${"berichtId"}}`, encodeURIComponent(String(berichtId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(jw301VerwerkRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * For an organisation whose iJw stream is on the VECOZO berichtenservice (ADR-049), Scrivio submits each selected bericht itself, oldest first: the stored document as the one file of a zip, routed `JW` / its berichtcode / 3.2 to the gemeente. A heenbericht opens a new conversation; a JW302 or JW320 quotes the `ConversatieId` and `TraceerId` of the JW301 or JW319 it answers. One that VECOZO accepts moves to `VERZONDEN` exactly as marking it sent does, with its envelope and the zip kept; one it does not accept stays in the outbox.  Each bericht is sent and committed on its own, so the response says per bericht what became of it. The selection is refused whole, before anything is sent, when the stream is on the portal, the organisation\'s AGB registration is unknown, or a bericht cannot go over the berichtenservice — see `IjwOutboxBericht.verzendBeletsel`. A JW302 or JW320 whose message arrived by portal upload carries no envelope to quote and stays a download. 
         * @summary Send outgoing iJw berichten over the VECOZO berichtenservice
         * @param {IjwOutboxSelectie} ijwOutboxSelectie 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verzendIjwOutbox: async (ijwOutboxSelectie: IjwOutboxSelectie, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'ijwOutboxSelectie' is not null or undefined
            assertParamExists('verzendIjwOutbox', 'ijwOutboxSelectie', ijwOutboxSelectie)
            const localVarPath = `/api/v1/jeugdwet/outbox/verzenden`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(ijwOutboxSelectie, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * JeugdwetBerichtenApi - functional programming interface
 */
export const JeugdwetBerichtenApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = JeugdwetBerichtenApiAxiosParamCreator(configuration)
    return {
        /**
         * The stored document byte for byte, as an attachment — the file a practice uploads to the gemeente while berichten move through the VECOZO portal by hand (#2757). The filename is the bericht\'s `bestandsnaam` (see `IjwBerichtXmlResponse`).  Visibility is that of `getIjwBericht`, and every download is recorded in the access audit (ADR-024) as a DOWNLOAD of the bericht. 
         * @summary Download one logged iJw bericht as its XML file
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadIjwBericht(berichtId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadIjwBericht(berichtId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.downloadIjwBericht']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The selected berichten as one ZIP holding one XML file per bericht, each the stored document byte for byte under its `bestandsnaam`. VECOZO\'s Berichtloket takes several berichten of several types at once as files dropped onto its indienscherm, so the practice unpacks the ZIP and drops the files.  Every queued bericht in the selection moves to `GEDOWNLOAD`; one already downloaded or sent is served again unchanged. Each bericht served is recorded in the access audit (ADR-024) as a DOWNLOAD. 
         * @summary Download outgoing iJw berichten together as a ZIP
         * @param {IjwOutboxSelectie} ijwOutboxSelectie 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadIjwOutbox(ijwOutboxSelectie: IjwOutboxSelectie, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadIjwOutbox(ijwOutboxSelectie, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.downloadIjwOutbox']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * One iJw 3.2 bericht from the message log (#4703, #4704) as it was exchanged, for the in-app viewer. `xml` is the stored document unchanged; the viewer formats it for reading.  A bericht concerning a cliënt the caller may not view is answered exactly as one that does not exist (404), so the response does not reveal that it does. A bericht naming no cliënt — a JW301 still waiting for intake — is visible to anyone holding `billing.read`.  Every view is recorded in the access audit (ADR-024) as a VIEW of the bericht. 
         * @summary View one logged iJw bericht
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getIjwBericht(berichtId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IjwBerichtXmlResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getIjwBericht(berichtId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.getIjwBericht']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The berichtcodes, gemeenten and cliënten the berichten of one direction carry, as `listIjwBerichten`\'s `berichtcode`, `gemeentecode` and `clientId` take them. Only berichten the caller may see count, by the same rule `listIjwBerichten` applies, so no cliënt is named here that its rows would hide. 
         * @summary What the logged iJw berichten of one direction can be narrowed to
         * @param {IjwBerichtDirection} direction 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getIjwBerichtFilters(direction: IjwBerichtDirection, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IjwBerichtFilters>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getIjwBerichtFilters(direction, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.getIjwBerichtFilters']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * A received JW301 read for processing (#4812): what it says about the cliënt and the relaties, each toegewezen product, and what the organisation already holds — the cliënt its BSN names, if any, that cliënt\'s Jeugdwet zorgtrajecten, and for each product whether its `ToewijzingNummer` is already a toewijzing here (it is then updated, never duplicated).  Nothing is written. Reading the document is recorded in the access audit (ADR-024) as a VIEW of the bericht, and visibility is that of `getIjwBericht`. 
         * @summary What processing a received JW301 would do
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getJw301Verwerking(berichtId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Jw301Verwerking>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getJw301Verwerking(berichtId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.getJw301Verwerking']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The cliënt\'s Berichtenverkeer (#4812): every logged iJw bericht linked to them, received and built alike, newest `dagtekening` first. A cliënt the caller may not view is answered as not found, as `getIjwBericht` answers a bericht about them. 
         * @summary List the iJw berichten linked to one cliënt
         * @param {string} clientId 
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of berichten per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientIjwBerichten(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IjwBerichtPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientIjwBerichten(clientId, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.listClientIjwBerichten']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The iJw message log (#4703) as the Berichten screen lists it (#4811): one direction at a time, newest `dagtekening` first, each row naming its gemeente and — where the bericht is linked to one — its cliënt.  A bericht about a cliënt the caller may not view (`clients.read.all`, or the behandelteam) is not listed, exactly as `getIjwBericht` answers it as not found; `totalElements` counts only what the caller may see. A bericht naming no cliënt is listed for anyone holding `billing.read`. A row carries nothing from the document itself: reading it goes through `getIjwBericht`, which records the read. 
         * @summary List the organisation\'s logged iJw berichten
         * @param {IjwBerichtDirection} direction Received (&#x60;INBOUND&#x60;, the inbox) or built by the practice (&#x60;OUTBOUND&#x60;)
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of berichten per page
         * @param {Array<IjwBerichtStatus>} [status] Only berichten in one of these statuses; every status when absent
         * @param {string} [gemeentecode] Only berichten exchanged with this gemeente (4-digit CBS code)
         * @param {string} [clientId] Only berichten concerning this cliënt
         * @param {Array<IjwBerichtcode>} [berichtcode] Only berichten of one of these berichtcodes; every code when absent
         * @param {string} [dagtekeningVanaf] Only berichten whose &#x60;dagtekening&#x60; is on or after this date
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listIjwBerichten(direction: IjwBerichtDirection, page?: number, size?: number, status?: Array<IjwBerichtStatus>, gemeentecode?: string, clientId?: string, berichtcode?: Array<IjwBerichtcode>, dagtekeningVanaf?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IjwBerichtPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listIjwBerichten(direction, page, size, status, gemeentecode, clientId, berichtcode, dagtekeningVanaf, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.listIjwBerichten']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The Berichten outbox (#4814): every outgoing iJw 3.2 bericht that is queued or downloaded and not yet marked sent, oldest first. Each is held to its XSD again when listed; one that does not satisfy it carries its `violations` and cannot be downloaded.  A bericht concerning a cliënt the caller may not view is left out. 
         * @summary List the outgoing iJw berichten waiting to go to VECOZO
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listIjwOutbox(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<IjwOutboxBericht>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listIjwOutbox(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.listIjwOutbox']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Records that the practice uploaded the selected berichten to VECOZO: each moves to `VERZONDEN` with the time and the person. What was sent is the stored document the download served, which never changes afterwards, so a later download serves the same bytes.  Only a `GEDOWNLOAD` bericht can be marked sent; the whole selection is refused otherwise. 
         * @summary Mark downloaded iJw berichten as sent
         * @param {IjwOutboxSelectie} ijwOutboxSelectie 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markIjwOutboxVerzonden(ijwOutboxSelectie: IjwOutboxSelectie, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markIjwOutboxVerzonden(ijwOutboxSelectie, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.markIjwOutboxVerzonden']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Uploads what a practice downloaded from the VECOZO portal — a ZIP, or a single XML — and stores every received iJw 3.2 bericht in it in the message log, as exchanged (#4811). Requires the `billing.update` permission. A retour or JW319 is applied as it is stored (#4813): a retour moves the outgoing bericht it answers to `GEACCEPTEERD` or `AFGEKEURD`, a JW319 is linked to the verzoek it answers and queues the practice\'s JW320 in the outbox, and one answering nothing sent through Scrivio is stored as `NIET_GEKOPPELD`. A JW301 is read and acted on separately.  Each file is classified by its own content — the root namespace of its XSD, which pins its `BerichtCode` — never by its filename, and validated against that XSD. **A file that cannot be stored is reported, never fatal**: the per-file `outcome` says which case each file fell into, and the response is `200` whatever the mix.  **Re-uploading is safe.** A bericht the organisation already holds — the same berichtcode from the same gemeente with the same `BerichtIdentificatie` (identificatie and dagtekening) — is reported as `DUPLICATE` and not stored again, so a download that overlaps the previous one changes nothing it already brought in. 
         * @summary Store the iJw berichten of a VECOZO-portal download
         * @param {File} file A ZIP of berichten from the VECOZO portal, or a single iJw XML
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadIjwBerichten(file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IjwBerichtUploadReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadIjwBerichten(file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.uploadIjwBerichten']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Applies a received JW301 (#4812), once, after a person has confirmed what `getJw301Verwerking` showed them:  - **Cliënt.** The cliënt its BSN names. When it names nobody, a cliënt is created from the   message — name, BSN, geboortedatum, geslacht, address and contact details — with every   `Relatie` as a care-circle contact, its soort and volgorde kept and gezag as `gezag`   states it. That needs `clients.create` as well. Nothing is merged into another cliënt. - **Toewijzingen.** One per `ToegewezenProduct`, source `JW301`. A `ToewijzingNummer` the   organisation already holds for this gemeente **updates** that toewijzing — einddatum,   omvang, status, reden wijziging — wherever it is held; a gemeente\'s follow-up JW301   replaces, it never adds. A new one is placed on `zorgtrajectId`, which must be one of the   cliënt\'s Jeugdwet zorgtrajecten; without one, a Jeugdwet zorgtraject is opened for the   cliënt from the earliest ingangsdatum. - **Retour.** A JW302 accepting the message is queued in the outbox as `AANGEMAAKT`.  The bericht is linked to the cliënt, and a JW301 that was waiting for intake becomes `ONTVANGEN`. One from another gemeente than the cliënt\'s verantwoordelijke gemeente is processed all the same, and raises the correction signal on the cliënt (#4749). 
         * @summary Process a received JW301 into the dossier
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {Jw301VerwerkRequest} jw301VerwerkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verwerkJw301(berichtId: string, jw301VerwerkRequest: Jw301VerwerkRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Jw301VerwerkResultaat>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verwerkJw301(berichtId, jw301VerwerkRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.verwerkJw301']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * For an organisation whose iJw stream is on the VECOZO berichtenservice (ADR-049), Scrivio submits each selected bericht itself, oldest first: the stored document as the one file of a zip, routed `JW` / its berichtcode / 3.2 to the gemeente. A heenbericht opens a new conversation; a JW302 or JW320 quotes the `ConversatieId` and `TraceerId` of the JW301 or JW319 it answers. One that VECOZO accepts moves to `VERZONDEN` exactly as marking it sent does, with its envelope and the zip kept; one it does not accept stays in the outbox.  Each bericht is sent and committed on its own, so the response says per bericht what became of it. The selection is refused whole, before anything is sent, when the stream is on the portal, the organisation\'s AGB registration is unknown, or a bericht cannot go over the berichtenservice — see `IjwOutboxBericht.verzendBeletsel`. A JW302 or JW320 whose message arrived by portal upload carries no envelope to quote and stays a download. 
         * @summary Send outgoing iJw berichten over the VECOZO berichtenservice
         * @param {IjwOutboxSelectie} ijwOutboxSelectie 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verzendIjwOutbox(ijwOutboxSelectie: IjwOutboxSelectie, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<IjwOutboxVerzending>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verzendIjwOutbox(ijwOutboxSelectie, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetBerichtenApi.verzendIjwOutbox']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * JeugdwetBerichtenApi - factory interface
 */
export const JeugdwetBerichtenApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = JeugdwetBerichtenApiFp(configuration)
    return {
        /**
         * The stored document byte for byte, as an attachment — the file a practice uploads to the gemeente while berichten move through the VECOZO portal by hand (#2757). The filename is the bericht\'s `bestandsnaam` (see `IjwBerichtXmlResponse`).  Visibility is that of `getIjwBericht`, and every download is recorded in the access audit (ADR-024) as a DOWNLOAD of the bericht. 
         * @summary Download one logged iJw bericht as its XML file
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadIjwBericht(berichtId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadIjwBericht(berichtId, options).then((request) => request(axios, basePath));
        },
        /**
         * The selected berichten as one ZIP holding one XML file per bericht, each the stored document byte for byte under its `bestandsnaam`. VECOZO\'s Berichtloket takes several berichten of several types at once as files dropped onto its indienscherm, so the practice unpacks the ZIP and drops the files.  Every queued bericht in the selection moves to `GEDOWNLOAD`; one already downloaded or sent is served again unchanged. Each bericht served is recorded in the access audit (ADR-024) as a DOWNLOAD. 
         * @summary Download outgoing iJw berichten together as a ZIP
         * @param {IjwOutboxSelectie} ijwOutboxSelectie 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadIjwOutbox(ijwOutboxSelectie: IjwOutboxSelectie, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadIjwOutbox(ijwOutboxSelectie, options).then((request) => request(axios, basePath));
        },
        /**
         * One iJw 3.2 bericht from the message log (#4703, #4704) as it was exchanged, for the in-app viewer. `xml` is the stored document unchanged; the viewer formats it for reading.  A bericht concerning a cliënt the caller may not view is answered exactly as one that does not exist (404), so the response does not reveal that it does. A bericht naming no cliënt — a JW301 still waiting for intake — is visible to anyone holding `billing.read`.  Every view is recorded in the access audit (ADR-024) as a VIEW of the bericht. 
         * @summary View one logged iJw bericht
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getIjwBericht(berichtId: string, options?: RawAxiosRequestConfig): AxiosPromise<IjwBerichtXmlResponse> {
            return localVarFp.getIjwBericht(berichtId, options).then((request) => request(axios, basePath));
        },
        /**
         * The berichtcodes, gemeenten and cliënten the berichten of one direction carry, as `listIjwBerichten`\'s `berichtcode`, `gemeentecode` and `clientId` take them. Only berichten the caller may see count, by the same rule `listIjwBerichten` applies, so no cliënt is named here that its rows would hide. 
         * @summary What the logged iJw berichten of one direction can be narrowed to
         * @param {IjwBerichtDirection} direction 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getIjwBerichtFilters(direction: IjwBerichtDirection, options?: RawAxiosRequestConfig): AxiosPromise<IjwBerichtFilters> {
            return localVarFp.getIjwBerichtFilters(direction, options).then((request) => request(axios, basePath));
        },
        /**
         * A received JW301 read for processing (#4812): what it says about the cliënt and the relaties, each toegewezen product, and what the organisation already holds — the cliënt its BSN names, if any, that cliënt\'s Jeugdwet zorgtrajecten, and for each product whether its `ToewijzingNummer` is already a toewijzing here (it is then updated, never duplicated).  Nothing is written. Reading the document is recorded in the access audit (ADR-024) as a VIEW of the bericht, and visibility is that of `getIjwBericht`. 
         * @summary What processing a received JW301 would do
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getJw301Verwerking(berichtId: string, options?: RawAxiosRequestConfig): AxiosPromise<Jw301Verwerking> {
            return localVarFp.getJw301Verwerking(berichtId, options).then((request) => request(axios, basePath));
        },
        /**
         * The cliënt\'s Berichtenverkeer (#4812): every logged iJw bericht linked to them, received and built alike, newest `dagtekening` first. A cliënt the caller may not view is answered as not found, as `getIjwBericht` answers a bericht about them. 
         * @summary List the iJw berichten linked to one cliënt
         * @param {string} clientId 
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of berichten per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientIjwBerichten(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<IjwBerichtPage> {
            return localVarFp.listClientIjwBerichten(clientId, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * The iJw message log (#4703) as the Berichten screen lists it (#4811): one direction at a time, newest `dagtekening` first, each row naming its gemeente and — where the bericht is linked to one — its cliënt.  A bericht about a cliënt the caller may not view (`clients.read.all`, or the behandelteam) is not listed, exactly as `getIjwBericht` answers it as not found; `totalElements` counts only what the caller may see. A bericht naming no cliënt is listed for anyone holding `billing.read`. A row carries nothing from the document itself: reading it goes through `getIjwBericht`, which records the read. 
         * @summary List the organisation\'s logged iJw berichten
         * @param {IjwBerichtDirection} direction Received (&#x60;INBOUND&#x60;, the inbox) or built by the practice (&#x60;OUTBOUND&#x60;)
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of berichten per page
         * @param {Array<IjwBerichtStatus>} [status] Only berichten in one of these statuses; every status when absent
         * @param {string} [gemeentecode] Only berichten exchanged with this gemeente (4-digit CBS code)
         * @param {string} [clientId] Only berichten concerning this cliënt
         * @param {Array<IjwBerichtcode>} [berichtcode] Only berichten of one of these berichtcodes; every code when absent
         * @param {string} [dagtekeningVanaf] Only berichten whose &#x60;dagtekening&#x60; is on or after this date
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listIjwBerichten(direction: IjwBerichtDirection, page?: number, size?: number, status?: Array<IjwBerichtStatus>, gemeentecode?: string, clientId?: string, berichtcode?: Array<IjwBerichtcode>, dagtekeningVanaf?: string, options?: RawAxiosRequestConfig): AxiosPromise<IjwBerichtPage> {
            return localVarFp.listIjwBerichten(direction, page, size, status, gemeentecode, clientId, berichtcode, dagtekeningVanaf, options).then((request) => request(axios, basePath));
        },
        /**
         * The Berichten outbox (#4814): every outgoing iJw 3.2 bericht that is queued or downloaded and not yet marked sent, oldest first. Each is held to its XSD again when listed; one that does not satisfy it carries its `violations` and cannot be downloaded.  A bericht concerning a cliënt the caller may not view is left out. 
         * @summary List the outgoing iJw berichten waiting to go to VECOZO
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listIjwOutbox(options?: RawAxiosRequestConfig): AxiosPromise<Array<IjwOutboxBericht>> {
            return localVarFp.listIjwOutbox(options).then((request) => request(axios, basePath));
        },
        /**
         * Records that the practice uploaded the selected berichten to VECOZO: each moves to `VERZONDEN` with the time and the person. What was sent is the stored document the download served, which never changes afterwards, so a later download serves the same bytes.  Only a `GEDOWNLOAD` bericht can be marked sent; the whole selection is refused otherwise. 
         * @summary Mark downloaded iJw berichten as sent
         * @param {IjwOutboxSelectie} ijwOutboxSelectie 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markIjwOutboxVerzonden(ijwOutboxSelectie: IjwOutboxSelectie, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.markIjwOutboxVerzonden(ijwOutboxSelectie, options).then((request) => request(axios, basePath));
        },
        /**
         * Uploads what a practice downloaded from the VECOZO portal — a ZIP, or a single XML — and stores every received iJw 3.2 bericht in it in the message log, as exchanged (#4811). Requires the `billing.update` permission. A retour or JW319 is applied as it is stored (#4813): a retour moves the outgoing bericht it answers to `GEACCEPTEERD` or `AFGEKEURD`, a JW319 is linked to the verzoek it answers and queues the practice\'s JW320 in the outbox, and one answering nothing sent through Scrivio is stored as `NIET_GEKOPPELD`. A JW301 is read and acted on separately.  Each file is classified by its own content — the root namespace of its XSD, which pins its `BerichtCode` — never by its filename, and validated against that XSD. **A file that cannot be stored is reported, never fatal**: the per-file `outcome` says which case each file fell into, and the response is `200` whatever the mix.  **Re-uploading is safe.** A bericht the organisation already holds — the same berichtcode from the same gemeente with the same `BerichtIdentificatie` (identificatie and dagtekening) — is reported as `DUPLICATE` and not stored again, so a download that overlaps the previous one changes nothing it already brought in. 
         * @summary Store the iJw berichten of a VECOZO-portal download
         * @param {File} file A ZIP of berichten from the VECOZO portal, or a single iJw XML
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadIjwBerichten(file: File, options?: RawAxiosRequestConfig): AxiosPromise<IjwBerichtUploadReport> {
            return localVarFp.uploadIjwBerichten(file, options).then((request) => request(axios, basePath));
        },
        /**
         * Applies a received JW301 (#4812), once, after a person has confirmed what `getJw301Verwerking` showed them:  - **Cliënt.** The cliënt its BSN names. When it names nobody, a cliënt is created from the   message — name, BSN, geboortedatum, geslacht, address and contact details — with every   `Relatie` as a care-circle contact, its soort and volgorde kept and gezag as `gezag`   states it. That needs `clients.create` as well. Nothing is merged into another cliënt. - **Toewijzingen.** One per `ToegewezenProduct`, source `JW301`. A `ToewijzingNummer` the   organisation already holds for this gemeente **updates** that toewijzing — einddatum,   omvang, status, reden wijziging — wherever it is held; a gemeente\'s follow-up JW301   replaces, it never adds. A new one is placed on `zorgtrajectId`, which must be one of the   cliënt\'s Jeugdwet zorgtrajecten; without one, a Jeugdwet zorgtraject is opened for the   cliënt from the earliest ingangsdatum. - **Retour.** A JW302 accepting the message is queued in the outbox as `AANGEMAAKT`.  The bericht is linked to the cliënt, and a JW301 that was waiting for intake becomes `ONTVANGEN`. One from another gemeente than the cliënt\'s verantwoordelijke gemeente is processed all the same, and raises the correction signal on the cliënt (#4749). 
         * @summary Process a received JW301 into the dossier
         * @param {string} berichtId The logged bericht\&#39;s id
         * @param {Jw301VerwerkRequest} jw301VerwerkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwerkJw301(berichtId: string, jw301VerwerkRequest: Jw301VerwerkRequest, options?: RawAxiosRequestConfig): AxiosPromise<Jw301VerwerkResultaat> {
            return localVarFp.verwerkJw301(berichtId, jw301VerwerkRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * For an organisation whose iJw stream is on the VECOZO berichtenservice (ADR-049), Scrivio submits each selected bericht itself, oldest first: the stored document as the one file of a zip, routed `JW` / its berichtcode / 3.2 to the gemeente. A heenbericht opens a new conversation; a JW302 or JW320 quotes the `ConversatieId` and `TraceerId` of the JW301 or JW319 it answers. One that VECOZO accepts moves to `VERZONDEN` exactly as marking it sent does, with its envelope and the zip kept; one it does not accept stays in the outbox.  Each bericht is sent and committed on its own, so the response says per bericht what became of it. The selection is refused whole, before anything is sent, when the stream is on the portal, the organisation\'s AGB registration is unknown, or a bericht cannot go over the berichtenservice — see `IjwOutboxBericht.verzendBeletsel`. A JW302 or JW320 whose message arrived by portal upload carries no envelope to quote and stays a download. 
         * @summary Send outgoing iJw berichten over the VECOZO berichtenservice
         * @param {IjwOutboxSelectie} ijwOutboxSelectie 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verzendIjwOutbox(ijwOutboxSelectie: IjwOutboxSelectie, options?: RawAxiosRequestConfig): AxiosPromise<Array<IjwOutboxVerzending>> {
            return localVarFp.verzendIjwOutbox(ijwOutboxSelectie, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * JeugdwetBerichtenApi - object-oriented interface
 */
export class JeugdwetBerichtenApi extends BaseAPI {
    /**
     * The stored document byte for byte, as an attachment — the file a practice uploads to the gemeente while berichten move through the VECOZO portal by hand (#2757). The filename is the bericht\'s `bestandsnaam` (see `IjwBerichtXmlResponse`).  Visibility is that of `getIjwBericht`, and every download is recorded in the access audit (ADR-024) as a DOWNLOAD of the bericht. 
     * @summary Download one logged iJw bericht as its XML file
     * @param {string} berichtId The logged bericht\&#39;s id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadIjwBericht(berichtId: string, options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).downloadIjwBericht(berichtId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The selected berichten as one ZIP holding one XML file per bericht, each the stored document byte for byte under its `bestandsnaam`. VECOZO\'s Berichtloket takes several berichten of several types at once as files dropped onto its indienscherm, so the practice unpacks the ZIP and drops the files.  Every queued bericht in the selection moves to `GEDOWNLOAD`; one already downloaded or sent is served again unchanged. Each bericht served is recorded in the access audit (ADR-024) as a DOWNLOAD. 
     * @summary Download outgoing iJw berichten together as a ZIP
     * @param {IjwOutboxSelectie} ijwOutboxSelectie 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadIjwOutbox(ijwOutboxSelectie: IjwOutboxSelectie, options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).downloadIjwOutbox(ijwOutboxSelectie, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * One iJw 3.2 bericht from the message log (#4703, #4704) as it was exchanged, for the in-app viewer. `xml` is the stored document unchanged; the viewer formats it for reading.  A bericht concerning a cliënt the caller may not view is answered exactly as one that does not exist (404), so the response does not reveal that it does. A bericht naming no cliënt — a JW301 still waiting for intake — is visible to anyone holding `billing.read`.  Every view is recorded in the access audit (ADR-024) as a VIEW of the bericht. 
     * @summary View one logged iJw bericht
     * @param {string} berichtId The logged bericht\&#39;s id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getIjwBericht(berichtId: string, options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).getIjwBericht(berichtId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The berichtcodes, gemeenten and cliënten the berichten of one direction carry, as `listIjwBerichten`\'s `berichtcode`, `gemeentecode` and `clientId` take them. Only berichten the caller may see count, by the same rule `listIjwBerichten` applies, so no cliënt is named here that its rows would hide. 
     * @summary What the logged iJw berichten of one direction can be narrowed to
     * @param {IjwBerichtDirection} direction 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getIjwBerichtFilters(direction: IjwBerichtDirection, options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).getIjwBerichtFilters(direction, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * A received JW301 read for processing (#4812): what it says about the cliënt and the relaties, each toegewezen product, and what the organisation already holds — the cliënt its BSN names, if any, that cliënt\'s Jeugdwet zorgtrajecten, and for each product whether its `ToewijzingNummer` is already a toewijzing here (it is then updated, never duplicated).  Nothing is written. Reading the document is recorded in the access audit (ADR-024) as a VIEW of the bericht, and visibility is that of `getIjwBericht`. 
     * @summary What processing a received JW301 would do
     * @param {string} berichtId The logged bericht\&#39;s id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getJw301Verwerking(berichtId: string, options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).getJw301Verwerking(berichtId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The cliënt\'s Berichtenverkeer (#4812): every logged iJw bericht linked to them, received and built alike, newest `dagtekening` first. A cliënt the caller may not view is answered as not found, as `getIjwBericht` answers a bericht about them. 
     * @summary List the iJw berichten linked to one cliënt
     * @param {string} clientId 
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of berichten per page
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientIjwBerichten(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).listClientIjwBerichten(clientId, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The iJw message log (#4703) as the Berichten screen lists it (#4811): one direction at a time, newest `dagtekening` first, each row naming its gemeente and — where the bericht is linked to one — its cliënt.  A bericht about a cliënt the caller may not view (`clients.read.all`, or the behandelteam) is not listed, exactly as `getIjwBericht` answers it as not found; `totalElements` counts only what the caller may see. A bericht naming no cliënt is listed for anyone holding `billing.read`. A row carries nothing from the document itself: reading it goes through `getIjwBericht`, which records the read. 
     * @summary List the organisation\'s logged iJw berichten
     * @param {IjwBerichtDirection} direction Received (&#x60;INBOUND&#x60;, the inbox) or built by the practice (&#x60;OUTBOUND&#x60;)
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of berichten per page
     * @param {Array<IjwBerichtStatus>} [status] Only berichten in one of these statuses; every status when absent
     * @param {string} [gemeentecode] Only berichten exchanged with this gemeente (4-digit CBS code)
     * @param {string} [clientId] Only berichten concerning this cliënt
     * @param {Array<IjwBerichtcode>} [berichtcode] Only berichten of one of these berichtcodes; every code when absent
     * @param {string} [dagtekeningVanaf] Only berichten whose &#x60;dagtekening&#x60; is on or after this date
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listIjwBerichten(direction: IjwBerichtDirection, page?: number, size?: number, status?: Array<IjwBerichtStatus>, gemeentecode?: string, clientId?: string, berichtcode?: Array<IjwBerichtcode>, dagtekeningVanaf?: string, options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).listIjwBerichten(direction, page, size, status, gemeentecode, clientId, berichtcode, dagtekeningVanaf, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The Berichten outbox (#4814): every outgoing iJw 3.2 bericht that is queued or downloaded and not yet marked sent, oldest first. Each is held to its XSD again when listed; one that does not satisfy it carries its `violations` and cannot be downloaded.  A bericht concerning a cliënt the caller may not view is left out. 
     * @summary List the outgoing iJw berichten waiting to go to VECOZO
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listIjwOutbox(options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).listIjwOutbox(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Records that the practice uploaded the selected berichten to VECOZO: each moves to `VERZONDEN` with the time and the person. What was sent is the stored document the download served, which never changes afterwards, so a later download serves the same bytes.  Only a `GEDOWNLOAD` bericht can be marked sent; the whole selection is refused otherwise. 
     * @summary Mark downloaded iJw berichten as sent
     * @param {IjwOutboxSelectie} ijwOutboxSelectie 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public markIjwOutboxVerzonden(ijwOutboxSelectie: IjwOutboxSelectie, options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).markIjwOutboxVerzonden(ijwOutboxSelectie, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Uploads what a practice downloaded from the VECOZO portal — a ZIP, or a single XML — and stores every received iJw 3.2 bericht in it in the message log, as exchanged (#4811). Requires the `billing.update` permission. A retour or JW319 is applied as it is stored (#4813): a retour moves the outgoing bericht it answers to `GEACCEPTEERD` or `AFGEKEURD`, a JW319 is linked to the verzoek it answers and queues the practice\'s JW320 in the outbox, and one answering nothing sent through Scrivio is stored as `NIET_GEKOPPELD`. A JW301 is read and acted on separately.  Each file is classified by its own content — the root namespace of its XSD, which pins its `BerichtCode` — never by its filename, and validated against that XSD. **A file that cannot be stored is reported, never fatal**: the per-file `outcome` says which case each file fell into, and the response is `200` whatever the mix.  **Re-uploading is safe.** A bericht the organisation already holds — the same berichtcode from the same gemeente with the same `BerichtIdentificatie` (identificatie and dagtekening) — is reported as `DUPLICATE` and not stored again, so a download that overlaps the previous one changes nothing it already brought in. 
     * @summary Store the iJw berichten of a VECOZO-portal download
     * @param {File} file A ZIP of berichten from the VECOZO portal, or a single iJw XML
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadIjwBerichten(file: File, options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).uploadIjwBerichten(file, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Applies a received JW301 (#4812), once, after a person has confirmed what `getJw301Verwerking` showed them:  - **Cliënt.** The cliënt its BSN names. When it names nobody, a cliënt is created from the   message — name, BSN, geboortedatum, geslacht, address and contact details — with every   `Relatie` as a care-circle contact, its soort and volgorde kept and gezag as `gezag`   states it. That needs `clients.create` as well. Nothing is merged into another cliënt. - **Toewijzingen.** One per `ToegewezenProduct`, source `JW301`. A `ToewijzingNummer` the   organisation already holds for this gemeente **updates** that toewijzing — einddatum,   omvang, status, reden wijziging — wherever it is held; a gemeente\'s follow-up JW301   replaces, it never adds. A new one is placed on `zorgtrajectId`, which must be one of the   cliënt\'s Jeugdwet zorgtrajecten; without one, a Jeugdwet zorgtraject is opened for the   cliënt from the earliest ingangsdatum. - **Retour.** A JW302 accepting the message is queued in the outbox as `AANGEMAAKT`.  The bericht is linked to the cliënt, and a JW301 that was waiting for intake becomes `ONTVANGEN`. One from another gemeente than the cliënt\'s verantwoordelijke gemeente is processed all the same, and raises the correction signal on the cliënt (#4749). 
     * @summary Process a received JW301 into the dossier
     * @param {string} berichtId The logged bericht\&#39;s id
     * @param {Jw301VerwerkRequest} jw301VerwerkRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verwerkJw301(berichtId: string, jw301VerwerkRequest: Jw301VerwerkRequest, options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).verwerkJw301(berichtId, jw301VerwerkRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * For an organisation whose iJw stream is on the VECOZO berichtenservice (ADR-049), Scrivio submits each selected bericht itself, oldest first: the stored document as the one file of a zip, routed `JW` / its berichtcode / 3.2 to the gemeente. A heenbericht opens a new conversation; a JW302 or JW320 quotes the `ConversatieId` and `TraceerId` of the JW301 or JW319 it answers. One that VECOZO accepts moves to `VERZONDEN` exactly as marking it sent does, with its envelope and the zip kept; one it does not accept stays in the outbox.  Each bericht is sent and committed on its own, so the response says per bericht what became of it. The selection is refused whole, before anything is sent, when the stream is on the portal, the organisation\'s AGB registration is unknown, or a bericht cannot go over the berichtenservice — see `IjwOutboxBericht.verzendBeletsel`. A JW302 or JW320 whose message arrived by portal upload carries no envelope to quote and stays a download. 
     * @summary Send outgoing iJw berichten over the VECOZO berichtenservice
     * @param {IjwOutboxSelectie} ijwOutboxSelectie 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verzendIjwOutbox(ijwOutboxSelectie: IjwOutboxSelectie, options?: RawAxiosRequestConfig) {
        return JeugdwetBerichtenApiFp(this.configuration).verzendIjwOutbox(ijwOutboxSelectie, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * JeugdwetToewijzingsverzoekenApi - axios parameter creator
 */
export const JeugdwetToewijzingsverzoekenApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * What the \"Toewijzing aanvragen\" dialog offers (#4810): the gemeente the JW315 is addressed to — the cliënt\'s confirmed Jeugdwet verantwoordelijke gemeente today (#4749) — and every live, active Jeugdwet gemeente contract that covers it on `ingangsdatum`, with the products a specifiek request is chosen from and the categorieën (with their budget in force) an aspecifiek one is.  **Nothing here refuses.** No confirmed gemeente, or no contract covering it, is a `waarschuwingen` entry; the request itself decides what it can and cannot be sent without. `wordtAchttienOp` lets the dialog warn when the cliënt turns 18 within the period asked for (ADR-030 A6). 
         * @summary What a toewijzing request for this zorgtraject can be made of
         * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
         * @param {string} ingangsdatum The requested ingangsdatum, which the contracts must be in force on
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getToewijzingsverzoekOpties: async (zorgtrajectId: string, ingangsdatum: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('getToewijzingsverzoekOpties', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'ingangsdatum' is not null or undefined
            assertParamExists('getToewijzingsverzoekOpties', 'ingangsdatum', ingangsdatum)
            const localVarPath = `/api/v1/jeugdwet/zorgtrajecten/{zorgtrajectId}/toewijzingsverzoek`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (ingangsdatum !== undefined) {
                localVarQueryParameter['ingangsdatum'] = (ingangsdatum as any instanceof Date) ?
                    (ingangsdatum as any).toISOString().substring(0,10) :
                    ingangsdatum;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Builds the JW315 verzoek om toewijzing for this zorgtraject (#4810), addressed to the cliënt\'s confirmed Jeugdwet verantwoordelijke gemeente today, and queues it in the Berichten outbox (#4814) — no second screen.  A specifiek request names a product of the chosen contract and its omvang; an aspecifiek one names a categorie of the contract and a **budget, required**, which may not exceed the contract\'s budget for that categorie on the ingangsdatum (#4805). When a contract covers the gemeente the request must name one and choose from it; only when none does is a typed product accepted. Every violation is a `400` with one `errors` entry per field.  A JW315 the dossier cannot fill — no confirmed gemeente, no BSN, no geboortedatum, no verwijzer iJw has a code for, no organisation AGB — is a `422` naming what is missing. 
         * @summary Request a toewijzing — build a JW315 and queue it in the outbox
         * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
         * @param {ToewijzingsverzoekRequest} toewijzingsverzoekRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        requestToewijzing: async (zorgtrajectId: string, toewijzingsverzoekRequest: ToewijzingsverzoekRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('requestToewijzing', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'toewijzingsverzoekRequest' is not null or undefined
            assertParamExists('requestToewijzing', 'toewijzingsverzoekRequest', toewijzingsverzoekRequest)
            const localVarPath = `/api/v1/jeugdwet/zorgtrajecten/{zorgtrajectId}/toewijzingsverzoek`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(toewijzingsverzoekRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Builds the JW317 verzoek om wijziging for this toewijzing, addressed to the gemeente that allocated it, and queues it in the Berichten outbox — no second screen. The toewijzing\'s `wijzigingsverzoek` then says where it stands, through the gemeente\'s JW318 and JW319.  The JW317 states the cliënt\'s whole set of products with that gemeente as it should become: every other toewijzing of the cliënt from it that still runs on `gewensteIngangsdatum` goes along unchanged. What may change is what iJw lets a `TeWijzigenProduct` carry — `einddatum`, `omvang`, `budget`. A different `productCode`, in the toewijzing\'s own categorie and only on one that names a product, is asked for as a new product instead, which leaves this toewijzing out for the gemeente to end; when a Jeugdwet contract covers the gemeente the product must be one it holds, and an `omvang` is required.  Every violation of a rule OpenAPI cannot express — a date before the toewijzing\'s ingangsdatum or an einddatum before the gewenste ingangsdatum, a product the contract does not hold, a request that changes nothing (`wijziging`) — is a `400` with one `errors` entry per field. 
         * @summary Request a change to a toewijzing — build a JW317 and queue it in the outbox
         * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
         * @param {string} toewijzingId The toewijzing on that zorgtraject to change
         * @param {ToewijzingWijzigingsverzoekRequest} toewijzingWijzigingsverzoekRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        requestToewijzingWijziging: async (zorgtrajectId: string, toewijzingId: string, toewijzingWijzigingsverzoekRequest: ToewijzingWijzigingsverzoekRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('requestToewijzingWijziging', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'toewijzingId' is not null or undefined
            assertParamExists('requestToewijzingWijziging', 'toewijzingId', toewijzingId)
            // verify required parameter 'toewijzingWijzigingsverzoekRequest' is not null or undefined
            assertParamExists('requestToewijzingWijziging', 'toewijzingWijzigingsverzoekRequest', toewijzingWijzigingsverzoekRequest)
            const localVarPath = `/api/v1/jeugdwet/zorgtrajecten/{zorgtrajectId}/toewijzingen/{toewijzingId}/wijzigingsverzoek`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)))
                .replace(`{${"toewijzingId"}}`, encodeURIComponent(String(toewijzingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(toewijzingWijzigingsverzoekRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * JeugdwetToewijzingsverzoekenApi - functional programming interface
 */
export const JeugdwetToewijzingsverzoekenApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = JeugdwetToewijzingsverzoekenApiAxiosParamCreator(configuration)
    return {
        /**
         * What the \"Toewijzing aanvragen\" dialog offers (#4810): the gemeente the JW315 is addressed to — the cliënt\'s confirmed Jeugdwet verantwoordelijke gemeente today (#4749) — and every live, active Jeugdwet gemeente contract that covers it on `ingangsdatum`, with the products a specifiek request is chosen from and the categorieën (with their budget in force) an aspecifiek one is.  **Nothing here refuses.** No confirmed gemeente, or no contract covering it, is a `waarschuwingen` entry; the request itself decides what it can and cannot be sent without. `wordtAchttienOp` lets the dialog warn when the cliënt turns 18 within the period asked for (ADR-030 A6). 
         * @summary What a toewijzing request for this zorgtraject can be made of
         * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
         * @param {string} ingangsdatum The requested ingangsdatum, which the contracts must be in force on
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getToewijzingsverzoekOpties(zorgtrajectId: string, ingangsdatum: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ToewijzingsverzoekOpties>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getToewijzingsverzoekOpties(zorgtrajectId, ingangsdatum, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetToewijzingsverzoekenApi.getToewijzingsverzoekOpties']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Builds the JW315 verzoek om toewijzing for this zorgtraject (#4810), addressed to the cliënt\'s confirmed Jeugdwet verantwoordelijke gemeente today, and queues it in the Berichten outbox (#4814) — no second screen.  A specifiek request names a product of the chosen contract and its omvang; an aspecifiek one names a categorie of the contract and a **budget, required**, which may not exceed the contract\'s budget for that categorie on the ingangsdatum (#4805). When a contract covers the gemeente the request must name one and choose from it; only when none does is a typed product accepted. Every violation is a `400` with one `errors` entry per field.  A JW315 the dossier cannot fill — no confirmed gemeente, no BSN, no geboortedatum, no verwijzer iJw has a code for, no organisation AGB — is a `422` naming what is missing. 
         * @summary Request a toewijzing — build a JW315 and queue it in the outbox
         * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
         * @param {ToewijzingsverzoekRequest} toewijzingsverzoekRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async requestToewijzing(zorgtrajectId: string, toewijzingsverzoekRequest: ToewijzingsverzoekRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ToewijzingsverzoekResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.requestToewijzing(zorgtrajectId, toewijzingsverzoekRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetToewijzingsverzoekenApi.requestToewijzing']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Builds the JW317 verzoek om wijziging for this toewijzing, addressed to the gemeente that allocated it, and queues it in the Berichten outbox — no second screen. The toewijzing\'s `wijzigingsverzoek` then says where it stands, through the gemeente\'s JW318 and JW319.  The JW317 states the cliënt\'s whole set of products with that gemeente as it should become: every other toewijzing of the cliënt from it that still runs on `gewensteIngangsdatum` goes along unchanged. What may change is what iJw lets a `TeWijzigenProduct` carry — `einddatum`, `omvang`, `budget`. A different `productCode`, in the toewijzing\'s own categorie and only on one that names a product, is asked for as a new product instead, which leaves this toewijzing out for the gemeente to end; when a Jeugdwet contract covers the gemeente the product must be one it holds, and an `omvang` is required.  Every violation of a rule OpenAPI cannot express — a date before the toewijzing\'s ingangsdatum or an einddatum before the gewenste ingangsdatum, a product the contract does not hold, a request that changes nothing (`wijziging`) — is a `400` with one `errors` entry per field. 
         * @summary Request a change to a toewijzing — build a JW317 and queue it in the outbox
         * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
         * @param {string} toewijzingId The toewijzing on that zorgtraject to change
         * @param {ToewijzingWijzigingsverzoekRequest} toewijzingWijzigingsverzoekRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async requestToewijzingWijziging(zorgtrajectId: string, toewijzingId: string, toewijzingWijzigingsverzoekRequest: ToewijzingWijzigingsverzoekRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ToewijzingsverzoekResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.requestToewijzingWijziging(zorgtrajectId, toewijzingId, toewijzingWijzigingsverzoekRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JeugdwetToewijzingsverzoekenApi.requestToewijzingWijziging']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * JeugdwetToewijzingsverzoekenApi - factory interface
 */
export const JeugdwetToewijzingsverzoekenApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = JeugdwetToewijzingsverzoekenApiFp(configuration)
    return {
        /**
         * What the \"Toewijzing aanvragen\" dialog offers (#4810): the gemeente the JW315 is addressed to — the cliënt\'s confirmed Jeugdwet verantwoordelijke gemeente today (#4749) — and every live, active Jeugdwet gemeente contract that covers it on `ingangsdatum`, with the products a specifiek request is chosen from and the categorieën (with their budget in force) an aspecifiek one is.  **Nothing here refuses.** No confirmed gemeente, or no contract covering it, is a `waarschuwingen` entry; the request itself decides what it can and cannot be sent without. `wordtAchttienOp` lets the dialog warn when the cliënt turns 18 within the period asked for (ADR-030 A6). 
         * @summary What a toewijzing request for this zorgtraject can be made of
         * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
         * @param {string} ingangsdatum The requested ingangsdatum, which the contracts must be in force on
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getToewijzingsverzoekOpties(zorgtrajectId: string, ingangsdatum: string, options?: RawAxiosRequestConfig): AxiosPromise<ToewijzingsverzoekOpties> {
            return localVarFp.getToewijzingsverzoekOpties(zorgtrajectId, ingangsdatum, options).then((request) => request(axios, basePath));
        },
        /**
         * Builds the JW315 verzoek om toewijzing for this zorgtraject (#4810), addressed to the cliënt\'s confirmed Jeugdwet verantwoordelijke gemeente today, and queues it in the Berichten outbox (#4814) — no second screen.  A specifiek request names a product of the chosen contract and its omvang; an aspecifiek one names a categorie of the contract and a **budget, required**, which may not exceed the contract\'s budget for that categorie on the ingangsdatum (#4805). When a contract covers the gemeente the request must name one and choose from it; only when none does is a typed product accepted. Every violation is a `400` with one `errors` entry per field.  A JW315 the dossier cannot fill — no confirmed gemeente, no BSN, no geboortedatum, no verwijzer iJw has a code for, no organisation AGB — is a `422` naming what is missing. 
         * @summary Request a toewijzing — build a JW315 and queue it in the outbox
         * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
         * @param {ToewijzingsverzoekRequest} toewijzingsverzoekRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        requestToewijzing(zorgtrajectId: string, toewijzingsverzoekRequest: ToewijzingsverzoekRequest, options?: RawAxiosRequestConfig): AxiosPromise<ToewijzingsverzoekResponse> {
            return localVarFp.requestToewijzing(zorgtrajectId, toewijzingsverzoekRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Builds the JW317 verzoek om wijziging for this toewijzing, addressed to the gemeente that allocated it, and queues it in the Berichten outbox — no second screen. The toewijzing\'s `wijzigingsverzoek` then says where it stands, through the gemeente\'s JW318 and JW319.  The JW317 states the cliënt\'s whole set of products with that gemeente as it should become: every other toewijzing of the cliënt from it that still runs on `gewensteIngangsdatum` goes along unchanged. What may change is what iJw lets a `TeWijzigenProduct` carry — `einddatum`, `omvang`, `budget`. A different `productCode`, in the toewijzing\'s own categorie and only on one that names a product, is asked for as a new product instead, which leaves this toewijzing out for the gemeente to end; when a Jeugdwet contract covers the gemeente the product must be one it holds, and an `omvang` is required.  Every violation of a rule OpenAPI cannot express — a date before the toewijzing\'s ingangsdatum or an einddatum before the gewenste ingangsdatum, a product the contract does not hold, a request that changes nothing (`wijziging`) — is a `400` with one `errors` entry per field. 
         * @summary Request a change to a toewijzing — build a JW317 and queue it in the outbox
         * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
         * @param {string} toewijzingId The toewijzing on that zorgtraject to change
         * @param {ToewijzingWijzigingsverzoekRequest} toewijzingWijzigingsverzoekRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        requestToewijzingWijziging(zorgtrajectId: string, toewijzingId: string, toewijzingWijzigingsverzoekRequest: ToewijzingWijzigingsverzoekRequest, options?: RawAxiosRequestConfig): AxiosPromise<ToewijzingsverzoekResponse> {
            return localVarFp.requestToewijzingWijziging(zorgtrajectId, toewijzingId, toewijzingWijzigingsverzoekRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * JeugdwetToewijzingsverzoekenApi - object-oriented interface
 */
export class JeugdwetToewijzingsverzoekenApi extends BaseAPI {
    /**
     * What the \"Toewijzing aanvragen\" dialog offers (#4810): the gemeente the JW315 is addressed to — the cliënt\'s confirmed Jeugdwet verantwoordelijke gemeente today (#4749) — and every live, active Jeugdwet gemeente contract that covers it on `ingangsdatum`, with the products a specifiek request is chosen from and the categorieën (with their budget in force) an aspecifiek one is.  **Nothing here refuses.** No confirmed gemeente, or no contract covering it, is a `waarschuwingen` entry; the request itself decides what it can and cannot be sent without. `wordtAchttienOp` lets the dialog warn when the cliënt turns 18 within the period asked for (ADR-030 A6). 
     * @summary What a toewijzing request for this zorgtraject can be made of
     * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
     * @param {string} ingangsdatum The requested ingangsdatum, which the contracts must be in force on
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getToewijzingsverzoekOpties(zorgtrajectId: string, ingangsdatum: string, options?: RawAxiosRequestConfig) {
        return JeugdwetToewijzingsverzoekenApiFp(this.configuration).getToewijzingsverzoekOpties(zorgtrajectId, ingangsdatum, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Builds the JW315 verzoek om toewijzing for this zorgtraject (#4810), addressed to the cliënt\'s confirmed Jeugdwet verantwoordelijke gemeente today, and queues it in the Berichten outbox (#4814) — no second screen.  A specifiek request names a product of the chosen contract and its omvang; an aspecifiek one names a categorie of the contract and a **budget, required**, which may not exceed the contract\'s budget for that categorie on the ingangsdatum (#4805). When a contract covers the gemeente the request must name one and choose from it; only when none does is a typed product accepted. Every violation is a `400` with one `errors` entry per field.  A JW315 the dossier cannot fill — no confirmed gemeente, no BSN, no geboortedatum, no verwijzer iJw has a code for, no organisation AGB — is a `422` naming what is missing. 
     * @summary Request a toewijzing — build a JW315 and queue it in the outbox
     * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
     * @param {ToewijzingsverzoekRequest} toewijzingsverzoekRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public requestToewijzing(zorgtrajectId: string, toewijzingsverzoekRequest: ToewijzingsverzoekRequest, options?: RawAxiosRequestConfig) {
        return JeugdwetToewijzingsverzoekenApiFp(this.configuration).requestToewijzing(zorgtrajectId, toewijzingsverzoekRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Builds the JW317 verzoek om wijziging for this toewijzing, addressed to the gemeente that allocated it, and queues it in the Berichten outbox — no second screen. The toewijzing\'s `wijzigingsverzoek` then says where it stands, through the gemeente\'s JW318 and JW319.  The JW317 states the cliënt\'s whole set of products with that gemeente as it should become: every other toewijzing of the cliënt from it that still runs on `gewensteIngangsdatum` goes along unchanged. What may change is what iJw lets a `TeWijzigenProduct` carry — `einddatum`, `omvang`, `budget`. A different `productCode`, in the toewijzing\'s own categorie and only on one that names a product, is asked for as a new product instead, which leaves this toewijzing out for the gemeente to end; when a Jeugdwet contract covers the gemeente the product must be one it holds, and an `omvang` is required.  Every violation of a rule OpenAPI cannot express — a date before the toewijzing\'s ingangsdatum or an einddatum before the gewenste ingangsdatum, a product the contract does not hold, a request that changes nothing (`wijziging`) — is a `400` with one `errors` entry per field. 
     * @summary Request a change to a toewijzing — build a JW317 and queue it in the outbox
     * @param {string} zorgtrajectId The Jeugdwet zorgtraject the toewijzing is requested for
     * @param {string} toewijzingId The toewijzing on that zorgtraject to change
     * @param {ToewijzingWijzigingsverzoekRequest} toewijzingWijzigingsverzoekRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public requestToewijzingWijziging(zorgtrajectId: string, toewijzingId: string, toewijzingWijzigingsverzoekRequest: ToewijzingWijzigingsverzoekRequest, options?: RawAxiosRequestConfig) {
        return JeugdwetToewijzingsverzoekenApiFp(this.configuration).requestToewijzingWijziging(zorgtrajectId, toewijzingId, toewijzingWijzigingsverzoekRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * SettingsApi - axios parameter creator
 */
export const SettingsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Retrieves a single treatment setting by ID.
         * @summary Get setting by ID
         * @param {string} settingId Setting UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSettingById: async (settingId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'settingId' is not null or undefined
            assertParamExists('getSettingById', 'settingId', settingId)
            const localVarPath = `/api/v1/billing/settings/{settingId}`
                .replace(`{${"settingId"}}`, encodeURIComponent(String(settingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all active treatment settings (behandelsettings). Settings are the unified source of truth for: - NZA billing dimension (for tariff lookup) - Kwaliteitsstatuut info (for practitioner requirements)  Used for dropdowns when creating zorgtrajecten and filtering billing. 
         * @summary List treatment settings
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSettings: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/billing/settings`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SettingsApi - functional programming interface
 */
export const SettingsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SettingsApiAxiosParamCreator(configuration)
    return {
        /**
         * Retrieves a single treatment setting by ID.
         * @summary Get setting by ID
         * @param {string} settingId Setting UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getSettingById(settingId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SettingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getSettingById(settingId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SettingsApi.getSettingById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all active treatment settings (behandelsettings). Settings are the unified source of truth for: - NZA billing dimension (for tariff lookup) - Kwaliteitsstatuut info (for practitioner requirements)  Used for dropdowns when creating zorgtrajecten and filtering billing. 
         * @summary List treatment settings
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listSettings(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<SettingResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listSettings(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SettingsApi.listSettings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SettingsApi - factory interface
 */
export const SettingsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SettingsApiFp(configuration)
    return {
        /**
         * Retrieves a single treatment setting by ID.
         * @summary Get setting by ID
         * @param {string} settingId Setting UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSettingById(settingId: string, options?: RawAxiosRequestConfig): AxiosPromise<SettingResponse> {
            return localVarFp.getSettingById(settingId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all active treatment settings (behandelsettings). Settings are the unified source of truth for: - NZA billing dimension (for tariff lookup) - Kwaliteitsstatuut info (for practitioner requirements)  Used for dropdowns when creating zorgtrajecten and filtering billing. 
         * @summary List treatment settings
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSettings(options?: RawAxiosRequestConfig): AxiosPromise<Array<SettingResponse>> {
            return localVarFp.listSettings(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SettingsApi - object-oriented interface
 */
export class SettingsApi extends BaseAPI {
    /**
     * Retrieves a single treatment setting by ID.
     * @summary Get setting by ID
     * @param {string} settingId Setting UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getSettingById(settingId: string, options?: RawAxiosRequestConfig) {
        return SettingsApiFp(this.configuration).getSettingById(settingId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all active treatment settings (behandelsettings). Settings are the unified source of truth for: - NZA billing dimension (for tariff lookup) - Kwaliteitsstatuut info (for practitioner requirements)  Used for dropdowns when creating zorgtrajecten and filtering billing. 
     * @summary List treatment settings
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listSettings(options?: RawAxiosRequestConfig) {
        return SettingsApiFp(this.configuration).listSettings(options).then((request) => request(this.axios, this.basePath));
    }
}



